import React, { useState, useEffect, useRef, useMemo } from 'react';
import { supabase } from './supabase';
import { cacheInterventions, getCachedInterventions, queueOp, flushQueue, queueCount, newTempId } from './offlineSync';
import LandingPage from './LandingPage';
import Tutorial from './Tutorial';
import PushNotifications from './PushNotifications';
import AiAssistant from './AiAssistant';
import CareerWrapped from './CareerWrapped';
import JourneyMap from './JourneyMap';
import TrajetReplay from './TrajetReplay';
import UrssafDeclaration from './UrssafDeclaration';
import AddressAutocomplete from './AddressAutocomplete';

// Mis à false le temps que l'utilisateur décide d'activer la facturation
// Gemini (voir commentaire sur le rendu de <AiAssistant> plus bas).
const AI_ASSISTANT_ACTIVE = false;

// Chargement différé (code-splitting) : ces pages ne sont pas sur le chemin
// critique (derrière l'auth ou sur des routes rares). Les sortir du bundle
// principal accélère le tout premier chargement du site.
const Onboarding = React.lazy(() => import('./Onboarding'));
const PaymentPage = React.lazy(() => import('./PaymentPage'));
const PaymentSuccess = React.lazy(() => import('./PaymentSuccess'));
const BlogPage = React.lazy(() => import('./BlogPage'));
const BlogPost = React.lazy(() => import('./BlogPost'));
const ReviewPage = React.lazy(() => import('./ReviewPage'));
const ProPublicPage = React.lazy(() => import('./ProPublicPage'));
const AdminDashboard = React.lazy(() => import('./AdminDashboard'));
const SeoLocalPage = React.lazy(() => import('./SeoLocalPage'));
const CalculateursPage = React.lazy(() => import('./CalculateursPage'));
const DevisSignPage = React.lazy(() => import('./DevisSignPage'));
const BatimentPage = React.lazy(() => import('./BatimentPage'));

// Boundary de chargement pour les composants différés (évite tout crash de suspense).
const PageLoader = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', fontFamily: "'Open Sans', -apple-system, sans-serif", color: '#64748b', fontSize: '15px' }}>
    Chargement…
  </div>
);
const withSuspense = (el) => <React.Suspense fallback={<PageLoader />}>{el}</React.Suspense>;

// Fil de discussion réutilisable (messagerie client ↔ pro).
// Distance à vol d'oiseau entre deux points GPS (km) — formule de Haversine.
function distanceKm(a, b) {
  const R = 6371;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}
// Optimise l'ordre de visite (plus proche voisin, glouton) à partir du premier point de la liste.
// Simple et gratuit (pas de service de cartographie payant) — donne un très bon résultat pour
// une dizaine d'arrêts dans la journée.
function optimiserTournee(points) {
  if (points.length <= 2) return { ordre: points, distanceKm: points.length === 2 ? distanceKm(points[0], points[1]) : 0 };
  const restants = [...points.slice(1)];
  const ordre = [points[0]];
  let courant = points[0];
  let total = 0;
  while (restants.length) {
    let iMin = 0, dMin = Infinity;
    restants.forEach((p, i) => { const d = distanceKm(courant, p); if (d < dMin) { dMin = d; iMin = i; } });
    total += dMin;
    courant = restants[iMin];
    ordre.push(courant);
    restants.splice(iMin, 1);
  }
  return { ordre, distanceKm: total };
}

function ChatThread({ messages, myRole, value, onChange, onSend, sending, darkMode, emptyLabel }) {
  const endRef = React.useRef(null);
  React.useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);
  const DM = darkMode;
  const bulleMoi = { alignSelf: 'flex-end', background: '#2563eb', color: '#fff' };
  const bulleAutre = { alignSelf: 'flex-start', background: DM ? '#2c2c2e' : '#eef2f7', color: DM ? '#fff' : '#111827' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', padding: '4px 2px 12px' }}>
        {messages.length === 0 && (
          <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '14px', margin: 'auto' }}>{emptyLabel || 'Aucun message. Écrivez le premier !'}</p>
        )}
        {messages.map((m) => (
          <div key={m.id} style={{ maxWidth: '78%', padding: '9px 13px', borderRadius: '16px', fontSize: '14px', lineHeight: 1.45, wordBreak: 'break-word', ...(m.sender === myRole ? bulleMoi : bulleAutre) }}>
            {m.texte}
            <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '3px', textAlign: 'right' }}>
              {m.created_at ? new Date(m.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div style={{ display: 'flex', gap: '8px', paddingTop: '10px', borderTop: `1px solid ${DM ? '#2c2c2e' : '#e5e7eb'}` }}>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
          placeholder="Votre message…"
          style={{ flex: 1, padding: '11px 14px', borderRadius: '980px', border: `1px solid ${DM ? '#2c2c2e' : '#e5e7eb'}`, fontSize: '14px', background: DM ? '#111827' : '#fff', color: DM ? '#fff' : '#111827', outline: 'none' }}
        />
        <button onClick={onSend} disabled={sending || !value.trim()} style={{ padding: '11px 20px', borderRadius: '980px', border: 'none', background: (sending || !value.trim()) ? '#9ca3af' : '#2563eb', color: '#fff', fontWeight: 700, fontSize: '14px', cursor: (sending || !value.trim()) ? 'default' : 'pointer' }}>
          Envoyer
        </button>
      </div>
    </div>
  );
}

// Composant de signature tactile (canvas)
function SignaturePad({ value, onChange, label, darkMode }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      img.src = value;
    }
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = getPos(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.strokeStyle = '#1e3a8a';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    last.current = pos;
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    onChange('');
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <label style={{ fontSize: '13px', fontWeight: 'bold', color: darkMode ? '#fff' : '#374151' }}>{label}</label>
        <button onClick={clear} style={{ padding: '4px 10px', background: '#6b7280', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>Effacer</button>
      </div>
      <canvas
        ref={canvasRef}
        width={500}
        height={150}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        style={{ width: '100%', height: '150px', border: '2px solid #e5e7eb', borderRadius: '8px', background: '#fff', touchAction: 'none', cursor: 'crosshair' }}
      />
    </div>
  );
}

// Logo PlanifPro : symbole moderne (coche dans un carré arrondi dégradé) + texte
function Logo({ size = 28, color = '#0a0a0a', showText = true }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: size * 0.32 }}>
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="pmLogoGrad" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
            <stop stopColor="#2563eb" />
            <stop offset="1" stopColor="#1e3a8a" />
          </linearGradient>
        </defs>
        <rect width="40" height="40" rx="11" fill="url(#pmLogoGrad)" />
        <path d="M12 20.5L17.5 26L28 14.5" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {showText && (
        <span style={{ fontSize: size * 0.74, fontWeight: 700, color: color, letterSpacing: '-0.02em' }}>
          Planif<span style={{ color: '#2563eb' }}>Pro</span>
        </span>
      )}
    </div>
  );
}

function AvisRecus({ userId, darkMode, onNewAvis }) {
  const [avis, setAvis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyId, setReplyId] = useState(null);
  const [replyText, setReplyText] = useState('');

  useEffect(() => {
    supabase.from('avis').select('*').eq('pro_id', userId).order('created_at', { ascending: false })
      .then(({ data }) => { setAvis(data || []); setLoading(false); });
  }, [userId]);

  // Alerte temps réel dès qu'un nouvel avis arrive (le client vient de le poster).
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`avis-${userId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'avis', filter: `pro_id=eq.${userId}` }, (payload) => {
        setAvis((prev) => [payload.new, ...prev]);
        onNewAvis && onNewAvis(payload.new);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveReponse = async (avisId) => {
    if (!replyText.trim()) return;
    const { error } = await supabase.from('avis').update({ reponse: replyText.trim(), reponse_at: new Date().toISOString() }).eq('id', avisId);
    if (error) { alert('Erreur: ' + error.message); return; }
    setAvis(avis.map(a => a.id === avisId ? { ...a, reponse: replyText.trim() } : a));
    setReplyId(null); setReplyText('');
  };

  const moyenne = avis.length ? (avis.reduce((s, a) => s + a.note, 0) / avis.length).toFixed(1) : null;

  return (
    <div style={{ padding: '28px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>Avis reçus</h3>
        {moyenne && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '28px', fontWeight: 800, color: '#f59e0b' }}>{moyenne}</span>
            <div>
              <div style={{ display: 'flex', gap: '2px' }}>
                {[1,2,3,4,5].map(s => <span key={s} style={{ fontSize: '16px', color: s <= Math.round(moyenne) ? '#f59e0b' : '#e5e7eb' }}>★</span>)}
              </div>
              <span style={{ fontSize: '12px', color: '#86868b' }}>{avis.length} avis</span>
            </div>
          </div>
        )}
      </div>
      {loading ? (
        <p style={{ color: '#86868b', fontSize: '14px' }}>Chargement...</p>
      ) : avis.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <p style={{ fontSize: '32px', marginBottom: '8px' }}>⭐</p>
          <p style={{ color: '#86868b', fontSize: '14px' }}>Aucun avis pour l'instant. Partagez votre QR code après chaque prestation !</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {avis.map(a => (
            <div key={a.id} style={{ padding: '16px', borderRadius: '12px', background: darkMode ? '#2c2c2e' : '#f8faff', border: darkMode ? '1px solid #3c3c3e' : '1px solid #e8ecf8' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: '14px' }}>{a.auteur}</span>
                  <div style={{ display: 'flex', gap: '2px', marginTop: '2px' }}>
                    {[1,2,3,4,5].map(s => <span key={s} style={{ fontSize: '14px', color: s <= a.note ? '#f59e0b' : '#e5e7eb' }}>★</span>)}
                  </div>
                </div>
                <span style={{ fontSize: '12px', color: '#86868b' }}>{new Date(a.created_at).toLocaleDateString('fr-FR')}</span>
              </div>
              {a.commentaire && <p style={{ fontSize: '14px', color: darkMode ? '#d1d5db' : '#374151', lineHeight: 1.6, margin: 0 }}>{a.commentaire}</p>}
              {a.reponse ? (
                <div style={{ marginTop: '10px', padding: '10px 12px', background: darkMode ? '#1c1c1e' : '#eef4ff', borderRadius: '10px', borderLeft: '3px solid #2563eb' }}>
                  <p style={{ fontSize: '11px', fontWeight: 700, color: '#2563eb', margin: '0 0 3px' }}>↩️ Votre réponse</p>
                  <p style={{ fontSize: '13px', color: darkMode ? '#d1d5db' : '#374151', margin: 0 }}>{a.reponse}</p>
                </div>
              ) : replyId === a.id ? (
                <div style={{ marginTop: '10px' }}>
                  <textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Votre réponse publique..." rows={2} style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #d2d2d7', fontSize: '13px', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                    <button onClick={() => saveReponse(a.id)} style={{ padding: '6px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>Publier</button>
                    <button onClick={() => { setReplyId(null); setReplyText(''); }} style={{ padding: '6px 14px', background: 'none', color: '#6b7280', border: 'none', cursor: 'pointer', fontSize: '12px' }}>Annuler</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => { setReplyId(a.id); setReplyText(''); }} style={{ marginTop: '8px', padding: '5px 12px', background: darkMode ? '#1c1c1e' : '#fff', color: '#2563eb', border: '1px solid #2563eb', borderRadius: '980px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>↩️ Répondre</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [entrepriseNom, setEntrepriseNom] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [resetMode, setResetMode] = useState(false); // demande de lien de réinitialisation
  const [resetEmail, setResetEmail] = useState('');
  const [resetSending, setResetSending] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [newPasswordMode, setNewPasswordMode] = useState(false); // arrivée depuis le lien email (?type=recovery)
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [newPasswordSaving, setNewPasswordSaving] = useState(false);
  const [newPasswordDone, setNewPasswordDone] = useState(false);
  const [pwChangeOpen, setPwChangeOpen] = useState(false); // changer son mot de passe depuis "Mon compte" (déjà connecté)
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [registerFromLanding, setRegisterFromLanding] = useState(false); // true = venu de la landing, cache les modes collab/client
  const [showAuth, setShowAuth] = useState(false); // false = landing, true = formulaire connexion
  const [showLegal, setShowLegal] = useState(false); // page mentions légales
  const [legalTab, setLegalTab] = useState('mentions'); // onglet légal actif
  const [showCookieBanner, setShowCookieBanner] = useState(false); // bandeau cookies
  // Portail client final : demandes d'intervention
  const [demandes, setDemandes] = useState([]);
  // Matériel DJ (location)
  const [materiels, setMateriels] = useState([]);
  const [matNom, setMatNom] = useState('');
  const [matQuantite, setMatQuantite] = useState('1');
  const [matStatut, setMatStatut] = useState('dispo');
  const [matLouePar, setMatLouePar] = useState('');
  const [matRetour, setMatRetour] = useState('');
  // Relance clients (mode beauté)
  const [relanceDelai, setRelanceDelai] = useState('60'); // jours
  const [relanceMessage, setRelanceMessage] = useState('');
  const [relanceMessageInit, setRelanceMessageInit] = useState(false);
  const [relanceFiltre, setRelanceFiltre] = useState('tous');
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  // Fidélité (mode beauté) — configuration
  const [fidObjectif, setFidObjectif] = useState('100');
  const [fidRecompense, setFidRecompense] = useState('Soin offert ou -20€');
  const [fidPrestations, setFidPrestations] = useState([]); // [{nom, points}]
  const [fidPrestaNom, setFidPrestaNom] = useState('');
  const [fidPrestaPoints, setFidPrestaPoints] = useState('');
  const [fidConfigInit, setFidConfigInit] = useState(false);
  const [demTitre, setDemTitre] = useState('');
  const [demDescription, setDemDescription] = useState('');
  const [demAdresse, setDemAdresse] = useState('');
  const [demUrgence, setDemUrgence] = useState('normale');
  const [demCreneaux, setDemCreneaux] = useState('');
  const [demDateSouhaitee, setDemDateSouhaitee] = useState('');
  const [demHeureSouhaitee, setDemHeureSouhaitee] = useState('');
  const [demPhotos, setDemPhotos] = useState([]);
  const [showDemandeForm, setShowDemandeForm] = useState(false);
  const [savingDemande, setSavingDemande] = useState(false);
  const [savingIntervention, setSavingIntervention] = useState(false);
  const [savingClient, setSavingClient] = useState(false);
  // Espace client final : ses cartes de fidélité et historique
  const [mesCartes, setMesCartes] = useState([]);
  const [monHistorique, setMonHistorique] = useState([]);
  const [mesFacturesClient, setMesFacturesClient] = useState([]);
  const [clientVue, setClientVue] = useState('demandes'); // 'demandes' / 'interventions' / 'fidelite'
  const [clientDetailIntervention, setClientDetailIntervention] = useState(null);
  // Messagerie temps réel client ↔ pro
  const [messages, setMessages] = useState([]);          // conversation ouverte
  const [msgInput, setMsgInput] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const [convClientId, setConvClientId] = useState(null); // (côté pro) conversation sélectionnée
  const [proConvs, setProConvs] = useState([]);           // (côté pro) liste des conversations
  // Suivi technicien en temps réel
  const [trackingId, setTrackingId] = useState(null);     // (pro) intervention suivie en ce moment
  const [trackingClientNom, setTrackingClientNom] = useState('');
  const watchIdRef = useRef(null);
  const lastPushRef = useRef(0);
  const lastPingRef = useRef(0); // throttle séparé pour position_pings (trace du replay, 60s)
  // Consentement géoloc (Replay GPS) : demandé une seule fois avant la
  // toute première activation du suivi, jamais après.
  const [showGeolocConsent, setShowGeolocConsent] = useState(false);
  const [pendingTrackIntervention, setPendingTrackIntervention] = useState(null);
  const [clientTracking, setClientTracking] = useState(null); // (client) suivi actif le concernant
  const [avisMoyenne, setAvisMoyenne] = useState(null); // note moyenne /5 (score de santé)
  const [benchmark, setBenchmark] = useState(undefined); // undefined = pas encore chargé, null = sous le seuil d'anonymat
  // Coordonnées GPS (optimisation de tournée)
  const [formLat, setFormLat] = useState(null);
  const [formLng, setFormLng] = useState(null);
  const [cliLat, setCliLat] = useState(null);
  const [cliLng, setCliLng] = useState(null);
  const [prestataireMetier, setPrestataireMetier] = useState(''); // métier du prestataire du client
  // Profil client (Phase 2 : particulier / pro)
  const [cpType, setCpType] = useState('particulier'); // 'particulier' | 'pro'
  const [cpNom, setCpNom] = useState('');
  const [cpSiret, setCpSiret] = useState('');
  const [cpTel, setCpTel] = useState('');
  const [cpAdresse, setCpAdresse] = useState('');
  const [cpSaving, setCpSaving] = useState(false);
  // Annuaire de proximité (Phase 4)
  const [annAdresse, setAnnAdresse] = useState('');
  const [annLat, setAnnLat] = useState(null);
  const [annLng, setAnnLng] = useState(null);
  const [annMetier, setAnnMetier] = useState('');
  const [annResults, setAnnResults] = useState(null);
  const [annSearching, setAnnSearching] = useState(false);
  // Édition du profil compte
  const [editProfil, setEditProfil] = useState(false);
  const [profilNom, setProfilNom] = useState('');
  const [profilSiret, setProfilSiret] = useState('');
  const [profilAdresse, setProfilAdresse] = useState('');
  const [profilVille, setProfilVille] = useState('');
  const [profilLat, setProfilLat] = useState(null);
  const [profilLng, setProfilLng] = useState(null);
  const [profilAnnuaireVisible, setProfilAnnuaireVisible] = useState(false);
  const [profilBio, setProfilBio] = useState('');
  const [profilPhoto, setProfilPhoto] = useState('');
  const [profilTel, setProfilTel] = useState('');
  const [profilTvaRegime, setProfilTvaRegime] = useState('franchise');
  const [profilTvaNumero, setProfilTvaNumero] = useState('');
  const [profilIban, setProfilIban] = useState('');
  const [profilBic, setProfilBic] = useState('');
  const [profilBanque, setProfilBanque] = useState('');
  const [profilPenalitesTaux, setProfilPenalitesTaux] = useState(''); // % annuel pénalités de retard
  const [profilCgv, setProfilCgv] = useState(''); // mentions légales / CGV personnalisées
  const [profilMetier, setProfilMetier] = useState('artisan');
  const [profilMetierLabel, setProfilMetierLabel] = useState(''); // intitulé libre si métier "autre" (ex: Photographe)
  const [profilAutreMode, setProfilAutreMode] = useState(false); // affiche le sous-panneau "autre métier" dans Mon compte
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [savingOnboarding, setSavingOnboarding] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [savingProfil, setSavingProfil] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [filterTechnicien, setFilterTechnicien] = useState('');
  // Mode hors-ligne (techniciens sans réseau)
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [pendingSync, setPendingSync] = useState(0);
  // Notifications temps réel (Supabase Realtime)
  const [realtimeToast, setRealtimeToast] = useState(null); // { message } | null
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);

  // Multi-comptes : directeur / agent
  const [registerMode, setRegisterMode] = useState('directeur'); // 'directeur' ou 'agent'
  const [registerMetier, setRegisterMetier] = useState('artisan'); // 'artisan' ou 'dj'
  const [codeSociete, setCodeSociete] = useState(''); // saisi par l'agent
  const [pendingAgents, setPendingAgents] = useState([]); // demandes en attente (vue directeur)
  const [validatedAgents, setValidatedAgents] = useState([]);
  const [pendingClients, setPendingClients] = useState([]); // clients à valider (vue directeur)
  const [validatedClients, setValidatedClients] = useState([]);

  // Netlify sert les pages pré-rendues (SEO local, calculateurs, blog) depuis de
  // vrais dossiers statiques (ex: build/calculateurs/index.html) et redirige donc
  // en 301 toute URL sans slash final vers sa version AVEC slash final — hors nos
  // liens/canonicals n'ont jamais de slash final. Sans cette normalisation, ces
  // ~240 pages s'affichaient correctement le temps du HTML pré-rendu, puis React
  // les remplaçait par la landing page générique dès l'hydratation (le pathname
  // avec le "/" final ne correspondait plus à aucune regex de route).
  const pathname = window.location.pathname.replace(/^(.+)\/$/, '$1');

  // BLOG ROUTING
  const [blogView, setBlogView] = useState(() => {
    if (pathname.startsWith('/blog/')) return { page: 'article', slug: pathname.replace('/blog/', '') };
    if (pathname === '/blog') return { page: 'blog' };
    return null;
  });

  const reviewUserId = (() => {
    const m = pathname.match(/^\/avis\/([^/]+)$/);
    return m ? m[1] : null;
  })();

  const proSlug = (() => {
    const m = pathname.match(/^\/pro\/([^/]+)$/);
    return m ? m[1] : null;
  })();

  const devisSignId = (() => {
    const m = pathname.match(/^\/devis\/([0-9a-fA-F-]{8,})$/);
    return m ? m[1] : null;
  })();

  const batimentId = (() => {
    const m = pathname.match(/^\/batiment\/([0-9a-fA-F-]{8,})$/);
    return m ? m[1] : null;
  })();

  const seoLocal = (() => {
    const m = pathname.match(/^\/(artisan|dj|beaute|aide-domicile)(?:-([a-z-]+))?$/);
    if (!m) return null;
    const metier = m[1] === 'aide-domicile' ? 'aide_domicile' : m[1];
    return { metier, ville: m[2] || null };
  })();

  const calculateurRoute = (() => {
    const m = pathname.match(/^\/calculateurs(?:\/([a-z0-9-]+))?$/);
    return m ? { slug: m[1] || null } : null;
  })();

  // Permet au lien "Voir mes prospects →" de l'email hebdo de démarrer direct
  // sur l'onglet admin Prospection (?admintab=prospection), au lieu d'atterrir
  // sur l'accueil et devoir naviguer soi-même jusqu'à l'onglet à chaque fois.
  const [showAdmin, setShowAdmin] = useState(() => new URLSearchParams(window.location.search).get('admintab') === 'prospection');

  const navigateBlog = (page, slug) => {
    if (page === 'home') { setBlogView(null); window.history.pushState({}, '', '/'); return; }
    if (page === 'blog') { setBlogView({ page: 'blog' }); window.history.pushState({}, '', '/blog'); return; }
    if (page === 'article') { setBlogView({ page: 'article', slug }); window.history.pushState({}, '', `/blog/${slug}`); }
  };

  // CLIENT TABS
  const [activeTab, setActiveTab] = useState('dashboard');
  const [openNavMenu, setOpenNavMenu] = useState(null); // groupe de nav ouvert
  const [onboardingHidden, setOnboardingHidden] = useState(() => typeof window !== 'undefined' && localStorage.getItem('planifpro_onboarding_hidden') === '1');
  const [facturxInfoHidden, setFacturxInfoHidden] = useState(() => typeof window !== 'undefined' && localStorage.getItem('planifpro_facturx_info_hidden') === '1');
  // Tant que loadClientData() n'a pas fini, clientsList/interventions/factures
  // sont encore vides — sans ce flag, la checklist d'onboarding se calculait sur
  // ces tableaux vides et s'affichait brièvement à chaque connexion avant de se
  // masquer dès que les vraies données arrivaient (flash d'une fraction de seconde).
  const [clientDataLoaded, setClientDataLoaded] = useState(false);
  const [showCareerWrapped, setShowCareerWrapped] = useState(false);
  const [showJourneyMap, setShowJourneyMap] = useState(false);
  const [replayIntervention, setReplayIntervention] = useState(null);
  const [batimentQrId, setBatimentQrId] = useState(null);
  const [batimentQrDataUrl, setBatimentQrDataUrl] = useState(null);
  useEffect(() => {
    if (!batimentQrId) { setBatimentQrDataUrl(null); return; }
    let cancelled = false;
    (async () => {
      const QRCode = await import('qrcode');
      const lien = `${window.location.origin}/batiment/${batimentQrId}`;
      const url = await QRCode.toDataURL(lien, { margin: 1, width: 260 });
      if (!cancelled) setBatimentQrDataUrl(url);
    })();
    return () => { cancelled = true; };
  }, [batimentQrId]);
  // ── Objectif mensuel (carte "🎯 Objectif du mois" du tableau de bord) ──
  const [editingObjectif, setEditingObjectif] = useState(false);
  const [objectifInput, setObjectifInput] = useState('');
  const [savingObjectif, setSavingObjectif] = useState(false);
  const saveObjectif = async () => {
    const val = parseFloat(objectifInput);
    if (!val || val <= 0) { alert('Indique un montant valide.'); return; }
    setSavingObjectif(true);
    try {
      const { error } = await supabase.from('users').update({ objectif_mensuel: val }).eq('id', user.id);
      if (error) throw error;
      setUser({ ...user, objectif_mensuel: val });
      setEditingObjectif(false);
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setSavingObjectif(false);
    }
  };
  const [histoMonth, setHistoMonth] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [interventions, setInterventions] = useState([]);
  const [showModalIntervention, setShowModalIntervention] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedIntervention, setSelectedIntervention] = useState(null);
  const [modalMode, setModalMode] = useState('create');
  
  // Modal form
  const [formTitre, setFormTitre] = useState('');
  const [formClient, setFormClient] = useState('');
  const [formClientId, setFormClientId] = useState('');
  const [formAdresse, setFormAdresse] = useState('');
  const [formHeure, setFormHeure] = useState('09:00');
  const [formDuree, setFormDuree] = useState('60');
  const [formRecurrence, setFormRecurrence] = useState('none'); // none | weekly | biweekly | monthly
  const [formRecurrenceCount, setFormRecurrenceCount] = useState('4');
  // Champs RAPPORT
  const [formTechnicien, setFormTechnicien] = useState('');
  const [formTechniciensExtra, setFormTechniciensExtra] = useState([]);
  const [formVehicule, setFormVehicule] = useState('');
  const [formNature, setFormNature] = useState('');
  const [formHeureArrivee, setFormHeureArrivee] = useState('');
  const [formHeureDepart, setFormHeureDepart] = useState('');
  const [formTypeMission, setFormTypeMission] = useState('');
  const [formTravaux, setFormTravaux] = useState('');
  const [formObservations, setFormObservations] = useState('');
  const [formMateriel, setFormMateriel] = useState('');
  const [formPieces, setFormPieces] = useState('');
  const [formBilan, setFormBilan] = useState('');
  const [formChecklist, setFormChecklist] = useState([]);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [activeModalTab, setActiveModalTab] = useState('infos');
  
  // Minuteur
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  
  // Photos : tableau d'objets { url, categorie, legende }
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoCategorie, setPhotoCategorie] = useState('avant');
  
  // Factures
  const [factures, setFactures] = useState([]);
  const [newFactureClient, setNewFactureClient] = useState('');
  const [newFactureIntitule, setNewFactureIntitule] = useState('');
  const [newFactureAmount, setNewFactureAmount] = useState('');
  const [newFactureDelai, setNewFactureDelai] = useState('30');
  const [newFactureMontantType, setNewFactureMontantType] = useState('ttc');
  const [filtreFactures, setFiltreFactures] = useState('toutes'); // toutes / paye / impaye

  // Devis
  const [devis, setDevis] = useState([]);
  const [showModalDevis, setShowModalDevis] = useState(false);
  const [editingDevisId, setEditingDevisId] = useState(null);
  const [devisClient, setDevisClient] = useState('');
  const [devisClientId, setDevisClientId] = useState('');
  const [devisObjet, setDevisObjet] = useState('');
  const [devisLignes, setDevisLignes] = useState([{ description: '', quantite: '1', prix: '' }]);
  const [devisValidite, setDevisValidite] = useState('30');
  const [devisNotes, setDevisNotes] = useState('');
  const [filtreDevis, setFiltreDevis] = useState('tous'); // tous / brouillon / envoye / accepte / refuse

  // Catalogue de prestations
  const [prestations, setPrestations] = useState([]);
  const [newPrestaNom, setNewPrestaNom] = useState('');
  const [newPrestaPrix, setNewPrestaPrix] = useState('');

  // Dépenses
  const [depenses, setDepenses] = useState([]);
  const [newDepLibelle, setNewDepLibelle] = useState('');
  const [newDepMontant, setNewDepMontant] = useState('');
  const [newDepCategorie, setNewDepCategorie] = useState('Achats');
  const [newDepDate, setNewDepDate] = useState(new Date().toISOString().split('T')[0]);

  // Team
  const [team, setTeam] = useState([]);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamRole, setNewTeamRole] = useState('');

  // Fiches Clients (carnet d'adresses)
  const [clientsList, setClientsList] = useState([]);
  const [showModalClient, setShowModalClient] = useState(false);
  const [editingClientId, setEditingClientId] = useState(null);
  const [cliNom, setCliNom] = useState('');
  const [cliType, setCliType] = useState('particulier');
  const [cliEntreprise, setCliEntreprise] = useState('');
  const [cliSiret, setCliSiret] = useState('');
  const [cliAdresse, setCliAdresse] = useState('');
  const [cliTel, setCliTel] = useState('');
  const [cliEmail, setCliEmail] = useState('');
  const [cliNotes, setCliNotes] = useState('');
  const [cliLogo, setCliLogo] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  // Signatures (intervention)
  const [signatureClient, setSignatureClient] = useState('');
  const [signatureTech, setSignatureTech] = useState('');
  
  // Absences
  const [absences, setAbsences] = useState([]);
  const [newAbsenceName, setNewAbsenceName] = useState('');
  const [newAbsenceDate, setNewAbsenceDate] = useState('');
  const [newAbsenceDateFin, setNewAbsenceDateFin] = useState('');

  // ADMIN TABS
  const [adminTab, setAdminTab] = useState('dashboard');
  const [allClients, setAllClients] = useState([]);
  const [allFactures, setAllFactures] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketMessage, setTicketMessage] = useState('');
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [ticketResponse, setTicketResponse] = useState('');

  const isAdmin = user && user.role === 'admin';
  const isAgent = user && user.role_societe === 'agent';
  const isResponsable = isAgent && user.agent_niveau === 'responsable';
  // Un technicien ne peut pas supprimer factures/fiches clients (restreint aussi côté RLS).
  const canDeleteFinancial = !isAgent || isResponsable;
  const isClientFinal = user && user.role_societe === 'client_final';
  // Pour un agent, utiliser le métier du directeur (prestataireMetier), pas le sien (toujours null)
  const metierEffectif = isAgent ? prestataireMetier : (user ? user.metier : null);
  const isDJ = user && metierEffectif === 'dj';
  const isBeaute = user && (metierEffectif === 'beaute' || metierEffectif === 'aide_domicile');
  // "Style artisan" = tout métier qui n'est ni DJ ni rendez-vous récurrent (beauté/aide à domicile).
  // Couvre 'artisan', 'paysagiste', un métier libre (ex: photographe, toiletteur...), ou non renseigné.
  const isArtisan = user && !isDJ && !isBeaute;
  // Régime TVA : franchise (micro) ou exonéré → pas de TVA, on parle en TTC. Sinon HT.
  const sansTva = ['franchise', 'exonere'].includes((user && user.tva_regime) || 'franchise');
  const labelMontant = sansTva ? 'TTC' : 'HT';
  // L'id qui sert à charger les données partagées : pour un agent c'est son directeur, sinon lui-même
  const dataOwnerId = user ? (isAgent && user.societe_id ? user.societe_id : user.id) : null;
  // Essai gratuit de 10 jours, posé à l'inscription par le trigger serveur
  // `guard_users_insert`. Une date de fin dans le futur vaut accès, qu'il
  // s'agisse de l'essai ou d'un abonnement en cours — c'est la même colonne.
  // Sans ça, l'essai promis sur la page d'accueil n'existait pas : tout
  // nouveau compte tombait directement sur le mur de paiement.
  const essaiOuAboActif = !!(user?.subscriptionEnd && new Date(user.subscriptionEnd) > new Date());
  // Un agent validé a accès ; un directeur a accès s'il paie ou est en essai
  const hasAccess = user && (
    user.isPaid ||
    essaiOuAboActif ||
    user.role === 'admin' ||
    email === 'kreeswaves@gmail.com' ||
    (isAgent && user.agent_valide)
  );

  // Limite de personnes (directeur inclus) selon la formule
  const getSeatLimit = (subType) => {
    if (subType === '1month') return 1;       // Indépendant : 1 personne
    if (subType === '6months') return 6;      // Pro : 6 personnes
    if (subType === '12months') return 12;    // Business : 12 personnes
    if (subType === 'lifetime') return 999;   // compte spécial illimité
    return 1; // par défaut
  };
  // Nombre de places techniciens disponibles (la limite inclut le directeur, donc -1)
  const seatLimit = user ? getSeatLimit(user.subscriptionType) : 1;
  const maxTechniciens = Math.max(0, seatLimit - 1);
  const techniciensActuels = validatedAgents.length;
  const placesRestantes = maxTechniciens - techniciensActuels;

  // ===== MINUTEUR EFFECT =====
  useEffect(() => {
    let interval = null;
    if (timerRunning) {
      interval = setInterval(() => {
        setTimerSeconds(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timerRunning]);

  // Détecte mobile pour adapter l'affichage du calendrier
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Message de relance par défaut adapté au métier
  useEffect(() => {
    if (user && !relanceMessageInit) {
      let msg;
      if (user.metier === 'beaute') {
        msg = "Bonjour 👋 Cela fait un moment depuis votre dernier rendez-vous. Il est peut-être temps de reprendre rendez-vous ! N'hésitez pas à me contacter pour réserver votre prochain créneau. À très vite 😊";
      } else if (user.metier === 'dj') {
        msg = "Bonjour 👋 J'espère que votre dernier événement s'est bien passé ! Si vous avez une soirée ou un événement à venir, je serais ravi de m'en occuper. N'hésitez pas à me recontacter. 🎧";
      } else {
        msg = "Bonjour 👋 Cela fait un moment depuis notre dernière intervention. Pensez à l'entretien régulier de vos installations ! N'hésitez pas à me contacter pour planifier un rendez-vous. À bientôt.";
      }
      setRelanceMessage(msg);
      setRelanceMessageInit(true);
    }
  }, [user, relanceMessageInit]);

  // Charger la configuration fidélité depuis le profil
  useEffect(() => {
    if (user && !fidConfigInit) {
      if (user.fidelite_config) {
        try {
          const cfg = typeof user.fidelite_config === 'string' ? JSON.parse(user.fidelite_config) : user.fidelite_config;
          if (cfg.objectif) setFidObjectif(String(cfg.objectif));
          if (cfg.recompense) setFidRecompense(cfg.recompense);
          if (cfg.prestations) setFidPrestations(cfg.prestations);
        } catch (e) { console.error('Config fidélité illisible'); }
      }
      setFidConfigInit(true);
    }
  }, [user, fidConfigInit]);

  // ===== RETOUR DE STRIPE (paiement) =====
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paiement = params.get('paiement');
    if (paiement === 'success') {
      const uid = params.get('uid');
      if (uid) {
        (async () => {
          // L'abonnement est activé UNIQUEMENT par le webhook Stripe côté serveur
          // (signature vérifiée), jamais par ces paramètres d'URL falsifiables.
          // On attend juste que le webhook ait fait son travail (quasi instantané),
          // en ré-interrogeant la base quelques fois avant d'abandonner.
          let u = null;
          for (let tentative = 0; tentative < 8; tentative++) {
            const { data } = await supabase.from('users').select('*').eq('id', uid).maybeSingle();
            if (data?.isPaid) { u = data; break; }
            u = data;
            await new Promise((r) => setTimeout(r, 1000));
          }
          if (u) {
            setUser(u);
            loadClientData(u.id);
            if (u.role_societe === 'directeur') { loadAgentsList(u.id); loadClientsList(u.id); }
          }
          // Nettoyer l'URL
          window.history.replaceState({}, '', window.location.pathname);
          setShowPaymentSuccess(true);
        })();
      }
    } else if (paiement === 'cancel') {
      window.history.replaceState({}, '', window.location.pathname);
      alert('Paiement annulé. Tu peux réessayer quand tu veux.');
    }
  }, []);

  // ===== LIEN D'INVITATION CLIENT (?client=PM-XXXXX) =====
  // Met le visiteur directement sur le bon formulaire (compte "Client"),
  // avec le code prestataire pré-rempli → impossible de créer un espace pro par erreur.
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('client');
    if (code) {
      setRegisterMode('client_final');
      setCodeSociete(code.trim().toUpperCase());
      setRegisterFromLanding(false);
      setIsRegister(true);
      setShowAuth(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // ===== LIEN "MOT DE PASSE OUBLIÉ" (redirection Supabase, event PASSWORD_RECOVERY) =====
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setNewPasswordMode(true);
        setShowAuth(true);
      }
    });
    return () => sub?.subscription?.unsubscribe();
  }, []);

  // ===== RESTAURATION DE SESSION (reste connecté après rechargement) =====
  useEffect(() => {
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session && session.user) {
          const { data } = await supabase.from('users').select('*').eq('id', session.user.id).maybeSingle();
          if (data) {
            setUser(data);
            setShowAuth(true);
            if (data.role === 'admin') {
              loadAdminData();
            } else if (data.role_societe === 'client_final') {
              loadDemandes(data);
              loadMaFidelite(data);
            } else if (data.role_societe === 'agent') {
              if (data.agent_valide && data.societe_id) {
                loadClientData(data.societe_id);
                if (data.entrepriseNom) setFilterTechnicien(data.entrepriseNom);
              }
            } else if (data.isPaid || data.email === 'kreeswaves@gmail.com') {
              loadClientData(data.id);
              loadAgentsList(data.id);
              loadClientsList(data.id);
              loadDemandes(data);
              if (data.metier === 'dj') loadMateriels(data.id);
            }
          }
        }
      } catch (err) {
        console.error('Erreur restauration session:', err);
      }
    })();
  }, []);

  // ===== CSS RESPONSIVE GLOBAL (mobile/tablette) =====
  useEffect(() => {
    // Meta viewport (pour le zoom mobile correct)
    let meta = document.querySelector('meta[name="viewport"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'viewport';
      document.head.appendChild(meta);
    }
    meta.content = 'width=device-width, initial-scale=1, maximum-scale=5';

    // Feuille de style responsive
    if (document.getElementById('pm-responsive-style')) return;
    const style = document.createElement('style');
    style.id = 'pm-responsive-style';
    style.textContent = `
      * { box-sizing: border-box; }

      /* ── MOBILE (≤ 768px) ── */
      @media (max-width: 768px) {
        /* Padding main réduit */
        main { padding: 16px !important; }

        /* Grilles stats 4 col → 2 col */
        [style*="repeat(4, 1fr)"] { grid-template-columns: repeat(2, 1fr) !important; }
        /* Grilles 3 col → 1 col */
        [style*="repeat(3, 1fr)"] { grid-template-columns: 1fr !important; }
        /* Grilles auto-fit larges → 1 col (évite débordement horizontal) */
        [style*="minmax(300px"],
        [style*="minmax(340px"],
        [style*="minmax(320px"] { grid-template-columns: 1fr !important; }
        /* Élément qui s'étend sur 2 colonnes → 1 seule sur mobile */
        [style*="span 2"] { grid-column: span 1 !important; }
        /* Grilles 2 col → 1 col */
        [style*="grid-template-columns: 1fr 1fr"],
        [style*="1fr 2fr"],
        [style*="2fr 1fr"] { grid-template-columns: 1fr !important; }

        /* Calendrier : garde 7 col mais plus compact */
        [style*="repeat(7, 1fr)"] { gap: 2px !important; }

        /* Titres de section */
        h1 { font-size: clamp(20px, 5vw, 28px) !important; }
        h2 { font-size: clamp(18px, 4.5vw, 24px) !important; }

        /* Modales : plein écran sur mobile */
        [style*="maxWidth: '600px'"],
        [style*="max-width: 600px"],
        [style*="maxWidth: '500px'"],
        [style*="maxWidth: '760px'"] {
          max-width: 100% !important;
          width: 100% !important;
          border-radius: 16px 16px 0 0 !important;
          margin: auto 0 0 0 !important;
        }

        /* Flex rows → colonnes */
        [style*="display: flex"][style*="gap: '20px'"],
        [style*="display: flex"][style*="gap: '24px'"],
        [style*="display: flex"][style*="gap: '28px'"],
        [style*="display: flex"][style*="gap: '32px'"] {
          flex-wrap: wrap !important;
        }

        /* Tableaux : scroll horizontal */
        table { display: block; overflow-x: auto; white-space: nowrap; }

        /* Cartes avec padding réduit */
        [style*="padding: '40px'"],
        [style*="padding: '36px'"] { padding: 20px !important; }

        /* Boutons full width dans les formulaires */
        form button[type="submit"] { width: 100% !important; }

        /* Header de l'app : cacher les labels longs des tabs */
        .pm-tab-label-long { display: none; }
      }

      /* ── PETIT MOBILE (≤ 480px) ── */
      @media (max-width: 480px) {
        main { padding: 12px !important; }

        /* Tout en 1 colonne */
        [style*="repeat(4, 1fr)"],
        [style*="repeat(3, 1fr)"],
        [style*="repeat(2, 1fr)"] { grid-template-columns: 1fr !important; }

        /* Cellules calendrier plus petites */
        [style*="minHeight: '120px'"] { min-height: 60px !important; }
        [style*="minHeight: '72px'"]  { min-height: 50px !important; }

        /* Textes calendrier très petits */
        [style*="repeat(7, 1fr)"] * { font-size: 9px !important; }

        /* Padding réduit sur les cartes */
        [style*="padding: '40px 32px'"],
        [style*="padding: '32px'"] { padding: 16px !important; }
      }

      /* ── GÉNÉRAL ── */
      /* Touch targets confortables */
      input, select, textarea { font-size: 16px !important; } /* évite le zoom iOS */
      button { min-height: 44px; cursor: pointer; }

      /* Scroll horizontal pour les onglets */
      .pm-tabs-scroll {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
      }
      .pm-tabs-scroll::-webkit-scrollbar { display: none; }

      /* ===== MODE SOMBRE : forcer la lisibilité des textes ===== */
      body.pm-dark, body.pm-dark > div { color: #f5f5f7; }
      /* Les textes gris foncés deviennent gris clairs pour ressortir */
      body.pm-dark [style*="color: #374151"],
      body.pm-dark [style*="color: #1f2937"],
      body.pm-dark [style*="color: #111827"],
      body.pm-dark [style*="color: #0a0a0a"],
      body.pm-dark [style*="color: #000"] { color: #f5f5f7 !important; }
      /* Les gris moyens deviennent plus clairs */
      body.pm-dark [style*="color: #6b7280"],
      body.pm-dark [style*="color: #6e6e73"] { color: #b0b0b8 !important; }
      /* Les inputs en mode sombre */
      body.pm-dark input, body.pm-dark select, body.pm-dark textarea {
        background: #1c1c1e !important;
        color: #f5f5f7 !important;
        border-color: #3a3a3c !important;
      }
      body.pm-dark input::placeholder, body.pm-dark textarea::placeholder { color: #8e8e93 !important; }
    `;
    document.head.appendChild(style);
  }, []);

  // ===== TOGGLE classe dark sur le body =====
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('pm-dark');
    } else {
      document.body.classList.remove('pm-dark');
    }
  }, [darkMode]);

  // ===== BANDEAU COOKIES (affiché si pas encore de choix mémorisé) =====
  useEffect(() => {
    const choixFait = document.cookie.split('; ').find(c => c.startsWith('pm_cookie_consent='));
    if (!choixFait) {
      setShowCookieBanner(true);
    }
  }, []);

  const accepterCookies = () => {
    // Mémorise le choix pendant 1 an
    document.cookie = 'pm_cookie_consent=accepted; max-age=' + (365 * 24 * 60 * 60) + '; path=/';
    setShowCookieBanner(false);
  };

  // ===== REGISTER =====
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      alert('Remplis tous les champs!');
      return;
    }
    if (password.length < 6) {
      alert('Le mot de passe doit faire au moins 6 caractères.');
      return;
    }
    if (registerMode === 'directeur' && !entrepriseNom) {
      alert('Indique le nom de ton entreprise!');
      return;
    }
    if (registerMode === 'agent' && (!entrepriseNom || !codeSociete)) {
      alert('Indique ton nom et le code société!');
      return;
    }
    if (registerMode === 'client_final' && (!entrepriseNom || !codeSociete)) {
      alert('Indique ton nom et le code de ton prestataire!');
      return;
    }

    setLoading(true);
    try {
      // Pour un agent OU un client final, vérifier d'abord le code société
      let directeur = null;
      if (registerMode === 'agent' || registerMode === 'client_final') {
        const codeRecherche = codeSociete.trim();
        // Recherche insensible à la casse pour éviter les soucis de majuscules/minuscules
        const { data: dir, error: dirError } = await supabase.rpc('check_code_societe', { p_code: codeRecherche }).maybeSingle();
        if (dirError) {
          alert('Erreur de vérification du code. Réessaie dans un instant.');
          setLoading(false);
          return;
        }
        if (!dir) {
          alert('Code invalide. Vérifie le code fourni par ' + (registerMode === 'client_final' ? 'ton prestataire.' : 'ton directeur.') + '\n\n(Le code ressemble à PM-XXXXX, sans espace avant ou après.)');
          setLoading(false);
          return;
        }
        directeur = dir;
      }

      // Etape 1 - Creer le compte sécurisé dans Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: email,
        password: password
      });
      if (authError) {
        if (authError.message.includes('already registered')) {
          alert('Cet email a déjà un compte. Connecte-toi.');
        } else {
          alert('Erreur: ' + authError.message);
        }
        setLoading(false);
        return;
      }

      const authId = authData.user.id;

      // Etape 2 - Creer le profil dans la table users, lié à l'id Auth
      let profil;
      if (registerMode === 'agent') {
        profil = {
          id: authId,
          email,
          entrepriseNom,
          role: 'client',
          role_societe: 'agent',
          societe_id: directeur.id,
          agent_valide: false,
          isPaid: false
        };
      } else if (registerMode === 'client_final') {
        profil = {
          id: authId,
          email,
          entrepriseNom,
          role: 'client',
          role_societe: 'client_final',
          societe_id: directeur.id,
          client_valide: false, // en attente de validation par la société
          isPaid: false
        };
      } else {
        // Code long + généré via crypto (résistant au brute-force ET non prédictible,
        // contrairement à Math.random()). ~2,8×10¹² combinaisons.
        const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // sans 0/O/1/I ambigus
        const randBytes = crypto.getRandomValues(new Uint8Array(8));
        const code = 'PM-' + Array.from(randBytes, (b) => ALPHABET[b % ALPHABET.length]).join('');
        profil = {
          id: authId,
          email,
          entrepriseNom,
          role: email === 'admin@promanager.com' ? 'admin' : 'client',
          role_societe: 'directeur',
          metier: null,
          code_societe: code,
          isPaid: email === 'kreeswaves@gmail.com' ? true : false,
          subscriptionEnd: email === 'kreeswaves@gmail.com' ? new Date('2099-12-31').toISOString() : null,
          subscriptionType: email === 'kreeswaves@gmail.com' ? 'lifetime' : null
        };
      }

      const { data, error } = await supabase.from('users').insert([profil]).select();
      if (error) throw error;

      if (data && data[0]) {
        setUser(data[0]);
        setEmail(''); setPassword(''); setEntrepriseNom(''); setCodeSociete('');
        if (registerMode === 'directeur') {
          await logActivity(data[0].id, 'Nouveau compte créé');
          // Email de bienvenue
          try {
            await supabase.functions.invoke('welcome-email', {
              body: { email: data[0].email, entrepriseNom: data[0].entrepriseNom, metier: data[0].metier || 'artisan' }
            });
          } catch (_) { /* non-bloquant */ }
        }
      }
    } catch (err) {
      alert('Erreur registration: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ===== MOT DE PASSE OUBLIÉ =====
  const sendResetEmail = async (e) => {
    e.preventDefault();
    if (!resetEmail) { alert('Indique ton email.'); return; }
    setResetSending(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail.trim(), {
        redirectTo: window.location.origin,
      });
      if (error) throw error;
      setResetSent(true);
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setResetSending(false);
    }
  };

  const updateNewPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { alert('6 caractères minimum.'); return; }
    if (newPassword !== newPasswordConfirm) { alert('Les deux mots de passe ne correspondent pas.'); return; }
    setNewPasswordSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      await supabase.auth.signOut();
      setNewPasswordDone(true);
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setNewPasswordSaving(false);
    }
  };

  // ===== LOGIN =====
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      alert('Email et mot de passe requis!');
      return;
    }

    setLoading(true);
    try {
      // Etape 1 - Verifier l'identité via Supabase Auth - verifie vraiment le mot de passe
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: email,
        password: password
      });

      if (authError) {
        alert('Email ou mot de passe incorrect.');
        setLoading(false);
        return;
      }

      // Etape 2 - Charger le profil correspondant
      const { data } = await supabase.from('users').select('*').eq('id', authData.user.id).maybeSingle();
      if (!data) {
        alert('Profil introuvable. Contacte le support.');
        setLoading(false);
        return;
      }

      setUser(data);
      setEmail('');
      setPassword('');

      if (data.role === 'admin') {
        loadAdminData();
      } else if (data.role_societe === 'client_final') {
        loadDemandes(data);
        loadMaFidelite(data);
      } else if (data.role_societe === 'agent') {
        if (data.agent_valide && data.societe_id) {
          loadClientData(data.societe_id);
          // Charger le métier du directeur pour adapter l'interface
          const { data: dir } = await supabase.from('users').select('metier').eq('id', data.societe_id).maybeSingle();
          if (dir && dir.metier) setPrestataireMetier(dir.metier);
          // Auto-filtrer le planning sur le nom de l'agent
          if (data.entrepriseNom) setFilterTechnicien(data.entrepriseNom);
          // Tutoriel première connexion agent validé
          if (!localStorage.getItem('planifpro_tutorial_done')) {
            setShowTutorial(true);
          }
        }
      } else if (data.isPaid || data.email === 'kreeswaves@gmail.com') {
        loadClientData(data.id);
        loadAgentsList(data.id);
        loadClientsList(data.id);
        loadDemandes(data);
        if (data.metier === 'dj') loadMateriels(data.id);
      }
    } catch (err) {
      alert('Erreur login: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Charger la liste des agents (vue directeur)
  const loadAgentsList = async (directeurId) => {
    try {
      const { data: agents } = await supabase.from('users').select('*').eq('societe_id', directeurId).eq('role_societe', 'agent');
      setPendingAgents((agents || []).filter(a => !a.agent_valide));
      setValidatedAgents((agents || []).filter(a => a.agent_valide));
    } catch (err) {
      console.error('Erreur agents:', err);
    }
  };

  const validateAgent = async (agentId, validate) => {
    try {
      if (validate) {
        // Vérifier la limite de la formule
        if (techniciensActuels >= maxTechniciens) {
          alert(`⛔ Limite atteinte !\n\nVotre formule autorise ${maxTechniciens} technicien(s). Pour en ajouter plus, passez à une formule supérieure depuis votre tableau de bord.`);
          return;
        }
        await supabase.from('users').update({ agent_valide: true }).eq('id', agentId);
      } else {
        await supabase.from('users').delete().eq('id', agentId);
      }
      loadAgentsList(user.id);
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  // Charger les clients d'un directeur (à valider / validés)
  const loadClientsList = async (directeurId) => {
    try {
      const { data: clients } = await supabase.from('users').select('*').eq('societe_id', directeurId).eq('role_societe', 'client_final');
      // client_valide === false → en attente. null (anciens) ou true → validé.
      setPendingClients((clients || []).filter(c => c.client_valide === false));
      setValidatedClients((clients || []).filter(c => c.client_valide !== false));
    } catch (err) {
      console.error('Erreur clients:', err);
    }
  };

  // Accepter (validate=true) ou refuser (validate=false → suppression) un client
  const validateClient = async (clientId, validate) => {
    try {
      if (validate) {
        await supabase.from('users').update({ client_valide: true }).eq('id', clientId);
      } else {
        await supabase.from('users').delete().eq('id', clientId);
      }
      loadClientsList(user.id);
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  const setAgentNiveau = async (agentId, niveau) => {
    try {
      await supabase.from('users').update({ agent_niveau: niveau }).eq('id', agentId);
      setValidatedAgents(validatedAgents.map(a => a.id === agentId ? { ...a, agent_niveau: niveau } : a));
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  // ===== PAYMENT STRIPE =====
  // Liens de paiement Stripe (Payment Links)
  const PAYMENT_LINKS = {
    '1month': 'https://buy.stripe.com/8x29ASgqaev5cte0TtgjC03',
    '6months': 'https://buy.stripe.com/7sY8wO0rccmX8cY31BgjC04',
    '12months': 'https://buy.stripe.com/eVq28qfm6fz9bpadGfgjC05'
  };

  const handlePayment = async (amount, subscriptionType) => {
    const lien = PAYMENT_LINKS[subscriptionType];
    if (!lien) {
      alert('Lien de paiement introuvable.');
      return;
    }
    // On passe l'email pré-rempli + l'id pour reconnaître le client au retour
    const url = `${lien}?prefilled_email=${encodeURIComponent(user.email)}&client_reference_id=${user.id}`;
    window.location.href = url;
  };

  // ===== LOAD CLIENT DATA =====
  const loadClientData = async (userId) => {
    try {
      const { data: interventions } = await supabase.from('interventions').select('*').eq('user_id', userId);
      const { data: factures } = await supabase.from('factures').select('*').eq('user_id', userId);
      const { data: team } = await supabase.from('team').select('*').eq('user_id', userId);
      const { data: absences } = await supabase.from('absences').select('*').eq('user_id', userId);
      const { data: fiches } = await supabase.from('fiches_clients').select('*').eq('user_id', userId);
      const { data: devisData } = await supabase.from('devis').select('*').eq('user_id', userId).order('created_at', { ascending: false });
      const { data: prestaData } = await supabase.from('prestations').select('*').eq('user_id', userId).order('nom');
      const { data: depData } = await supabase.from('depenses').select('*').eq('user_id', userId).order('date', { ascending: false });

      // Interventions : on garde une copie locale pour le mode hors-ligne
      if (interventions) { setInterventions(interventions); cacheInterventions(interventions); }
      else { setInterventions(getCachedInterventions()); }
      setFactures(factures || []);
      setTeam(team || []);
      setAbsences(absences || []);
      setClientsList(fiches || []);
      setDevis(devisData || []);
      setPrestations(prestaData || []);
      setDepenses(depData || []);
    } catch (err) {
      console.error('Erreur loadClientData:', err);
      // Hors-ligne : on affiche les interventions mises en cache
      setInterventions(getCachedInterventions());
    } finally {
      setClientDataLoaded(true);
    }
  };

  // ===== SYNCHRO HORS-LIGNE : flush la file dès le retour du réseau =====
  useEffect(() => {
    setPendingSync(queueCount());
    const goOnline = async () => {
      setIsOnline(true);
      if (queueCount() > 0) {
        const res = await flushQueue(supabase);
        setPendingSync(res.remaining);
        if (dataOwnerId) loadClientData(dataOwnerId); // recharge l'état canonique
      }
    };
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    if (navigator.onLine && queueCount() > 0) goOnline(); // rattrapage au montage
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [dataOwnerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== NOTIFICATIONS TEMPS RÉEL (Supabase Realtime) =====
  // Pro (directeur + agents) : alerté dès qu'une nouvelle demande client arrive.
  useEffect(() => {
    if (!user || user.role_societe === 'client_final' || !dataOwnerId) return;
    const channel = supabase
      .channel(`demandes-${dataOwnerId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'demandes', filter: `societe_id=eq.${dataOwnerId}` }, (payload) => {
        setDemandes((prev) => [payload.new, ...prev]);
        setRealtimeToast({ message: `📨 Nouvelle demande de ${payload.new.client_nom || 'un client'}` });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id, dataOwnerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Client final : alerté dès qu'une nouvelle facture le concernant est émise
  // (un abonnement par prestataire connu, filtré ensuite par correspondance de fiche).
  const prestataireIds = useMemo(
    () => Array.from(new Set((monHistorique || []).map((r) => r.prestataireId).filter(Boolean))),
    [monHistorique]
  );
  useEffect(() => {
    if (!user || user.role_societe !== 'client_final' || prestataireIds.length === 0) return;
    const channels = prestataireIds.map((ownerId) =>
      supabase
        .channel(`factures-${ownerId}-${user.id}`)
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'factures', filter: `user_id=eq.${ownerId}` }, (payload) => {
          // La policy RLS "client lit ses factures" garantit que seules MES factures
          // atteignent ce callback (Realtime applique la RLS avec le JWT de la session).
          const f = payload.new;
          setMesFacturesClient((prev) => [f, ...prev]);
          setRealtimeToast({ message: `🧾 Nouvelle facture disponible (${parseFloat(f.amount || 0).toFixed(2)}€)` });
        })
        .subscribe()
    );
    return () => { channels.forEach((c) => supabase.removeChannel(c)); };
  }, [user?.id, prestataireIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Client final : alerté quand le pro répond à un avis qu'il a laissé.
  useEffect(() => {
    if (!user || user.role_societe !== 'client_final') return;
    const channel = supabase
      .channel(`avis-reponse-${user.id}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'avis', filter: `auteur_id=eq.${user.id}` }, (payload) => {
        // Note : payload.old peut être incomplet selon REPLICA IDENTITY, donc on
        // ne compare pas old/new — un léger risque de re-notifier si le pro modifie
        // sa réponse déjà envoyée, acceptable ici (pas de conséquence gênante).
        if (payload.new.reponse) {
          setRealtimeToast({ message: `💬 Réponse à votre avis reçue` });
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id, user?.role_societe]); // eslint-disable-line react-hooks/exhaustive-deps

  // Client final : alerté quand le pro répond à sa demande (ce cas manquait —
  // seul un prospect sans compte reçoit un email via le trigger DB dédié ;
  // un client connecté ne voyait la réponse qu'en rouvrant l'app manuellement).
  useEffect(() => {
    if (!user || user.role_societe !== 'client_final') return;
    const channel = supabase
      .channel(`demande-reponse-${user.id}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'demandes', filter: `client_id=eq.${user.id}` }, (payload) => {
        if (payload.new.reponse) {
          setDemandes((prev) => prev.map((d) => (d.id === payload.new.id ? { ...d, ...payload.new } : d)));
          setRealtimeToast({ message: `💬 Réponse reçue à votre demande` });
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id, user?.role_societe]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-masquage du toast après quelques secondes
  useEffect(() => {
    if (!realtimeToast) return;
    const t = setTimeout(() => setRealtimeToast(null), 6000);
    return () => clearTimeout(t);
  }, [realtimeToast]);

  // ===== MESSAGERIE TEMPS RÉEL : nouveaux messages en direct =====
  useEffect(() => {
    if (!user) return;
    const estClient = user.role_societe === 'client_final';
    const filtre = estClient ? `client_id=eq.${user.id}` : `societe_id=eq.${dataOwnerId}`;
    if (!estClient && !dataOwnerId) return;
    const channel = supabase
      .channel(`messages-${estClient ? user.id : dataOwnerId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: filtre }, (payload) => {
        const m = payload.new;
        const monRole = estClient ? 'client' : 'pro';
        // Ajoute au fil si la conversation concernée est ouverte
        const convOuverte = estClient ? true : (m.client_id === convClientId);
        if (convOuverte) setMessages((prev) => prev.some(x => x.id === m.id) ? prev : [...prev, m]);
        // Toast si le message vient de l'autre partie
        if (m.sender !== monRole) setRealtimeToast({ message: '💬 Nouveau message' });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id, dataOwnerId, convClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // (côté pro) recharge la liste des conversations quand on ouvre l'onglet Messages
  useEffect(() => {
    if (activeTab === 'messages' && dataOwnerId && user && user.role_societe !== 'client_final') {
      loadProConversations(dataOwnerId);
    }
  }, [activeTab, dataOwnerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Note moyenne des avis (pilier "satisfaction" du score de santé)
  useEffect(() => {
    if (activeTab === 'analytics' && dataOwnerId) {
      supabase.from('avis').select('note').eq('pro_id', dataOwnerId).then(({ data }) => {
        if (data && data.length) setAvisMoyenne(data.reduce((s, a) => s + a.note, 0) / data.length);
      });
    }
  }, [activeTab, dataOwnerId]);

  // PlanifPro Insights : benchmark national par métier. La fonction renvoie
  // 0 ligne tant que < 5 entreprises du métier ne contribuent (k-anonymat
  // géré côté SQL) — `benchmark` reste alors `null`, jamais de faux chiffre.
  useEffect(() => {
    if (activeTab === 'analytics' && user?.metier) {
      supabase.rpc('get_benchmark_metier', { p_metier: user.metier }).then(({ data, error }) => {
        if (error) { console.error('Erreur benchmark:', error); return; }
        setBenchmark(data && data.length ? data[0] : null);
      });
    }
  }, [activeTab, user?.metier]);

  // ===== SUIVI TECHNICIEN TEMPS RÉEL (côté client) =====
  useEffect(() => {
    if (!user || user.role_societe !== 'client_final') return;
    (async () => {
      const { data } = await supabase.from('tech_tracking').select('*')
        .eq('client_id', user.id).eq('status', 'en_route')
        .order('updated_at', { ascending: false }).limit(1).maybeSingle();
      if (data) setClientTracking(data);
    })();
    const channel = supabase.channel(`tracking-${user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tech_tracking', filter: `client_id=eq.${user.id}` }, (payload) => {
        const t = payload.new;
        if (t && t.status === 'en_route') { setClientTracking(t); }
        else setClientTracking(null);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== PORTAIL CLIENT FINAL : demandes d'intervention =====
  // Charge les demandes : pour un client final = les siennes ; pour un directeur = celles reçues
  const loadDemandes = async (u) => {
    try {
      let query = supabase.from('demandes').select('*').order('created_at', { ascending: false });
      if (u.role_societe === 'client_final') {
        query = query.eq('client_id', u.id);
        // Récupérer le métier du prestataire pour adapter le vocabulaire
        if (u.societe_id) {
          const { data: presta } = await supabase.from('users').select('metier').eq('id', u.societe_id).maybeSingle();
          if (presta && presta.metier) setPrestataireMetier(presta.metier);
        }
      } else {
        // directeur : demandes adressées à sa société
        query = query.eq('societe_id', u.id);
      }
      const { data, error } = await query;
      if (error) { console.error('Erreur lecture demandes:', error); return; }
      setDemandes(data || []);
    } catch (err) {
      console.error('Erreur loadDemandes:', err);
    }
  };

  // ===== MESSAGERIE client ↔ pro =====
  // Charge une conversation (societeId + clientId) et marque comme lus les
  // messages reçus par l'utilisateur courant.
  const loadMessages = async (societeId, clientId, monRole) => {
    try {
      const { data } = await supabase.from('messages').select('*')
        .eq('societe_id', societeId).eq('client_id', clientId)
        .order('created_at', { ascending: true }).limit(500);
      setMessages(data || []);
      // marquer lus les messages venant de l'autre partie
      const recus = (monRole === 'client') ? 'pro' : 'client';
      const nonLus = (data || []).filter(m => m.sender === recus && !m.lu).map(m => m.id);
      if (nonLus.length) supabase.from('messages').update({ lu: true }).in('id', nonLus);
    } catch (err) {
      console.error('Erreur loadMessages:', err);
    }
  };

  const sendMessage = async (societeId, clientId, sender) => {
    const texte = msgInput.trim();
    if (!texte || !societeId || !clientId) return;
    setSendingMsg(true);
    const { data, error } = await supabase.from('messages')
      .insert({ societe_id: societeId, client_id: clientId, sender, texte })
      .select();
    if (!error && data && data[0]) setMessages(prev => [...prev, data[0]]);
    setMsgInput('');
    setSendingMsg(false);
  };

  // (côté pro) liste des conversations, groupées par client
  const loadProConversations = async (societeId) => {
    try {
      const { data } = await supabase.from('messages').select('*')
        .eq('societe_id', societeId).order('created_at', { ascending: true }).limit(2000);
      const parClient = {};
      (data || []).forEach((m) => {
        if (!parClient[m.client_id]) parClient[m.client_id] = { client_id: m.client_id, dernier: '', quand: null, unread: 0 };
        parClient[m.client_id].dernier = m.texte;
        parClient[m.client_id].quand = m.created_at;
        if (m.sender === 'client' && !m.lu) parClient[m.client_id].unread += 1;
      });
      setProConvs(Object.values(parClient).sort((a, b) => new Date(b.quand) - new Date(a.quand)));
    } catch (err) {
      console.error('Erreur loadProConversations:', err);
    }
  };

  // ===== SUIVI TECHNICIEN (partage de position) =====
  // Avant la toute première activation, on demande un consentement explicite
  // (Replay GPS) — jamais redemandé ensuite (geoloc_consent_at une fois posé).
  const startTracking = async (interv) => {
    if (!navigator.geolocation) { alert('Géolocalisation non disponible sur cet appareil.'); return; }
    if (!user.geoloc_consent_at) {
      setPendingTrackIntervention(interv);
      setShowGeolocConsent(true);
      return;
    }
    await lancerTracking(interv);
  };

  const accepterGeolocConsent = async () => {
    const horodatage = new Date().toISOString();
    const { error } = await supabase.from('users').update({ geoloc_consent_at: horodatage }).eq('id', user.id);
    if (!error) setUser({ ...user, geoloc_consent_at: horodatage });
    setShowGeolocConsent(false);
    const interv = pendingTrackIntervention;
    setPendingTrackIntervention(null);
    if (interv) await lancerTracking(interv);
  };

  const lancerTracking = async (interv) => {
    const eta = window.prompt("Heure d'arrivée estimée (ex : « 20 min » ou « 14h30 ») — optionnel :", '') || null;
    // Rattache le suivi au compte du client (via l'email de sa fiche) pour qu'il le voie
    let clientUserId = null;
    const fiche = clientsList.find(f => f.id === interv.client_id || f.nom === interv.client);
    if (fiche?.email) {
      const { data: cu } = await supabase.from('users').select('id').eq('email', fiche.email).eq('role_societe', 'client_final').maybeSingle();
      clientUserId = cu?.id || null;
    }
    lastPushRef.current = 0;
    lastPingRef.current = 0;
    const push = (lat, lng) => {
      const now = Date.now();
      if (now - lastPushRef.current >= 12000) { // throttle 12s : position live (ETA client)
        lastPushRef.current = now;
        supabase.from('tech_tracking').upsert({
          intervention_id: interv.id, societe_id: dataOwnerId, client_id: clientUserId,
          tech_nom: user.entrepriseNom || 'Votre technicien', lat, lng, eta, status: 'en_route', updated_at: new Date().toISOString(),
        }, { onConflict: 'intervention_id' });
      }
      if (now - lastPingRef.current >= 60000) { // throttle 60s : trace du replay (moins dense, suffisant pour rejouer un trajet)
        lastPingRef.current = now;
        supabase.from('position_pings').insert({
          intervention_id: interv.id, societe_id: dataOwnerId, tech_user_id: user.id, lat, lng,
        }).then(({ error }) => { if (error) console.error('Erreur position_pings:', error); });
      }
    };
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => push(pos.coords.latitude, pos.coords.longitude),
      () => alert('Impossible d\'accéder à la position. Autorisez la géolocalisation.'),
      { enableHighAccuracy: true, maximumAge: 8000, timeout: 20000 }
    );
    setTrackingId(interv.id);
    setTrackingClientNom(interv.client || 'le client');
    if (!clientUserId) alert('Suivi démarré. Note : ce client n\'a pas de compte PlanifPro, il ne verra pas le suivi en ligne.');
  };

  const stopTracking = async (arrived) => {
    if (watchIdRef.current != null) { navigator.geolocation.clearWatch(watchIdRef.current); watchIdRef.current = null; }
    if (trackingId) {
      await supabase.from('tech_tracking').update({ status: arrived ? 'arrive' : 'off', updated_at: new Date().toISOString() }).eq('intervention_id', trackingId);
    }
    setTrackingId(null); setTrackingClientNom('');
  };

  // ===== MATÉRIEL DJ (location) =====
  const loadMateriels = async (userId) => {
    try {
      const { data } = await supabase.from('materiels').select('*').eq('user_id', userId).order('nom');
      setMateriels(data || []);
    } catch (err) {
      console.error('Erreur loadMateriels:', err);
    }
  };

  const addMateriel = async () => {
    if (!matNom) {
      alert('Indique le nom du matériel.');
      return;
    }
    try {
      const { data, error } = await supabase.from('materiels').insert([{
        user_id: user.id,
        nom: matNom,
        quantite: parseInt(matQuantite) || 1,
        statut: 'dispo',
        loue_par: '',
        retour_prevu: ''
      }]).select();
      if (error) throw error;
      if (data) {
        setMateriels([...materiels, ...data]);
        setMatNom(''); setMatQuantite('1');
      }
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  const updateMateriel = async (id, updates) => {
    try {
      const { error } = await supabase.from('materiels').update(updates).eq('id', id);
      if (error) throw error;
      setMateriels(materiels.map(m => m.id === id ? { ...m, ...updates } : m));
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  const deleteMateriel = async (id) => {
    if (!window.confirm('Supprimer cet équipement ?')) return;
    try {
      await supabase.from('materiels').delete().eq('id', id);
      setMateriels(materiels.filter(m => m.id !== id));
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  // Charge les cartes de fidélité + historique de la cliente (par email, tous prestataires)
  const loadMaFidelite = async (u) => {
    try {
      // Toutes les fiches clients qui ont l'email de la cliente
      const { data: fiches } = await supabase.from('fiches_clients').select('*').eq('email', u.email);
      if (!fiches || fiches.length === 0) {
        setMesCartes([]);
        setMonHistorique([]);
        return;
      }
      // Pour chaque fiche, récupérer la config fidélité du prestataire + l'historique
      const cartes = [];
      let historique = [];
      for (const fiche of fiches) {
        const { data: presta } = await supabase.from('users').select('entrepriseNom, fidelite_config, metier').eq('id', fiche.user_id).maybeSingle();
        let config = null;
        if (presta && presta.fidelite_config) {
          try { config = typeof presta.fidelite_config === 'string' ? JSON.parse(presta.fidelite_config) : presta.fidelite_config; } catch (e) {}
        }
        if (config) {
          cartes.push({
            prestataire: presta.entrepriseNom || 'Prestataire',
            points: fiche.points_fidelite || 0,
            objectif: config.objectif || 100,
            recompense: config.recompense || ''
          });
        }
        // Historique des RDV de cette fiche
        const { data: rdv } = await supabase.from('interventions').select('*').or(`client_id.eq.${fiche.id},client.eq.${fiche.nom}`).eq('user_id', fiche.user_id);
        if (rdv) {
          historique = historique.concat(rdv.map(r => ({ ...r, prestataire: presta?.entrepriseNom || '', prestataireId: fiche.user_id })));
        }
      }
      historique.sort((a, b) => new Date(b.date) - new Date(a.date));
      // Charger les factures liées à cet email (par toutes les fiches trouvées)
      let toutesFactures = [];
      for (const fiche of fiches) {
        const { data: fcts } = await supabase.from('factures').select('*').eq('user_id', fiche.user_id).eq('client', fiche.nom);
        if (fcts) toutesFactures = toutesFactures.concat(fcts);
      }
      setMesFacturesClient(toutesFactures);
      setMesCartes(cartes);
      setMonHistorique(historique);
    } catch (err) {
      console.error('Erreur loadMaFidelite:', err);
    }
  };

  // Initialise le formulaire de profil client depuis le compte
  useEffect(() => {
    if (user && user.role_societe === 'client_final') {
      setCpType(user.client_type || 'particulier');
      setCpNom(user.entrepriseNom || '');
      setCpSiret(user.client_siret || '');
      setCpTel(user.client_telephone || '');
      setCpAdresse(user.client_adresse || '');
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Enregistre le profil client (particulier / pro)
  const saveClientProfile = async () => {
    setCpSaving(true);
    const patch = {
      client_type: cpType,
      entrepriseNom: cpNom,
      client_siret: cpType === 'pro' ? cpSiret : null,
      client_telephone: cpTel,
      client_adresse: cpAdresse,
    };
    const { error } = await supabase.from('users').update(patch).eq('id', user.id);
    setCpSaving(false);
    if (error) { alert('Erreur : ' + error.message); return; }
    setUser({ ...user, ...patch });
    alert('Profil enregistré ✅');
  };

  const searchAnnuaire = async () => {
    if (!annLat || !annLng) { alert("Sélectionnez une adresse dans les suggestions pour lancer la recherche."); return; }
    setAnnSearching(true);
    try {
      const { data, error } = await supabase.rpc('search_pros_proximity', {
        p_lat: annLat, p_lng: annLng, p_metier: annMetier || null, p_rayon_km: 30,
      });
      if (error) throw error;
      setAnnResults(data || []);
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setAnnSearching(false);
    }
  };

  const handleDemandePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setDemPhotos(prev => [...prev, ev.target.result]);
      };
      reader.readAsDataURL(file);
    }
  };

  const submitDemande = async () => {
    if (!demTitre || !demDescription) {
      alert('Indiquez au moins un titre et une description.');
      return;
    }
    setSavingDemande(true);
    try {
      // Sécurité : si societe_id manque, on le récupère depuis le profil
      let societeId = user.societe_id;
      if (!societeId) {
        const { data: moi } = await supabase.from('users').select('societe_id').eq('id', user.id).maybeSingle();
        societeId = moi?.societe_id || null;
      }
      if (!societeId) {
        alert('Votre compte n\'est rattaché à aucun prestataire. Reconnectez-vous ou contactez votre prestataire.');
        setSavingDemande(false);
        return;
      }
      const { data, error } = await supabase.from('demandes').insert([{
        client_id: user.id,
        client_nom: user.entrepriseNom || '',
        client_email: user.email,
        societe_id: societeId,
        titre: demTitre,
        description: demDescription,
        adresse: demAdresse,
        urgence: demUrgence,
        creneaux: demCreneaux,
        date_souhaitee: demDateSouhaitee,
        heure_souhaitee: demHeureSouhaitee,
        photos: demPhotos,
        statut: 'nouvelle',
        reponse: ''
      }]).select();
      if (error) throw error;
      if (data) {
        setDemandes([data[0], ...demandes]);
        setDemTitre(''); setDemDescription(''); setDemAdresse(''); setDemUrgence('normale'); setDemCreneaux(''); setDemDateSouhaitee(''); setDemHeureSouhaitee(''); setDemPhotos([]);
        setShowDemandeForm(false);
        alert('✅ Votre demande a bien été envoyée ! Votre prestataire la traitera rapidement.');
      }
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setSavingDemande(false);
    }
  };

  // Côté directeur : répondre / changer le statut d'une demande
  const updateDemande = async (demandeId, updates) => {
    try {
      const { error } = await supabase.from('demandes').update(updates).eq('id', demandeId);
      if (error) throw error;
      setDemandes(demandes.map(d => d.id === demandeId ? { ...d, ...updates } : d));
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  // Valider une demande → crée le RDV dans le planning du pro
  const validerDemandeEnRDV = async (demande) => {
    try {
      const dateRdv = demande.date_souhaitee || new Date().toISOString().split('T')[0];
      // Formatage de la date en local (évite le décalage UTC qui ajoutait un jour)
      const formatDateFr = (dateStr) => {
        const parts = String(dateStr).split('T')[0].split('-');
        if (parts.length !== 3) return dateStr;
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      };
      const interventionData = {
        user_id: dataOwnerId,
        titre: demande.titre || ('RDV - ' + (demande.client_nom || '')),
        client: demande.client_nom || '',
        adresse: demande.adresse || '',
        date: dateRdv,
        heure: demande.heure_souhaitee || '09:00',
        duree: '60',
        nature: demande.description || '',
        type_mission: isBeaute ? 'intervention' : 'rdv',
        statut: 'planifie'
      };
      const { data, error } = await supabase.from('interventions').insert([interventionData]).select();
      if (error) throw error;
      if (data) {
        setInterventions([...interventions, ...data]);
        await updateDemande(demande.id, { statut: 'planifiee', reponse: `RDV confirmé le ${formatDateFr(dateRdv)}${demande.heure_souhaitee ? ' à ' + demande.heure_souhaitee : ''}.` });
        alert('✅ RDV créé dans votre planning et demande confirmée !');
      }
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  // ===== FICHES CLIENTS (carnet) =====
  const openClientCreate = () => {
    setEditingClientId(null);
    setCliNom('');
    setCliType('particulier');
    setCliEntreprise('');
    setCliSiret('');
    setCliAdresse('');
    setCliTel('');
    setCliEmail('');
    setCliNotes('');
    setCliLogo('');
    setCliLat(null); setCliLng(null);
    setShowModalClient(true);
  };

  const openClientEdit = (c) => {
    setEditingClientId(c.id);
    setCliNom(c.nom || '');
    setCliType(c.type || 'particulier');
    setCliEntreprise(c.entreprise || '');
    setCliSiret(c.siret || '');
    setCliAdresse(c.adresse || '');
    setCliTel(c.tel || '');
    setCliEmail(c.email || '');
    setCliNotes(c.notes || '');
    setCliLogo(c.logo || '');
    setCliLat(c.lat ?? null); setCliLng(c.lng ?? null);
    setShowModalClient(true);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `logo_${Date.now()}.${fileExt}`;
      const filePath = `logos/${user.id}/${fileName}`;
      const { error: uploadError } = await supabase.storage.from('interventions').upload(filePath, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('interventions').getPublicUrl(filePath);
      setCliLogo(publicUrl);
    } catch (err) {
      alert('Erreur upload logo: ' + err.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const saveClient = async () => {
    if (!cliNom) {
      alert('Le nom est obligatoire!');
      return;
    }
    setSavingClient(true);
    try {
      const ficheData = {
        user_id: user.id,
        nom: cliNom,
        type: cliType,
        entreprise: cliEntreprise,
        siret: cliSiret,
        adresse: cliAdresse,
        tel: cliTel,
        email: cliEmail,
        notes: cliNotes,
        logo: cliLogo,
        lat: cliLat,
        lng: cliLng
      };
      if (editingClientId) {
        const { error } = await supabase.from('fiches_clients').update(ficheData).eq('id', editingClientId);
        if (error) throw error;
        setClientsList(clientsList.map(c => c.id === editingClientId ? { ...c, ...ficheData } : c));
      } else {
        const { data, error } = await supabase.from('fiches_clients').insert([ficheData]).select();
        if (error) throw error;
        if (data) setClientsList([...clientsList, ...data]);
      }
      setShowModalClient(false);
      setEditingClientId(null);
      setCliNom(''); setCliType('particulier'); setCliEntreprise('');
      setCliSiret(''); setCliAdresse(''); setCliTel(''); setCliEmail('');
      setCliNotes(''); setCliLogo('');
      setCliLat(null); setCliLng(null);
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setSavingClient(false);
    }
  };

  const deleteClient = async (id) => {
    if (!window.confirm('Supprimer cette fiche client?')) return;
    try {
      await supabase.from('fiches_clients').delete().eq('id', id);
      setClientsList(clientsList.filter(c => c.id !== id));
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  // ===== FIDÉLITÉ (mode beauté) =====
  const saveFideliteConfig = async () => {
    try {
      const config = {
        objectif: parseInt(fidObjectif) || 100,
        recompense: fidRecompense,
        prestations: fidPrestations
      };
      const { error } = await supabase.from('users').update({ fidelite_config: JSON.stringify(config) }).eq('id', user.id);
      if (error) throw error;
      setUser({ ...user, fidelite_config: JSON.stringify(config) });
      alert('✅ Configuration fidélité enregistrée !');
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  const addPrestation = () => {
    if (!fidPrestaNom || !fidPrestaPoints) {
      alert('Indique le nom de la prestation et ses points.');
      return;
    }
    setFidPrestations([...fidPrestations, { nom: fidPrestaNom, points: parseInt(fidPrestaPoints) || 0 }]);
    setFidPrestaNom('');
    setFidPrestaPoints('');
  };

  const removePrestation = (index) => {
    setFidPrestations(fidPrestations.filter((_, i) => i !== index));
  };

  // Ajouter/retirer des points à un client
  const updateClientPoints = async (client, delta) => {
    try {
      const nouveauxPoints = Math.max(0, (client.points_fidelite || 0) + delta);
      const { error } = await supabase.from('fiches_clients').update({ points_fidelite: nouveauxPoints }).eq('id', client.id);
      if (error) throw error;
      setClientsList(clientsList.map(c => c.id === client.id ? { ...c, points_fidelite: nouveauxPoints } : c));
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  // Réinitialiser la carte (récompense utilisée)
  const resetClientPoints = async (client) => {
    if (!window.confirm(`Réinitialiser la carte de ${client.nom} ? (récompense utilisée)`)) return;
    try {
      const { error } = await supabase.from('fiches_clients').update({ points_fidelite: 0 }).eq('id', client.id);
      if (error) throw error;
      setClientsList(clientsList.map(c => c.id === client.id ? { ...c, points_fidelite: 0 } : c));
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  // Pré-remplir l'intervention depuis une fiche client sélectionnée
  const applyClientToForm = (clientId) => {
    setFormClientId(clientId);
    if (!clientId) return;
    const c = clientsList.find(x => String(x.id) === String(clientId));
    if (!c) return;
    setFormClient(c.type === 'entreprise' && c.entreprise ? c.entreprise : c.nom);
    setFormAdresse(c.adresse || '');
    setFormLat(c.lat ?? null); setFormLng(c.lng ?? null);
  };

  // ===== PLANNING FUNCTIONS =====
  const openModalCreate = (date) => {
    setSelectedDate(date);
    setSelectedIntervention(null);
    setModalMode('create');
    setActiveModalTab('infos');
    setFormTitre('');
    setFormClient('');
    setFormClientId('');
    setFormAdresse('');
    setFormLat(null); setFormLng(null);
    setFormHeure('09:00');
    setFormDuree('60');
    setFormTechnicien('');
    setFormTechniciensExtra([]);
    setFormVehicule(user.vehicule_defaut || '');
    setFormNature('');
    setFormHeureArrivee('');
    setFormHeureDepart('');
    setFormTypeMission(isBeaute ? 'intervention' : 'rdv');
    setFormTravaux('');
    setFormObservations('');
    setFormMateriel('');
    setFormPieces('');
    setFormBilan('');
    setFormChecklist([]);
    setNewChecklistItem('');
    setPhotoCategorie('avant');
    setSignatureClient('');
    setSignatureTech('');
    setTimerSeconds(0);
    setTimerRunning(false);
    setPhotos([]);
    setShowModalIntervention(true);
  };

  const openModalEdit = (intervention) => {
    setSelectedIntervention(intervention);
    setSelectedDate(new Date(intervention.date));
    setModalMode('edit');
    setActiveModalTab('infos');
    setFormTitre(intervention.titre || '');
    setFormClient(intervention.client || '');
    setFormClientId(intervention.client_id || '');
    setFormAdresse(intervention.adresse || '');
    setFormLat(intervention.lat ?? null); setFormLng(intervention.lng ?? null);
    setFormHeure(intervention.heure || '09:00');
    setFormDuree(intervention.duree || '60');
    setFormTechnicien(intervention.technicien || '');
    setFormTechniciensExtra(intervention.techniciens_extra ? JSON.parse(intervention.techniciens_extra) : []);
    setFormVehicule(intervention.vehicule || '');
    setFormNature(intervention.nature || '');
    setFormHeureArrivee(intervention.heure_arrivee || intervention.heure || '');
    setFormHeureDepart(intervention.heure_depart || '');
    setFormTypeMission(intervention.type_mission || '');
    setFormTravaux(intervention.travaux || '');
    setFormObservations(intervention.observations || '');
    setFormMateriel(intervention.materiel || '');
    setFormPieces(intervention.pieces || '');
    setFormBilan(intervention.bilan || '');
    setFormChecklist(intervention.checklist ? JSON.parse(intervention.checklist) : []);
    setNewChecklistItem('');
    setPhotoCategorie('avant');
    setSignatureClient(intervention.signature_client || '');
    setSignatureTech(intervention.signature_tech || '');
    setTimerSeconds(intervention.timer_seconds || 0);
    setTimerRunning(false);
    // Compat: anciennes photos = tableau de strings → on convertit en objets
    let parsedPhotos = intervention.photos ? JSON.parse(intervention.photos) : [];
    parsedPhotos = parsedPhotos.map(p => typeof p === 'string' ? { url: p, categorie: 'pendant', legende: '' } : p);
    setPhotos(parsedPhotos);
    setShowModalIntervention(true);
  };

  const closeModal = () => {
    setShowModalIntervention(false);
    setSelectedIntervention(null);
    setSelectedDate(null);
    setFormTitre(''); setFormClient(''); setFormClientId('');
    setFormAdresse(''); setFormHeure('09:00'); setFormDuree('60');
    setFormRecurrence('none'); setFormRecurrenceCount('4');
    setFormTechnicien(''); setFormTechniciensExtra([]);
    setFormVehicule(''); setFormNature('');
    setFormHeureArrivee(''); setFormHeureDepart('');
    setFormTypeMission(''); setFormTravaux(''); setFormObservations('');
    setFormMateriel(''); setFormPieces(''); setFormBilan(''); setFormChecklist([]);
    setSignatureClient(''); setSignatureTech('');
    setActiveModalTab('infos');
  };

  const saveIntervention = async () => {
    if (formTypeMission === 'rdv') {
      if (!formClient || !formAdresse) {
        alert('Pour un RDV, indique au moins le client et l\'adresse.');
        return;
      }
    } else if (!formTitre || !formClient || !formAdresse) {
      alert('Remplis au moins le titre, le client et l\'adresse.');
      return;
    }
    setSavingIntervention(true);
    try {
      // En mode DJ/beauté, l'heure principale (affichée au planning) = l'heure de début saisie
      const heurePrincipale = ((isDJ || isBeaute) && formHeureArrivee) ? formHeureArrivee : formHeure;
      const interventionData = {
        user_id: dataOwnerId,
        titre: formTitre || ('RDV - ' + formClient),
        client: formClient,
        client_id: formClientId || null,
        adresse: formAdresse,
        lat: formLat,
        lng: formLng,
        date: `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`,
        heure: heurePrincipale,
        duree: formDuree,
        technicien: formTechnicien,
        techniciens_extra: JSON.stringify(formTechniciensExtra),
        vehicule: formVehicule,
        nature: formNature,
        heure_arrivee: formHeureArrivee,
        heure_depart: formHeureDepart,
        type_mission: formTypeMission,
        travaux: formTravaux,
        observations: formObservations,
        materiel: formMateriel,
        pieces: formPieces,
        bilan: formBilan,
        checklist: JSON.stringify(formChecklist),
        signature_client: signatureClient,
        signature_tech: signatureTech,
        timer_seconds: timerSeconds,
        photos: JSON.stringify(photos)
      };

      // ---- HORS-LIGNE : enregistrement local + file de synchro ----
      if (!navigator.onLine) {
        if (modalMode === 'create') {
          const tempId = newTempId();
          const localRow = { ...interventionData, id: tempId, statut: 'planifie', _pending: true };
          queueOp({ kind: 'create', table: 'interventions', tempId, data: interventionData });
          const next = [...interventions, localRow];
          setInterventions(next); cacheInterventions(next);
        } else if (modalMode === 'edit' && selectedIntervention) {
          queueOp({ kind: 'update', table: 'interventions', id: selectedIntervention.id, data: interventionData });
          const next = interventions.map(i => i.id === selectedIntervention.id ? { ...i, ...interventionData, _pending: true } : i);
          setInterventions(next); cacheInterventions(next);
        }
        setPendingSync(queueCount());
        setSavingIntervention(false);
        closeModal();
        alert('📴 Pas de réseau — rapport enregistré sur l\'appareil. Il partira tout seul dès le retour du réseau.');
        return;
      }

      // Mémoriser le véhicule comme véhicule par défaut (s'il a changé)
      if (formVehicule && formVehicule !== user.vehicule_defaut) {
        supabase.from('users').update({ vehicule_defaut: formVehicule }).eq('id', user.id).then(() => {
          setUser({ ...user, vehicule_defaut: formVehicule });
        });
      }

      if (modalMode === 'create') {
        let toInsert = [interventionData];
        // Récurrence : générer les occurrences suivantes
        if (formRecurrence !== 'none') {
          const nb = Math.min(parseInt(formRecurrenceCount) || 1, 52);
          const stepDays = formRecurrence === 'weekly' ? 7 : formRecurrence === 'biweekly' ? 14 : 0;
          for (let k = 1; k < nb; k++) {
            const d = new Date(selectedDate);
            if (formRecurrence === 'monthly') d.setMonth(d.getMonth() + k);
            else d.setDate(d.getDate() + stepDays * k);
            toInsert.push({ ...interventionData, date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` });
          }
        }
        const { data, error } = await supabase.from('interventions').insert(toInsert).select();
        if (error) throw error;
        if (data) {
          setInterventions([...interventions, ...data]);
          if (toInsert.length > 1) alert(`✅ ${toInsert.length} interventions créées (récurrence).`);
          closeModal();
        }
      } else if (modalMode === 'edit' && selectedIntervention) {
        const { error } = await supabase.from('interventions').update(interventionData).eq('id', selectedIntervention.id);
        if (error) throw error;
        setInterventions(interventions.map(i => i.id === selectedIntervention.id ? { ...i, ...interventionData } : i));
        closeModal();
      }
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setSavingIntervention(false);
    }
  };

  const deleteIntervention = async (id) => {
    if (!window.confirm('Supprimer cette intervention?')) return;
    try {
      await supabase.from('interventions').delete().eq('id', id);
      setInterventions(interventions.filter(i => i.id !== id));
      closeModal();
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  const toggleCloture = async (interventionToClose) => {
    const newStatut = interventionToClose.statut === 'termine' ? 'en_cours' : 'termine';
    try {
      const { error } = await supabase.from('interventions').update({ statut: newStatut }).eq('id', interventionToClose.id);
      if (error) throw error;
      setInterventions(interventions.map(i => i.id === interventionToClose.id ? { ...i, statut: newStatut } : i));
      if (selectedIntervention && selectedIntervention.id === interventionToClose.id) {
        setSelectedIntervention({ ...selectedIntervention, statut: newStatut });
      }
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingPhoto(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `photos/${user.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('interventions')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('interventions')
        .getPublicUrl(filePath);

      setPhotos([...photos, { url: publicUrl, categorie: photoCategorie, legende: '' }]);
    } catch (err) {
      alert('Erreur upload: ' + err.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const updatePhotoLegende = (index, legende) => {
    setPhotos(photos.map((p, i) => i === index ? { ...p, legende } : p));
  };

  const updatePhotoCategorie = (index, categorie) => {
    setPhotos(photos.map((p, i) => i === index ? { ...p, categorie } : p));
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  // ===== CHECKLIST =====
  const addChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    setFormChecklist([...formChecklist, { text: newChecklistItem.trim(), done: false }]);
    setNewChecklistItem('');
  };

  const toggleChecklistItem = (index) => {
    setFormChecklist(formChecklist.map((item, i) => i === index ? { ...item, done: !item.done } : item));
  };

  const removeChecklistItem = (index) => {
    setFormChecklist(formChecklist.filter((_, i) => i !== index));
  };

  const moveChecklistItem = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= formChecklist.length) return;
    const newList = [...formChecklist];
    // Échange les deux éléments
    [newList[index], newList[newIndex]] = [newList[newIndex], newList[index]];
    setFormChecklist(newList);
  };

  // ===== GENERATION PDF =====
  const generatePDF = async () => {
    setGeneratingPDF(true);
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const navy = [13, 35, 75];
      const orange = [237, 125, 49];

      const loadImg = (url) => new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = url;
      });

      // Numéro de rapport basé sur la date
      const dateStr = selectedDate ? selectedDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      const numRapport = dateStr.replace(/-/g, '');
      const dateFr = selectedDate ? selectedDate.toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR');

      // Liste des techniciens
      const allTechs = [formTechnicien, ...formTechniciensExtra].filter(Boolean).join(', ') || '—';

      // Logo du client (si défini dans sa fiche)
      const fiche = clientsList.find(x => String(x.id) === String(formClientId));
      const logoUrl = fiche && fiche.logo ? fiche.logo : '';
      let logoImg = null;
      if (logoUrl) logoImg = await loadImg(logoUrl);

      // ===== PAGE 1 : COUVERTURE =====
      doc.setFillColor(navy[0], navy[1], navy[2]);
      doc.rect(0, 0, pageW, 60, 'F');
      doc.rect(0, pageH - 50, pageW, 50, 'F');

      // Logo en haut (sur le bandeau)
      if (logoImg) {
        const lw = 45;
        const lh = (logoImg.height / logoImg.width) * lw;
        const finalH = Math.min(lh, 40);
        const finalW = (logoImg.width / logoImg.height) * finalH;
        try { doc.addImage(logoImg, 'PNG', (pageW - finalW) / 2, 10, finalW, finalH); } catch (e) {}
      }

      doc.setTextColor(orange[0], orange[1], orange[2]);
      doc.setFontSize(34);
      doc.setFont(undefined, 'bold');
      doc.text("Bon d'intervention", pageW / 2, 110, { align: 'center' });

      doc.setTextColor(navy[0], navy[1], navy[2]);
      doc.setFontSize(13);
      doc.text('24h/24 - 7j/7', pageW / 2, 122, { align: 'center' });

      // Bloc client au centre
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(12);
      let cy = 150;
      doc.setFont(undefined, 'bold');
      doc.text('Client :', 30, cy);
      doc.setFont(undefined, 'normal');
      doc.text(formClient || '—', 70, cy);
      cy += 9;
      if (formAdresse) {
        doc.setFont(undefined, 'bold');
        doc.text('Lieu :', 30, cy);
        doc.setFont(undefined, 'normal');
        const adrLines = doc.splitTextToSize(formAdresse, pageW - 100);
        doc.text(adrLines, 70, cy);
        cy += adrLines.length * 7;
      }
      doc.setFont(undefined, 'bold');
      doc.text('Date :', 30, cy);
      doc.setFont(undefined, 'normal');
      doc.text(dateFr, 70, cy);

      // ===== PAGE 2 : INFORMATIONS GENERALES =====
      doc.addPage();
      doc.setFillColor(255, 255, 255);
      doc.rect(0, 0, pageW, pageH, 'F');
      doc.setTextColor(orange[0], orange[1], orange[2]);
      doc.setFontSize(20);
      doc.setFont(undefined, 'bold');
      doc.text("Informations generales de l'intervention", pageW / 2, 25, { align: 'center' });

      const infos = [
        ["Rapport d'intervention N°", numRapport],
        ["Date de l'intervention", dateFr],
        ["Lieu de l'intervention", formAdresse || '—'],
        ["Nom du/des technicien(s)", allTechs],
        ["Vehicule utilise", formVehicule || '—'],
        ["Nature de la demande", formNature || formTypeMission || '—']
      ];

      let iy = 50;
      infos.forEach(([label, value]) => {
        doc.setFillColor(navy[0], navy[1], navy[2]);
        doc.rect(20, iy, 75, 14, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const labelLines = doc.splitTextToSize(label, 70);
        doc.text(labelLines, 23, iy + 9);

        doc.setFillColor(120, 120, 120);
        doc.rect(98, iy, pageW - 118, 14, 'F');
        doc.setTextColor(255, 255, 255);
        const valLines = doc.splitTextToSize(String(value), pageW - 124);
        doc.text(valLines, 101, iy + 9);
        iy += 18;
      });

      // ===== PAGES DEROULEMENT : photos AVANT puis PENDANT =====
      const photoAvant = photos.filter(p => p.categorie === 'avant');
      const photoPendant = photos.filter(p => p.categorie === 'pendant');
      const photoApres = photos.filter(p => p.categorie === 'apres');

      const addDeroulementPage = async (titre, photo, texteDefaut) => {
        doc.addPage();
        // Bandeau titre à droite façon collègue
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, pageW, pageH, 'F');
        doc.setTextColor(navy[0], navy[1], navy[2]);
        doc.setFontSize(18);
        doc.setFont(undefined, 'bold');
        doc.text(titre, pageW / 2, 20, { align: 'center' });
        doc.setDrawColor(orange[0], orange[1], orange[2]);
        doc.setLineWidth(1);
        doc.line(20, 26, pageW - 20, 26);

        let py = 38;
        if (photo) {
          const img = await loadImg(photo.url);
          if (img) {
            const maxW = pageW - 60;
            const maxH = 150;
            let w = maxW;
            let h = (img.height / img.width) * w;
            if (h > maxH) { h = maxH; w = (img.width / img.height) * h; }
            const x = (pageW - w) / 2;
            try { doc.addImage(img, 'JPEG', x, py, w, h); } catch (e) {}
            py += h + 12;
          }
        }
        const texte = (photo && photo.legende) ? photo.legende : texteDefaut;
        if (texte) {
          doc.setTextColor(0, 0, 0);
          doc.setFontSize(13);
          doc.setFont(undefined, 'normal');
          const tLines = doc.splitTextToSize(texte, pageW - 50);
          doc.text(tLines, 25, py);
        }
      };

      // Photos AVANT
      for (let i = 0; i < photoAvant.length; i++) {
        await addDeroulementPage("Deroulement de l'intervention", photoAvant[i], i === 0 ? formTravaux : '');
      }
      // Photos PENDANT
      for (let i = 0; i < photoPendant.length; i++) {
        await addDeroulementPage("Deroulement de l'intervention", photoPendant[i], (photoAvant.length === 0 && i === 0) ? formTravaux : '');
      }

      // Si aucune photo avant/pendant mais du texte travaux : une page texte
      if (photoAvant.length === 0 && photoPendant.length === 0 && formTravaux) {
        await addDeroulementPage("Deroulement de l'intervention", null, formTravaux);
      }

      // Observations / matériel / pièces sur une page déroulement si renseignés
      const details = [];
      if (formObservations) details.push('Observations : ' + formObservations);
      if (formMateriel) details.push('Materiel utilise : ' + formMateriel);
      if (formPieces) details.push('Pieces remplacees : ' + formPieces);
      if (details.length > 0) {
        await addDeroulementPage("Deroulement de l'intervention", null, details.join('\n\n'));
      }

      // ===== CHECK-LIST des tâches (si renseignée) =====
      if (formChecklist && formChecklist.length > 0) {
        doc.addPage();
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, pageW, pageH, 'F');
        doc.setTextColor(navy[0], navy[1], navy[2]);
        doc.setFontSize(18);
        doc.setFont(undefined, 'bold');
        doc.text('Check-list des taches', pageW / 2, 20, { align: 'center' });
        doc.setDrawColor(orange[0], orange[1], orange[2]);
        doc.setLineWidth(1);
        doc.line(20, 26, pageW - 20, 26);

        let chy = 42;
        doc.setFontSize(12);
        formChecklist.forEach((item, i) => {
          if (chy > pageH - 20) { doc.addPage(); chy = 25; }
          // Case cochée ou non
          doc.setDrawColor(150, 150, 150);
          doc.setLineWidth(0.4);
          doc.rect(25, chy - 4, 6, 6);
          if (item.done) {
            doc.setTextColor(16, 185, 129);
            doc.setFont(undefined, 'bold');
            doc.text('X', 26.3, chy + 1);
          }
          // Texte de la tâche
          doc.setTextColor(item.done ? 120 : 0, item.done ? 120 : 0, item.done ? 120 : 0);
          doc.setFont(undefined, 'normal');
          const itemLines = doc.splitTextToSize(item.text, pageW - 60);
          doc.text(itemLines, 38, chy + 1);
          chy += (itemLines.length * 6) + 8;
        });

        // Compteur en bas
        const done = formChecklist.filter(i => i.done).length;
        doc.setTextColor(100, 100, 100);
        doc.setFontSize(11);
        doc.setFont(undefined, 'italic');
        doc.text(`${done} / ${formChecklist.length} tache(s) realisee(s)`, 25, chy + 6);
      }

      // ===== PAGE FINALE : BILAN =====
      doc.addPage();
      doc.setFillColor(255, 255, 255);
      doc.rect(0, 0, pageW, pageH, 'F');
      doc.setTextColor(orange[0], orange[1], orange[2]);
      doc.setFontSize(20);
      doc.setFont(undefined, 'bold');
      doc.text("Bilan de l'intervention", pageW / 2, 20, { align: 'center' });
      doc.setDrawColor(orange[0], orange[1], orange[2]);
      doc.setLineWidth(1);
      doc.line(20, 26, pageW - 20, 26);

      let by = 38;
      // Photo après (la première)
      if (photoApres.length > 0) {
        const img = await loadImg(photoApres[0].url);
        if (img) {
          const maxW = pageW - 80;
          const maxH = 110;
          let w = maxW;
          let h = (img.height / img.width) * w;
          if (h > maxH) { h = maxH; w = (img.width / img.height) * h; }
          const x = (pageW - w) / 2;
          try { doc.addImage(img, 'JPEG', x, by, w, h); } catch (e) {}
          by += h + 10;
          if (photoApres[0].legende) {
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(12);
            doc.setFont(undefined, 'normal');
            const lg = doc.splitTextToSize(photoApres[0].legende, pageW - 50);
            doc.text(lg, 25, by);
            by += lg.length * 6 + 6;
          }
        }
      }

      // Texte bilan
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(13);
      doc.setFont(undefined, 'normal');
      if (formBilan) {
        const bLines = doc.splitTextToSize(formBilan, pageW - 50);
        doc.text(bLines, 25, by);
        by += bLines.length * 6 + 8;
      }

      // Encadré heures
      const h = Math.floor(timerSeconds / 3600);
      const m = Math.floor((timerSeconds % 3600) / 60);
      const tempsChrono = `${h}h ${String(m).padStart(2, '0')}min`;

      if (by > pageH - 70) { doc.addPage(); by = 30; }
      const heures = [
        ["Heure d'arrivee", formHeureArrivee || '—'],
        ['Heure de depart', formHeureDepart || '—'],
        ['Temps passe (chrono)', tempsChrono]
      ];
      heures.forEach(([label, value]) => {
        doc.setFillColor(120, 120, 120);
        doc.rect(40, by, 60, 12, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text(label, 43, by + 8);
        doc.setFillColor(navy[0], navy[1], navy[2]);
        doc.rect(102, by, 50, 12, 'F');
        doc.text(String(value), 105, by + 8);
        by += 16;
      });

      // ===== SIGNATURES =====
      if (signatureTech || signatureClient) {
        by += 10;
        if (by > pageH - 70) { doc.addPage(); by = 30; }
        doc.setTextColor(navy[0], navy[1], navy[2]);
        doc.setFontSize(13);
        doc.setFont(undefined, 'bold');
        doc.text('Signatures', pageW / 2, by, { align: 'center' });
        by += 8;

        const sigW = 75;
        const sigH = 30;
        const techImg = signatureTech ? await loadImg(signatureTech) : null;
        const cliImg = signatureClient ? await loadImg(signatureClient) : null;

        doc.setTextColor(0, 0, 0);
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        // Technicien à gauche
        doc.text('Technicien', 30, by + 5);
        doc.setDrawColor(200, 200, 200);
        doc.rect(30, by + 7, sigW, sigH);
        if (techImg) { try { doc.addImage(techImg, 'PNG', 31, by + 8, sigW - 2, sigH - 2); } catch (e) {} }
        // Client à droite
        doc.text('Client', pageW - 30 - sigW, by + 5);
        doc.rect(pageW - 30 - sigW, by + 7, sigW, sigH);
        if (cliImg) { try { doc.addImage(cliImg, 'PNG', pageW - 29 - sigW, by + 8, sigW - 2, sigH - 2); } catch (e) {} }
      }

      // Pied de page sur toutes les pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`${user.entrepriseNom || 'PlanifPro'} - Genere le ${new Date().toLocaleDateString('fr-FR')} - Page ${i}/${pageCount}`, pageW / 2, pageH - 6, { align: 'center' });
      }

      const fileName = `Bon_intervention_${(formClient || 'client').replace(/\s/g, '_')}_${dateStr}.pdf`;
      doc.save(fileName);
      alert(`✅ PDF téléchargé : ${fileName}`);
    } catch (err) {
      alert('Erreur PDF: ' + err.message);
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Envoyer au client : ouvre la messagerie pré-remplie
  const sendByEmail = () => {
    const fiche = clientsList.find(x => String(x.id) === String(formClientId));
    const destinataire = fiche && fiche.email ? fiche.email : '';
    const sujet = `Rapport d'intervention - ${formClient || ''} - ${selectedDate ? selectedDate.toLocaleDateString('fr-FR') : ''}`;
    const corps = `Bonjour,\n\nVeuillez trouver ci-joint le rapport de notre intervention${formClient ? ' chez ' + formClient : ''}${formAdresse ? ' (' + formAdresse + ')' : ''} réalisée le ${selectedDate ? selectedDate.toLocaleDateString('fr-FR') : ''}.\n\n⚠️ Pensez à joindre le PDF que vous venez de générer.\n\nCordialement,\n${user.entrepriseNom || 'PlanifPro'}`;
    const mailto = `mailto:${destinataire}?subject=${encodeURIComponent(sujet)}&body=${encodeURIComponent(corps)}`;
    window.location.href = mailto;
  };

  // Générer une facture depuis l'intervention
  const createFactureFromIntervention = async () => {
    const intituleDefaut = formTitre || formNature || '';
    const intitule = window.prompt('Désignation de la prestation (ex: Prestation DJ — Mariage) :', intituleDefaut);
    if (intitule === null) return;
    const montant = window.prompt('Montant de la facture (€) pour ' + (formClient || 'ce client') + ' :', '');
    if (montant === null) return;
    if (!montant || isNaN(parseFloat(montant))) {
      alert('Montant invalide.');
      return;
    }
    try {
      const echeance = new Date();
      echeance.setDate(echeance.getDate() + 30);
      const { data, error } = await supabase.from('factures').insert([{
        user_id: dataOwnerId,
        client: formClient,
        client_id: formClientId || null,
        intitule: intitule || null,
        amount: montant,
        date: new Date().toISOString().split('T')[0],
        echeance: echeance.toISOString().split('T')[0],
        delai: '30',
        status: 'impaye'
      }]).select();
      if (error) throw error;
      if (data) {
        setFactures([...factures, ...data]);
        alert('✅ Facture créée pour ' + formClient + ' (' + montant + '€, échéance 30 jours). Retrouve-la dans l\'onglet Factures.');
      }
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };


  const formatTimer = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // ===== CALENDAR FUNCTIONS =====
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getPreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const getNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const getInterventionsForDate = (date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return interventions
      .filter(i => {
        if (i.date !== dateStr) return false;
        if (isAgent) return true; // l'agent voit tout le planning de l'équipe
        if (!filterTechnicien) return true;
        const extra = i.techniciens_extra ? JSON.parse(i.techniciens_extra) : [];
        return i.technicien === filterTechnicien || extra.includes(filterTechnicien);
      })
      .sort((a, b) => (a.heure || '00:00').localeCompare(b.heure || '00:00'));
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];
    const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

    for (let i = 0; i < (firstDay === 0 ? 6 : firstDay - 1); i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i));
    }

    return (
      <div>
        <div style={{ background: darkMode ? '#1c1c1e' : '#fff', borderRadius: '18px', padding: isMobile ? '16px' : '24px', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 700, letterSpacing: '-0.02em' }}>
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={getPreviousMonth} style={{ width: '38px', height: '38px', background: darkMode ? '#2c2c2e' : '#f5f5f7', color: darkMode ? '#fff' : '#0a0a0a', border: 'none', borderRadius: '50%', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>‹</button>
              <button onClick={getNextMonth} style={{ width: '38px', height: '38px', background: darkMode ? '#2c2c2e' : '#f5f5f7', color: darkMode ? '#fff' : '#0a0a0a', border: 'none', borderRadius: '50%', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>›</button>
            </div>
          </div>

          {/* En-têtes jours */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: isMobile ? '2px' : '8px', marginBottom: isMobile ? '4px' : '8px' }}>
            {dayNames.map(day => (
              <div key={day} style={{ textAlign: 'center', fontWeight: 600, fontSize: isMobile ? '11px' : '13px', color: '#86868b', padding: isMobile ? '6px 0' : '12px' }}>
                {isMobile ? day.charAt(0) : day}
              </div>
            ))}
          </div>

          {isMobile ? (
            /* ===== VUE MOBILE STYLE APPLE ===== */
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '20px' }}>
                {days.map((date, idx) => {
                  const dayInterventions = date ? getInterventionsForDate(date) : [];
                  const isToday = date && new Date().toDateString() === date.toDateString();
                  const isSelected = date && selectedDate && date.toDateString() === selectedDate.toDateString();
                  return (
                    <div
                      key={idx}
                      onClick={() => date && setSelectedDate(new Date(date))}
                      style={{
                        aspectRatio: '1',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '3px',
                        borderRadius: '50%',
                        cursor: date ? 'pointer' : 'default',
                        background: isSelected ? '#2563eb' : (isToday ? (darkMode ? '#2c2c2e' : '#eef4ff') : 'transparent'),
                        position: 'relative'
                      }}
                    >
                      {date && (
                        <>
                          <span style={{ fontSize: '15px', fontWeight: isToday || isSelected ? 700 : 400, color: isSelected ? '#fff' : (isToday ? '#2563eb' : (darkMode ? '#fff' : '#0a0a0a')) }}>
                            {date.getDate()}
                          </span>
                          {dayInterventions.length > 0 && (
                            <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: isSelected ? '#fff' : '#2563eb', position: 'absolute', bottom: '6px' }} />
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Liste des RDV du jour sélectionné */}
              <div style={{ borderTop: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', paddingTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 700 }}>
                    {selectedDate ? selectedDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) : 'Sélectionnez un jour'}
                  </h3>
                  {selectedDate && (
                    <button onClick={() => openModalCreate(selectedDate)} style={{ width: '32px', height: '32px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '50%', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}>+</button>
                  )}
                </div>
                {selectedDate && (() => {
                  const dayInterventions = getInterventionsForDate(selectedDate);
                  if (dayInterventions.length === 0) {
                    return <p style={{ fontSize: '14px', color: '#86868b', textAlign: 'center', padding: '24px 0' }}>Aucun rendez-vous ce jour. Touchez + pour en ajouter.</p>;
                  }
                  return (
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {dayInterventions.map(intervention => (
                        <div
                          key={intervention.id}
                          onClick={() => openModalEdit(intervention)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '12px', padding: '14px', borderRadius: '14px', cursor: 'pointer',
                            background: intervention.statut === 'termine'
                              ? (darkMode ? '#052e16' : '#f0fdf4')
                              : (darkMode ? '#111827' : '#f5f5f7'),
                            border: `1px solid ${intervention.statut === 'termine' ? '#bbf7d0' : 'transparent'}`,
                            opacity: intervention.statut === 'termine' ? 0.9 : 1,
                          }}
                        >
                          <div style={{ width: '4px', alignSelf: 'stretch', borderRadius: '4px', background: intervention.statut === 'termine' ? '#10b981' : '#2563eb' }} />
                          <div style={{ flex: 1 }}>
                            <p style={{ fontSize: '15px', fontWeight: 600, textDecoration: intervention.statut === 'termine' ? 'line-through' : 'none', color: intervention.statut === 'termine' ? '#6b7280' : 'inherit' }}>{intervention.titre}</p>
                            <p style={{ fontSize: '13px', color: '#86868b' }}>{intervention.heure}{intervention.client ? ' · ' + intervention.client : ''}</p>
                          </div>
                          {intervention.statut === 'termine'
                            ? <span style={{ fontSize: '12px', color: '#10b981', fontWeight: 700, background: '#dcfce7', padding: '2px 8px', borderRadius: '6px' }}>✓ Fait</span>
                            : null}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </>
          ) : (
            /* ===== VUE DESKTOP (grille) ===== */
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px' }}>
              {days.map((date, idx) => {
                const dayInterventions = date ? getInterventionsForDate(date) : [];
                const isToday = date && new Date().toDateString() === date.toDateString();

                return (
                  <div
                    key={idx}
                    onClick={() => date && openModalCreate(date)}
                    style={{
                      minHeight: '120px',
                      border: isToday ? '2px solid #2563eb' : `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
                      borderRadius: '8px',
                      padding: '8px',
                      background: isToday ? (darkMode ? '#1e40af' : '#dbeafe') : (darkMode ? '#111827' : '#f9fafb'),
                      cursor: date ? 'pointer' : 'default',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    {date && (
                      <>
                        <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '4px', color: isToday ? '#2563eb' : '#6b7280' }}>
                          {date.getDate()}
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                          {dayInterventions.map(intervention => {
                            const isPast = date < new Date(new Date().setHours(0,0,0,0));
                            const bg = intervention.statut === 'termine' ? '#10b981'
                              : isPast ? '#f59e0b'
                              : '#2563eb';
                            return (
                            <div
                              key={intervention.id}
                              onClick={(e) => { e.stopPropagation(); openModalEdit(intervention); }}
                              style={{
                                background: bg,
                                color: '#fff',
                                fontSize: '11px',
                                padding: '4px 6px',
                                borderRadius: '4px',
                                marginBottom: '2px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                opacity: intervention.statut === 'termine' ? 0.85 : 1,
                              }}
                              title={intervention.titre}
                            >
                              {intervention.statut === 'termine' ? '✓ ' : ''}{intervention.heure} - {intervention.titre}
                            </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ===== FACTURES =====
  const addFacture = async () => {
    const montantBrut = parseFloat(newFactureAmount);
    if (!newFactureClient || !montantBrut || montantBrut <= 0) {
      alert('Indique le client et un montant valide (> 0) !');
      return;
    }
    try {
      const echeance = new Date();
      echeance.setDate(echeance.getDate() + parseInt(newFactureDelai || '30'));
      // Calcul TTC : si HT, on applique la TVA du profil
      const tauxTVA = user.tva_regime && !['franchise','exonere'].includes(user.tva_regime) ? parseFloat(user.tva_regime) : 0;
      const montantTTC = newFactureMontantType === 'ht' && tauxTVA > 0 ? montantBrut * (1 + tauxTVA / 100) : montantBrut;
      const ficheCli = clientsList.find(c => c.nom === newFactureClient || (c.entreprise && c.entreprise === newFactureClient));
      const { data, error } = await supabase.from('factures').insert([{
        user_id: user.id,
        client: newFactureClient,
        client_id: ficheCli?.id || null,
        intitule: newFactureIntitule || null,
        amount: Math.round(montantTTC * 100) / 100,
        montant_type: newFactureMontantType,
        date: new Date().toISOString().split('T')[0],
        echeance: echeance.toISOString().split('T')[0],
        delai: newFactureDelai,
        status: 'impaye'
      }]).select();
      if (error) throw error;
      if (data) {
        setFactures([...factures, ...data]);
        setNewFactureClient('');
        setNewFactureIntitule('');
        setNewFactureAmount('');
        setNewFactureDelai('30');
        alert('✅ Facture enregistrée !');
      }
    } catch (err) {
      alert('Erreur lors de la création : ' + err.message + '\n\n(Vérifie que la table factures a bien toutes ses colonnes.)');
    }
  };

  const exportComptaCSV = () => {
    const regime = user.tva_regime || 'franchise';
    const sansTva = (regime === 'franchise' || regime === 'exonere');
    const tauxTva = sansTva ? 0 : parseFloat(regime);
    const sep = ';'; // séparateur compatible Excel FR
    const entetes = ['Date', 'Numéro', 'Type', 'Client', 'Désignation', 'Montant HT', 'Taux TVA', 'Montant TVA', 'Montant TTC', 'Statut', 'Échéance'];
    const rows = [...factures]
      .sort((a, b) => (a.numero || '').localeCompare(b.numero || ''))
      .map(f => {
        const ttc = parseFloat(f.amount) || 0;
        const ht = sansTva ? ttc : (f.montant_type === 'ht' ? ttc : ttc / (1 + tauxTva / 100));
        const htCalc = f.montant_type === 'ht' && !sansTva ? ttc : ht;
        const montantTTC = sansTva ? ttc : (f.montant_type === 'ht' ? ttc * (1 + tauxTva / 100) : ttc);
        const baseHT = sansTva ? montantTTC : montantTTC / (1 + tauxTva / 100);
        const tva = montantTTC - baseHT;
        const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
        return [
          f.date || '',
          f.numero || '',
          (f.type === 'avoir' ? 'Avoir' : 'Facture'),
          f.client || '',
          f.intitule || 'Prestation de service',
          baseHT.toFixed(2).replace('.', ','),
          sansTva ? '0' : `${tauxTva}%`,
          tva.toFixed(2).replace('.', ','),
          montantTTC.toFixed(2).replace('.', ','),
          f.status === 'paye' ? 'Payée' : 'En attente',
          f.echeance || '',
        ].map(esc).join(sep);
      });
    const csv = '﻿' + [entetes.map(e => `"${e}"`).join(sep), ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `export_comptable_${new Date().getFullYear()}.csv`;
    link.click();
  };

  // ===== CATALOGUE DE PRESTATIONS =====
  const savePrestation = async () => {
    if (!newPrestaNom || !newPrestaPrix) return alert('Indiquez un nom et un prix.');
    const { data, error } = await supabase.from('prestations').insert([{ user_id: user.id, nom: newPrestaNom, prix: parseFloat(newPrestaPrix) }]).select().single();
    if (error) return alert('Erreur: ' + error.message);
    setPrestations([...prestations, data].sort((a, b) => (a.nom || '').localeCompare(b.nom || '')));
    setNewPrestaNom(''); setNewPrestaPrix('');
  };
  const deletePrestation = async (id) => {
    await supabase.from('prestations').delete().eq('id', id);
    setPrestations(prestations.filter(p => p.id !== id));
  };

  // ===== DÉPENSES =====
  const saveDepense = async () => {
    if (!newDepLibelle || !newDepMontant) return alert('Indiquez un libellé et un montant.');
    const { data, error } = await supabase.from('depenses').insert([{ user_id: user.id, libelle: newDepLibelle, montant: parseFloat(newDepMontant), categorie: newDepCategorie, date: newDepDate }]).select().single();
    if (error) return alert('Erreur: ' + error.message);
    setDepenses([data, ...depenses]);
    setNewDepLibelle(''); setNewDepMontant('');
  };
  const deleteDepense = async (id) => {
    await supabase.from('depenses').delete().eq('id', id);
    setDepenses(depenses.filter(d => d.id !== id));
  };

  // ===== FACTURE D'AVOIR (note de crédit) =====
  const creerAvoir = async (facture) => {
    if (!window.confirm(`Créer un avoir (note de crédit) pour la facture ${facture.numero || ''} ?\n\nCela annule comptablement cette facture avec un montant négatif.`)) return;
    const montantNeg = -Math.abs(parseFloat(facture.amount) || 0);
    const { data, error } = await supabase.from('factures').insert([{
      user_id: user.id,
      client: facture.client,
      client_id: facture.client_id || null,
      intitule: `Avoir sur facture ${facture.numero || ''}${facture.intitule ? ' — ' + facture.intitule : ''}`,
      amount: montantNeg,
      montant_type: facture.montant_type || 'ttc',
      type: 'avoir',
      avoir_de: facture.numero || null,
      date: new Date().toISOString().split('T')[0],
      echeance: new Date().toISOString().split('T')[0],
      delai: '0',
      status: 'paye',
      // Un avoir vient en déduction du CA de la période où il est émis.
      date_paiement: new Date().toISOString().split('T')[0],
    }]).select().single();
    if (error) return alert('Erreur: ' + error.message);
    setFactures([data, ...factures]);
    alert(`✅ Avoir créé (${montantNeg.toFixed(2)}€). Il apparaît dans la liste des factures.`);
  };

  // ===== ACOMPTE / PAIEMENT PARTIEL =====
  const enregistrerAcompte = async (facture) => {
    const total = parseFloat(facture.amount) || 0;
    const dejaVerse = parseFloat(facture.acompte) || 0;
    const saisie = window.prompt(`Montant total : ${total.toFixed(2)}€\nDéjà encaissé : ${dejaVerse.toFixed(2)}€\n\nMontant de l'acompte reçu (€) :`, '');
    if (saisie === null) return;
    const montant = parseFloat(saisie);
    if (isNaN(montant) || montant <= 0) return alert('Montant invalide.');
    const nouvelAcompte = Math.min(dejaVerse + montant, total);
    const soldee = nouvelAcompte >= total;
    const nouveauStatut = soldee ? 'paye' : facture.status;
    // Le solde final fixe la date d'encaissement retenue pour l'URSSAF :
    // c'est le jour où la facture est réellement couverte en totalité.
    const maj = { acompte: nouvelAcompte, status: nouveauStatut };
    if (soldee && !facture.date_paiement) maj.date_paiement = new Date().toISOString().split('T')[0];
    const { error } = await supabase.from('factures').update(maj).eq('id', facture.id);
    if (error) return alert('Erreur: ' + error.message);
    setFactures(factures.map(f => f.id === facture.id ? { ...f, ...maj } : f));
    const reste = total - nouvelAcompte;
    alert(reste <= 0 ? '✅ Facture entièrement réglée !' : `✅ Acompte enregistré. Reste à payer : ${reste.toFixed(2)}€`);
  };

  const togglePaiement = async (facture) => {
    const devientPaye = facture.status !== 'paye';
    const newStatus = devientPaye ? 'paye' : 'impaye';
    // Date d'encaissement réelle : aujourd'hui par défaut (cas courant, on
    // coche au moment où l'argent arrive), corrigeable ensuite d'un clic
    // dans la liste. C'est cette date — et non celle d'émission — qui
    // détermine dans quelle période URSSAF le montant doit être déclaré.
    const datePaiement = devientPaye ? new Date().toISOString().split('T')[0] : null;
    try {
      await supabase.from('factures').update({ status: newStatus, date_paiement: datePaiement }).eq('id', facture.id);
      setFactures(factures.map(f => f.id === facture.id ? { ...f, status: newStatus, date_paiement: datePaiement } : f));
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  // Correction manuelle de la date d'encaissement (chèque encaissé plus
  // tard, virement daté de la veille…).
  const majDatePaiement = async (facture, nouvelleDate) => {
    if (!nouvelleDate) return;
    const { error } = await supabase.from('factures').update({ date_paiement: nouvelleDate }).eq('id', facture.id);
    if (error) return alert('Erreur: ' + error.message);
    setFactures(factures.map(f => f.id === facture.id ? { ...f, date_paiement: nouvelleDate } : f));
  };

  const deleteFacture = async (id) => {
    if (!window.confirm('Supprimer cette facture définitivement ?')) return;
    try {
      await supabase.from('factures').delete().eq('id', id);
      setFactures(factures.filter(f => f.id !== id));
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  // ===== DEVIS : CRUD =====
  const saveDevis = async () => {
    if (!devisClient || !devisObjet) return alert('Client et objet requis.');
    const lignesValides = devisLignes.filter(l => l.description && l.prix);
    if (lignesValides.length === 0) return alert('Ajoutez au moins une ligne avec description et prix.');
    const totalHT = lignesValides.reduce((s, l) => s + (parseFloat(l.prix) || 0) * (parseFloat(l.quantite) || 1), 0);
    const dateExp = new Date(); dateExp.setDate(dateExp.getDate() + parseInt(devisValidite || 30));
    const dateExpStr = dateExp.toISOString().split('T')[0];
    const payload = {
      user_id: user.id,
      client: devisClient,
      client_id: devisClientId || null,
      objet: devisObjet,
      lignes: JSON.stringify(lignesValides),
      validite: parseInt(devisValidite || 30),
      date_expiration: dateExpStr,
      notes: devisNotes,
      montant_ht: totalHT.toFixed(2),
      statut: editingDevisId ? undefined : 'brouillon',
    };
    if (editingDevisId) {
      delete payload.statut;
      const { error } = await supabase.from('devis').update(payload).eq('id', editingDevisId);
      if (error) return alert('Erreur: ' + error.message);
      setDevis(devis.map(d => d.id === editingDevisId ? { ...d, ...payload, statut: d.statut } : d));
    } else {
      const { data, error } = await supabase.from('devis').insert([payload]).select().single();
      if (error) return alert('Erreur: ' + error.message);
      setDevis([data, ...devis]);
    }
    setShowModalDevis(false);
    setEditingDevisId(null);
    setDevisClient(''); setDevisClientId(''); setDevisObjet(''); setDevisLignes([{ description: '', quantite: '1', prix: '' }]); setDevisValidite('30'); setDevisNotes('');
  };

  const deleteDevis = async (id) => {
    if (!window.confirm('Supprimer ce devis ?')) return;
    await supabase.from('devis').delete().eq('id', id);
    setDevis(devis.filter(d => d.id !== id));
  };

  const updateStatutDevis = async (id, statut) => {
    await supabase.from('devis').update({ statut }).eq('id', id);
    setDevis(devis.map(d => d.id === id ? { ...d, statut } : d));
  };

  const convertirDevisEnFacture = async (d) => {
    const lignes = JSON.parse(d.lignes || '[]');
    const montant = lignes.reduce((s, l) => s + (parseFloat(l.prix)||0) * (parseFloat(l.quantite)||1), 0);
    const dateEch = new Date(); dateEch.setDate(dateEch.getDate() + 30);
    const payload = {
      user_id: user.id,
      client: d.client,
      client_id: d.client_id,
      intitule: d.objet || null,
      amount: Math.round(montant * 100) / 100,
      montant_type: 'ht',
      date: new Date().toISOString().split('T')[0],
      echeance: dateEch.toISOString().split('T')[0],
      delai: '30',
      status: 'impaye',
    };
    const { data: newFact, error } = await supabase.from('factures').insert([payload]).select().single();
    if (error) return alert('Erreur: ' + error.message);
    setFactures([newFact, ...factures]);
    await updateStatutDevis(d.id, 'accepte');
    alert('✅ Facture créée depuis le devis !');
    setActiveTab('factures');
  };

  const partagerLienDevis = async (d) => {
    const lien = `${window.location.origin}/devis/${d.id}`;
    try { await navigator.clipboard.writeText(lien); } catch (e) { /* ignore */ }
    // Email du client si on l'a en fiche
    const fiche = clientsList.find(c => c.id === d.client_id || c.nom === d.client);
    const email = fiche?.email;
    if (email) {
      if (window.confirm(`Lien de signature copié ✓\n\nEnvoyer le devis à ${email} par email ?`)) {
        const sujet = `Votre devis ${d.numero || ''} — ${d.objet || ''}`;
        const corps = `Bonjour,\n\nVeuillez trouver votre devis à consulter et signer en ligne :\n${lien}\n\nUn simple clic, vous le validez et le signez directement depuis votre téléphone.\n\nÀ bientôt,\n${user.entrepriseNom || ''}`;
        window.location.href = `mailto:${email}?subject=${encodeURIComponent(sujet)}&body=${encodeURIComponent(corps)}`;
      }
      // Marquer comme envoyé si encore brouillon
      if (d.statut === 'brouillon') updateStatutDevis(d.id, 'envoye');
    } else {
      alert(`Lien de signature copié dans le presse-papier ✓\n\n${lien}\n\nPartagez-le à votre client (SMS, WhatsApp, email...).`);
      if (d.statut === 'brouillon') updateStatutDevis(d.id, 'envoye');
    }
  };

  const openDevisEdit = (d) => {
    setEditingDevisId(d.id);
    setDevisClient(d.client || '');
    setDevisClientId(d.client_id || '');
    setDevisObjet(d.objet || '');
    setDevisLignes(JSON.parse(d.lignes || '[{"description":"","quantite":"1","prix":""}]'));
    setDevisValidite(String(d.validite || 30));
    setDevisNotes(d.notes || '');
    setShowModalDevis(true);
  };

  const generateDevisPDF = async (d) => {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const lignes = JSON.parse(d.lignes || '[]');
    const totalHT = lignes.reduce((s, l) => s + (parseFloat(l.prix)||0) * (parseFloat(l.quantite)||1), 0);
    // Régime TVA cohérent avec le reste de l'app (franchise/exonéré = pas de TVA)
    const tvaRate = sansTva ? 0 : parseFloat(user.tva_regime) || 0;
    const totalTVA = tvaRate > 0 ? totalHT * tvaRate / 100 : 0;
    const totalTTC = totalHT + totalTVA;
    const num = d.numero || `DEV-${d.id?.slice(0,6)?.toUpperCase()}`;

    // En-tête bleu
    doc.setFillColor(30, 58, 138);
    doc.rect(0, 0, 210, 42, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22); doc.setFont('helvetica', 'bold');
    doc.text('DEVIS', 20, 18);
    doc.setFontSize(11); doc.setFont('helvetica', 'normal');
    doc.text(num, 20, 26);
    doc.text(`Valable jusqu'au ${d.date_expiration ? new Date(d.date_expiration).toLocaleDateString('fr-FR') : ''}`, 20, 33);

    // Infos prestataire
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    let by = 52;
    doc.setFont('helvetica', 'bold');
    doc.text(user.entrepriseNom || '', 20, by);
    doc.setFont('helvetica', 'normal');
    if (user.adresse) { by += 5; doc.text(user.adresse, 20, by); }
    if (user.siret) { by += 5; doc.text(`SIRET : ${user.siret}`, 20, by); }
    if (user.tel) { by += 5; doc.text(`Tél : ${user.tel}`, 20, by); }
    if (user.email) { by += 5; doc.text(user.email, 20, by); }

    // Client
    doc.setFillColor(245, 247, 255);
    doc.roundedRect(120, 50, 75, 35, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9); doc.setTextColor(100, 100, 100);
    doc.text('CLIENT', 125, 57);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0); doc.setFontSize(10);
    doc.text(d.client || '', 125, 64);

    // Objet
    by = 96;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
    doc.text(`Objet : ${d.objet || ''}`, 20, by);
    by += 10;

    // Tableau lignes
    doc.setFillColor(30, 58, 138);
    doc.rect(15, by, 180, 8, 'F');
    doc.setTextColor(255,255,255); doc.setFontSize(9); doc.setFont('helvetica', 'bold');
    doc.text('Description', 20, by + 5.5);
    doc.text('Qté', 130, by + 5.5);
    doc.text(`Prix unit. ${labelMontant}`, 145, by + 5.5);
    doc.text(`Total ${labelMontant}`, 172, by + 5.5);
    by += 10;

    doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'normal');
    lignes.forEach((l, i) => {
      const total = (parseFloat(l.prix)||0) * (parseFloat(l.quantite)||1);
      if (i % 2 === 0) { doc.setFillColor(248, 250, 255); doc.rect(15, by - 1, 180, 8, 'F'); }
      doc.text(l.description || '', 20, by + 5);
      doc.text(String(l.quantite || '1'), 133, by + 5);
      doc.text(`${(parseFloat(l.prix)||0).toFixed(2)}€`, 148, by + 5);
      doc.text(`${total.toFixed(2)}€`, 174, by + 5);
      by += 8;
    });

    // Totaux
    by += 6;
    const totals = sansTva
      ? [['TOTAL', `${totalHT.toFixed(2)}€`]]
      : [
          ['Total HT', `${totalHT.toFixed(2)}€`],
          ...(tvaRate > 0 ? [[`TVA (${tvaRate}%)`, `${totalTVA.toFixed(2)}€`]] : []),
          ['TOTAL TTC', `${totalTTC.toFixed(2)}€`],
        ];
    totals.forEach(([label, val], i) => {
      const isTTC = i === totals.length - 1;
      if (isTTC) { doc.setFillColor(30, 58, 138); doc.rect(120, by - 1, 75, 9, 'F'); doc.setTextColor(255,255,255); }
      else { doc.setFillColor(245, 247, 255); doc.rect(120, by - 1, 75, 9, 'F'); doc.setTextColor(0,0,0); }
      doc.setFont('helvetica', isTTC ? 'bold' : 'normal'); doc.setFontSize(10);
      doc.text(label, 125, by + 5);
      doc.text(val, 183, by + 5, { align: 'right' });
      by += 9;
    });

    if (tvaRate === 0) {
      by += 4;
      doc.setFontSize(8); doc.setTextColor(100,100,100); doc.setFont('helvetica', 'italic');
      doc.text('TVA non applicable, art. 293 B du CGI', 20, by);
    }

    if (d.notes) {
      by += 10;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(80,80,80);
      doc.text(`Notes : ${d.notes}`, 20, by);
    }

    // CGV / mentions personnalisées
    if (user.cgv) {
      by += 10;
      doc.setFont('helvetica', 'italic'); doc.setFontSize(7.5); doc.setTextColor(120,120,120);
      doc.splitTextToSize(user.cgv, 175).forEach(l => { doc.text(l, 20, by); by += 3.6; });
    }

    doc.save(`devis_${num}.pdf`);
  };

  // ===== ATTESTATION FISCALE ANNUELLE (Services à la Personne) =====
  const generateAttestationFiscale = async (client, annee) => {
    // Factures payées de l'année pour ce client
    const factsClient = factures.filter(f => {
      if (f.client !== client.nom && f.client_id !== client.id) return false;
      if (f.status !== 'paye') return false;
      const d = new Date(f.date || f.created_at);
      return d.getFullYear() === annee;
    });
    const total = factsClient.reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
    if (factsClient.length === 0) {
      alert(`Aucune facture payée en ${annee} pour ${client.nom}. L'attestation ne peut pas être générée.`);
      return;
    }

    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });

    // En-tête
    doc.setFillColor(30, 58, 138);
    doc.rect(0, 0, 210, 38, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20); doc.setFont('helvetica', 'bold');
    doc.text('ATTESTATION FISCALE', 20, 17);
    doc.setFontSize(11); doc.setFont('helvetica', 'normal');
    doc.text(`Services à la personne — Année ${annee}`, 20, 26);

    // Prestataire
    doc.setTextColor(0, 0, 0);
    let y = 50;
    doc.setFontSize(10); doc.setFont('helvetica', 'bold');
    doc.text('Émis par :', 20, y); y += 6;
    doc.setFont('helvetica', 'normal');
    doc.text(user.entrepriseNom || '', 20, y); y += 5;
    if (user.adresse) { doc.text(user.adresse, 20, y); y += 5; }
    if (user.siret) { doc.text(`SIRET : ${user.siret}`, 20, y); y += 5; }
    if (user.numero_sap) { doc.text(`N° de déclaration SAP : ${user.numero_sap}`, 20, y); y += 5; }
    if (user.tel) { doc.text(`Tél : ${user.tel}`, 20, y); y += 5; }

    // Bénéficiaire
    y += 8;
    doc.setFont('helvetica', 'bold');
    doc.text('Bénéficiaire des prestations :', 20, y); y += 6;
    doc.setFont('helvetica', 'normal');
    doc.text(client.nom || '', 20, y); y += 5;
    if (client.adresse) { doc.text(client.adresse, 20, y); y += 5; }

    // Corps de l'attestation
    y += 10;
    doc.setFillColor(245, 247, 255);
    doc.roundedRect(15, y, 180, 38, 3, 3, 'F');
    doc.setFontSize(11); doc.setFont('helvetica', 'normal'); doc.setTextColor(40, 40, 40);
    const phrase = doc.splitTextToSize(
      `Je soussigné(e), représentant de ${user.entrepriseNom || 'l\'entreprise'}, atteste que ${client.nom} a versé la somme totale ci-dessous au titre de prestations de services à la personne réalisées à son domicile au cours de l'année ${annee}.`,
      170
    );
    doc.text(phrase, 20, y + 9);

    // Montant total
    y += 46;
    doc.setFillColor(30, 58, 138);
    doc.roundedRect(15, y, 180, 16, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12); doc.setFont('helvetica', 'bold');
    doc.text('Montant total versé en ' + annee, 22, y + 10);
    doc.setFontSize(15);
    doc.text(`${total.toFixed(2)} €`, 188, y + 10.5, { align: 'right' });

    // Détail nb factures
    y += 24;
    doc.setTextColor(90, 90, 90); doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.text(`Établie sur la base de ${factsClient.length} facture(s) acquittée(s).`, 20, y);

    // Mention légale
    y += 12;
    doc.setFontSize(8.5); doc.setTextColor(110, 110, 110); doc.setFont('helvetica', 'italic');
    const legal = doc.splitTextToSize(
      "Attestation établie en application de l'article 199 sexdecies du Code général des impôts, en vue de l'octroi d'une aide fiscale (crédit ou réduction d'impôt). Les sommes déclarées doivent correspondre aux montants effectivement payés. Le bénéficiaire conserve ses justificatifs en cas de contrôle de l'administration fiscale.",
      175
    );
    doc.text(legal, 20, y);

    // Date + signature
    y += 28;
    doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0); doc.setFontSize(10);
    doc.text(`Fait le ${new Date().toLocaleDateString('fr-FR')}`, 20, y);
    doc.text('Signature et cachet :', 130, y);
    doc.setDrawColor(200, 200, 200);
    doc.rect(130, y + 4, 60, 24);

    doc.save(`attestation_fiscale_${annee}_${(client.nom || 'client').replace(/\s+/g, '_')}.pdf`);
  };

  // ===== CV PROFESSIONNEL AUTO-GÉNÉRÉ (à partir de l'activité réelle sur PlanifPro) =====
  const [generatingCV, setGeneratingCV] = useState(false);
  const genererCV = async () => {
    setGeneratingCV(true);
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      const pageW = doc.internal.pageSize.getWidth();
      const navy = [30, 58, 138];
      const gris = [110, 110, 110];

      // Chiffres clés calculés depuis les vraies données (pas de saisie manuelle).
      const interTerminees = interventions.filter(i => i.statut === 'termine');
      const clientsServis = new Set(interTerminees.map(i => i.client).filter(Boolean)).size;
      const facturesPayees = factures.filter(f => f.status === 'paye' && f.type !== 'avoir');
      const caTotal = facturesPayees.reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
      let noteMoyenne = avisMoyenne;
      if (noteMoyenne == null) {
        const { data: avisData } = await supabase.from('avis').select('note').eq('pro_id', dataOwnerId);
        if (avisData && avisData.length) noteMoyenne = avisData.reduce((s, a) => s + a.note, 0) / avisData.length;
      }
      const anciennete = user.createdat ? Math.max(1, Math.round((Date.now() - new Date(user.createdat).getTime()) / (1000 * 60 * 60 * 24 * 30))) : null;

      // En-tête
      doc.setFillColor(navy[0], navy[1], navy[2]);
      doc.rect(0, 0, pageW, 52, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont(undefined, 'bold');
      doc.text(user.entrepriseNom || 'Indépendant(e)', 20, 26);
      doc.setFontSize(13);
      doc.setFont(undefined, 'normal');
      doc.text(user.metier_label || { artisan: 'Artisan', dj: 'DJ / Artiste', beaute: 'Professionnel(le) de la beauté', aide_domicile: "Aide à domicile", paysagiste: 'Paysagiste' }[user.metier] || 'Indépendant(e)', 20, 36);
      const contact = [user.ville, user.tel, user.email].filter(Boolean).join('  ·  ');
      if (contact) { doc.setFontSize(9.5); doc.text(contact, 20, 44); }

      let y = 68;
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(13);
      doc.setFont(undefined, 'bold');
      doc.text('Profil', 20, y); y += 8;
      doc.setFontSize(10.5);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(gris[0], gris[1], gris[2]);
      const ancienneteTxt = anciennete ? (anciennete >= 12 ? `${Math.floor(anciennete / 12)} an${Math.floor(anciennete / 12) > 1 ? 's' : ''}` : `${anciennete} mois`) : null;
      const profilTxt = `Indépendant(e) dans le secteur ${(user.metier_label || 'des services').toLowerCase()}${ancienneteTxt ? `, en activité depuis ${ancienneteTxt}` : ''}. Gestion rigoureuse des interventions, devis et facturation via PlanifPro.`;
      doc.splitTextToSize(profilTxt, pageW - 40).forEach(l => { doc.text(l, 20, y); y += 5.5; });

      y += 8;
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(13);
      doc.setFont(undefined, 'bold');
      doc.text('Chiffres clés', 20, y); y += 4;

      const chiffres = [
        [`${clientsServis}`, 'clients servis'],
        [`${interTerminees.length}`, 'interventions réalisées'],
        [`${caTotal.toFixed(0)} €`, 'de chiffre d\'affaires'],
        noteMoyenne != null ? [`${noteMoyenne.toFixed(1)}/5`, "note moyenne clients"] : null,
      ].filter(Boolean);
      const colW = (pageW - 40) / chiffres.length;
      chiffres.forEach(([valeur, label], i) => {
        const x = 20 + i * colW;
        doc.setFillColor(245, 247, 255);
        doc.roundedRect(x, y, colW - 6, 28, 3, 3, 'F');
        doc.setTextColor(navy[0], navy[1], navy[2]);
        doc.setFontSize(15);
        doc.setFont(undefined, 'bold');
        doc.text(valeur, x + (colW - 6) / 2, y + 13, { align: 'center' });
        doc.setTextColor(gris[0], gris[1], gris[2]);
        doc.setFontSize(7.5);
        doc.setFont(undefined, 'normal');
        doc.splitTextToSize(label, colW - 10).forEach((l, li) => doc.text(l, x + (colW - 6) / 2, y + 19 + li * 3.4, { align: 'center' }));
      });
      y += 40;

      if (prestations && prestations.length > 0) {
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(13);
        doc.setFont(undefined, 'bold');
        doc.text('Services proposés', 20, y); y += 8;
        doc.setFontSize(10.5);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(gris[0], gris[1], gris[2]);
        prestations.slice(0, 12).forEach(p => {
          doc.text(`•  ${p.nom}${p.prix ? `  —  ${p.prix} €` : ''}`, 22, y);
          y += 6;
        });
      }

      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Profil généré via PlanifPro le ${new Date().toLocaleDateString('fr-FR')}`, pageW / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });

      doc.save(`Profil_${(user.entrepriseNom || 'independant').replace(/[^a-z0-9]/gi, '_')}.pdf`);
    } catch (err) {
      alert('Erreur lors de la génération du profil : ' + err.message);
    } finally {
      setGeneratingCV(false);
    }
  };

  // ===== GÉNÉRER UNE FACTURE PDF (construction partagée par le téléchargement et l'envoi) =====
  const buildFactureDoc = async (facture) => {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      const pageW = doc.internal.pageSize.getWidth();
      const navy = [30, 58, 138];
      const gris = [110, 110, 110];

      // En-tête : émetteur (les infos du directeur)
      doc.setFontSize(22);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(navy[0], navy[1], navy[2]);
      doc.text(facture.type === 'avoir' ? 'AVOIR' : 'FACTURE', 20, 25);

      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 0);
      let ey = 38;
      doc.setFont(undefined, 'bold');
      doc.text(user.entrepriseNom || 'Mon entreprise', 20, ey); ey += 6;
      doc.setFont(undefined, 'normal');
      doc.setTextColor(gris[0], gris[1], gris[2]);
      if (user.adresse) { doc.text(user.adresse, 20, ey); ey += 5; }
      if (user.tel) { doc.text('Tel : ' + user.tel, 20, ey); ey += 5; }
      doc.text('Email : ' + user.email, 20, ey); ey += 5;
      if (user.siret) { doc.text('SIRET : ' + user.siret, 20, ey); ey += 5; }

      // Numéro + dates (à droite)
      const numFacture = facture.numero ? ('N° ' + facture.numero) : ('FAC-' + (facture.id ? String(facture.id).substring(0, 8).toUpperCase() : Date.now()));
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.text(numFacture, pageW - 20, 38, { align: 'right' });
      doc.text('Date : ' + (facture.date ? new Date(facture.date).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR')), pageW - 20, 44, { align: 'right' });
      if (facture.echeance) doc.text('Échéance : ' + new Date(facture.echeance).toLocaleDateString('fr-FR'), pageW - 20, 50, { align: 'right' });

      // Bloc destinataire (client facturé) — avec infos de la fiche client
      const ficheClient = clientsList.find(c => c.id === facture.client_id || c.nom === facture.client);
      // Lignes d'infos sous le nom (adresse, SIRET, TVA)
      const infoLignes = [];
      if (ficheClient) {
        if (ficheClient.type === 'entreprise' && ficheClient.entreprise && ficheClient.nom) infoLignes.push(ficheClient.nom);
        if (ficheClient.adresse) doc.splitTextToSize(ficheClient.adresse, 62).forEach(l => infoLignes.push(l));
        if (ficheClient.siret) infoLignes.push('SIRET : ' + ficheClient.siret);
        if (ficheClient.tva_numero) infoLignes.push('TVA : ' + ficheClient.tva_numero);
      }
      const nomFacture = (ficheClient && ficheClient.type === 'entreprise' && ficheClient.entreprise) ? ficheClient.entreprise : (facture.client || 'Client');
      const boxH = 20 + infoLignes.length * 4.5;
      let cy = ey + 12;
      doc.setDrawColor(220, 220, 220);
      doc.setFillColor(247, 247, 250);
      doc.rect(pageW - 90, cy, 70, boxH, 'F');
      doc.setFontSize(9);
      doc.setTextColor(gris[0], gris[1], gris[2]);
      doc.text('FACTURÉ À', pageW - 85, cy + 7);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(nomFacture, pageW - 85, cy + 15);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(gris[0], gris[1], gris[2]);
      let iy = cy + 20;
      infoLignes.forEach(l => { doc.text(l, pageW - 85, iy); iy += 4.5; });

      // Tableau du montant
      let ty = cy + boxH + 17;
      doc.setFillColor(navy[0], navy[1], navy[2]);
      doc.rect(20, ty, pageW - 40, 10, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('Désignation', 25, ty + 7);
      doc.text('Montant', pageW - 25, ty + 7, { align: 'right' });

      ty += 16;
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(11);

      // ===== CALCUL TVA selon le régime de l'émetteur =====
      const regime = user.tva_regime || 'franchise';
      const montantSaisi = parseFloat(facture.amount) || 0;
      const sansTva = (regime === 'franchise' || regime === 'exonere');
      const tauxTva = sansTva ? 0 : parseFloat(regime);
      // facture.montant_type : 'ht' ou 'ttc' (par défaut ttc si non précisé)
      const saisiEnHT = facture.montant_type === 'ht';
      let montantHT, montantTVA, montantTTC;
      if (sansTva) {
        montantHT = montantSaisi; montantTVA = 0; montantTTC = montantSaisi;
      } else if (saisiEnHT) {
        montantHT = montantSaisi;
        montantTVA = montantHT * (tauxTva / 100);
        montantTTC = montantHT + montantTVA;
      } else {
        montantTTC = montantSaisi;
        montantHT = montantTTC / (1 + tauxTva / 100);
        montantTVA = montantTTC - montantHT;
      }

      doc.text(facture.intitule || 'Prestation de service', 25, ty);
      doc.text(montantHT.toFixed(2) + ' €' + (sansTva ? '' : ' HT'), pageW - 25, ty, { align: 'right' });

      ty += 14;
      doc.setDrawColor(220, 220, 220);
      doc.line(pageW - 90, ty, pageW - 20, ty);
      ty += 8;

      if (!sansTva) {
        // Sous-total HT + TVA + TTC
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        doc.text('Total HT', pageW - 90, ty);
        doc.text(montantHT.toFixed(2) + ' €', pageW - 25, ty, { align: 'right' });
        ty += 7;
        doc.text(`TVA ${tauxTva}%`, pageW - 90, ty);
        doc.text(montantTVA.toFixed(2) + ' €', pageW - 25, ty, { align: 'right' });
        ty += 9;
        doc.setFont(undefined, 'bold');
        doc.setFontSize(13);
        doc.text('TOTAL TTC', pageW - 90, ty);
        doc.text(montantTTC.toFixed(2) + ' €', pageW - 25, ty, { align: 'right' });
      } else {
        doc.setFont(undefined, 'bold');
        doc.setFontSize(13);
        doc.text('TOTAL', pageW - 90, ty);
        doc.text(montantTTC.toFixed(2) + ' €', pageW - 25, ty, { align: 'right' });
      }

      // Acompte versé + reste à payer
      const acompteVerse = parseFloat(facture.acompte) || 0;
      if (acompteVerse > 0 && facture.type !== 'avoir') {
        ty += 9;
        doc.setFont(undefined, 'normal'); doc.setFontSize(10); doc.setTextColor(gris[0], gris[1], gris[2]);
        doc.text('Acompte versé', pageW - 90, ty);
        doc.text('- ' + acompteVerse.toFixed(2) + ' €', pageW - 25, ty, { align: 'right' });
        ty += 9;
        doc.setFont(undefined, 'bold'); doc.setFontSize(12); doc.setTextColor(0, 0, 0);
        doc.text('RESTE À PAYER', pageW - 90, ty);
        doc.text((montantTTC - acompteVerse).toFixed(2) + ' €', pageW - 25, ty, { align: 'right' });
      }

      // Mention TVA selon le régime
      ty += 16;
      doc.setFont(undefined, 'italic');
      doc.setFontSize(9);
      doc.setTextColor(gris[0], gris[1], gris[2]);
      if (regime === 'franchise') {
        doc.text('TVA non applicable, art. 293 B du CGI', 20, ty);
      } else if (regime === 'exonere') {
        doc.text('Exonéré de TVA', 20, ty);
      } else if (user.tva_numero) {
        doc.text('N° TVA intracommunautaire : ' + user.tva_numero, 20, ty);
      }

      // Mentions pénalités de retard + indemnité forfaitaire (obligatoire entre pros) + CGV
      if (facture.type !== 'avoir') {
        ty += 8;
        doc.setFont(undefined, 'normal'); doc.setFontSize(7.5); doc.setTextColor(120, 120, 120);
        const tauxPen = user.penalites_taux ? `au taux annuel de ${user.penalites_taux} %` : 'au taux de trois fois le taux d\'intérêt légal';
        const mentionPen = `En cas de retard de paiement, des pénalités de retard ${tauxPen} seront appliquées, ainsi qu'une indemnité forfaitaire pour frais de recouvrement de 40 € (art. L441-10 et D441-5 du Code de commerce). Pas d'escompte pour paiement anticipé.`;
        doc.splitTextToSize(mentionPen, pageW - 40).forEach(l => { doc.text(l, 20, ty); ty += 3.6; });
        if (user.cgv) {
          ty += 3;
          doc.setFont(undefined, 'italic');
          doc.splitTextToSize(user.cgv, pageW - 40).forEach(l => { doc.text(l, 20, ty); ty += 3.6; });
        }
      }

      // QR code vers la page d'avis publique du prestataire (encourage le client à
      // laisser un avis après règlement). Purement optionnel : si la génération du
      // QR échoue pour une raison quelconque, la facture doit quand même sortir.
      try {
        const QRCode = await import('qrcode');
        const lienAvis = `${window.location.origin}/avis/${user.id}`;
        const qrDataUrl = await QRCode.toDataURL(lienAvis, { margin: 1, width: 200 });
        const qrSize = 24;
        const qrX = pageW - 20 - qrSize;
        const qrY = doc.internal.pageSize.getHeight() - 48;
        doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
        doc.setFontSize(7.5);
        doc.setTextColor(gris[0], gris[1], gris[2]);
        doc.text('Votre avis compte', qrX + qrSize / 2, qrY + qrSize + 5, { align: 'center' });
      } catch (_) { /* QR optionnel */ }

      // Pied de page p1
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Facture générée via PlanifPro le ${new Date().toLocaleDateString('fr-FR')}`, pageW / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });

      // ===== PAGE 2 : RIB (si renseigné) =====
      if (user.iban || user.bic || user.banque) {
        doc.addPage();
        const pH = doc.internal.pageSize.getHeight();

        // En-tête bleu
        doc.setFillColor(navy[0], navy[1], navy[2]);
        doc.rect(0, 0, pageW, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont(undefined, 'bold');
        doc.text('COORDONNÉES BANCAIRES', pageW / 2, 18, { align: 'center' });
        doc.setFontSize(11);
        doc.setFont(undefined, 'normal');
        doc.text(`Règlement de la facture ${numFacture}`, pageW / 2, 30, { align: 'center' });

        // Encadré RIB
        doc.setTextColor(0, 0, 0);
        doc.setDrawColor(220, 230, 250);
        doc.setFillColor(247, 250, 255);
        doc.roundedRect(20, 55, pageW - 40, 70, 4, 4, 'FD');

        let ry = 72;
        const labelStyle = () => { doc.setFont(undefined, 'normal'); doc.setFontSize(9); doc.setTextColor(gris[0], gris[1], gris[2]); };
        const valueStyle = () => { doc.setFont(undefined, 'bold'); doc.setFontSize(12); doc.setTextColor(0, 0, 0); };

        if (user.banque) {
          labelStyle(); doc.text('Banque', 30, ry);
          valueStyle(); doc.text(user.banque, 30, ry + 7); ry += 20;
        }
        if (user.iban) {
          labelStyle(); doc.text('IBAN', 30, ry);
          valueStyle(); doc.text(user.iban, 30, ry + 7); ry += 20;
        }
        if (user.bic) {
          labelStyle(); doc.text('BIC / SWIFT', 30, ry);
          valueStyle(); doc.text(user.bic, 30, ry + 7);
        }

        // Titulaire du compte
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        doc.setTextColor(gris[0], gris[1], gris[2]);
        doc.text(`Titulaire : ${user.entrepriseNom || ''}`, 30, 135);

        // Note de bas de page
        doc.setFontSize(9);
        doc.setTextColor(150, 150, 150);
        doc.text('Merci d\'indiquer le numéro de facture en référence de votre virement.', pageW / 2, 155, { align: 'center' });

        // Pied de page p2
        doc.setFontSize(8);
        doc.text(`Facture générée via PlanifPro le ${new Date().toLocaleDateString('fr-FR')}`, pageW / 2, pH - 10, { align: 'center' });
      }

      const fname = `Facture_${numFacture}_${(facture.client || 'client').replace(/[^a-z0-9]/gi, '_')}.pdf`;
      return { doc, fname };
  };

  // Télécharge la facture PDF sur l'appareil.
  const generateFacturePDF = async (facture) => {
    try {
      const { doc, fname } = await buildFactureDoc(facture);
      doc.save(fname);
      alert(`✅ Facture téléchargée${user.iban ? ' (avec RIB en page 2)' : ''} : ${fname}`);
    } catch (err) {
      alert('Erreur génération facture : ' + err.message);
    }
  };

  // Télécharge la facture PDF ET ouvre l'email du client avec un message
  // pré-rempli — le PDF doit être joint manuellement (aucun navigateur ne
  // permet de joindre un fichier à un mailto: automatiquement).
  const envoyerFacture = async (facture) => {
    try {
      const { doc, fname } = await buildFactureDoc(facture);
      doc.save(fname);
      const fiche = clientsList.find(c => c.id === facture.client_id || c.nom === facture.client);
      const email = fiche?.email;
      const corps = `Bonjour,\n\nVoici comme convenu la facture (fichier "${fname}" téléchargé — pensez à le joindre à cet email).\n\nMerci`;
      if (email) {
        window.location.href = `mailto:${email}?subject=${encodeURIComponent('Votre facture' + (facture.numero ? ' ' + facture.numero : ''))}&body=${encodeURIComponent(corps)}`;
      } else {
        try { await navigator.clipboard.writeText(corps); } catch (_) { /* ignore */ }
        alert(`✅ Facture téléchargée : ${fname}\n\nCe message a été copié dans le presse-papier :\n\n${corps}\n\nOuvrez votre email ou SMS, joignez le PDF téléchargé, et collez ce message.`);
      }
    } catch (err) {
      alert('Erreur lors de l\'envoi : ' + err.message);
    }
  };

  // ===== FACTUR-X XML (profil MINIMUM — EN 16931) =====
  const generateFacturXML = (facture) => {
    try {
      const regime = user.tva_regime || 'franchise';
      const sansTva = (regime === 'franchise' || regime === 'exonere');
      const tauxTva = sansTva ? 0 : parseFloat(regime);
      const montantSaisi = parseFloat(facture.amount) || 0;
      const saisiEnHT = facture.montant_type === 'ht';
      let montantHT, montantTVA, montantTTC;
      if (sansTva) {
        montantHT = montantSaisi; montantTVA = 0; montantTTC = montantSaisi;
      } else if (saisiEnHT) {
        montantHT = montantSaisi;
        montantTVA = montantHT * (tauxTva / 100);
        montantTTC = montantHT + montantTVA;
      } else {
        montantTTC = montantSaisi;
        montantHT = montantTTC / (1 + tauxTva / 100);
        montantTVA = montantTTC - montantHT;
      }

      const numFacture = facture.numero
        ? String(facture.numero)
        : ('FAC-' + (facture.id ? String(facture.id).substring(0, 8).toUpperCase() : Date.now()));

      const dateFacture = facture.date ? new Date(facture.date) : new Date();
      const dateStr = dateFacture.getFullYear()
        + String(dateFacture.getMonth() + 1).padStart(2, '0')
        + String(dateFacture.getDate()).padStart(2, '0');

      const dateEcheance = facture.echeance ? new Date(facture.echeance) : null;
      const dateEchStr = dateEcheance
        ? dateEcheance.getFullYear() + String(dateEcheance.getMonth() + 1).padStart(2, '0') + String(dateEcheance.getDate()).padStart(2, '0')
        : null;

      const siret = (user.siret || '').replace(/\s/g, '');
      const tvaNumero = user.tva_numero || '';

      // Ligne TVA : si franchise, TypeCode AE (autoliquidation/exonéré) sinon S (standard)
      const taxTypeCode = sansTva ? 'E' : 'S';
      const taxExemptReason = sansTva
        ? (regime === 'franchise'
            ? 'TVA non applicable - art. 293 B CGI'
            : 'Exonéré de TVA')
        : null;

      const xml = `<?xml version="1.0" encoding="UTF-8"?>
<rsm:CrossIndustryInvoice
  xmlns:rsm="urn:un:unece:uncefact:data:standard:CrossIndustryInvoice:100"
  xmlns:ram="urn:un:unece:uncefact:data:standard:ReusableAggregateBusinessInformationEntity:100"
  xmlns:udt="urn:un:unece:uncefact:data:standard:UnqualifiedDataType:100"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">

  <rsm:ExchangedDocumentContext>
    <ram:GuidelineSpecifiedDocumentContextParameter>
      <ram:ID>urn:factur-x.eu:1p0:minimum</ram:ID>
    </ram:GuidelineSpecifiedDocumentContextParameter>
  </rsm:ExchangedDocumentContext>

  <rsm:ExchangedDocument>
    <ram:ID>${numFacture}</ram:ID>
    <ram:TypeCode>380</ram:TypeCode>
    <ram:IssueDateTime>
      <udt:DateTimeString format="102">${dateStr}</udt:DateTimeString>
    </ram:IssueDateTime>
  </rsm:ExchangedDocument>

  <rsm:SupplyChainTradeTransaction>
    <ram:ApplicableHeaderTradeAgreement>
      <ram:SellerTradeParty>
        <ram:Name>${(user.entrepriseNom || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</ram:Name>
        ${siret ? `<ram:SpecifiedLegalOrganization>
          <ram:ID schemeID="0002">${siret}</ram:ID>
        </ram:SpecifiedLegalOrganization>` : ''}
        <ram:PostalTradeAddress>
          <ram:CountryID>FR</ram:CountryID>
        </ram:PostalTradeAddress>
        ${tvaNumero ? `<ram:SpecifiedTaxRegistration>
          <ram:ID schemeID="VA">${tvaNumero}</ram:ID>
        </ram:SpecifiedTaxRegistration>` : ''}
      </ram:SellerTradeParty>
      <ram:BuyerTradeParty>
        <ram:Name>${(facture.client || 'Client').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</ram:Name>
      </ram:BuyerTradeParty>
    </ram:ApplicableHeaderTradeAgreement>

    <ram:ApplicableHeaderTradeDelivery/>

    <ram:ApplicableHeaderTradeSettlement>
      <ram:InvoiceCurrencyCode>EUR</ram:InvoiceCurrencyCode>
      <ram:ApplicableTradeTax>
        <ram:CalculatedAmount>${montantTVA.toFixed(2)}</ram:CalculatedAmount>
        <ram:TypeCode>VAT</ram:TypeCode>
        ${taxExemptReason ? `<ram:ExemptionReason>${taxExemptReason}</ram:ExemptionReason>` : ''}
        <ram:BasisAmount>${montantHT.toFixed(2)}</ram:BasisAmount>
        <ram:CategoryCode>${taxTypeCode}</ram:CategoryCode>
        <ram:RateApplicablePercent>${tauxTva.toFixed(2)}</ram:RateApplicablePercent>
      </ram:ApplicableTradeTax>
      ${dateEchStr ? `<ram:SpecifiedTradePaymentTerms>
        <ram:DueDateDateTime>
          <udt:DateTimeString format="102">${dateEchStr}</udt:DateTimeString>
        </ram:DueDateDateTime>
      </ram:SpecifiedTradePaymentTerms>` : ''}
      <ram:SpecifiedTradeSettlementHeaderMonetarySummation>
        <ram:TaxBasisTotalAmount>${montantHT.toFixed(2)}</ram:TaxBasisTotalAmount>
        <ram:TaxTotalAmount currencyID="EUR">${montantTVA.toFixed(2)}</ram:TaxTotalAmount>
        <ram:GrandTotalAmount>${montantTTC.toFixed(2)}</ram:GrandTotalAmount>
        <ram:DuePayableAmount>${montantTTC.toFixed(2)}</ram:DuePayableAmount>
      </ram:SpecifiedTradeSettlementHeaderMonetarySummation>
    </ram:ApplicableHeaderTradeSettlement>
  </rsm:SupplyChainTradeTransaction>
</rsm:CrossIndustryInvoice>`;

      const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factur-x_${numFacture}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erreur génération Factur-X : ' + err.message);
    }
  };

  // ===== TEAM =====
  const addTeamMember = async () => {
    if (!newTeamName || !newTeamRole) {
      alert('Remplis tous les champs!');
      return;
    }
    try {
      const { data, error } = await supabase.from('team').insert([{
        user_id: user.id,
        name: newTeamName,
        role: newTeamRole
      }]).select();
      if (error) throw error;
      if (data) {
        setTeam([...team, ...data]);
        setNewTeamName('');
        setNewTeamRole('');
      }
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  const deleteTeamMember = async (id) => {
    if (!window.confirm('Retirer ce membre de l\'équipe ?')) return;
    try {
      await supabase.from('team').delete().eq('id', id);
      setTeam(team.filter(t => t.id !== id));
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  // ===== ABSENCES =====
  const addAbsence = async () => {
    if (!newAbsenceName || !newAbsenceDate) {
      alert('Remplis au moins le nom et la date de début!');
      return;
    }
    // Si pas de date de fin, on prend la date de début (absence d'un jour)
    const dateFin = newAbsenceDateFin || newAbsenceDate;
    if (new Date(dateFin) < new Date(newAbsenceDate)) {
      alert('La date de fin doit être après la date de début.');
      return;
    }
    try {
      const { data, error } = await supabase.from('absences').insert([{
        user_id: user.id,
        name: newAbsenceName,
        date: newAbsenceDate,
        date_fin: dateFin
      }]).select();
      if (error) throw error;
      if (data) {
        setAbsences([...absences, ...data]);
        setNewAbsenceName('');
        setNewAbsenceDate('');
        setNewAbsenceDateFin('');
      }
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  const deleteAbsence = async (id) => {
    if (!window.confirm('Supprimer cette absence ?')) return;
    try {
      await supabase.from('absences').delete().eq('id', id);
      setAbsences(absences.filter(a => a.id !== id));
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  // ===== ADMIN DATA =====
  const loadAdminData = async () => {
    try {
      const { data: clients } = await supabase.from('users').select('*');
      const { data: factures } = await supabase.from('factures').select('*');
      const { data: tickets } = await supabase.from('support_tickets').select('*').order('created_at', { ascending: false });
      const { data: logs } = await supabase.from('activity_logs').select('*').order('created_at', { ascending: false }).limit(50);

      // `users` est la seule table du projet dont la colonne de date de
      // création s'appelle `createdat` (sans underscore) plutôt que
      // `created_at` — aliasé ici pour que le reste du code (tri "Inscrits
      // non convertis", badge "il y a X jours") fonctionne sans y penser.
      setAllClients((clients || []).map(c => ({ ...c, created_at: c.createdat })));
      setAllFactures(factures || []);
      setTickets(tickets || []);
      setActivityLogs(logs || []);
    } catch (err) {
      console.error('Erreur admin:', err);
    }
  };

  const logActivity = async (userId, action) => {
    try {
      await supabase.from('activity_logs').insert([{
        user_id: userId,
        action: action,
        details: 'Auto-logged'
      }]);
    } catch (err) {
      console.error('Erreur log:', err);
    }
  };

  const blockClient = async (clientId) => {
    try {
      const { data } = await supabase.from('client_status').select('*').eq('userId', clientId).maybeSingle();
      if (data) {
        await supabase.from('client_status').update({ isBlocked: !data.isBlocked }).eq('userId', clientId);
      } else {
        await supabase.from('client_status').insert([{ userId: clientId, isBlocked: true, isActive: true }]);
      }
      loadAdminData();
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  // Activer un abonné depuis l'admin (sans aller dans la base)
  const activerClient = async (client, subType) => {
    try {
      const subscriptionEnd = new Date();
      subscriptionEnd.setMonth(subscriptionEnd.getMonth() + 1); // +1 mois
      const updates = {
        isPaid: true,
        subscriptionType: subType,
        subscriptionEnd: subscriptionEnd.toISOString()
      };
      const { error } = await supabase.from('users').update(updates).eq('id', client.id);
      if (error) throw error;
      // Mettre à jour la liste locale immédiatement
      setAllClients(allClients.map(c => c.id === client.id ? { ...c, ...updates } : c));
      alert(`✅ ${client.entrepriseNom} est maintenant activé en formule ${subType === '1month' ? 'Indépendant' : subType === '6months' ? 'Pro' : 'Business'} !`);
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  const desactiverClient = async (client) => {
    if (!window.confirm(`Désactiver l'abonnement de ${client.entrepriseNom} ?`)) return;
    try {
      const { error } = await supabase.from('users').update({ isPaid: false }).eq('id', client.id);
      if (error) throw error;
      setAllClients(allClients.map(c => c.id === client.id ? { ...c, isPaid: false } : c));
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  const sendEmail = async () => {
    if (!emailSubject || !emailContent) {
      alert('Remplis les champs!');
      return;
    }
    const nbDest = allClients.filter(c => c.isPaid).length;
    if (!window.confirm(`Envoyer cet email à ${nbDest} client(s) payant(s) ? Cette action est irréversible.`)) return;
    setEmailSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-broadcast-email', {
        body: { subject: emailSubject, content: emailContent },
      });
      if (error || !data?.ok) throw new Error(data?.error || error?.message || 'Échec de l\'envoi');
      alert(`✅ Email envoyé à ${data.sent}/${data.total} client(s)${data.erreurs?.length ? `\n⚠️ ${data.erreurs.length} erreur(s)` : ''}`);
      setEmailSubject('');
      setEmailContent('');
      loadAdminData();
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setEmailSending(false);
    }
  };

  const createTicket = async () => {
    if (!ticketSubject || !ticketMessage) {
      alert('Remplis tous les champs!');
      return;
    }
    try {
      const { error } = await supabase.from('support_tickets').insert([{
        user_id: user.id,
        user_email: user.email,
        user_nom: user.entrepriseNom || '',
        subject: ticketSubject,
        message: ticketMessage,
        status: 'open'
      }]);
      if (error) throw error;
      alert('✅ Votre demande a bien été envoyée ! Nous vous répondrons par email à ' + user.email);
      setTicketSubject('');
      setTicketMessage('');
      if (isAdmin) loadAdminData();
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  const respondToTicket = async () => {
    if (!ticketResponse || !selectedTicketId) return;
    try {
      await supabase.from('support_tickets').update({
        response: ticketResponse,
        status: 'closed'
      }).eq('id', selectedTicketId);
      alert('✅ Réponse envoyée!');
      setTicketResponse('');
      setSelectedTicketId(null);
      loadAdminData();
    } catch (err) {
      alert('Erreur: ' + err.message);
    }
  };

  const exportFacturesCSV = () => {
    let csv = 'Client,Montant,Date,Statut\n';
    allFactures.forEach(f => {
      csv += `${f.client},${f.amount},${f.date},${f.status}\n`;
    });
    const link = document.createElement('a');
    link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    link.download = 'factures.csv';
    link.click();
  };

  const openEditProfil = () => {
    setProfilNom(user.entrepriseNom || '');
    setProfilSiret(user.siret || '');
    setProfilAdresse(user.adresse || '');
    setProfilVille(user.ville || '');
    setProfilLat(user.lat ?? null);
    setProfilLng(user.lng ?? null);
    setProfilAnnuaireVisible(!!user.annuaire_visible);
    setProfilBio(user.bio || '');
    setProfilPhoto(user.photo || '');
    setProfilTel(user.tel || '');
    setProfilTvaRegime(user.tva_regime || 'franchise');
    setProfilTvaNumero(user.tva_numero || '');
    setProfilMetier(user.metier || 'artisan');
    setProfilMetierLabel(user.metier_label || '');
    setProfilAutreMode(!!user.metier_label);
    setProfilIban(user.iban || '');
    setProfilBic(user.bic || '');
    setProfilBanque(user.banque || '');
    setProfilPenalitesTaux(user.penalites_taux || '');
    setProfilCgv(user.cgv || '');
    setEditProfil(true);
  };

  const changePasswordInApp = async () => {
    if (pwNew.length < 6) { alert('6 caractères minimum.'); return; }
    if (pwNew !== pwConfirm) { alert('Les deux mots de passe ne correspondent pas.'); return; }
    setPwSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pwNew });
      if (error) throw error;
      alert('✅ Mot de passe mis à jour.');
      setPwNew(''); setPwConfirm(''); setPwChangeOpen(false);
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setPwSaving(false);
    }
  };

  const saveProfil = async () => {
    if (!profilNom) {
      alert('Le nom ne peut pas être vide.');
      return;
    }
    if (profilAnnuaireVisible && (!profilLat || !profilLng)) {
      alert("Pour apparaître dans l'annuaire, resélectionnez votre adresse dans les suggestions qui apparaissent quand vous tapez (nécessaire pour vous géolocaliser).");
      return;
    }
    setSavingProfil(true);
    try {
      const updates = {
        entrepriseNom: profilNom,
        siret: profilSiret,
        adresse: profilAdresse,
        ville: profilVille,
        lat: profilLat,
        lng: profilLng,
        annuaire_visible: profilAnnuaireVisible,
        tel: profilTel,
        tva_regime: profilTvaRegime,
        tva_numero: profilTvaNumero,
        metier: profilMetier,
        metier_label: profilMetierLabel || null,
        iban: profilIban,
        bic: profilBic,
        banque: profilBanque,
        penalites_taux: profilPenalitesTaux,
        cgv: profilCgv,
        bio: profilBio,
        photo: profilPhoto,
      };
      const { error } = await supabase.from('users').update(updates).eq('id', user.id);
      if (error) throw error;
      setUser({ ...user, ...updates });
      setEditProfil(false);
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setSavingProfil(false);
    }
  };

  const saveOnboarding = async () => {
    if (!profilNom) {
      alert('Indiquez le nom de votre entreprise.');
      return;
    }
    setSavingOnboarding(true);
    try {
      const updates = {
        entrepriseNom: profilNom,
        siret: profilSiret,
        adresse: profilAdresse,
        ville: profilVille,
        tel: profilTel,
        metier: profilMetier,
        metier_label: profilMetierLabel || null,
        tva_regime: profilTvaRegime,
        tva_numero: profilTvaNumero,
        onboarding_done: true
      };
      const { error } = await supabase.from('users').update(updates).eq('id', user.id);
      if (error) throw error;
      setUser({ ...user, ...updates });
      if (!localStorage.getItem('planifpro_tutorial_done')) {
        setShowTutorial(true);
      }
    } catch (err) {
      alert('Erreur: ' + err.message);
    } finally {
      setSavingOnboarding(false);
    }
  };

  const handleLogout = async () => {
    try { await supabase.auth.signOut(); } catch (e) {}
    setUser(null);
  };

  // ===== PAGE MENTIONS LÉGALES =====
  if (!user && showLegal) {
    const sectionTitle = { fontSize: '20px', fontWeight: 'bold', marginTop: '32px', marginBottom: '12px' };
    const para = { color: '#4b5563' };
    const onglets = [
      { id: 'mentions', label: 'Mentions légales' },
      { id: 'cgv', label: 'CGV' },
      { id: 'cgu', label: 'CGU' },
      { id: 'cookies', label: 'Cookies' }
    ];
    return (
      <div style={{ minHeight: '100vh', background: '#fff', color: '#1f2937' }}>
        <header style={{ borderBottom: '1px solid #e5e7eb', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => setShowLegal(false)}>
            <Logo size={24} />
          </div>
          <button onClick={() => setShowLegal(false)} style={{ padding: '8px 16px', background: '#1e3a8a', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 'bold' }}>← Retour</button>
        </header>

        {/* Onglets de navigation entre les documents */}
        <div style={{ borderBottom: '1px solid #e5e7eb', padding: '0 24px', display: 'flex', gap: '8px', overflowX: 'auto', maxWidth: '800px', margin: '0 auto' }}>
          {onglets.map(o => (
            <button key={o.id} onClick={() => setLegalTab(o.id)} style={{
              padding: '14px 18px', background: 'none', border: 'none', borderBottom: legalTab === o.id ? '2px solid #2563eb' : '2px solid transparent',
              color: legalTab === o.id ? '#2563eb' : '#6b7280', cursor: 'pointer', fontWeight: 600, fontSize: '14px', whiteSpace: 'nowrap'
            }}>{o.label}</button>
          ))}
        </div>

        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '48px 24px', lineHeight: '1.7' }}>
          {/* ===== MENTIONS LÉGALES ===== */}
          {legalTab === 'mentions' && (
            <div>
              <h1 style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '32px' }}>Mentions légales</h1>
              <h2 style={sectionTitle}>1. Éditeur du service</h2>
              <p style={para}>
                Le service PlanifPro est édité par Christopher PROCUREUR (entrepreneur individuel).<br/>
                SIRET : 84398770200019<br/>
                Adresse : 9 cour Wacrenier, 59160 Lille<br/>
                Email : contact@kreeswaves.fr<br/>
                Directeur de la publication : Christopher PROCUREUR
              </p>
              <h2 style={sectionTitle}>2. Hébergement</h2>
              <p style={para}>
                Le site est hébergé par Netlify, Inc., 512 2nd Street, Suite 200, San Francisco, CA 94107, USA.<br/>
                La base de données est hébergée par Supabase.
              </p>
              <h2 style={sectionTitle}>3. Propriété intellectuelle</h2>
              <p style={para}>
                L'ensemble des éléments du service PlanifPro (code, design, logo, contenus) est protégé par le droit de la propriété intellectuelle et reste la propriété exclusive de l'éditeur.
              </p>
              <h2 style={sectionTitle}>4. Responsabilité</h2>
              <p style={para}>
                L'éditeur s'efforce d'assurer la disponibilité et la fiabilité du service mais ne saurait être tenu responsable des interruptions, pertes de données ou dommages indirects liés à l'utilisation du service.
              </p>
            </div>
          )}

          {/* ===== CGV ===== */}
          {legalTab === 'cgv' && (
            <div>
              <h1 style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '32px' }}>Conditions Générales de Vente (CGV)</h1>
              <h2 style={sectionTitle}>1. Objet</h2>
              <p style={para}>
                Les présentes CGV régissent la vente d'abonnements au service PlanifPro, logiciel de gestion des interventions destiné aux professionnels des services à domicile.
              </p>
              <h2 style={sectionTitle}>2. Tarifs et formules</h2>
              <p style={para}>
                L'accès au service est proposé par abonnement mensuel sans engagement : formule Indépendant à 14,99€/mois (1 utilisateur), formule Pro à 74,99€/mois (jusqu'à 6 utilisateurs) et formule Business à 119,99€/mois (jusqu'à 12 utilisateurs). Tous les prix sont indiqués TTC.
              </p>
              <h2 style={sectionTitle}>3. Paiement</h2>
              <p style={para}>
                Le paiement est sécurisé et géré par Stripe. L'abonnement est renouvelé automatiquement chaque mois jusqu'à résiliation par le client. Aucune information bancaire n'est stockée par l'éditeur.
              </p>
              <h2 style={sectionTitle}>4. Droit de rétractation</h2>
              <p style={para}>
                Conformément à l'article L221-18 du Code de la consommation, le client dispose d'un délai de 14 jours pour exercer son droit de rétractation. Une garantie satisfait ou remboursé de 7 jours est également proposée.
              </p>
              <h2 style={sectionTitle}>5. Résiliation</h2>
              <p style={para}>
                Le client peut résilier son abonnement à tout moment depuis son espace de facturation. La résiliation prend effet à la fin de la période mensuelle en cours, sans remboursement du mois entamé.
              </p>
              <h2 style={sectionTitle}>6. Disponibilité du service</h2>
              <p style={para}>
                L'éditeur s'engage à fournir le service avec diligence mais ne garantit pas une disponibilité ininterrompue. Des opérations de maintenance peuvent occasionner des interruptions temporaires.
              </p>
            </div>
          )}

          {/* ===== CGU ===== */}
          {legalTab === 'cgu' && (
            <div>
              <h1 style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '32px' }}>Conditions Générales d'Utilisation (CGU)</h1>
              <h2 style={sectionTitle}>1. Acceptation</h2>
              <p style={para}>
                En créant un compte et en utilisant PlanifPro, l'utilisateur accepte sans réserve les présentes conditions d'utilisation.
              </p>
              <h2 style={sectionTitle}>2. Accès au service</h2>
              <p style={para}>
                Le service est accessible aux professionnels disposant d'un compte valide. L'utilisateur est responsable de la confidentialité de ses identifiants de connexion et de toute activité réalisée depuis son compte.
              </p>
              <h2 style={sectionTitle}>3. Utilisation conforme</h2>
              <p style={para}>
                L'utilisateur s'engage à utiliser le service conformément à sa destination et à la législation en vigueur. Il s'interdit toute utilisation frauduleuse, toute tentative d'atteinte à la sécurité du service ou d'accès non autorisé aux données d'autres utilisateurs.
              </p>
              <h2 style={sectionTitle}>4. Contenu de l'utilisateur</h2>
              <p style={para}>
                L'utilisateur reste propriétaire des données qu'il saisit (clients, interventions, photos). Il garantit disposer des droits nécessaires sur ces contenus et est seul responsable de leur exactitude et de leur licéité.
              </p>
              <h2 style={sectionTitle}>5. Suspension</h2>
              <p style={para}>
                L'éditeur se réserve le droit de suspendre ou de supprimer un compte en cas de non-respect des présentes conditions, sans préavis ni remboursement.
              </p>
              <h2 style={sectionTitle}>6. Évolution du service</h2>
              <p style={para}>
                L'éditeur peut faire évoluer les fonctionnalités du service à tout moment. Les présentes conditions peuvent être mises à jour ; l'utilisateur en sera informé.
              </p>
            </div>
          )}

          {/* ===== POLITIQUE COOKIES ===== */}
          {legalTab === 'cookies' && (
            <div>
              <h1 style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '32px' }}>Politique de gestion des cookies</h1>
              <h2 style={sectionTitle}>1. Qu'est-ce qu'un cookie ?</h2>
              <p style={para}>
                Un cookie est un petit fichier déposé sur votre appareil lors de la visite d'un site. Il permet notamment de mémoriser des informations relatives à votre navigation.
              </p>
              <h2 style={sectionTitle}>2. Cookies utilisés par PlanifPro</h2>
              <p style={para}>
                PlanifPro utilise uniquement des cookies strictement nécessaires au fonctionnement du service : maintien de votre session de connexion et sécurité. Ces cookies sont indispensables et ne nécessitent pas de consentement préalable.
              </p>
              <h2 style={sectionTitle}>3. Cookies tiers</h2>
              <p style={para}>
                Le paiement est géré par Stripe, qui peut déposer ses propres cookies de sécurité lors d'une transaction. Ces cookies sont régis par la politique de confidentialité de Stripe.
              </p>
              <h2 style={sectionTitle}>4. Absence de suivi publicitaire</h2>
              <p style={para}>
                PlanifPro n'utilise aucun cookie publicitaire ni de traçage à des fins marketing. Vos données de navigation ne sont ni revendues ni partagées à des fins commerciales.
              </p>
              <h2 style={sectionTitle}>5. Gestion des cookies</h2>
              <p style={para}>
                Vous pouvez à tout moment configurer votre navigateur pour refuser les cookies. Toutefois, le refus des cookies strictement nécessaires peut empêcher le bon fonctionnement du service (notamment la connexion).
              </p>
              <h2 style={sectionTitle}>6. Données personnelles (RGPD)</h2>
              <p style={para}>
                Les données collectées (email, informations d'entreprise, données d'interventions) sont utilisées uniquement pour le fonctionnement du service. Conformément au RGPD, vous disposez d'un droit d'accès, de rectification et de suppression de vos données en contactant contact@kreeswaves.fr.
              </p>
            </div>
          )}

          <p style={{ color: '#9ca3af', marginTop: '40px', fontSize: '13px' }}>Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
        </div>
      </div>
    );
  }

  // ===== PAGE CONFIRMATION PAIEMENT =====
  if (showPaymentSuccess) {
    return withSuspense(
      <PaymentSuccess
        user={user}
        darkMode={darkMode}
        onContinue={() => setShowPaymentSuccess(false)}
      />
    );
  }

  // ===== PAGE AVIS CLIENT =====
  if (reviewUserId) return withSuspense(<ReviewPage userId={reviewUserId} />);

  // ===== PAGE PUBLIQUE PRO =====
  if (proSlug) return withSuspense(<ProPublicPage slug={proSlug} onRegister={() => { window.history.pushState({}, '', '/'); window.location.reload(); }} />);

  // ===== PAGE PUBLIQUE SIGNATURE DEVIS =====
  if (devisSignId) return withSuspense(<DevisSignPage devisId={devisSignId} />);

  // ===== PAGE PUBLIQUE CARNET DE BÂTIMENT =====
  if (batimentId) return withSuspense(<BatimentPage batimentId={batimentId} />);

  // ===== DÉMO =====

  // ===== ADMIN =====
  if (showAdmin && user) return withSuspense(<AdminDashboard currentUser={user} onBack={() => setShowAdmin(false)} />);

  // ===== PAGES SEO LOCALES =====
  if (seoLocal) return withSuspense(<SeoLocalPage metier={seoLocal.metier} ville={seoLocal.ville} onRegister={() => { window.history.pushState({}, '', '/'); window.location.reload(); }} onLogin={() => { window.history.pushState({}, '', '/'); window.location.reload(); }} />);

  // ===== CALCULATEURS GRATUITS (lead magnets SEO) =====
  if (calculateurRoute) {
    const goCalc = (page, slug) => {
      if (page === 'home') { window.history.pushState({}, '', '/'); window.location.reload(); return; }
      if (page === 'calculateurs') { window.history.pushState({}, '', '/calculateurs'); window.location.reload(); return; }
      if (page === 'calculateur') { window.history.pushState({}, '', `/calculateurs/${slug}`); window.location.reload(); return; }
    };
    return withSuspense(<CalculateursPage slug={calculateurRoute.slug} onNavigate={goCalc}
      onRegister={() => { window.history.pushState({}, '', '/'); window.location.reload(); }}
      onLogin={() => { window.history.pushState({}, '', '/'); window.location.reload(); }} />);
  }

  // ===== NOUVEAU MOT DE PASSE (arrivée depuis le lien "mot de passe oublié") =====
  if (newPasswordMode) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: darkMode ? '#111827' : '#fbfbfd', padding: '24px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
        <div style={{ width: '100%', maxWidth: '420px', padding: '44px', borderRadius: '24px', boxShadow: darkMode ? 'none' : '0 10px 50px rgba(0,0,0,0.08)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', background: darkMode ? '#1c1c1e' : '#fff' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '14px' }}>
              <Logo size={40} color={darkMode ? '#fff' : '#0a0a0a'} />
            </div>
          </div>
          {newPasswordDone ? (
            <div style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '32px', margin: '0 0 12px' }}>✅</p>
              <h2 style={{ fontSize: '20px', fontWeight: 700, margin: '0 0 8px', color: darkMode ? '#fff' : '#0a0a0a' }}>Mot de passe mis à jour</h2>
              <p style={{ fontSize: '14px', color: darkMode ? '#9ca3af' : '#6b7280', margin: '0 0 24px' }}>Tu peux maintenant te connecter avec ton nouveau mot de passe.</p>
              <button onClick={() => { setNewPasswordMode(false); setNewPassword(''); setNewPasswordConfirm(''); setNewPasswordDone(false); window.history.replaceState({}, '', window.location.pathname); }} style={{ width: '100%', padding: '14px', background: '#2563eb', color: '#fff', fontWeight: 600, borderRadius: '980px', border: 'none', cursor: 'pointer', fontSize: '16px' }}>
                Se connecter →
              </button>
            </div>
          ) : (
            <form onSubmit={updateNewPassword} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <h2 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '4px', letterSpacing: '-0.02em', color: darkMode ? '#fff' : '#0a0a0a', textAlign: 'center' }}>
                Choisir un nouveau mot de passe
              </h2>
              <input type="password" placeholder="Nouveau mot de passe (6 caractères min.)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} style={{ padding: '14px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '15px', outline: 'none' }} />
              <input type="password" placeholder="Confirmer le mot de passe" value={newPasswordConfirm} onChange={(e) => setNewPasswordConfirm(e.target.value)} style={{ padding: '14px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '15px', outline: 'none' }} />
              <button type="submit" disabled={newPasswordSaving} style={{ padding: '14px', background: newPasswordSaving ? '#ccc' : '#2563eb', color: '#fff', fontWeight: 600, borderRadius: '980px', border: 'none', cursor: 'pointer', fontSize: '16px', marginTop: '4px' }}>
                {newPasswordSaving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // ===== BLOG =====
  if (blogView) {
    if (blogView.page === 'blog') return withSuspense(<BlogPage onNavigate={navigateBlog} onLogin={() => { setBlogView(null); setIsRegister(false); setShowAuth(true); }} onRegister={() => { setBlogView(null); setIsRegister(true); setShowAuth(true); }} />);
    if (blogView.page === 'article') return withSuspense(<BlogPost slug={blogView.slug} onNavigate={navigateBlog} onRegister={() => { setBlogView(null); setIsRegister(true); setShowAuth(true); }} />);
  }

  // ===== LANDING PAGE =====
  if (!user && !showAuth) {
    return (
      <LandingPage
        onLogin={() => { setIsRegister(false); setRegisterFromLanding(false); setShowAuth(true); }}
        onRegister={() => { setIsRegister(true); setRegisterFromLanding(true); setRegisterMode('directeur'); setShowAuth(true); }}
        onLegal={(tab) => { setLegalTab(tab); setShowLegal(true); }}
        onBlog={() => navigateBlog('blog')}
        showCookieBanner={showCookieBanner}
        onAcceptCookies={accepterCookies}
      />
    );
  }

    // ===== LOGIN PAGE =====
  if (!user) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: darkMode ? '#111827' : '#fbfbfd', padding: '24px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
        <div style={{ width: '100%', maxWidth: '420px', padding: '44px', borderRadius: '24px', boxShadow: darkMode ? 'none' : '0 10px 50px rgba(0,0,0,0.08)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', background: darkMode ? '#1c1c1e' : '#fff' }}>
          <div style={{ textAlign: 'center', marginBottom: '36px' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '14px' }}>
              <Logo size={40} color={darkMode ? '#fff' : '#0a0a0a'} />
            </div>
            <p style={{ fontSize: '15px', color: darkMode ? '#9ca3af' : '#86868b', marginTop: '8px' }}>Gestion d'interventions pour les pros</p>
          </div>

          {resetMode ? (
            <form onSubmit={sendResetEmail} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <h2 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '4px', letterSpacing: '-0.02em', color: darkMode ? '#fff' : '#0a0a0a', textAlign: 'center' }}>
                Mot de passe oublié
              </h2>
              {resetSent ? (
                <p style={{ fontSize: '14px', color: darkMode ? '#9ca3af' : '#6b7280', textAlign: 'center', lineHeight: 1.5 }}>
                  ✅ Si un compte existe pour <strong>{resetEmail}</strong>, un email vient d'être envoyé avec un lien pour choisir un nouveau mot de passe.
                </p>
              ) : (
                <>
                  <p style={{ fontSize: '13px', color: darkMode ? '#9ca3af' : '#6b7280', textAlign: 'center', margin: '0 0 4px' }}>
                    On t'envoie un lien par email pour en choisir un nouveau.
                  </p>
                  <input type="email" placeholder="Email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} style={{ padding: '14px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '15px', outline: 'none' }} />
                  <button type="submit" disabled={resetSending} style={{ padding: '14px', background: resetSending ? '#ccc' : '#2563eb', color: '#fff', fontWeight: 600, borderRadius: '980px', border: 'none', cursor: 'pointer', fontSize: '16px', marginTop: '4px' }}>
                    {resetSending ? 'Envoi...' : 'Envoyer le lien'}
                  </button>
                </>
              )}
              <button type="button" onClick={() => { setResetMode(false); setResetSent(false); setResetEmail(''); }} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontWeight: 600, fontSize: '14px', marginTop: '8px' }}>
                ← Retour à la connexion
              </button>
            </form>
          ) : (
          <form onSubmit={isRegister ? handleRegister : handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <h2 style={{ fontSize: '26px', fontWeight: 700, marginBottom: '8px', letterSpacing: '-0.02em', color: darkMode ? '#fff' : '#0a0a0a', textAlign: 'center' }}>
              {isRegister ? 'Créer un compte' : 'Se connecter'}
            </h2>

            {isRegister && !registerFromLanding && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '4px' }}>
                {[
                  { mode: 'directeur', label: 'Je crée mon espace pro', sub: 'Artisan, DJ, beauté — je gère mon activité', icon: '🏢' },
                  { mode: 'agent', label: 'Je rejoins une équipe', sub: "J'ai un code fourni par mon employeur", icon: '🔧' },
                  { mode: 'client_final', label: 'Je suis client d\'un prestataire', sub: "J'ai un code fourni par mon prestataire", icon: '🏠' },
                ].map(({ mode, label, sub, icon }) => (
                  <button key={mode} type="button" onClick={() => setRegisterMode(mode)} style={{ padding: '12px 14px', borderRadius: '14px', border: registerMode === mode ? '2px solid #2563eb' : '1.5px solid #d2d2d7', background: registerMode === mode ? '#eef4ff' : 'transparent', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' }}>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: registerMode === mode ? '#2563eb' : '#0a0a0a' }}>{icon} {label}</div>
                    <div style={{ fontSize: '12px', color: '#6e6e73', marginTop: '2px' }}>{sub}</div>
                  </button>
                ))}
              </div>
            )}
            {isRegister && registerFromLanding && (
              <div style={{ background: '#eef4ff', border: '1.5px solid #bfdbfe', borderRadius: '14px', padding: '12px 14px' }}>
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#2563eb' }}>🏢 Création d'espace pro</div>
                <div style={{ fontSize: '12px', color: '#3b82f6', marginTop: '2px' }}>Collaborateur ou client ? <button type="button" onClick={() => setRegisterFromLanding(false)} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '12px', fontWeight: 700, padding: 0, textDecoration: 'underline' }}>Changer</button></div>
              </div>
            )}

            {isRegister && registerMode === 'client_final' && (
              <p style={{ fontSize: '12px', color: '#6e6e73', background: '#f5f5f7', padding: '10px 12px', borderRadius: '10px', margin: 0 }}>
                Espace client : suivez vos demandes d'intervention auprès de votre prestataire.
              </p>
            )}

            {isRegister && (
              <input 
                type="text" 
                placeholder={(registerMode === 'agent' || registerMode === 'client_final') ? 'Ton nom complet' : "Nom de l'entreprise"} 
                value={entrepriseNom} 
                onChange={(e) => setEntrepriseNom(e.target.value)} 
                style={{ padding: '14px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '15px', outline: 'none' }} 
              />
            )}

            {isRegister && registerMode === 'agent' && (
              <input 
                type="text" 
                placeholder="Code société (donné par ton directeur)" 
                value={codeSociete} 
                onChange={(e) => setCodeSociete(e.target.value)} 
                style={{ padding: '14px', borderRadius: '12px', border: '1px solid #f59e0b', fontSize: '15px', textTransform: 'uppercase', outline: 'none' }} 
              />
            )}

            {isRegister && registerMode === 'client_final' && (
              <input 
                type="text" 
                placeholder="Code prestataire (donné par votre entreprise)" 
                value={codeSociete} 
                onChange={(e) => setCodeSociete(e.target.value)} 
                style={{ padding: '14px', borderRadius: '12px', border: '1px solid #2563eb', fontSize: '15px', textTransform: 'uppercase', outline: 'none' }} 
              />
            )}
            
            <input 
              type="email" 
              placeholder="Email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              style={{ padding: '14px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '15px', outline: 'none' }} 
            />
            
            <input
              type="password"
              placeholder="Mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ padding: '14px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '15px', outline: 'none' }}
            />

            {!isRegister && (
              <button type="button" onClick={() => { setResetMode(true); setResetEmail(email); }} style={{ background: 'none', border: 'none', color: '#86868b', cursor: 'pointer', fontSize: '13px', textAlign: 'right', padding: 0, marginTop: '-6px' }}>
                Mot de passe oublié ?
              </button>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{ 
                padding: '14px', 
                background: loading ? '#ccc' : '#2563eb', 
                color: '#fff', 
                fontWeight: 600, 
                borderRadius: '980px', 
                border: 'none', 
                cursor: 'pointer', 
                fontSize: '16px',
                marginTop: '4px'
              }}>
              {loading ? 'Chargement...' : (isRegister ? 'Créer mon compte' : 'Se connecter')}
            </button>
          </form>
          )}

          <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {resetMode ? <span /> : (
              <button
                onClick={() => setIsRegister(!isRegister)}
                style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>
                {isRegister ? '← Déjà un compte ?' : 'Pas de compte ? →'}
              </button>
            )}
            <button
              onClick={() => setDarkMode(!darkMode)}
              style={{ background: darkMode ? '#2c2c2e' : '#f5f5f7', border: 'none', width: '38px', height: '38px', borderRadius: '50%', cursor: 'pointer', fontSize: '17px' }}>
              {darkMode ? '☀️' : '🌙'}
            </button>
          </div>

          <button
            onClick={() => setShowAuth(false)}
            style={{ width: '100%', marginTop: '20px', background: 'none', border: 'none', color: '#86868b', cursor: 'pointer', fontSize: '14px' }}>
            ← Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  // ===== PORTAIL CLIENT FINAL =====
  if (isClientFinal) {
    // ── Verrou : en attente de validation par la société ──
    if (user.client_valide === false) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: darkMode ? '#111827' : '#f5f7ff', padding: '24px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
          <div style={{ maxWidth: '440px', textAlign: 'center', background: darkMode ? '#1c1c1e' : '#fff', borderRadius: '20px', padding: '40px 32px', boxShadow: darkMode ? 'none' : '0 10px 40px rgba(0,0,0,0.08)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>⏳</div>
            <h2 style={{ fontSize: '22px', fontWeight: 800, margin: '0 0 10px', color: darkMode ? '#fff' : '#0a0a0a' }}>Compte en attente de validation</h2>
            <p style={{ fontSize: '15px', color: darkMode ? '#9ca3af' : '#6b7280', lineHeight: 1.5, margin: '0 0 24px' }}>
              Votre prestataire a bien reçu votre demande. Vous recevrez l'accès à votre espace dès qu'il aura validé votre compte.
            </p>
            <button onClick={handleLogout} style={{ padding: '11px 24px', background: darkMode ? '#2c2c2e' : '#f5f5f7', color: darkMode ? '#fff' : '#0a0a0a', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>Se déconnecter</button>
          </div>
        </div>
      );
    }
    const urgenceColors = { 'urgente': '#ef4444', 'normale': '#2563eb', 'faible': '#10b981' };
    const statutDemColors = {
      'nouvelle': { bg: '#fef9c3', color: '#92400e', label: 'Nouvelle' },
      'en_cours': { bg: '#dbeafe', color: '#1d4ed8', label: 'En cours' },
      'planifiee': { bg: '#ede9fe', color: '#6d28d9', label: 'Planifiée' },
      'terminee': { bg: '#dcfce7', color: '#16a34a', label: 'Terminée' },
      'refusee': { bg: '#fee2e2', color: '#dc2626', label: 'Refusée' },
    };
    const aujourd_hui = new Date().toISOString().split('T')[0];
    const prochainRDV = monHistorique.filter(r => r.date >= aujourd_hui && r.statut !== 'termine').sort((a,b) => a.date > b.date ? 1 : -1)[0];
    const rdvPassés = monHistorique.filter(r => r.statut === 'termine');
    const rdvAVenir = monHistorique.filter(r => r.date >= aujourd_hui && r.statut !== 'termine');
    const vocab = prestataireMetier === 'beaute'
      ? { sousTitre: 'Vos demandes de rendez-vous beauté.', formTitre: 'Nouvelle demande de rendez-vous', titrePlaceholder: 'Prestation souhaitée (ex: Coupe + couleur)', descPlaceholder: 'Décrivez ce que vous souhaitez...', adressePlaceholder: 'Adresse du rendez-vous' }
      : prestataireMetier === 'dj'
      ? { sousTitre: 'Vos demandes de booking.', formTitre: 'Nouvelle demande de booking', titrePlaceholder: 'Type d\'événement (ex: Mariage, anniversaire)', descPlaceholder: 'Décrivez votre événement...', adressePlaceholder: 'Lieu de l\'événement' }
      : prestataireMetier === 'aide_domicile'
      ? { sousTitre: 'Vos demandes de passage.', formTitre: 'Nouvelle demande de passage', titrePlaceholder: 'Type de prestation (ex: Ménage, courses)', descPlaceholder: 'Décrivez vos besoins...', adressePlaceholder: 'Adresse d\'intervention' }
      : { sousTitre: 'Vos demandes d\'intervention.', formTitre: 'Nouvelle demande', titrePlaceholder: 'Titre de votre demande (ex: Séance photo, Cours particulier, Réparation...)', descPlaceholder: 'Décrivez votre besoin...', adressePlaceholder: 'Adresse du rendez-vous' };

    const DM = darkMode;
    const bg = DM ? '#111827' : '#f5f7ff';
    const card = DM ? '#1c1c1e' : '#fff';
    const border = DM ? '#2c2c2e' : '#e8ecf8';
    const txt = DM ? '#fff' : '#0a0a0a';
    const sub = DM ? '#9ca3af' : '#6b7280';

    return (
      <div style={{ minHeight: '100vh', background: bg, color: txt, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>

        {/* Notification temps réel */}
        {realtimeToast && (
          <div onClick={() => setRealtimeToast(null)} style={{ position: 'fixed', top: '16px', left: '50%', transform: 'translateX(-50%)', zIndex: 1000, background: '#2563eb', color: '#fff', padding: '12px 22px', borderRadius: '980px', fontWeight: 700, fontSize: '14px', boxShadow: '0 8px 24px rgba(37,99,235,0.35)', cursor: 'pointer' }}>
            {realtimeToast.message}
          </div>
        )}

        {/* Header */}
        <header style={{ borderBottom: `1px solid ${border}`, padding: '0 24px', height: '60px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: DM ? 'rgba(28,28,30,0.9)' : 'rgba(255,255,255,0.9)', backdropFilter: 'blur(20px)', position: 'sticky', top: 0, zIndex: 100 }}>
          <Logo size={24} color={DM ? '#fff' : '#0a0a0a'} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '14px', color: sub, display: 'none' }}>Bonjour {user.entrepriseNom}</span>
            <button onClick={() => setDarkMode(!DM)} style={{ background: DM ? '#2c2c2e' : '#f5f5f7', border: 'none', width: '36px', height: '36px', borderRadius: '50%', cursor: 'pointer', fontSize: '16px' }}>{DM ? '☀️' : '🌙'}</button>
            <button onClick={handleLogout} style={{ padding: '7px 16px', background: DM ? '#2c2c2e' : '#f5f5f7', color: txt, border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>Déconnexion</button>
          </div>
        </header>

        <main style={{ maxWidth: '780px', margin: '0 auto', padding: '28px 20px' }}>

          {/* Bannière bienvenue + prochain RDV */}
          <div style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563eb)', borderRadius: '20px', padding: '24px 28px', marginBottom: '24px', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <p style={{ fontSize: '13px', opacity: 0.8, margin: '0 0 4px' }}>Bonjour</p>
              <h2 style={{ fontSize: '22px', fontWeight: 800, margin: 0 }}>{user.entrepriseNom || user.email}</h2>
              <p style={{ fontSize: '13px', opacity: 0.75, margin: '4px 0 0' }}>{demandes.length} demande{demandes.length !== 1 ? 's' : ''} · {rdvPassés.length} intervention{rdvPassés.length !== 1 ? 's' : ''} passée{rdvPassés.length !== 1 ? 's' : ''}</p>
            </div>
            {prochainRDV ? (
              <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '14px', padding: '14px 18px', minWidth: '180px' }}>
                <p style={{ fontSize: '11px', opacity: 0.8, margin: '0 0 4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Prochain RDV</p>
                <p style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 2px' }}>{prochainRDV.titre || prochainRDV.nature || 'Intervention'}</p>
                <p style={{ fontSize: '13px', opacity: 0.85, margin: 0 }}>{new Date(prochainRDV.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}{prochainRDV.heure ? ' · ' + prochainRDV.heure : ''}</p>
              </div>
            ) : (
              <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '14px', padding: '14px 18px' }}>
                <p style={{ fontSize: '13px', opacity: 0.8, margin: 0 }}>Pas de RDV planifié</p>
              </div>
            )}
          </div>

          {/* Suivi technicien en direct */}
          {clientTracking && clientTracking.lat && (
            <div style={{ background: 'linear-gradient(135deg, #065f46, #10b981)', borderRadius: '20px', padding: '20px 22px', marginBottom: '24px', color: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
                <div>
                  <p style={{ fontSize: '13px', opacity: 0.9, margin: 0, fontWeight: 700 }}>🚗 {clientTracking.tech_nom || 'Votre technicien'} est en route</p>
                  {clientTracking.eta && <p style={{ fontSize: '15px', fontWeight: 800, margin: '4px 0 0' }}>Arrivée estimée : {clientTracking.eta}</p>}
                </div>
                <span style={{ fontSize: '11px', opacity: 0.85 }}>Position mise à jour {clientTracking.updated_at ? new Date(clientTracking.updated_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
              </div>
              <iframe
                title="Position du technicien"
                style={{ width: '100%', height: '220px', border: 0, borderRadius: '12px' }}
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${clientTracking.lng - 0.012}%2C${clientTracking.lat - 0.008}%2C${clientTracking.lng + 0.012}%2C${clientTracking.lat + 0.008}&layer=mapnik&marker=${clientTracking.lat}%2C${clientTracking.lng}`}
              />
            </div>
          )}

          {/* Onglets */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '24px', flexWrap: 'wrap' }}>
            {[
              { id: 'demandes', label: 'Mes demandes', count: demandes.filter(d => d.statut !== 'terminee' && d.statut !== 'refusee').length },
              { id: 'interventions', label: 'Mes interventions', count: rdvAVenir.length },
              { id: 'factures', label: 'Mes factures', count: mesFacturesClient.filter(f => f.status !== 'paye').length },
              { id: 'messagerie', label: 'Messagerie', count: messages.filter(m => m.sender === 'pro' && !m.lu).length },
              { id: 'fidelite', label: 'Ma fidélité', count: mesCartes.length },
              { id: 'annuaire', label: '📍 Trouver un pro' },
              { id: 'profil', label: 'Mon profil' },
            ].map(t => (
              <button key={t.id} onClick={() => { setClientVue(t.id); if (t.id === 'messagerie' && user.societe_id) loadMessages(user.societe_id, user.id, 'client'); }} style={{
                padding: '9px 18px', borderRadius: '980px', border: 'none', cursor: 'pointer',
                fontWeight: 600, fontSize: '13px',
                background: clientVue === t.id ? '#2563eb' : card,
                color: clientVue === t.id ? '#fff' : sub,
                boxShadow: clientVue === t.id ? '0 4px 14px rgba(37,99,235,0.3)' : `0 1px 4px rgba(0,0,0,0.08)`,
                display: 'flex', alignItems: 'center', gap: '7px',
              }}>
                {t.label}
                {t.count > 0 && <span style={{ background: clientVue === t.id ? 'rgba(255,255,255,0.25)' : '#dbeafe', color: clientVue === t.id ? '#fff' : '#2563eb', borderRadius: '10px', fontSize: '11px', fontWeight: 700, padding: '1px 7px' }}>{t.count}</span>}
              </button>
            ))}
          </div>

          {/* ── VUE DEMANDES ── */}
          {clientVue === 'demandes' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                <div>
                  <h1 style={{ fontSize: '26px', fontWeight: 700, letterSpacing: '-0.02em', margin: 0 }}>Mes demandes</h1>
                  <p style={{ fontSize: '13px', color: sub, margin: '4px 0 0' }}>{vocab.sousTitre}</p>
                </div>
                <button onClick={() => setShowDemandeForm(!showDemandeForm)} style={{ padding: '10px 20px', background: showDemandeForm ? '#f3f4f6' : '#2563eb', color: showDemandeForm ? sub : '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>
                  {showDemandeForm ? '✕ Annuler' : '+ Nouvelle demande'}
                </button>
              </div>

              {showDemandeForm && (
                <div style={{ padding: '24px', borderRadius: '18px', background: card, boxShadow: DM ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: `1px solid ${border}`, marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '17px', fontWeight: 700, marginBottom: '18px', color: txt }}>{vocab.formTitre}</h3>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    <input type="text" placeholder={vocab.titrePlaceholder} value={demTitre} onChange={e => setDemTitre(e.target.value)} style={{ padding: '12px', borderRadius: '12px', border: `1px solid ${border}`, fontSize: '14px', background: DM ? '#111827' : '#fff', color: txt, boxSizing: 'border-box' }} />
                    <textarea placeholder={vocab.descPlaceholder} value={demDescription} onChange={e => setDemDescription(e.target.value)} style={{ padding: '12px', borderRadius: '12px', border: `1px solid ${border}`, fontSize: '14px', minHeight: '90px', background: DM ? '#111827' : '#fff', color: txt, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }} />
                    <input type="text" placeholder={vocab.adressePlaceholder} value={demAdresse} onChange={e => setDemAdresse(e.target.value)} style={{ padding: '12px', borderRadius: '12px', border: `1px solid ${border}`, fontSize: '14px', background: DM ? '#111827' : '#fff', color: txt, boxSizing: 'border-box' }} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                      <div>
                        <label style={{ fontSize: '12px', color: sub, display: 'block', marginBottom: '5px', fontWeight: 600 }}>Urgence</label>
                        <select value={demUrgence} onChange={e => setDemUrgence(e.target.value)} style={{ width: '100%', padding: '11px', borderRadius: '10px', border: `1px solid ${border}`, fontSize: '13px', background: DM ? '#111827' : '#fff', color: txt, boxSizing: 'border-box' }}>
                          <option value="faible">Pas urgent</option>
                          <option value="normale">Normal</option>
                          <option value="urgente">Urgent</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', color: sub, display: 'block', marginBottom: '5px', fontWeight: 600 }}>Date souhaitée</label>
                        <input type="date" value={demDateSouhaitee} onChange={e => setDemDateSouhaitee(e.target.value)} style={{ width: '100%', padding: '11px', borderRadius: '10px', border: `1px solid ${border}`, fontSize: '13px', background: DM ? '#111827' : '#fff', color: txt, boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', color: sub, display: 'block', marginBottom: '5px', fontWeight: 600 }}>Heure souhaitée</label>
                        <input type="time" value={demHeureSouhaitee} onChange={e => setDemHeureSouhaitee(e.target.value)} style={{ width: '100%', padding: '11px', borderRadius: '10px', border: `1px solid ${border}`, fontSize: '13px', background: DM ? '#111827' : '#fff', color: txt, boxSizing: 'border-box' }} />
                      </div>
                    </div>
                    <input type="text" placeholder="Précisions sur vos disponibilités (optionnel)" value={demCreneaux} onChange={e => setDemCreneaux(e.target.value)} style={{ padding: '12px', borderRadius: '12px', border: `1px solid ${border}`, fontSize: '14px', background: DM ? '#111827' : '#fff', color: txt, boxSizing: 'border-box' }} />
                    <div>
                      <label style={{ fontSize: '12px', color: sub, display: 'block', marginBottom: '5px', fontWeight: 600 }}>Photos (optionnel)</label>
                      <input type="file" accept="image/*" multiple onChange={handleDemandePhotos} style={{ fontSize: '13px' }} />
                      {demPhotos.length > 0 && (
                        <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                          {demPhotos.map((p, i) => (
                            <div key={i} style={{ position: 'relative' }}>
                              <img src={p} alt="" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px' }} />
                              <button onClick={() => setDemPhotos(demPhotos.filter((_, j) => j !== i))} style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', fontSize: '11px' }}>✕</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <button onClick={submitDemande} disabled={savingDemande} style={{ padding: '13px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 700, fontSize: '15px' }}>
                      {savingDemande ? 'Envoi...' : 'Envoyer ma demande'}
                    </button>
                  </div>
                </div>
              )}

              {demandes.length === 0 && !showDemandeForm ? (
                <div style={{ padding: '48px', borderRadius: '18px', background: card, textAlign: 'center', border: `1px solid ${border}` }}>
                  <p style={{ fontSize: '36px', marginBottom: '12px' }}>📋</p>
                  <p style={{ fontSize: '15px', color: sub }}>Aucune demande pour le moment.</p>
                  <p style={{ fontSize: '13px', color: sub, marginTop: '4px' }}>Cliquez sur "Nouvelle demande" pour commencer.</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {demandes.map(d => {
                    const sc = statutDemColors[d.statut] || { bg: '#f3f4f6', color: '#374151', label: d.statut };
                    return (
                      <div key={d.id} style={{ padding: '20px', borderRadius: '16px', background: card, boxShadow: DM ? 'none' : '0 2px 12px rgba(0,0,0,0.05)', border: `1px solid ${border}`, borderLeft: `4px solid ${urgenceColors[d.urgence] || '#2563eb'}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', gap: '10px' }}>
                          <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: txt }}>{d.titre}</h3>
                          <span style={{ padding: '3px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, background: sc.bg, color: sc.color, whiteSpace: 'nowrap', flexShrink: 0 }}>{sc.label}</span>
                        </div>
                        <p style={{ fontSize: '14px', color: DM ? '#d1d5db' : '#374151', marginBottom: '8px', lineHeight: 1.5 }}>{d.description}</p>
                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                          {d.adresse && <span style={{ fontSize: '12px', color: sub }}>📍 {d.adresse}</span>}
                          {d.creneaux && <span style={{ fontSize: '12px', color: sub }}>🕐 {d.creneaux}</span>}
                          {d.created_at && <span style={{ fontSize: '12px', color: sub }}>Envoyée le {new Date(d.created_at).toLocaleDateString('fr-FR')}</span>}
                        </div>
                        {d.photos && d.photos.length > 0 && (
                          <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                            {d.photos.map((p, i) => <img key={i} src={p} alt="" style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '8px' }} />)}
                          </div>
                        )}
                        {d.reponse && (
                          <div style={{ marginTop: '12px', background: DM ? '#0f2a1f' : '#f0fdf4', padding: '12px 14px', borderRadius: '10px', borderLeft: '3px solid #10b981' }}>
                            <p style={{ fontSize: '11px', fontWeight: 700, color: '#10b981', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Réponse de votre prestataire</p>
                            <p style={{ fontSize: '14px', color: DM ? '#a7f3d0' : '#065f46', margin: 0 }}>{d.reponse}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* ── VUE INTERVENTIONS ── */}
          {clientVue === 'interventions' && (() => {
            // Vue détail d'une intervention
            if (clientDetailIntervention) {
              const r = clientDetailIntervention;
              const photos = r.photos ? (() => { try { return JSON.parse(r.photos); } catch { return []; } })() : [];
              const checklist = r.checklist ? (() => { try { return JSON.parse(r.checklist); } catch { return []; } })() : [];
              const factureLinked = mesFacturesClient.find(f => f.client === r.client && f.user_id === r.prestataireId);
              return (
                <div>
                  <button onClick={() => setClientDetailIntervention(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2563eb', fontWeight: 600, fontSize: '14px', marginBottom: '20px', padding: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>← Retour</button>
                  <h1 style={{ fontSize: '24px', fontWeight: 800, margin: '0 0 4px', letterSpacing: '-0.02em' }}>{r.titre || r.nature || 'Intervention'}</h1>
                  <p style={{ fontSize: '14px', color: sub, margin: '0 0 24px' }}>{r.prestataire} · {r.date ? new Date(r.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : ''}{r.heure ? ' · ' + r.heure : ''}</p>

                  {/* Infos rapides */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                    {[
                      { label: 'Statut', value: r.statut === 'termine' ? '✅ Terminé' : r.statut === 'en_cours' ? '⚡ En cours' : '📅 Planifié', col: r.statut === 'termine' ? '#10b981' : r.statut === 'en_cours' ? '#2563eb' : '#6b7280' },
                      r.adresse && { label: 'Adresse', value: r.adresse, col: txt },
                      r.technicien && { label: 'Technicien', value: r.technicien, col: txt },
                      r.heure_arrivee && { label: 'Arrivée', value: r.heure_arrivee, col: txt },
                      r.heure_depart && { label: 'Départ', value: r.heure_depart, col: txt },
                    ].filter(Boolean).map((item, i) => (
                      <div key={i} style={{ padding: '14px 16px', borderRadius: '12px', background: card, border: `1px solid ${border}` }}>
                        <p style={{ fontSize: '11px', color: sub, margin: '0 0 4px', textTransform: 'uppercase', fontWeight: 700 }}>{item.label}</p>
                        <p style={{ fontSize: '14px', fontWeight: 600, color: item.col, margin: 0 }}>{item.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Rapport */}
                  {(r.travaux || r.observations || r.bilan) && (
                    <div style={{ padding: '20px', borderRadius: '16px', background: card, border: `1px solid ${border}`, marginBottom: '20px' }}>
                      <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 16px', color: txt }}>📋 Compte-rendu</h3>
                      {r.travaux && <div style={{ marginBottom: '12px' }}><p style={{ fontSize: '12px', color: sub, fontWeight: 700, margin: '0 0 4px', textTransform: 'uppercase' }}>Travaux effectués</p><p style={{ fontSize: '14px', color: txt, margin: 0, lineHeight: 1.6 }}>{r.travaux}</p></div>}
                      {r.observations && <div style={{ marginBottom: '12px' }}><p style={{ fontSize: '12px', color: sub, fontWeight: 700, margin: '0 0 4px', textTransform: 'uppercase' }}>Observations</p><p style={{ fontSize: '14px', color: txt, margin: 0, lineHeight: 1.6 }}>{r.observations}</p></div>}
                      {r.bilan && <div><p style={{ fontSize: '12px', color: sub, fontWeight: 700, margin: '0 0 4px', textTransform: 'uppercase' }}>Bilan</p><p style={{ fontSize: '14px', color: txt, margin: 0, lineHeight: 1.6 }}>{r.bilan}</p></div>}
                    </div>
                  )}

                  {/* Checklist */}
                  {checklist.length > 0 && (
                    <div style={{ padding: '20px', borderRadius: '16px', background: card, border: `1px solid ${border}`, marginBottom: '20px' }}>
                      <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 12px', color: txt }}>✅ Tâches / Produits</h3>
                      <div style={{ display: 'grid', gap: '6px' }}>
                        {checklist.map((item, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: item.done ? '#10b981' : txt }}>
                            <span>{item.done ? '☑' : '☐'}</span>
                            <span style={{ textDecoration: item.done ? 'line-through' : 'none', opacity: item.done ? 0.7 : 1 }}>{item.text}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Photos */}
                  {photos.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 12px', color: txt }}>📸 Photos ({photos.length})</h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
                        {photos.map((p, i) => (
                          <div key={i} style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', aspectRatio: '1', cursor: 'pointer' }} onClick={() => window.open(p.url || p, '_blank')}>
                            <img src={p.url || p} alt={`Photo ${i+1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            {p.categorie && <span style={{ position: 'absolute', bottom: '6px', left: '6px', fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '4px', background: 'rgba(0,0,0,0.6)', color: '#fff', textTransform: 'uppercase' }}>{p.categorie}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Signatures */}
                  {(r.signature_client || r.signature_tech) && (
                    <div style={{ padding: '20px', borderRadius: '16px', background: card, border: `1px solid ${border}`, marginBottom: '20px' }}>
                      <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 12px', color: txt }}>✍️ Signatures</h3>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        {r.signature_client && <div style={{ textAlign: 'center' }}><p style={{ fontSize: '12px', color: sub, margin: '0 0 6px' }}>Client</p><img src={r.signature_client} alt="Signature client" style={{ maxWidth: '100%', height: '80px', objectFit: 'contain', border: `1px solid ${border}`, borderRadius: '8px', padding: '4px' }} /></div>}
                        {r.signature_tech && <div style={{ textAlign: 'center' }}><p style={{ fontSize: '12px', color: sub, margin: '0 0 6px' }}>Technicien</p><img src={r.signature_tech} alt="Signature technicien" style={{ maxWidth: '100%', height: '80px', objectFit: 'contain', border: `1px solid ${border}`, borderRadius: '8px', padding: '4px' }} /></div>}
                      </div>
                    </div>
                  )}

                  {/* Facture */}
                  {factureLinked && (
                    <div style={{ padding: '20px', borderRadius: '16px', background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', border: '1px solid #bfdbfe', marginBottom: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                        <div>
                          <h3 style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 4px', color: '#1e3a8a' }}>🧾 Facture</h3>
                          <p style={{ fontSize: '14px', color: '#1d4ed8', margin: 0 }}>{factureLinked.numero || `FAC-${factureLinked.id?.slice(0,6)}`} · {parseFloat(factureLinked.amount||0).toFixed(2)}€ TTC</p>
                          <p style={{ fontSize: '12px', color: '#3b82f6', margin: '2px 0 0' }}>
                            {factureLinked.status === 'paye' ? '✅ Payée' : '⏳ En attente de règlement'}
                            {factureLinked.date ? ` · ${new Date(factureLinked.date).toLocaleDateString('fr-FR')}` : ''}
                          </p>
                        </div>
                        <button onClick={() => generateFacturePDF(factureLinked)} style={{ padding: '10px 18px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '13px' }}>⬇️ Télécharger PDF</button>
                      </div>
                    </div>
                  )}

                  {/* Laisser un avis (intervention terminée uniquement) */}
                  {r.statut === 'termine' && r.prestataireId && (
                    <div style={{ padding: '20px', borderRadius: '16px', background: 'linear-gradient(135deg, #2563eb, #1e3a8a)', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', color: '#fff' }}>
                      <div>
                        <p style={{ fontWeight: 800, fontSize: '15px', margin: '0 0 4px' }}>Comment s'est passée cette intervention ?</p>
                        <p style={{ fontSize: '13px', opacity: 0.85, margin: 0 }}>Votre avis aide {r.prestataire || 'ce prestataire'} et les futurs clients.</p>
                      </div>
                      <a href={`/avis/${r.prestataireId}`} target="_blank" rel="noopener noreferrer" style={{ padding: '11px 22px', background: '#fff', color: '#2563eb', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 700, fontSize: '14px', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                        ⭐ Laisser un avis
                      </a>
                    </div>
                  )}
                </div>
              );
            }

            // Liste des interventions
            return (
              <div>
                <h1 style={{ fontSize: '26px', fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 6px' }}>Mes interventions</h1>
                <p style={{ fontSize: '13px', color: sub, margin: '0 0 24px' }}>Cliquez sur une intervention terminée pour voir le rapport, les photos et la facture.</p>

                {rdvAVenir.length > 0 && (
                  <>
                    <p style={{ fontSize: '12px', fontWeight: 700, color: '#2563eb', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>À venir</p>
                    <div style={{ display: 'grid', gap: '10px', marginBottom: '28px' }}>
                      {rdvAVenir.map((r, i) => (
                        <div key={i} style={{ padding: '16px 20px', borderRadius: '14px', background: '#eff6ff', border: '1px solid #bfdbfe', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                            <div style={{ background: '#2563eb', borderRadius: '10px', padding: '8px 12px', textAlign: 'center', minWidth: '52px' }}>
                              <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.8)', margin: 0, fontWeight: 600 }}>{new Date(r.date).toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase()}</p>
                              <p style={{ fontSize: '20px', fontWeight: 800, color: '#fff', margin: 0, lineHeight: 1 }}>{new Date(r.date).getDate()}</p>
                            </div>
                            <div>
                              <p style={{ fontWeight: 700, fontSize: '15px', color: '#1e3a8a', margin: '0 0 2px' }}>{r.titre || r.nature || 'Intervention'}</p>
                              <p style={{ fontSize: '13px', color: '#3b82f6', margin: 0 }}>{r.prestataire}{r.heure ? ' · ' + r.heure : ''}</p>
                            </div>
                          </div>
                          <span style={{ padding: '4px 12px', borderRadius: '8px', background: '#dbeafe', color: '#1d4ed8', fontSize: '12px', fontWeight: 700 }}>Planifié</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {rdvPassés.length > 0 && (
                  <>
                    <p style={{ fontSize: '12px', fontWeight: 700, color: sub, margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Historique</p>
                    <div style={{ display: 'grid', gap: '10px' }}>
                      {rdvPassés.map((r, i) => {
                        const hasPhotos = r.photos && JSON.parse(r.photos || '[]').length > 0;
                        const hasRapport = !!(r.travaux || r.bilan || r.observations);
                        const hasFacture = mesFacturesClient.some(f => f.client === r.client && f.user_id === r.prestataireId);
                        return (
                          <div key={i} onClick={() => setClientDetailIntervention(r)} style={{ padding: '16px 20px', borderRadius: '14px', background: card, border: `1px solid ${border}`, cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(37,99,235,0.1)'}
                            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                              <div>
                                <p style={{ fontWeight: 700, fontSize: '15px', color: txt, margin: '0 0 4px' }}>{r.titre || r.nature || 'Intervention'}</p>
                                <p style={{ fontSize: '13px', color: sub, margin: '0 0 8px' }}>
                                  {r.prestataire && `${r.prestataire} · `}
                                  {r.date ? new Date(r.date).toLocaleDateString('fr-FR') : ''}
                                  {r.heure ? ' · ' + r.heure : ''}
                                </p>
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                  {hasPhotos && <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '6px', background: '#f3f4f6', color: '#6b7280', fontWeight: 600 }}>📸 Photos</span>}
                                  {hasRapport && <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '6px', background: '#f3f4f6', color: '#6b7280', fontWeight: 600 }}>📋 Rapport</span>}
                                  {hasFacture && <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '6px', background: '#eff6ff', color: '#2563eb', fontWeight: 600 }}>🧾 Facture</span>}
                                </div>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }}>
                                <span style={{ padding: '4px 12px', borderRadius: '8px', background: '#dcfce7', color: '#16a34a', fontSize: '12px', fontWeight: 700 }}>Terminé</span>
                                <span style={{ color: '#9ca3af', fontSize: '18px' }}>›</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {monHistorique.length === 0 && (
                  <div style={{ padding: '48px', borderRadius: '18px', background: card, textAlign: 'center', border: `1px solid ${border}` }}>
                    <p style={{ fontSize: '36px', marginBottom: '12px' }}>📅</p>
                    <p style={{ fontSize: '15px', color: sub }}>Aucune intervention enregistrée.</p>
                    <p style={{ fontSize: '13px', color: sub, marginTop: '4px' }}>Vos rendez-vous apparaîtront ici dès que votre prestataire les aura planifiés.</p>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── VUE MESSAGERIE ── */}
          {clientVue === 'messagerie' && (
            <div>
              <h1 style={{ fontSize: '26px', fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 6px' }}>Messagerie</h1>
              <p style={{ fontSize: '13px', color: sub, margin: '0 0 16px' }}>Échangez directement avec votre prestataire.</p>
              <div style={{ background: card, borderRadius: '18px', border: `1px solid ${border}`, padding: '16px', height: '60vh', minHeight: '380px', display: 'flex', flexDirection: 'column' }}>
                <ChatThread
                  messages={messages}
                  myRole="client"
                  value={msgInput}
                  onChange={setMsgInput}
                  onSend={() => sendMessage(user.societe_id, user.id, 'client')}
                  sending={sendingMsg}
                  darkMode={darkMode}
                  emptyLabel="Aucun message pour l'instant. Écrivez à votre prestataire !"
                />
              </div>
            </div>
          )}

          {/* ── VUE FIDÉLITÉ ── */}
          {clientVue === 'fidelite' && (
            <div>
              <h1 style={{ fontSize: '26px', fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 6px' }}>Ma fidélité</h1>
              <p style={{ fontSize: '13px', color: sub, margin: '0 0 24px' }}>Vos cartes de fidélité et récompenses.</p>

              {mesCartes.length === 0 ? (
                <div style={{ padding: '48px', borderRadius: '18px', background: card, textAlign: 'center', border: `1px solid ${border}` }}>
                  <p style={{ fontSize: '36px', marginBottom: '12px' }}>⭐</p>
                  <p style={{ fontSize: '15px', color: sub }}>Aucune carte de fidélité pour le moment.</p>
                  <p style={{ fontSize: '13px', color: sub, marginTop: '4px' }}>Vos cartes apparaîtront ici dès que votre prestataire vous aura ajouté des points.</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '14px' }}>
                  {mesCartes.map((c, i) => {
                    const pct = Math.min(100, Math.round((c.points / c.objectif) * 100));
                    const atteint = c.points >= c.objectif;
                    return (
                      <div key={i} style={{ padding: '24px', borderRadius: '20px', background: atteint ? 'linear-gradient(135deg, #f59e0b, #f97316)' : 'linear-gradient(135deg, #1e3a8a, #2563eb)', color: '#fff', boxShadow: '0 8px 30px rgba(0,0,0,0.12)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                          <div>
                            <p style={{ fontSize: '11px', opacity: 0.75, margin: 0, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Carte de fidélité</p>
                            <h3 style={{ fontSize: '19px', fontWeight: 700, margin: '4px 0 0' }}>{c.prestataire}</h3>
                          </div>
                          <span style={{ fontSize: '26px' }}>{atteint ? '🎁' : '⭐'}</span>
                        </div>
                        <p style={{ fontSize: '30px', fontWeight: 800, margin: '0 0 2px' }}>{c.points} <span style={{ fontSize: '14px', fontWeight: 400, opacity: 0.8 }}>/ {c.objectif} pts</span></p>
                        <div style={{ height: '8px', background: 'rgba(255,255,255,0.25)', borderRadius: '4px', overflow: 'hidden', margin: '12px 0 10px' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: '#fff', borderRadius: '4px', transition: 'width 0.6s ease' }} />
                        </div>
                        <p style={{ fontSize: '13px', opacity: 0.9, margin: 0 }}>
                          {atteint ? `🎉 Récompense disponible : ${c.recompense}` : `Encore ${c.objectif - c.points} points pour : ${c.recompense}`}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── VUE FACTURES ── */}
          {clientVue === 'factures' && (
            <div>
              <h1 style={{ fontSize: '26px', fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 6px' }}>Mes factures</h1>
              <p style={{ fontSize: '13px', color: sub, margin: '0 0 24px' }}>Vos factures émises par vos prestataires.</p>
              {mesFacturesClient.length === 0 ? (
                <div style={{ padding: '48px', borderRadius: '18px', background: card, textAlign: 'center', border: `1px solid ${border}` }}>
                  <p style={{ fontSize: '36px', marginBottom: '12px' }}>🧾</p>
                  <p style={{ fontSize: '15px', color: sub }}>Aucune facture pour le moment.</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '10px' }}>
                  {[...mesFacturesClient].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)).map(f => {
                    const paye = f.status === 'paye';
                    return (
                      <div key={f.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', padding: '16px 18px', borderRadius: '14px', background: card, border: `1px solid ${border}` }}>
                        <div>
                          <p style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 2px', color: txt }}>{f.numero || `FAC-${f.id?.slice(0, 6)}`} · {parseFloat(f.amount || 0).toFixed(2)}€</p>
                          <p style={{ fontSize: '12px', color: sub, margin: 0 }}>
                            {f.date ? new Date(f.date).toLocaleDateString('fr-FR') : ''}{f.intitule ? ` · ${f.intitule}` : ''}
                          </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '12px', fontWeight: 700, padding: '4px 10px', borderRadius: '8px', background: paye ? '#dcfce7' : '#fef9c3', color: paye ? '#16a34a' : '#92400e' }}>
                            {paye ? '✅ Payée' : '⏳ En attente'}
                          </span>
                          <button onClick={() => generateFacturePDF(f)} style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '13px' }}>⬇️ PDF</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── ANNUAIRE DE PROXIMITÉ ── */}
          {clientVue === 'annuaire' && (() => {
            const inp = { width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '12px', border: `1px solid ${border}`, fontSize: '14px', background: DM ? '#111827' : '#fff', color: txt };
            const metiers = [
              ['', 'Tous les métiers'],
              ['artisan', '🔧 Artisan'],
              ['dj', '🎧 DJ / Animation'],
              ['beaute', '💇 Beauté / Bien-être'],
              ['aide_domicile', '🏠 Aide à domicile'],
              ['paysagiste', '🌳 Paysagiste / Jardinier'],
            ];
            return (
              <div>
                <h1 style={{ fontSize: '26px', fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 6px' }}>📍 Trouver un pro près de chez vous</h1>
                <p style={{ fontSize: '13px', color: sub, margin: '0 0 24px' }}>Recherchez parmi les professionnels PlanifPro inscrits dans l'annuaire, dans un rayon de 30 km.</p>
                <div style={{ padding: '24px', borderRadius: '18px', background: card, border: `1px solid ${border}`, display: 'grid', gap: '14px', marginBottom: '24px' }}>
                  <AddressAutocomplete
                    value={annAdresse}
                    onChange={setAnnAdresse}
                    onSelectCoords={(lat, lng) => { setAnnLat(lat); setAnnLng(lng); }}
                    darkMode={DM}
                    placeholder="Votre adresse ou votre ville"
                    style={inp}
                  />
                  <select value={annMetier} onChange={(e) => setAnnMetier(e.target.value)} style={inp}>
                    {metiers.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
                  </select>
                  <button onClick={searchAnnuaire} disabled={annSearching} style={{ padding: '13px', background: annSearching ? '#9ca3af' : '#2563eb', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 700, fontSize: '15px' }}>
                    {annSearching ? 'Recherche...' : '🔍 Rechercher'}
                  </button>
                </div>

                {annResults !== null && (
                  annResults.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', background: card, borderRadius: '18px', border: `1px solid ${border}` }}>
                      <p style={{ fontSize: '32px', marginBottom: '8px' }}>🔍</p>
                      <p style={{ color: sub, fontSize: '15px' }}>Aucun professionnel inscrit à l'annuaire dans ce rayon pour l'instant.</p>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {annResults.map(p => (
                        <a key={p.slug} href={`/pro/${p.slug}`} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', padding: '18px 20px', borderRadius: '16px', background: card, border: `1px solid ${border}`, textDecoration: 'none', color: txt }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
                            <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: p.photo ? '#fff' : 'linear-gradient(135deg,#2563eb,#60a5fa)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: '#fff', overflow: 'hidden', flexShrink: 0 }}>
                              {p.photo ? <img src={p.photo} alt={p.entrepriseNom} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (p.entrepriseNom || 'P')[0].toUpperCase()}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <p style={{ fontWeight: 700, fontSize: '15px', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.entrepriseNom}</p>
                              <p style={{ fontSize: '13px', color: sub, margin: '2px 0 0' }}>{p.metier_label || metiers.find(m => m[0] === p.metier)?.[1] || p.metier}{p.ville ? ` · ${p.ville}` : ''}</p>
                              {p.nb_avis > 0 && (
                                <p style={{ fontSize: '12px', color: '#f59e0b', margin: '2px 0 0', fontWeight: 700 }}>★ {p.note_moyenne} <span style={{ color: sub, fontWeight: 500 }}>({p.nb_avis} avis)</span></p>
                              )}
                            </div>
                          </div>
                          <span style={{ fontSize: '13px', fontWeight: 700, color: '#2563eb', whiteSpace: 'nowrap' }}>{p.distance_km} km →</span>
                        </a>
                      ))}
                    </div>
                  )
                )}
              </div>
            );
          })()}

          {/* ── VUE PROFIL CLIENT ── */}
          {clientVue === 'profil' && (() => {
            const inp = { width: '100%', boxSizing: 'border-box', padding: '12px', borderRadius: '12px', border: `1px solid ${border}`, fontSize: '14px', background: DM ? '#111827' : '#fff', color: txt };
            const lab = { fontSize: '12px', fontWeight: 700, color: sub, display: 'block', margin: '0 0 6px', textTransform: 'uppercase' };
            return (
              <div>
                <h1 style={{ fontSize: '26px', fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 6px' }}>Mon profil</h1>
                <p style={{ fontSize: '13px', color: sub, margin: '0 0 24px' }}>Renseignez vos informations pour vos prestataires.</p>
                <div style={{ padding: '24px', borderRadius: '18px', background: card, border: `1px solid ${border}`, display: 'grid', gap: '16px' }}>
                  <div>
                    <label style={lab}>Vous êtes</label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      {[['particulier', '🏠 Particulier'], ['pro', '🏢 Professionnel']].map(([val, label]) => (
                        <button key={val} type="button" onClick={() => setCpType(val)} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: cpType === val ? '2px solid #2563eb' : `1.5px solid ${border}`, background: cpType === val ? '#eef4ff' : 'transparent', color: cpType === val ? '#2563eb' : txt, fontWeight: 700, fontSize: '14px', cursor: 'pointer' }}>{label}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label style={lab}>{cpType === 'pro' ? 'Raison sociale' : 'Nom complet'}</label>
                    <input value={cpNom} onChange={e => setCpNom(e.target.value)} style={inp} placeholder={cpType === 'pro' ? 'Ex : Dupont SARL' : 'Ex : Jean Dupont'} />
                  </div>
                  {cpType === 'pro' && (
                    <div>
                      <label style={lab}>SIRET</label>
                      <input value={cpSiret} onChange={e => setCpSiret(e.target.value)} style={inp} placeholder="14 chiffres" />
                    </div>
                  )}
                  <div>
                    <label style={lab}>Téléphone</label>
                    <input value={cpTel} onChange={e => setCpTel(e.target.value)} style={inp} placeholder="06 12 34 56 78" />
                  </div>
                  <div>
                    <label style={lab}>Adresse</label>
                    <input value={cpAdresse} onChange={e => setCpAdresse(e.target.value)} style={inp} placeholder="N°, rue, code postal, ville" />
                  </div>
                  <button onClick={saveClientProfile} disabled={cpSaving} style={{ padding: '13px', background: cpSaving ? '#9ca3af' : '#2563eb', color: '#fff', border: 'none', borderRadius: '12px', cursor: cpSaving ? 'wait' : 'pointer', fontWeight: 700, fontSize: '15px' }}>
                    {cpSaving ? 'Enregistrement…' : '💾 Enregistrer mon profil'}
                  </button>
                </div>

                {/* Sécurité : changer son mot de passe */}
                <div style={{ marginTop: '20px', padding: '24px', borderRadius: '18px', background: card, border: `1px solid ${border}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: pwChangeOpen ? '16px' : 0 }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>🔒 Sécurité</h3>
                    {!pwChangeOpen && (
                      <button onClick={() => setPwChangeOpen(true)} style={{ padding: '8px 16px', background: DM ? '#111827' : '#f5f5f7', color: txt, border: `1px solid ${border}`, borderRadius: '980px', cursor: 'pointer', fontWeight: 500, fontSize: '13px' }}>Changer le mot de passe</button>
                    )}
                  </div>
                  {pwChangeOpen && (
                    <div style={{ display: 'grid', gap: '10px', maxWidth: '360px' }}>
                      <input type="password" placeholder="Nouveau mot de passe (6 caractères min.)" value={pwNew} onChange={(e) => setPwNew(e.target.value)} style={inp} />
                      <input type="password" placeholder="Confirmer le mot de passe" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)} style={inp} />
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={changePasswordInApp} disabled={pwSaving} style={{ flex: 1, padding: '11px', background: pwSaving ? '#9ca3af' : '#2563eb', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>
                          {pwSaving ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                        <button onClick={() => { setPwChangeOpen(false); setPwNew(''); setPwConfirm(''); }} style={{ padding: '11px 18px', background: 'none', border: 'none', color: sub, cursor: 'pointer', fontWeight: 500, fontSize: '14px' }}>Annuler</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </main>
      </div>
    );
  }

  // ===== ADMIN DASHBOARD =====
  if (isAdmin) {
    return (
      <div style={{ minHeight: '100vh', background: darkMode ? '#111827' : '#f9fafb', color: darkMode ? '#fff' : '#000' }}>
        <header style={{ background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)', color: '#fff', padding: '20px' }}>
          <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ fontSize: '28px', fontWeight: 'bold' }}>🔴 ADMIN</h1>
              <p style={{ fontSize: '13px', color: '#fee2e2' }}>Contrôle total</p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setDarkMode(!darkMode)} style={{ padding: '8px 12px', background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '18px', color: '#fff' }}>
                {darkMode ? '☀️' : '🌙'}
              </button>
              <button onClick={handleLogout} style={{ padding: '8px 24px', background: '#fca5a5', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                Déconnexion
              </button>
            </div>
          </div>
        </header>

        <div style={{ background: darkMode ? '#1f2937' : '#fff', borderBottom: '2px solid #e5e7eb', padding: '0 20px', display: 'flex', gap: '10px', overflowX: 'auto' }}>
          {['dashboard', 'clients', 'factures', 'support', 'emails', 'logs'].map(tab => (
            <button
              key={tab}
              onClick={() => setAdminTab(tab)}
              style={{
                padding: '12px 16px',
                background: adminTab === tab ? '#dc2626' : 'transparent',
                color: adminTab === tab ? '#fff' : darkMode ? '#9ca3af' : '#6b7280',
                border: 'none',
                cursor: 'pointer',
                fontWeight: adminTab === tab ? 'bold' : '500',
                fontSize: '13px'
              }}
            >
              {tab === 'dashboard' && '📊 Dashboard'}
              {tab === 'clients' && '👥 Clients'}
              {tab === 'factures' && '💰 Factures'}
              {tab === 'support' && '🎟️ Support'}
              {tab === 'emails' && '📧 Emails'}
              {tab === 'logs' && '📋 Logs'}
            </button>
          ))}
        </div>

        <main style={{ maxWidth: '1280px', margin: '0 auto', padding: 'clamp(12px, 3vw, 32px)' }}>
          {adminTab === 'dashboard' && (() => {
            // Calculs business
            const directeurs = allClients.filter(c => c.role_societe !== 'agent' && c.role !== 'admin');
            const clientsPayes = directeurs.filter(c => c.isPaid && c.subscriptionType !== 'lifetime');
            const agents = allClients.filter(c => c.role_societe === 'agent');
            // Répartition par formule
            const prixFormule = { '1month': 14.99, '6months': 74.99, '12months': 119.99 };
            const nomFormule = { '1month': 'Indépendant', '6months': 'Pro', '12months': 'Business' };
            const repartition = { '1month': 0, '6months': 0, '12months': 0 };
            let mrr = 0; // revenu mensuel récurrent
            clientsPayes.forEach(c => {
              if (repartition[c.subscriptionType] !== undefined) {
                repartition[c.subscriptionType]++;
                mrr += prixFormule[c.subscriptionType] || 0;
              }
            });
            const maxRep = Math.max(repartition['1month'], repartition['6months'], repartition['12months'], 1);
            // Abonnements expirant dans 7 jours
            const expirantBientot = clientsPayes.filter(c => {
              if (!c.subscriptionEnd) return false;
              const j = Math.ceil((new Date(c.subscriptionEnd) - new Date()) / (1000 * 60 * 60 * 24));
              return j >= 0 && j <= 7;
            }).length;
            // Inscrits non convertis (créés mais pas payés)
            const nonConvertis = directeurs.filter(c => !c.isPaid).length;
            const tauxConversion = directeurs.length > 0 ? Math.round((clientsPayes.length / directeurs.length) * 100) : 0;

            return (
              <div>
                <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '24px', letterSpacing: '-0.02em' }}>📊 Tableau de bord</h2>

                {/* KPIs principaux */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ padding: '24px', borderRadius: '18px', background: 'linear-gradient(135deg, #2563eb, #1e3a8a)', color: '#fff' }}>
                    <p style={{ fontSize: '13px', opacity: 0.9, marginBottom: '8px' }}>💰 Revenu mensuel (MRR)</p>
                    <p style={{ fontSize: '32px', fontWeight: 'bold' }}>{mrr.toFixed(2)}€</p>
                    <p style={{ fontSize: '12px', opacity: 0.8, marginTop: '4px' }}>{(mrr * 12).toFixed(0)}€ / an estimé</p>
                  </div>
                  <div style={{ padding: '24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                    <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>✅ Clients payants</p>
                    <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#10b981' }}>{clientsPayes.length}</p>
                  </div>
                  <div style={{ padding: '24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                    <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>👤 Comptes totaux</p>
                    <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#2563eb' }}>{directeurs.length}</p>
                    <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>+ {agents.length} techniciens</p>
                  </div>
                  <div style={{ padding: '24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                    <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>🎟️ Tickets ouverts</p>
                    <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#f59e0b' }}>{tickets.filter(t => t.status === 'open').length}</p>
                  </div>
                </div>

                {/* Conversion + non convertis */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ padding: '24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                    <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>📈 Taux de conversion</p>
                    <p style={{ fontSize: '32px', fontWeight: 'bold', color: tauxConversion >= 30 ? '#10b981' : '#f59e0b' }}>{tauxConversion}%</p>
                    <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{clientsPayes.length} payants / {directeurs.length} inscrits</p>
                  </div>
                  <div style={{ padding: '24px', borderRadius: '18px', background: nonConvertis > 0 ? 'linear-gradient(135deg, #f59e0b, #f97316)' : (darkMode ? '#1c1c1e' : '#fff'), color: nonConvertis > 0 ? '#fff' : 'inherit', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: nonConvertis > 0 ? 'none' : (darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0') }}>
                    <p style={{ fontSize: '13px', opacity: 0.9, marginBottom: '8px' }}>⏳ Inscrits à relancer</p>
                    <p style={{ fontSize: '32px', fontWeight: 'bold' }}>{nonConvertis}</p>
                    <p style={{ fontSize: '12px', opacity: 0.85, marginTop: '4px' }}>{nonConvertis > 0 ? 'Onglet Clients → bouton Relancer' : 'Tout le monde a payé 🎉'}</p>
                  </div>
                </div>

                {/* Répartition par formule + alertes */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ padding: '28px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '20px' }}>📦 Répartition par formule</h3>
                    {['1month', '6months', '12months'].map(type => {
                      const colors = { '1month': '#2563eb', '6months': '#a855f7', '12months': '#10b981' };
                      return (
                        <div key={type} style={{ marginBottom: '16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                            <span style={{ fontSize: '14px', fontWeight: 500 }}>{nomFormule[type]} <span style={{ color: '#9ca3af', fontSize: '12px' }}>({prixFormule[type]}€)</span></span>
                            <span style={{ fontSize: '14px', fontWeight: 'bold', color: colors[type] }}>{repartition[type]} client(s)</span>
                          </div>
                          <div style={{ height: '10px', background: darkMode ? '#2c2c2e' : '#f0f0f0', borderRadius: '5px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${(repartition[type] / maxRep) * 100}%`, background: colors[type], borderRadius: '5px', transition: 'width 0.3s' }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ padding: '28px', borderRadius: '18px', background: expirantBientot > 0 ? 'linear-gradient(135deg, #f59e0b, #f97316)' : (darkMode ? '#1c1c1e' : '#fff'), color: expirantBientot > 0 ? '#fff' : 'inherit', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: expirantBientot > 0 ? 'none' : (darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0'), display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <p style={{ fontSize: '13px', opacity: 0.9, marginBottom: '8px' }}>⏰ Expirent sous 7 jours</p>
                    <p style={{ fontSize: '36px', fontWeight: 'bold' }}>{expirantBientot}</p>
                    <p style={{ fontSize: '12px', opacity: 0.8, marginTop: '4px' }}>{expirantBientot > 0 ? 'À relancer pour le renouvellement' : 'Aucun renouvellement urgent'}</p>
                  </div>
                </div>
              </div>
            );
          })()}

          {adminTab === 'clients' && (() => {
            // Comptes directeurs en attente d'activation (non payés)
            const enAttente = allClients
              .filter(c => c.role !== 'admin' && c.role_societe !== 'agent' && c.role_societe !== 'client_final' && !c.isPaid)
              .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
            const joursDepuis = (d) => d ? Math.floor((Date.now() - new Date(d)) / (1000 * 60 * 60 * 24)) : null;
            const relancerInscrit = (client) => {
              const sujet = `Finalisez votre inscription à PlanifPro`;
              const corps = `Bonjour,\n\nVous avez créé un compte PlanifPro${client.entrepriseNom ? ` pour ${client.entrepriseNom}` : ''} mais vous n'avez pas encore activé votre abonnement.\n\nPlanifPro vous attend : planning, devis, factures électroniques (Factur-X), portail client et bien plus, le tout dans un seul outil pensé pour votre métier.\n\n👉 Connectez-vous et choisissez votre formule pour démarrer : https://planifpro.fr\n\nUne question avant de vous lancer ? Répondez simplement à cet email.\n\nÀ très vite,\nL'équipe PlanifPro`;
              window.location.href = `mailto:${client.email}?subject=${encodeURIComponent(sujet)}&body=${encodeURIComponent(corps)}`;
            };
            return (
              <div>
                <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '24px', letterSpacing: '-0.02em' }}>👥 Clients</h2>

                {/* COMPTES EN ATTENTE D'ACTIVATION */}
                {enAttente.length > 0 && (
                  <div style={{ marginBottom: '32px' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px', color: '#f59e0b' }}>⏳ Inscrits non convertis ({enAttente.length})</h3>
                    <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>Comptes créés mais sans abonnement actif. Relancez-les par email, ou activez-les après réception d'un paiement Stripe.</p>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {enAttente.map(client => {
                        const jours = joursDepuis(client.created_at);
                        return (
                        <div key={client.id} style={{ padding: '20px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid #f59e0b' }}>
                          <div style={{ marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                            <div>
                              <h3 style={{ fontSize: '16px', fontWeight: 'bold' }}>{client.entrepriseNom || '(sans nom)'}{client.metier ? <span style={{ fontSize: '12px', fontWeight: 500, color: '#9ca3af' }}> · {client.metier_label || client.metier}</span> : null}</h3>
                              <p style={{ fontSize: '13px', color: '#6b7280' }}>📧 {client.email}</p>
                              {client.created_at && (
                                <p style={{ fontSize: '12px', color: jours > 7 ? '#dc2626' : '#9ca3af', marginTop: '2px', fontWeight: jours > 7 ? 600 : 400 }}>
                                  🕐 Inscrit le {new Date(client.created_at).toLocaleDateString('fr-FR')}{jours !== null ? ` — il y a ${jours} jour${jours > 1 ? 's' : ''}` : ''}
                                </p>
                              )}
                            </div>
                            <button onClick={() => relancerInscrit(client)} style={{ padding: '10px 16px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap' }}>📧 Relancer</button>
                          </div>
                          <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: 600 }}>Ou activer manuellement en :</p>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button onClick={() => activerClient(client, '1month')} style={{ padding: '10px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>👤 Indépendant (14,99€)</button>
                            <button onClick={() => activerClient(client, '6months')} style={{ padding: '10px 16px', background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>👥 Pro (74,99€)</button>
                            <button onClick={() => activerClient(client, '12months')} style={{ padding: '10px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>👥 Business (119,99€)</button>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* TOUS LES COMPTES */}
                <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '16px' }}>Tous les comptes</h3>
                <div style={{ display: 'grid', gap: '12px' }}>
                  {allClients.map(client => (
                    <div key={client.id} style={{ padding: '20px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                      <div>
                        <h3 style={{ fontSize: '16px', fontWeight: 'bold' }}>{client.entrepriseNom}</h3>
                        <p style={{ fontSize: '13px', color: '#6b7280' }}>📧 {client.email}</p>
                        <p style={{ fontSize: '13px', color: client.isPaid ? '#10b981' : '#ef4444' }}>
                          {client.isPaid ? `✅ Payé (${client.subscriptionType})` : '❌ Non payé'}
                          {client.role_societe === 'agent' && ' · 🔧 Collaborateur'}
                          {client.role_societe === 'client_final' && ' · 🏠 Client final'}
                        </p>
                      </div>
                      {client.role !== 'admin' && (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {client.isPaid && client.role_societe !== 'agent' && client.role_societe !== 'client_final' && (
                            <button onClick={() => desactiverClient(client)} style={{ padding: '8px 16px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
                              ⏸️ Désactiver
                            </button>
                          )}
                          <button onClick={() => blockClient(client.id)} style={{ padding: '8px 16px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
                            🚫 Bloquer
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {adminTab === 'factures' && (
            <div>
              <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '24px', letterSpacing: '-0.02em' }}>💰 Factures</h2>
              <button onClick={exportFacturesCSV} style={{ marginBottom: '24px', padding: '12px 24px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                📥 Exporter CSV
              </button>
              <div style={{ display: 'grid', gap: '12px' }}>
                {allFactures.map(f => (
                  <div key={f.id} style={{ padding: '20px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div>
                        <h3 style={{ fontSize: '16px', fontWeight: 'bold' }}>{f.client}</h3>
                        <p style={{ fontSize: '13px', color: '#6b7280' }}>Montant: {f.amount}€</p>
                      </div>
                      <span style={{ background: '#fef08a', color: '#854d0e', padding: '8px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold' }}>
                        {f.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {adminTab === 'support' && (
            <div>
              <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '8px', letterSpacing: '-0.02em' }}>🎟️ Support</h2>
              <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '24px' }}>Demandes envoyées par vos clients. {tickets.filter(t => t.status === 'open').length} en attente.</p>
              <div style={{ display: 'grid', gap: '12px' }}>
                {tickets.length === 0 && (
                  <div style={{ padding: '48px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', textAlign: 'center', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                    <p style={{ fontSize: '40px', marginBottom: '12px' }}>📭</p>
                    <p style={{ fontSize: '15px', color: '#6b7280' }}>Aucune demande pour le moment.</p>
                  </div>
                )}
                {tickets.map(t => (
                  <div key={t.id} style={{ padding: '20px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', borderLeft: `4px solid ${t.status === 'open' ? '#f59e0b' : '#10b981'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: 'bold' }}>{t.subject}</h3>
                      <span style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', background: t.status === 'open' ? '#fef3c7' : '#d1fae5', color: t.status === 'open' ? '#92400e' : '#065f46' }}>
                        {t.status === 'open' ? '🟡 En attente' : '✅ Répondu'}
                      </span>
                    </div>
                    <p style={{ fontSize: '13px', color: '#2563eb', marginBottom: '4px' }}>
                      👤 {t.user_nom || 'Client'} — {t.user_email || 'email inconnu'}
                    </p>
                    {t.created_at && <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '12px' }}>{new Date(t.created_at).toLocaleString('fr-FR')}</p>}
                    <p style={{ fontSize: '14px', color: darkMode ? '#d1d5db' : '#374151', background: darkMode ? '#111827' : '#f9fafb', padding: '12px', borderRadius: '10px', marginBottom: '12px' }}>{t.message}</p>

                    {t.response && (
                      <div style={{ background: '#d1fae5', padding: '12px', borderRadius: '10px', marginBottom: '12px' }}>
                        <p style={{ fontSize: '12px', fontWeight: 'bold', color: '#065f46', marginBottom: '4px' }}>Votre réponse :</p>
                        <p style={{ fontSize: '14px', color: '#065f46' }}>{t.response}</p>
                      </div>
                    )}

                    {t.status === 'open' && (
                      <div style={{ marginTop: '8px' }}>
                        <textarea placeholder="Votre réponse..." value={selectedTicketId === t.id ? ticketResponse : ''} onChange={(e) => { setSelectedTicketId(t.id); setTicketResponse(e.target.value); }} style={{ width: '100%', padding: '10px', borderRadius: '12px', border: '1px solid #d2d2d7', marginBottom: '8px', fontSize: '13px', minHeight: '80px', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <button onClick={respondToTicket} style={{ padding: '8px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
                            ✅ Marquer répondu
                          </button>
                          <a href={`mailto:${t.user_email}?subject=Re: ${encodeURIComponent(t.subject)}&body=${encodeURIComponent('Bonjour,\n\n')}`} style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', borderRadius: '980px', textDecoration: 'none', fontSize: '13px', fontWeight: 'bold' }}>
                            📧 Répondre par email
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {adminTab === 'emails' && (
            <div>
              <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '24px', letterSpacing: '-0.02em' }}>📧 Emails</h2>
              <div style={{ padding: '24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                <input type="text" placeholder="Sujet" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '12px', border: '1px solid #d2d2d7', marginBottom: '12px', fontSize: '14px' }} />
                <textarea placeholder="Contenu..." value={emailContent} onChange={(e) => setEmailContent(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '12px', border: '1px solid #d2d2d7', marginBottom: '12px', fontSize: '14px', minHeight: '150px' }} />
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>📧 {allClients.filter(c => c.isPaid).length} clients</p>
                <button onClick={sendEmail} disabled={emailSending} style={{ width: '100%', padding: '12px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', cursor: emailSending ? 'not-allowed' : 'pointer', fontWeight: 'bold', opacity: emailSending ? 0.6 : 1 }}>
                  {emailSending ? 'Envoi en cours...' : '📨 Envoyer'}
                </button>
              </div>
            </div>
          )}

          {adminTab === 'logs' && (
            <div>
              <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '24px', letterSpacing: '-0.02em' }}>📋 Logs</h2>
              <div style={{ display: 'grid', gap: '12px' }}>
                {activityLogs.map(log => (
                  <div key={log.id} style={{ padding: '16px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', borderLeft: '4px solid #2563eb' }}>
                    <p style={{ fontSize: '14px', fontWeight: 'bold' }}>{log.action}</p>
                    <p style={{ fontSize: '12px', color: '#6b7280' }}>
                      {log.created_at ? new Date(log.created_at).toLocaleString('fr-FR') : 'N/A'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  // ===== PAYMENT PAGE =====
  // ===== AGENT EN ATTENTE DE VALIDATION =====
  if (user && isAgent && !user.agent_valide) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: darkMode ? '#111827' : 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)', padding: '20px' }}>
        <div style={{ maxWidth: '460px', width: '100%', padding: '40px', borderRadius: '20px', background: darkMode ? '#1f2937' : '#fff', textAlign: 'center', boxShadow: '0 25px 50px rgba(0,0,0,0.3)' }}>
          <div style={{ fontSize: '60px', marginBottom: '16px' }}>⏳</div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '12px', color: darkMode ? '#fff' : '#000' }}>En attente de validation</h1>
          <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '24px' }}>
            Ton compte agent a bien été créé ! Ton directeur doit valider ton accès depuis son espace. Reviens te connecter une fois validé.
          </p>
          <button onClick={handleLogout} style={{ padding: '12px 24px', background: '#6b7280', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  if (!hasAccess) {
    return withSuspense(
      <PaymentPage
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        user={user}
        handleLogout={handleLogout}
        handlePayment={(amount, type) => handlePayment(amount, type)}
        paymentLoading={paymentLoading}
      />
    );
  }

    // ===== ONBOARDING PREMIÈRE CONNEXION =====
  if (!isAgent && !isClientFinal && !isAdmin && !user.onboarding_done) {
    return withSuspense(
      <Onboarding
        darkMode={darkMode}
        user={user}
        profilMetier={profilMetier} setProfilMetier={setProfilMetier}
        profilMetierLabel={profilMetierLabel} setProfilMetierLabel={setProfilMetierLabel}
        profilNom={profilNom} setProfilNom={setProfilNom}
        profilSiret={profilSiret} setProfilSiret={setProfilSiret}
        profilAdresse={profilAdresse} setProfilAdresse={setProfilAdresse}
        profilTel={profilTel} setProfilTel={setProfilTel}
        profilTvaRegime={profilTvaRegime} setProfilTvaRegime={setProfilTvaRegime}
        savingOnboarding={savingOnboarding}
        saveOnboarding={saveOnboarding}
      />
    );
  }

    // ===== CLIENT DASHBOARD =====
  return (
    <div style={{ minHeight: '100vh', background: darkMode ? '#111827' : '#fbfbfd', color: darkMode ? '#fff' : '#0a0a0a', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {(!isOnline || pendingSync > 0) && (
        <div style={{ padding: '8px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 700, color: '#fff', background: !isOnline ? '#b45309' : '#2563eb' }}>
          {!isOnline
            ? `📴 Hors-ligne — vos rapports sont enregistrés sur l'appareil${pendingSync > 0 ? ` · ${pendingSync} en attente` : ''}. Synchro automatique au retour du réseau.`
            : `🔄 Synchronisation de ${pendingSync} rapport${pendingSync > 1 ? 's' : ''}…`}
        </div>
      )}
      {realtimeToast && (
        <div onClick={() => setRealtimeToast(null)} style={{ position: 'fixed', top: '16px', left: '50%', transform: 'translateX(-50%)', zIndex: 1000, background: '#2563eb', color: '#fff', padding: '12px 22px', borderRadius: '980px', fontWeight: 700, fontSize: '14px', boxShadow: '0 8px 24px rgba(37,99,235,0.35)', cursor: 'pointer' }}>
          {realtimeToast.message}
        </div>
      )}
      {trackingId && (
        <div style={{ padding: '8px 16px', textAlign: 'center', fontSize: '13px', fontWeight: 700, color: '#fff', background: '#10b981', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span>🚗 Suivi de position en cours pour {trackingClientNom}</span>
          <button onClick={() => stopTracking(true)} style={{ padding: '3px 12px', borderRadius: '980px', border: 'none', background: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}>✅ Arrivé</button>
          <button onClick={() => stopTracking(false)} style={{ padding: '3px 12px', borderRadius: '980px', border: 'none', background: 'rgba(0,0,0,0.2)', color: '#fff', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}>Arrêter</button>
        </div>
      )}
      <header style={{ background: darkMode ? 'rgba(17,24,39,0.8)' : 'rgba(255,255,255,0.8)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: darkMode ? '1px solid #1f2937' : '1px solid #f0f0f0', padding: '12px 24px', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <svg width="30" height="30" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
              <defs>
                <linearGradient id="pmHeadGrad" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#2563eb" />
                  <stop offset="1" stopColor="#1e3a8a" />
                </linearGradient>
              </defs>
              <rect width="40" height="40" rx="11" fill="url(#pmHeadGrad)" />
              <path d="M12 20.5L17.5 26L28 14.5" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.02em', color: darkMode ? '#fff' : '#0a0a0a' }}>Planif<span style={{ color: '#2563eb' }}>Pro</span></h1>
              <p style={{ fontSize: '12px', color: darkMode ? '#9ca3af' : '#86868b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '180px' }}>{user.entrepriseNom}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
            <button onClick={() => setDarkMode(!darkMode)} style={{ width: '36px', height: '36px', background: darkMode ? '#1f2937' : '#f5f5f7', border: 'none', borderRadius: '50%', cursor: 'pointer', fontSize: '16px', color: darkMode ? '#fff' : '#0a0a0a' }}>
              {darkMode ? '☀️' : '🌙'}
            </button>
            <button onClick={handleLogout} style={{ padding: '8px 18px', background: darkMode ? '#1f2937' : '#f5f5f7', color: darkMode ? '#fff' : '#0a0a0a', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 500, fontSize: '14px' }}>
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div style={{ background: darkMode ? '#111827' : '#fbfbfd', borderBottom: darkMode ? '1px solid #1f2937' : '1px solid #f0f0f0', padding: '10px 16px', position: 'sticky', top: '65px', zIndex: 90 }}>
        {(() => {
          const nbDemandes = demandes.filter(d => d.statut === 'nouvelle').length;
          const nbDevis = devis.filter(d => d.statut === 'brouillon').length;
          const tabLabel = (tab) => {
            switch (tab) {
              case 'dashboard': return '📊 Dashboard';
              case 'controle': return '🗼 Tour de contrôle';
              case 'analytics': return '📈 Analytics';
              case 'planning': return isDJ ? '📅 Calendrier' : '📅 Planning';
              case 'demandes': return `📨 Demandes${nbDemandes > 0 ? ` (${nbDemandes})` : ''}`;
              case 'clients': return '👤 Clients';
              case 'messages': return '💬 Messages';
              case 'historique': return isDJ ? '📚 Mes bookings' : '📚 Historique';
              case 'devis': return `📝 Devis${nbDevis > 0 ? ` (${nbDevis})` : ''}`;
              case 'factures': return '📄 Factures';
              case 'catalogue': return '📚 Catalogue';
              case 'depenses': return '💸 Dépenses';
              case 'urssaf': return '🧾 URSSAF';
              case 'materiel': return '🎛️ Mon matériel';
              case 'relance': return '🔔 Clients à recontacter';
              case 'fidelite': return '⭐ Fidélité';
              case 'team': return '👥 Équipe';
              case 'absences': return '📅 Absences';
              case 'compte': return '⚙️ Mon compte';
              case 'faq': return '❓ Aide';
              default: return tab;
            }
          };

          // Navigation à plat pour les agents (peu d'onglets)
          if (isAgent) {
            const agentTabs = isResponsable
              ? ['dashboard', 'planning', 'clients', 'historique', 'devis', 'factures', 'compte', 'faq']
              : ['dashboard', 'planning', 'compte', 'faq'];
            return (
              <div className="pm-tabs-scroll" style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', gap: '4px', flexWrap: isMobile ? 'nowrap' : 'wrap', rowGap: '6px' }}>
                {agentTabs.map(tab => (
                  <button key={tab} id={`tab-${tab}`} onClick={() => setActiveTab(tab)}
                    style={{ padding: '8px 16px', background: activeTab === tab ? (darkMode ? '#fff' : '#0a0a0a') : 'transparent', color: activeTab === tab ? (darkMode ? '#0a0a0a' : '#fff') : (darkMode ? '#9ca3af' : '#6e6e73'), border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 500, fontSize: '14px', whiteSpace: 'nowrap', transition: 'all 0.2s' }}>
                    {tabLabel(tab)}
                  </button>
                ))}
              </div>
            );
          }

          // Groupes pour les directeurs
          const groups = [
            { id: 'pilotage', label: '📊 Pilotage', tabs: ['dashboard', 'controle', 'analytics'] },
            { id: 'planningG', label: isDJ ? '📅 Agenda' : '📅 Planning', tabs: ['planning', 'historique'] },
            { id: 'clientsG', label: '👤 Clients', tabs: ['clients', 'demandes', 'messages', 'relance', ...(metierEffectif === 'beaute' ? ['fidelite'] : [])] },
            { id: 'factuG', label: '📄 Facturation', tabs: ['devis', 'factures', 'catalogue', 'depenses', 'urssaf'] },
            ...(isDJ ? [{ id: 'matG', label: '🎛️ Matériel', tabs: ['materiel'] }] : []),
            { id: 'equipeG', label: '👥 Équipe', tabs: ['team', 'absences'] },
            { id: 'compteG', label: '⚙️ Compte', tabs: ['compte', 'faq'] },
          ];

          return (
            <>
              {/* Overlay fermeture menu */}
              {openNavMenu && <div onClick={() => setOpenNavMenu(null)} style={{ position: 'fixed', inset: 0, zIndex: 95 }} />}
              <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', gap: '6px', flexWrap: 'wrap', rowGap: '6px', position: 'relative', zIndex: 96 }}>
                {groups.map(g => {
                  const containsActive = g.tabs.includes(activeTab);
                  const isOpen = openNavMenu === g.id;
                  // Pastille d'alerte sur le groupe
                  const groupAlert = (g.tabs.includes('demandes') && nbDemandes > 0) || (g.tabs.includes('devis') && nbDevis > 0);
                  // Groupe à 1 seul onglet → bouton direct
                  if (g.tabs.length === 1) {
                    const tab = g.tabs[0];
                    return (
                      <button key={g.id} onClick={() => { setActiveTab(tab); setOpenNavMenu(null); }}
                        style={{ padding: '8px 16px', background: activeTab === tab ? (darkMode ? '#fff' : '#0a0a0a') : 'transparent', color: activeTab === tab ? (darkMode ? '#0a0a0a' : '#fff') : (darkMode ? '#9ca3af' : '#6e6e73'), border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 500, fontSize: '14px', whiteSpace: 'nowrap', transition: 'all 0.2s' }}>
                        {tabLabel(tab)}
                      </button>
                    );
                  }
                  return (
                    <div key={g.id} style={{ position: 'relative' }}>
                      <button onClick={() => setOpenNavMenu(isOpen ? null : g.id)}
                        style={{ padding: '8px 14px', background: containsActive ? (darkMode ? '#fff' : '#0a0a0a') : 'transparent', color: containsActive ? (darkMode ? '#0a0a0a' : '#fff') : (darkMode ? '#9ca3af' : '#6e6e73'), border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 500, fontSize: '14px', whiteSpace: 'nowrap', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '6px', position: 'relative' }}>
                        {g.label}
                        <span style={{ fontSize: '10px', opacity: 0.7, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
                        {groupAlert && !containsActive && <span style={{ position: 'absolute', top: '4px', right: '8px', width: '7px', height: '7px', borderRadius: '50%', background: '#ef4444' }} />}
                      </button>
                      {isOpen && (
                        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, minWidth: '210px', background: darkMode ? '#1c1c1e' : '#fff', borderRadius: '14px', boxShadow: '0 12px 40px rgba(0,0,0,0.18)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', padding: '6px', zIndex: 97 }}>
                          {g.tabs.map(tab => (
                            <button key={tab} onClick={() => { setActiveTab(tab); setOpenNavMenu(null); }}
                              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', background: activeTab === tab ? (darkMode ? '#2c2c2e' : '#eef4ff') : 'transparent', color: activeTab === tab ? '#2563eb' : (darkMode ? '#e5e7eb' : '#374151'), border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: activeTab === tab ? 700 : 500, fontSize: '14px', whiteSpace: 'nowrap', transition: 'background 0.15s' }}
                              onMouseEnter={e => { if (activeTab !== tab) e.currentTarget.style.background = darkMode ? '#111827' : '#f8faff'; }}
                              onMouseLeave={e => { if (activeTab !== tab) e.currentTarget.style.background = 'transparent'; }}>
                              {tabLabel(tab)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          );
        })()}
      </div>

      <main style={{ maxWidth: '1280px', margin: '0 auto', padding: 'clamp(12px, 3vw, 32px)' }}>
        {/* ALERTE EXPIRATION ABONNEMENT (directeur uniquement) */}
        {!isAgent && user.subscriptionEnd && user.subscriptionType !== 'lifetime' && (() => {
          const fin = new Date(user.subscriptionEnd);
          const maintenant = new Date();
          const joursRestants = Math.ceil((fin - maintenant) / (1000 * 60 * 60 * 24));

          // Un compte en essai n'a jamais payé : même colonne de date, mais
          // message radicalement différent. Lui annoncer un "renouvellement
          // automatique" alors qu'il n'a donné aucune carte serait faux —
          // et le meilleur moyen de le faire fuir avant d'avoir essayé.
          if (!user.isPaid && joursRestants >= 0) {
            const presqueFini = joursRestants <= 3;
            return (
              <div style={{ marginBottom: '24px', padding: '20px 24px', borderRadius: '16px', background: presqueFini ? 'linear-gradient(135deg, #f59e0b, #f97316)' : 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <p style={{ fontSize: '16px', fontWeight: 700, marginBottom: '4px' }}>
                    {presqueFini ? '⏳' : '🎁'} Essai gratuit — {joursRestants === 0 ? 'dernier jour' : `${joursRestants} jour${joursRestants > 1 ? 's' : ''} restant${joursRestants > 1 ? 's' : ''}`}
                  </p>
                  <p style={{ fontSize: '14px', opacity: 0.9 }}>
                    {presqueFini
                      ? `Votre essai se termine le ${fin.toLocaleDateString('fr-FR')}. Choisissez une formule pour ne rien perdre de vos données.`
                      : 'Toutes les fonctionnalités sont débloquées. Aucune carte bancaire enregistrée.'}
                  </p>
                </div>
                <button onClick={() => setActiveTab('compte')} style={{ padding: '12px 24px', background: '#fff', color: presqueFini ? '#c2410c' : '#1d4ed8', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '14px', whiteSpace: 'nowrap' }}>Voir les formules</button>
              </div>
            );
          }

          // Expiré
          if (joursRestants < 0) {
            return (
              <div style={{ marginBottom: '24px', padding: '20px 24px', borderRadius: '16px', background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <p style={{ fontSize: '16px', fontWeight: 700, marginBottom: '4px' }}>⛔ Votre abonnement a expiré</p>
                  <p style={{ fontSize: '14px', opacity: 0.9 }}>Renouvelez pour continuer à profiter de toutes les fonctionnalités.</p>
                </div>
                <button onClick={() => handlePayment(14.99, '1month')} style={{ padding: '12px 24px', background: '#fff', color: '#dc2626', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '14px', whiteSpace: 'nowrap' }}>Renouveler maintenant</button>
              </div>
            );
          }
          // Reste 7 jours ou moins : abonnement Stripe encore actif, le
          // renouvellement est automatique (recharge récurrente) — pas de
          // bouton "Renouveler" ici, sinon un clic créerait un 2e abonnement
          // en parallèle (double facturation). Simple rappel informatif.
          if (joursRestants <= 7) {
            return (
              <div style={{ marginBottom: '24px', padding: '20px 24px', borderRadius: '16px', background: 'linear-gradient(135deg, #f59e0b, #f97316)', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <p style={{ fontSize: '16px', fontWeight: 700, marginBottom: '4px' }}>⚠️ Renouvellement dans {joursRestants} jour{joursRestants > 1 ? 's' : ''}</p>
                  <p style={{ fontSize: '14px', opacity: 0.9 }}>Votre abonnement se renouvelle automatiquement le {fin.toLocaleDateString('fr-FR')}. Vérifiez que votre carte est toujours valide.</p>
                </div>
              </div>
            );
          }
          return null;
        })()}

        {activeTab === 'fidelite' && (() => {
          const objectif = parseInt(fidObjectif) || 100;
          return (
            <div style={{ maxWidth: '820px' }}>
              <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '8px', letterSpacing: '-0.02em' }}>⭐ Carte de fidélité</h2>
              <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '24px' }}>Récompensez vos clients fidèles. Les points s'ajoutent à chaque prestation.</p>

              {/* Configuration */}
              <div style={{ padding: '24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>⚙️ Réglages de votre carte</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ fontSize: '13px', color: '#86868b', display: 'block', marginBottom: '6px' }}>Objectif (points)</label>
                    <input type="number" value={fidObjectif} onChange={(e) => setFidObjectif(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', color: '#86868b', display: 'block', marginBottom: '6px' }}>Récompense à l'objectif</label>
                    <input type="text" value={fidRecompense} onChange={(e) => setFidRecompense(e.target.value)} placeholder="Ex: Soin offert ou -20€" style={{ width: '100%', padding: '10px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                  </div>
                </div>

                <label style={{ fontSize: '13px', color: '#86868b', display: 'block', marginBottom: '6px' }}>Vos prestations et leurs points</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  {fidPrestations.map((p, i) => (
                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: darkMode ? '#111827' : '#eef4ff', color: '#2563eb', borderRadius: '20px', fontSize: '13px', fontWeight: 600 }}>
                      {p.nom} · {p.points} pts
                      <button onClick={() => removePrestation(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px', padding: 0 }}>✕</button>
                    </span>
                  ))}
                  {fidPrestations.length === 0 && <span style={{ fontSize: '13px', color: '#9ca3af' }}>Aucune prestation définie.</span>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: '8px', marginBottom: '16px' }}>
                  <input type="text" value={fidPrestaNom} onChange={(e) => setFidPrestaNom(e.target.value)} placeholder="Ex: Coupe, Coloration, Barbe..." style={{ padding: '10px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px' }} />
                  <input type="number" value={fidPrestaPoints} onChange={(e) => setFidPrestaPoints(e.target.value)} placeholder="Points" style={{ padding: '10px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px' }} />
                  <button onClick={addPrestation} style={{ padding: '10px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}>+ Ajouter</button>
                </div>
                <button onClick={saveFideliteConfig} style={{ padding: '12px 24px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>💾 Enregistrer les réglages</button>
              </div>

              {/* Cartes clients */}
              <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '16px' }}>Cartes de mes clients</h3>
              {clientsList.length === 0 ? (
                <div style={{ padding: '48px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', textAlign: 'center', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                  <p style={{ fontSize: '40px', marginBottom: '12px' }}>⭐</p>
                  <p style={{ fontSize: '15px', color: '#6b7280' }}>Ajoutez des fiches clients pour gérer leurs cartes de fidélité.</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {clientsList.map(c => {
                    const points = c.points_fidelite || 0;
                    const progression = Math.min(100, Math.round((points / objectif) * 100));
                    const objectifAtteint = points >= objectif;
                    return (
                      <div key={c.id} style={{ padding: '20px', borderRadius: '18px', background: objectifAtteint ? 'linear-gradient(135deg, #f59e0b, #f97316)' : (darkMode ? '#1c1c1e' : '#fff'), color: objectifAtteint ? '#fff' : 'inherit', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: objectifAtteint ? 'none' : (darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0') }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                          <div>
                            <h3 style={{ fontSize: '17px', fontWeight: 'bold' }}>{c.nom}</h3>
                            <p style={{ fontSize: '24px', fontWeight: 800, marginTop: '4px' }}>{points} <span style={{ fontSize: '14px', fontWeight: 400, opacity: 0.7 }}>/ {objectif} pts</span></p>
                          </div>
                          {objectifAtteint && (
                            <span style={{ background: 'rgba(255,255,255,0.25)', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 700 }}>🎁 {fidRecompense}</span>
                          )}
                        </div>

                        {/* Barre de progression */}
                        <div style={{ height: '10px', background: objectifAtteint ? 'rgba(255,255,255,0.3)' : (darkMode ? '#374151' : '#e5e7eb'), borderRadius: '6px', overflow: 'hidden', marginBottom: '14px' }}>
                          <div style={{ width: `${progression}%`, height: '100%', background: objectifAtteint ? '#fff' : '#10b981', borderRadius: '6px', transition: 'width 0.3s' }} />
                        </div>

                        {/* Boutons ajout de points par prestation */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
                          {fidPrestations.map((p, i) => (
                            <button key={i} onClick={() => updateClientPoints(c, p.points)} style={{ padding: '6px 12px', background: objectifAtteint ? 'rgba(255,255,255,0.25)' : '#eef4ff', color: objectifAtteint ? '#fff' : '#2563eb', border: 'none', borderRadius: '20px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>+ {p.nom} ({p.points})</button>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button onClick={() => updateClientPoints(c, -10)} style={{ padding: '6px 12px', background: 'transparent', color: objectifAtteint ? '#fff' : '#6b7280', border: `1px solid ${objectifAtteint ? 'rgba(255,255,255,0.5)' : '#d2d2d7'}`, borderRadius: '20px', cursor: 'pointer', fontSize: '12px' }}>− 10 pts</button>
                          {objectifAtteint && (
                            <button onClick={() => resetClientPoints(c)} style={{ padding: '6px 12px', background: '#fff', color: '#f59e0b', border: 'none', borderRadius: '20px', cursor: 'pointer', fontSize: '12px', fontWeight: 700 }}>✅ Récompense utilisée (réinitialiser)</button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {activeTab === 'relance' && (() => {
          // Pour chaque fiche client, trouver la date du dernier RDV
          const aujourdhui = new Date();
          const clientsAvecDernierRdv = clientsList.map(c => {
            const rdvClient = interventions
              .filter(i => (i.client_id && i.client_id === c.id) || i.client === c.nom)
              .filter(i => i.date)
              .sort((a, b) => new Date(b.date) - new Date(a.date));
            const dernierRdv = rdvClient.length > 0 ? new Date(rdvClient[0].date) : null;
            const joursEcoules = dernierRdv ? Math.floor((aujourdhui - dernierRdv) / (1000 * 60 * 60 * 24)) : null;
            return { ...c, dernierRdv, joursEcoules };
          })
          // Garder seulement ceux qui ont déjà eu un RDV et qui dépassent le délai choisi
          .filter(c => c.joursEcoules !== null && c.joursEcoules >= parseInt(relanceDelai))
          .sort((a, b) => b.joursEcoules - a.joursEcoules);

          // Filtre supplémentaire par tranche
          const clientsFiltres = clientsAvecDernierRdv.filter(c => {
            if (relanceFiltre === 'tous') return true;
            if (relanceFiltre === '2mois') return c.joursEcoules >= 60 && c.joursEcoules < 180;
            if (relanceFiltre === '6mois') return c.joursEcoules >= 180 && c.joursEcoules < 365;
            if (relanceFiltre === '1an') return c.joursEcoules >= 365;
            return true;
          });

          const formatDelai = (jours) => {
            const mois = Math.floor(jours / 30);
            const joursRestants = jours % 30;
            if (mois === 0) return `${jours} jour${jours > 1 ? 's' : ''}`;
            if (joursRestants === 0) return `${mois} mois`;
            return `${mois} mois et ${joursRestants} jour${joursRestants > 1 ? 's' : ''}`;
          };

          return (
            <div style={{ maxWidth: '820px' }}>
              <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '8px', letterSpacing: '-0.02em' }}>🔔 Clients à recontacter</h2>
              <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '24px' }}>{isDJ ? 'Relancez les organisateurs qui ne vous ont pas rebooké pour leur prochain événement.' : metierEffectif === 'beaute' ? 'Relancez les clientes qui ne sont pas revenues depuis un moment pour les fidéliser.' : metierEffectif === 'aide_domicile' ? 'Relancez les bénéficiaires sans passage récent pour maintenir le lien et proposer vos services.' : "Relancez les clients sans intervention récente (entretien, maintenance...) pour générer de nouveaux rendez-vous."}</p>

              {/* Réglages de relance */}
              <div style={{ padding: '24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>⚙️ Réglages</h3>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '13px', color: '#86868b', display: 'block', marginBottom: '6px' }}>Relancer les clients absents depuis (en jours)</label>
                  <input type="number" value={relanceDelai} onChange={(e) => setRelanceDelai(e.target.value)} style={{ width: '120px', padding: '10px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px' }} />
                  <span style={{ fontSize: '13px', color: '#9ca3af', marginLeft: '8px' }}>(ex: 60 = 2 mois)</span>
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: '#86868b', display: 'block', marginBottom: '6px' }}>Message SMS pré-rempli</label>
                  <textarea value={relanceMessage} onChange={(e) => setRelanceMessage(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', minHeight: '90px', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                  <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>Ce message s'ouvrira dans votre application SMS, prêt à envoyer.</p>
                </div>
              </div>

              {/* Filtres */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                {[
                  { id: 'tous', label: 'Tous' },
                  { id: '2mois', label: '2 mois +' },
                  { id: '6mois', label: '6 mois +' },
                  { id: '1an', label: '+ d\'1 an' }
                ].map(f => (
                  <button key={f.id} onClick={() => setRelanceFiltre(f.id)} style={{ padding: '8px 16px', borderRadius: '980px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, border: relanceFiltre === f.id ? 'none' : '1px solid #d2d2d7', background: relanceFiltre === f.id ? '#2563eb' : 'transparent', color: relanceFiltre === f.id ? '#fff' : '#6b7280' }}>{f.label}</button>
                ))}
              </div>

              {/* Liste */}
              {clientsFiltres.length === 0 ? (
                <div style={{ padding: '48px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', textAlign: 'center', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                  <p style={{ fontSize: '40px', marginBottom: '12px' }}>🎉</p>
                  <p style={{ fontSize: '15px', color: '#6b7280' }}>Aucun client à recontacter pour ce délai.</p>
                  <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: '4px' }}>Tous vos clients sont à jour, ou ils ont des fiches sans rendez-vous enregistré.</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {clientsFiltres.map(c => {
                    const statutColor = c.joursEcoules >= 90 ? '#ef4444' : '#f59e0b';
                    const statutLabel = c.joursEcoules >= 90 ? '🔴 Inactif +3 mois' : '🟠 À relancer';
                    const smsMessage = encodeURIComponent(relanceMessage);
                    const telClean = (c.tel || '').replace(/\s/g, '');
                    return (
                      <div key={c.id} style={{ padding: '20px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', borderLeft: `4px solid ${statutColor}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                        <div>
                          <h3 style={{ fontSize: '16px', fontWeight: 'bold' }}>{c.nom}</h3>
                          <p style={{ fontSize: '13px', color: '#6b7280' }}>Dernier RDV : {c.dernierRdv.toLocaleDateString('fr-FR')} — il y a {formatDelai(c.joursEcoules)}</p>
                          <span style={{ fontSize: '12px', color: statutColor, fontWeight: 600 }}>{statutLabel}</span>
                        </div>
                        {telClean ? (
                          <a href={`sms:${telClean}?&body=${smsMessage}`} style={{ padding: '12px 20px', background: '#10b981', color: '#fff', borderRadius: '980px', textDecoration: 'none', fontWeight: 600, fontSize: '14px', whiteSpace: 'nowrap' }}>
                            💬 Recontacter
                          </a>
                        ) : (
                          <span style={{ fontSize: '12px', color: '#9ca3af' }}>Pas de numéro</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {activeTab === 'materiel' && (
          <div>
            <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '8px', letterSpacing: '-0.02em' }}>🎛️ Mon matériel</h2>
            <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '24px' }}>Gérez votre matériel : ce qui est disponible, loué, et à qui.</p>

            {/* Récap */}
            {materiels.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
                <div style={{ padding: '18px', borderRadius: '16px', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff' }}>
                  <p style={{ fontSize: '12px', opacity: 0.9 }}>✅ Disponible</p>
                  <p style={{ fontSize: '24px', fontWeight: 700 }}>{materiels.filter(m => m.statut === 'dispo').length}</p>
                </div>
                <div style={{ padding: '18px', borderRadius: '16px', background: darkMode ? '#1c1c1e' : '#fff', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                  <p style={{ fontSize: '12px', color: '#f59e0b' }}>📤 En location</p>
                  <p style={{ fontSize: '24px', fontWeight: 700, color: '#f59e0b' }}>{materiels.filter(m => m.statut === 'loue').length}</p>
                </div>
                <div style={{ padding: '18px', borderRadius: '16px', background: darkMode ? '#1c1c1e' : '#fff', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                  <p style={{ fontSize: '12px', color: '#6b7280' }}>📦 Total</p>
                  <p style={{ fontSize: '24px', fontWeight: 700, color: '#2563eb' }}>{materiels.length}</p>
                </div>
              </div>
            )}

            {/* Ajout matériel */}
            <div style={{ padding: '24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Ajouter du matériel</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <input type="text" placeholder="Ex: Platine Pioneer CDJ-3000" value={matNom} onChange={(e) => setMatNom(e.target.value)} style={{ padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px' }} />
                <input type="number" placeholder="Quantité" value={matQuantite} onChange={(e) => setMatQuantite(e.target.value)} style={{ padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px' }} />
              </div>
              <button onClick={addMateriel} style={{ width: '100%', padding: '12px', background: '#2563eb', color: '#fff', fontWeight: 600, borderRadius: '980px', border: 'none', cursor: 'pointer' }}>+ Ajouter</button>
            </div>

            {/* Liste matériel */}
            <div style={{ display: 'grid', gap: '12px' }}>
              {materiels.length === 0 && (
                <div style={{ padding: '48px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', textAlign: 'center', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                  <p style={{ fontSize: '40px', marginBottom: '12px' }}>🎛️</p>
                  <p style={{ fontSize: '15px', color: '#6b7280' }}>Aucun matériel enregistré.</p>
                </div>
              )}
              {materiels.map(m => (
                <div key={m.id} style={{ padding: '20px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', borderLeft: `4px solid ${m.statut === 'dispo' ? '#10b981' : '#f59e0b'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <div>
                      <h3 style={{ fontSize: '16px', fontWeight: 'bold' }}>{m.nom}</h3>
                      <p style={{ fontSize: '13px', color: '#6b7280' }}>Quantité : {m.quantite} · {m.statut === 'dispo' ? '✅ Disponible' : '📤 En location'}</p>
                    </div>
                    <button onClick={() => deleteMateriel(m.id)} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '18px', cursor: 'pointer' }}>✕</button>
                  </div>

                  {m.statut === 'loue' && (
                    <div style={{ background: darkMode ? '#111827' : '#fff7ed', padding: '12px', borderRadius: '10px', marginBottom: '10px' }}>
                      <p style={{ fontSize: '13px', color: darkMode ? '#fbbf24' : '#92400e' }}>📤 Loué à : <strong>{m.loue_par || '—'}</strong></p>
                      {m.retour_prevu && <p style={{ fontSize: '13px', color: darkMode ? '#fbbf24' : '#92400e' }}>📅 Retour prévu : {new Date(m.retour_prevu).toLocaleDateString('fr-FR')}</p>}
                    </div>
                  )}

                  {m.statut === 'dispo' ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', alignItems: 'end' }}>
                      <input type="text" placeholder="Loué à qui ?" value={matLouePar} onChange={(e) => setMatLouePar(e.target.value)} style={{ padding: '10px', borderRadius: '10px', border: '1px solid #d2d2d7', fontSize: '13px' }} />
                      <input type="date" value={matRetour} onChange={(e) => setMatRetour(e.target.value)} style={{ padding: '10px', borderRadius: '10px', border: '1px solid #d2d2d7', fontSize: '13px' }} />
                      <button onClick={() => { updateMateriel(m.id, { statut: 'loue', loue_par: matLouePar, retour_prevu: matRetour }); setMatLouePar(''); setMatRetour(''); }} style={{ padding: '10px 16px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}>📤 Louer</button>
                    </div>
                  ) : (
                    <button onClick={() => updateMateriel(m.id, { statut: 'dispo', loue_par: '', retour_prevu: '' })} style={{ padding: '10px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>✅ Marquer comme rendu</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'messages' && (() => {
          const cardBg = darkMode ? '#1c1c1e' : '#fff';
          const bord = darkMode ? '#2c2c2e' : '#e8ecf8';
          const txtC = darkMode ? '#fff' : '#0a0a0a';
          const subC = darkMode ? '#9ca3af' : '#6b7280';
          const nomClient = (id) => [...validatedClients, ...pendingClients].find(u => u.id === id)?.entrepriseNom || 'Client';
          return (
            <div>
              <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '8px', letterSpacing: '-0.02em' }}>💬 Messages</h2>
              <p style={{ fontSize: '15px', color: subC, marginBottom: '20px' }}>Discutez en direct avec vos clients (ils vous écrivent depuis leur espace).</p>
              <div className="pm-msg-layout" style={{ display: 'flex', gap: '16px', height: '64vh', minHeight: '420px' }}>
                {/* Liste des conversations */}
                <div style={{ width: '290px', flexShrink: 0, background: cardBg, borderRadius: '16px', border: `1px solid ${bord}`, overflowY: 'auto' }}>
                  {proConvs.length === 0 ? (
                    <p style={{ padding: '24px', color: subC, fontSize: '14px', textAlign: 'center' }}>Aucune conversation. Vos clients peuvent vous écrire depuis leur espace.</p>
                  ) : proConvs.map((c) => {
                    const actif = convClientId === c.client_id;
                    return (
                      <button key={c.client_id} onClick={() => { setConvClientId(c.client_id); loadMessages(dataOwnerId, c.client_id, 'pro'); setProConvs(prev => prev.map(x => x.client_id === c.client_id ? { ...x, unread: 0 } : x)); }}
                        style={{ width: '100%', textAlign: 'left', padding: '14px 16px', border: 'none', borderBottom: `1px solid ${bord}`, background: actif ? (darkMode ? '#2c2c2e' : '#eff6ff') : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: '14px', fontWeight: 700, color: txtC, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nomClient(c.client_id)}</p>
                          <p style={{ fontSize: '12px', color: subC, margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.dernier}</p>
                        </div>
                        {c.unread > 0 && <span style={{ flexShrink: 0, background: '#2563eb', color: '#fff', fontSize: '11px', fontWeight: 700, borderRadius: '980px', padding: '1px 7px' }}>{c.unread}</span>}
                      </button>
                    );
                  })}
                </div>
                {/* Fil de la conversation sélectionnée */}
                <div style={{ flex: 1, background: cardBg, borderRadius: '16px', border: `1px solid ${bord}`, padding: '16px', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  {convClientId ? (
                    <>
                      <p style={{ fontSize: '15px', fontWeight: 700, color: txtC, margin: '0 0 12px', paddingBottom: '12px', borderBottom: `1px solid ${bord}` }}>{nomClient(convClientId)}</p>
                      <ChatThread messages={messages} myRole="pro" value={msgInput} onChange={setMsgInput} onSend={() => sendMessage(dataOwnerId, convClientId, 'pro')} sending={sendingMsg} darkMode={darkMode} />
                    </>
                  ) : (
                    <p style={{ margin: 'auto', color: subC, fontSize: '14px' }}>← Sélectionnez une conversation</p>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {activeTab === 'demandes' && (() => {
          const urgenceColors = { 'urgente': '#ef4444', 'normale': '#2563eb', 'faible': '#10b981' };
          const statutLabels = { 'nouvelle': '🟡 Nouvelle', 'en_cours': '🔵 En cours', 'planifiee': '📅 Planifiée', 'terminee': '✅ Terminée', 'refusee': '❌ Refusée' };
          return (
            <div>
              <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '8px', letterSpacing: '-0.02em' }}>📨 Demandes clients</h2>
              <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '16px' }}>Demandes d'intervention envoyées par vos clients via leur espace.</p>

              {/* Lien d'invitation client (anti-erreur : dépose le client direct sur le bon formulaire) */}
              <div style={{ padding: '16px 18px', borderRadius: '14px', background: darkMode ? '#0f1f3a' : '#eff6ff', border: `1px solid ${darkMode ? '#1e3a5f' : '#bfdbfe'}`, marginBottom: '24px' }}>
                <p style={{ fontSize: '14px', fontWeight: 700, color: '#2563eb', margin: '0 0 4px' }}>📩 Inviter un client</p>
                <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 10px' }}>Envoyez ce lien par SMS / WhatsApp : votre client arrive directement sur le bon formulaire, votre code déjà rempli.</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '12px', color: '#2563eb', wordBreak: 'break-all', flex: '1 1 220px', background: darkMode ? '#1c1c1e' : '#fff', padding: '8px 10px', borderRadius: '8px', border: '1px solid #dbeafe' }}>
                    {window.location.origin}/?client={user.code_societe}
                  </span>
                  <button onClick={() => {
                      const lien = `${window.location.origin}/?client=${user.code_societe}`;
                      navigator.clipboard.writeText(lien);
                      alert('Lien d\'invitation copié ! Envoyez-le à votre client.');
                    }}
                    style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    📋 Copier le lien
                  </button>
                </div>
              </div>

              {/* Clients en attente de validation */}
              {pendingClients.length > 0 && (
                <div style={{ padding: '18px 20px', borderRadius: '14px', background: darkMode ? '#2a1f0a' : '#fffbeb', border: `1px solid ${darkMode ? '#4a3a1a' : '#fde68a'}`, marginBottom: '24px' }}>
                  <p style={{ fontSize: '14px', fontWeight: 700, color: '#b45309', margin: '0 0 12px' }}>⏳ {pendingClients.length} client{pendingClients.length > 1 ? 's' : ''} en attente de validation</p>
                  <div style={{ display: 'grid', gap: '10px' }}>
                    {pendingClients.map(c => (
                      <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', padding: '10px 14px', borderRadius: '10px', background: darkMode ? '#1c1c1e' : '#fff', border: `1px solid ${darkMode ? '#2c2c2e' : '#f0f0f0'}` }}>
                        <div>
                          <p style={{ fontSize: '14px', fontWeight: 600, margin: 0, color: darkMode ? '#fff' : '#0a0a0a' }}>{c.entrepriseNom || 'Client'}</p>
                          <p style={{ fontSize: '12px', color: '#9ca3af', margin: '2px 0 0' }}>{c.email}</p>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button onClick={() => validateClient(c.id, true)} style={{ padding: '7px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 700, fontSize: '13px' }}>✓ Accepter</button>
                          <button onClick={() => { if (window.confirm('Refuser et supprimer ce client ?')) validateClient(c.id, false); }} style={{ padding: '7px 16px', background: darkMode ? '#2c2c2e' : '#f3f4f6', color: '#dc2626', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 700, fontSize: '13px' }}>✕ Refuser</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {demandes.length === 0 && (
                <div style={{ padding: '48px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', textAlign: 'center', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                  <p style={{ fontSize: '40px', marginBottom: '12px' }}>📭</p>
                  <p style={{ fontSize: '15px', color: '#6b7280' }}>Aucune demande pour le moment.</p>
                  <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: '8px' }}>Vos clients peuvent créer un compte "Client" avec votre code société : <strong>{user.code_societe}</strong></p>
                </div>
              )}

              {/* DEMANDES À TRAITER (nouvelle / en cours) */}
              {(() => {
                const actives = demandes.filter(d => d.statut === 'nouvelle' || d.statut === 'en_cours');
                const historique = demandes.filter(d => d.statut === 'planifiee' || d.statut === 'refusee' || d.statut === 'terminee');
                return (
                  <>
                    {actives.length > 0 && (
                      <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#2563eb', marginBottom: '12px' }}>📥 À traiter ({actives.length})</h3>
                    )}
                    <div style={{ display: 'grid', gap: '14px', marginBottom: historique.length > 0 ? '32px' : '0' }}>
                      {actives.map(d => (
                        <div key={d.id} style={{ padding: '20px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', borderLeft: `4px solid ${urgenceColors[d.urgence] || '#2563eb'}` }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', gap: '12px' }}>
                            <h3 style={{ fontSize: '17px', fontWeight: 700, margin: 0 }}>{d.titre}</h3>
                            <span style={{ fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}>{statutLabels[d.statut] || d.statut}</span>
                          </div>
                          <p style={{ fontSize: '13px', color: '#2563eb', marginBottom: '6px' }}>👤 {d.client_nom} — {d.client_email}</p>
                          <p style={{ fontSize: '14px', color: darkMode ? '#d1d5db' : '#374151', marginBottom: '8px' }}>{d.description}</p>
                          {d.adresse && (
                            <p style={{ fontSize: '13px', color: '#86868b' }}>📍 {d.adresse}
                              <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(d.adresse)}`} target="_blank" rel="noopener noreferrer" style={{ marginLeft: '8px', color: '#2563eb', fontSize: '13px' }}>🧭 Itinéraire</a>
                            </p>
                          )}
                          {(d.date_souhaitee || d.heure_souhaitee) && (
                            <p style={{ fontSize: '14px', color: '#2563eb', fontWeight: 600, marginTop: '4px' }}>
                              📅 Créneau demandé : {d.date_souhaitee ? d.date_souhaitee.split('-').reverse().join('/') : ''}{d.heure_souhaitee ? ' à ' + d.heure_souhaitee : ''}
                            </p>
                          )}
                          {d.creneaux && <p style={{ fontSize: '13px', color: '#86868b' }}>🕐 Précisions : {d.creneaux}</p>}
                          <p style={{ fontSize: '13px', color: '#86868b' }}>⚡ Urgence : <strong style={{ color: urgenceColors[d.urgence] }}>{d.urgence}</strong></p>
                          {d.created_at && <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>Reçue le {new Date(d.created_at).toLocaleString('fr-FR')}</p>}
                          {d.photos && d.photos.length > 0 && (
                            <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                              {d.photos.map((p, i) => <img key={i} src={p} alt="" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }} />)}
                            </div>
                          )}
                          {d.reponse && (
                            <div style={{ marginTop: '12px', background: darkMode ? '#0f2a1f' : '#d1fae5', padding: '12px', borderRadius: '10px' }}>
                              <p style={{ fontSize: '12px', fontWeight: 'bold', color: '#10b981', marginBottom: '4px' }}>Votre réponse :</p>
                              <p style={{ fontSize: '14px', color: darkMode ? '#a7f3d0' : '#065f46' }}>{d.reponse}</p>
                            </div>
                          )}
                          <div style={{ marginTop: '14px', borderTop: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', paddingTop: '14px' }}>
                            <textarea
                              placeholder="Répondre au client..."
                              value={selectedTicketId === d.id ? ticketResponse : (d.reponse || '')}
                              onChange={(e) => { setSelectedTicketId(d.id); setTicketResponse(e.target.value); }}
                              style={{ width: '100%', padding: '10px', borderRadius: '12px', border: '1px solid #d2d2d7', marginBottom: '10px', fontSize: '13px', minHeight: '60px', boxSizing: 'border-box', fontFamily: 'inherit' }}
                            />
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <button onClick={() => validerDemandeEnRDV(d)} style={{ padding: '8px 14px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>✅ Valider et créer le RDV</button>
                              <button onClick={() => updateDemande(d.id, { reponse: selectedTicketId === d.id ? ticketResponse : d.reponse, statut: 'en_cours' })} style={{ padding: '8px 14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>💬 Répondre</button>
                              <button onClick={() => updateDemande(d.id, { statut: 'refusee' })} style={{ padding: '8px 14px', background: 'transparent', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '980px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>❌ Refuser</button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* HISTORIQUE (demandes traitées, en version compacte) */}
                    {historique.length > 0 && (
                      <>
                        <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#6b7280', marginBottom: '12px' }}>📚 Historique ({historique.length})</h3>
                        <div style={{ display: 'grid', gap: '8px' }}>
                          {historique.map(d => (
                            <div key={d.id} style={{ padding: '12px 16px', borderRadius: '12px', background: darkMode ? '#1c1c1e' : '#fff', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.titre}</p>
                                <p style={{ fontSize: '12px', color: '#9ca3af' }}>{d.client_nom}{d.date_souhaitee ? ' · ' + d.date_souhaitee.split('-').reverse().join('/') : ''}</p>
                              </div>
                              <span style={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>{statutLabels[d.statut] || d.statut}</span>
                              <button onClick={() => updateDemande(d.id, { statut: 'en_cours' })} style={{ padding: '5px 12px', background: 'transparent', color: '#6b7280', border: '1px solid #d2d2d7', borderRadius: '980px', cursor: 'pointer', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' }}>↩️ Rouvrir</button>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          );
        })()}

        {activeTab === 'dashboard' && (
          <div>
            <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '24px', letterSpacing: '-0.02em' }}>Tableau de bord</h2>

            {/* ── ONBOARDING : premiers pas (directeur uniquement) ── */}
            {!isAgent && !onboardingHidden && clientDataLoaded && (() => {
              const steps = [
                { id: 'profil', label: 'Complétez votre profil', sub: 'SIRET, TVA, coordonnées', done: !!(user.siret), tab: 'compte', icon: '⚙️' },
                { id: 'client', label: 'Ajoutez un premier client', sub: 'Créez une fiche client', done: clientsList.length > 0, tab: 'clients', icon: '👤' },
                { id: 'inter', label: isDJ ? 'Planifiez un booking' : 'Planifiez une intervention', sub: 'Ajoutez-la au planning', done: interventions.length > 0, tab: 'planning', icon: '📅' },
                { id: 'facture', label: 'Générez votre première facture', sub: 'Ou un devis', done: factures.length > 0 || devis.length > 0, tab: 'factures', icon: '📄' },
              ];
              const doneCount = steps.filter(s => s.done).length;
              if (doneCount === steps.length) return null; // tout fait → on masque
              const pct = Math.round((doneCount / steps.length) * 100);
              return (
                <div style={{ marginBottom: '24px', padding: '24px', borderRadius: '20px', background: 'linear-gradient(135deg, #1e3a8a, #2563eb)', color: '#fff', boxShadow: '0 8px 30px rgba(37,99,235,0.25)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' }}>
                    <div>
                      <p style={{ fontSize: '18px', fontWeight: 800, margin: '0 0 4px' }}>🚀 Bienvenue ! Configurons votre espace</p>
                      <p style={{ fontSize: '13px', opacity: 0.85, margin: 0 }}>{doneCount}/{steps.length} étapes complétées — vous y êtes presque.</p>
                    </div>
                    <button onClick={() => { localStorage.setItem('planifpro_onboarding_hidden', '1'); setOnboardingHidden(true); }} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>Masquer</button>
                  </div>
                  {/* barre de progression */}
                  <div style={{ height: '8px', background: 'rgba(255,255,255,0.2)', borderRadius: '980px', overflow: 'hidden', marginBottom: '20px' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: '#fff', borderRadius: '980px', transition: 'width 0.4s ease' }} />
                  </div>
                  <div style={{ display: 'grid', gap: '10px' }}>
                    {steps.map(s => (
                      <div key={s.id} onClick={() => !s.done && setActiveTab(s.tab)} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 16px', borderRadius: '12px', background: s.done ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.18)', cursor: s.done ? 'default' : 'pointer', opacity: s.done ? 0.7 : 1, transition: 'background 0.15s' }}
                        onMouseEnter={e => { if (!s.done) e.currentTarget.style.background = 'rgba(255,255,255,0.28)'; }}
                        onMouseLeave={e => { if (!s.done) e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}>
                        <span style={{ flexShrink: 0, width: '28px', height: '28px', borderRadius: '50%', background: s.done ? '#10b981' : 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px' }}>
                          {s.done ? '✓' : s.icon}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: '15px', fontWeight: 600, margin: 0, textDecoration: s.done ? 'line-through' : 'none' }}>{s.label}</p>
                          <p style={{ fontSize: '12px', opacity: 0.8, margin: '1px 0 0' }}>{s.sub}</p>
                        </div>
                        {!s.done && <span style={{ flexShrink: 0, fontSize: '13px', fontWeight: 700, opacity: 0.9 }}>Commencer →</span>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* ── OBJECTIF DU MOIS (directeur uniquement) ── */}
            {!isAgent && (() => {
              const maintenant = new Date();
              const moisActuel = maintenant.getMonth();
              const anneeActuelle = maintenant.getFullYear();
              // Pas de colonne "date de paiement" en base — approximation par la
              // date d'émission des factures payées, cohérent avec le reste de
              // l'app (Analytics utilise déjà `date` comme repère temporel).
              const caCeMois = factures
                .filter(f => f.status === 'paye' && f.type !== 'avoir' && f.date)
                .filter(f => { const d = new Date(f.date); return d.getMonth() === moisActuel && d.getFullYear() === anneeActuelle; })
                .reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
              const objectif = parseFloat(user.objectif_mensuel) || 0;
              const dernierJourMois = new Date(anneeActuelle, moisActuel + 1, 0).getDate();
              const joursRestants = Math.max(dernierJourMois - maintenant.getDate(), 0);

              if (!objectif && !editingObjectif) {
                return (
                  <div style={{ marginBottom: '24px', padding: '20px 24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                    <div>
                      <p style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 2px' }}>🎯 Fixez votre objectif du mois</p>
                      <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>Suivez votre progression en temps réel, et le rythme nécessaire pour l'atteindre.</p>
                    </div>
                    <button onClick={() => { setObjectifInput(''); setEditingObjectif(true); }} style={{ padding: '10px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 700, fontSize: '14px', whiteSpace: 'nowrap' }}>Définir un objectif</button>
                  </div>
                );
              }

              if (editingObjectif) {
                return (
                  <div style={{ marginBottom: '24px', padding: '20px 24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                    <p style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 12px' }}>🎯 Objectif de chiffre d'affaires pour ce mois</p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <input type="number" value={objectifInput} onChange={e => setObjectifInput(e.target.value)} placeholder={objectif ? String(objectif) : 'Ex: 4000'} autoFocus
                        style={{ flex: '1 1 160px', padding: '11px 14px', borderRadius: '10px', border: '1px solid #d2d2d7', fontSize: '15px', boxSizing: 'border-box' }} />
                      <button onClick={saveObjectif} disabled={savingObjectif} style={{ padding: '11px 22px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 700, fontSize: '14px' }}>{savingObjectif ? '...' : 'Valider'}</button>
                      {objectif > 0 && <button onClick={() => setEditingObjectif(false)} style={{ padding: '11px 18px', background: 'transparent', color: '#6b7280', border: '1px solid #d2d2d7', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>Annuler</button>}
                    </div>
                  </div>
                );
              }

              const pct = Math.min(Math.round((caCeMois / objectif) * 100), 100);
              const reste = Math.max(objectif - caCeMois, 0);
              const rythme = joursRestants > 0 ? reste / joursRestants : reste;
              const atteint = caCeMois >= objectif;

              return (
                <div style={{ marginBottom: '24px', padding: '24px', borderRadius: '20px', background: atteint ? 'linear-gradient(135deg, #059669, #10b981)' : 'linear-gradient(135deg, #1e3a8a, #2563eb)', color: '#fff', boxShadow: '0 8px 30px rgba(37,99,235,0.25)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', gap: '12px', flexWrap: 'wrap' }}>
                    <div>
                      <p style={{ fontSize: '18px', fontWeight: 800, margin: '0 0 4px' }}>{atteint ? '🎉 Objectif atteint !' : '🎯 Objectif du mois'}</p>
                      <p style={{ fontSize: '13px', opacity: 0.85, margin: 0 }}>{caCeMois.toFixed(0)}€ / {objectif.toFixed(0)}€</p>
                    </div>
                    <button onClick={() => { setObjectifInput(String(objectif)); setEditingObjectif(true); }} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>Modifier</button>
                  </div>
                  <div style={{ height: '10px', background: 'rgba(255,255,255,0.2)', borderRadius: '980px', overflow: 'hidden', marginBottom: '12px' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: '#fff', borderRadius: '980px', transition: 'width 0.4s ease' }} />
                  </div>
                  <p style={{ fontSize: '14px', fontWeight: 700, margin: 0 }}>{pct}%</p>
                  {!atteint && (
                    <p style={{ fontSize: '13px', opacity: 0.9, margin: '6px 0 0' }}>
                      {joursRestants > 0
                        ? `Il reste ${reste.toFixed(0)}€ à faire en ${joursRestants} jour${joursRestants > 1 ? 's' : ''}, soit ${rythme.toFixed(0)}€/jour.`
                        : `Il reste ${reste.toFixed(0)}€ pour atteindre l'objectif ce mois-ci.`}
                    </p>
                  )}
                </div>
              );
            })()}

            {/* CODE SOCIETE + AGENTS (directeur uniquement) */}
            {!isAgent && user.code_societe && (
              <div style={{ marginBottom: '24px', padding: '20px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>🔑 Code société (à donner à tes agents)</p>
                    <p style={{ fontSize: '24px', fontWeight: 'bold', color: '#2563eb', letterSpacing: '2px' }}>{user.code_societe}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: '13px', color: '#6b7280' }}>👥 Techniciens : <strong>{techniciensActuels} / {maxTechniciens}</strong></p>
                    <p style={{ fontSize: '13px', color: '#f59e0b' }}>⏳ En attente : <strong>{pendingAgents.length}</strong></p>
                    {placesRestantes <= 0 && maxTechniciens > 0 && (
                      <p style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>Limite atteinte — passez à la formule supérieure</p>
                    )}
                    {placesRestantes > 0 && (
                      <p style={{ fontSize: '12px', color: '#10b981', marginTop: '4px' }}>{placesRestantes} place(s) disponible(s)</p>
                    )}
                  </div>
                </div>

                {pendingAgents.length > 0 && (
                  <div style={{ marginTop: '16px', borderTop: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`, paddingTop: '16px' }}>
                    <p style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '12px' }}>🔔 Demandes d'agents à valider :</p>
                    {pendingAgents.map(a => (
                      <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', background: darkMode ? '#111827' : '#f9fafb', borderRadius: '8px', marginBottom: '8px' }}>
                        <div>
                          <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{a.entrepriseNom}</span>
                          <span style={{ fontSize: '13px', color: '#6b7280' }}> — {a.email}</span>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button onClick={() => validateAgent(a.id, true)} style={{ padding: '6px 14px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>✅ Valider</button>
                          <button onClick={() => validateAgent(a.id, false)} style={{ padding: '6px 14px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>✕ Refuser</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {validatedAgents.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <p style={{ fontSize: '13px', fontWeight: 700, color: '#6b7280', marginBottom: '10px' }}>Mon équipe ({validatedAgents.length})</p>
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {validatedAgents.map(a => {
                        const resp = a.agent_niveau === 'responsable';
                        return (
                          <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', padding: '12px 16px', borderRadius: '12px', background: darkMode ? '#111827' : '#f8faff', border: darkMode ? '1px solid #2c2c2e' : '1px solid #eef2ff' }}>
                            <span style={{ fontSize: '14px', fontWeight: 600, color: darkMode ? '#fff' : '#111827' }}>{a.entrepriseNom}</span>
                            <div style={{ display: 'flex', gap: '4px', background: darkMode ? '#1c1c1e' : '#fff', borderRadius: '980px', padding: '3px', border: '1px solid #e5e7eb' }}>
                              <button onClick={() => setAgentNiveau(a.id, 'technicien')} style={{ padding: '6px 14px', borderRadius: '980px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 700, background: !resp ? '#2563eb' : 'transparent', color: !resp ? '#fff' : '#6b7280' }}>Technicien</button>
                              <button onClick={() => setAgentNiveau(a.id, 'responsable')} style={{ padding: '6px 14px', borderRadius: '980px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 700, background: resp ? '#7c3aed' : 'transparent', color: resp ? '#fff' : '#6b7280' }}>Responsable</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>👤 <strong>Technicien</strong> : voit uniquement le planning. 👑 <strong>Responsable</strong> : voit aussi les clients, factures et le chiffre d'affaires.</p>
                  </div>
                )}
              </div>
            )}

            {/* NOTIFICATIONS : interventions dans les 24h */}
            {(() => {
              const now = new Date();
              now.setHours(0, 0, 0, 0);
              const demain = new Date(now);
              demain.setDate(demain.getDate() + 1);
              // Parse une date "YYYY-MM-DD" en date locale (évite le décalage UTC)
              const parseLocalDate = (dateStr) => {
                if (!dateStr) return null;
                const parts = String(dateStr).split('T')[0].split('-');
                if (parts.length !== 3) return new Date(dateStr);
                return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
              };
              const upcoming = interventions
                .filter(i => {
                  if (!i.date || i.statut === 'termine') return false;
                  const d = parseLocalDate(i.date);
                  if (!d) return false;
                  d.setHours(0, 0, 0, 0);
                  return d >= now && d <= demain;
                })
                .sort((a, b) => {
                  const da = parseLocalDate(a.date) - parseLocalDate(b.date);
                  if (da !== 0) return da;
                  return (a.heure || '00:00').localeCompare(b.heure || '00:00');
                });
              if (upcoming.length === 0) return null;
              return (
                <div style={{ marginBottom: '24px', padding: '20px', borderRadius: '12px', background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)', color: '#fff', boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>🔔 {isDJ ? 'Bookings à venir' : metierEffectif === 'beaute' ? 'Rendez-vous à venir' : metierEffectif === 'aide_domicile' ? 'Passages à venir' : 'Interventions à venir'} ({upcoming.length})</h3>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {upcoming.map(i => {
                      const d = parseLocalDate(i.date);
                      d.setHours(0, 0, 0, 0);
                      const diffDays = Math.round((d - now) / (1000 * 60 * 60 * 24));
                      const quand = diffDays === 0 ? "Aujourd'hui" : diffDays === 1 ? 'Demain' : `Dans ${diffDays} jours`;
                      return (
                        <div key={i.id} onClick={() => { setActiveTab('planning'); openModalEdit(i); }} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'rgba(255,255,255,0.2)', borderRadius: '8px', cursor: 'pointer' }}>
                          <div>
                            <span style={{ fontWeight: 'bold' }}>{i.titre}</span>
                            <span style={{ fontSize: '13px', opacity: 0.9 }}> — {i.client}</span>
                          </div>
                          <span style={{ fontSize: '13px', fontWeight: 'bold', background: 'rgba(255,255,255,0.25)', padding: '4px 10px', borderRadius: '6px', whiteSpace: 'nowrap' }}>
                            {quand} • {i.heure}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* STATS DU JOUR */}
            {(() => {
              const _td = new Date();
              const todayStr = `${_td.getFullYear()}-${String(_td.getMonth() + 1).padStart(2, '0')}-${String(_td.getDate()).padStart(2, '0')}`;
              const todayFr = new Date().toLocaleDateString('fr-FR');
              // Interventions du jour
              const interventionsJour = interventions.filter(i => i.date === todayStr).sort((a, b) => (a.heure || '00:00').localeCompare(b.heure || '00:00'));
              const interventionsJourTerminees = interventionsJour.filter(i => i.statut === 'termine');
              // Factures
              const facturesReglees = factures.filter(f => f.status === 'paye');
              const facturesImpayees = factures.filter(f => f.status !== 'paye');
              const montantImpaye = facturesImpayees.reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
              // Absents du jour
              const absentsJour = absences.filter(a => a.date === todayStr);
              const absentsNoms = absentsJour.map(a => a.name);
              const presents = team.filter(t => !absentsNoms.includes(t.name));

              return (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>📌 Aujourd'hui — {todayFr}</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
                    {/* Interventions du jour */}
                    <div style={{ padding: '20px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', borderTop: '4px solid #2563eb' }}>
                      <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>{isDJ ? '🎧 Bookings aujourd\'hui' : metierEffectif === 'beaute' ? '💇 RDV aujourd\'hui' : metierEffectif === 'aide_domicile' ? '🏠 Passages aujourd\'hui' : '🔧 Interventions aujourd\'hui'}</p>
                      <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#2563eb' }}>{interventionsJour.length}</p>
                      <p style={{ fontSize: '12px', color: '#10b981', marginTop: '4px' }}>✓ {interventionsJourTerminees.length} terminé(s)</p>
                    </div>
                    {/* Équipe présente */}
                    <div style={{ padding: '20px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', borderTop: '4px solid #10b981' }}>
                      <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>👥 Équipe présente</p>
                      <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#10b981' }}>{presents.length}<span style={{ fontSize: '16px', color: '#6b7280' }}> / {team.length}</span></p>
                      {absentsJour.length > 0 && <p style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>🚫 Absent(s) : {absentsNoms.join(', ')}</p>}
                      {absentsJour.length === 0 && team.length > 0 && <p style={{ fontSize: '12px', color: '#10b981', marginTop: '4px' }}>Tout le monde est là ✓</p>}
                    </div>
                    {/* Factures (directeur + responsable) */}
                    {(!isAgent || isResponsable) && (<>
                    <div style={{ padding: '20px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', borderTop: '4px solid #10b981' }}>
                      <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>✅ Factures réglées</p>
                      <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#10b981' }}>{facturesReglees.length}</p>
                    </div>
                    <div style={{ padding: '20px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', borderTop: '4px solid #ef4444' }}>
                      <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>🔴 Factures impayées</p>
                      <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#ef4444' }}>{facturesImpayees.length}</p>
                      {montantImpaye > 0 && <p style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>{montantImpaye.toFixed(2)}€ à encaisser</p>}
                    </div>
                    </>)}
                  </div>
                </div>
              );
            })()}

            {(!isAgent || isResponsable) && (<>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '32px' }}>
              <div style={{ padding: '24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>📅 {isDJ ? 'Bookings (total)' : metierEffectif === 'beaute' ? 'Rendez-vous (total)' : metierEffectif === 'aide_domicile' ? 'Passages (total)' : 'Interventions (total)'}</p>
                <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#2563eb' }}>{interventions.length}</p>
              </div>
              <div style={{ padding: '24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>💰 CA total (€)</p>
                <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#a855f7' }}>{factures.reduce((sum, f) => sum + (parseFloat(f.amount) || 0), 0).toFixed(0)}€</p>
              </div>
              <div style={{ padding: '24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>📄 Factures</p>
                <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#f59e0b' }}>{factures.length}</p>
              </div>
              <div style={{ padding: '24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>👥 Équipe</p>
                <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#06b6d4' }}>{team.length}</p>
              </div>
            </div>

            {/* ═══ STATISTIQUES CA ═══ */}
            {(() => {
              const now = new Date();
              // CA par mois (6 derniers mois)
              const caParMois = Array.from({ length: 6 }).map((_, i) => {
                const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
                const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                const label = d.toLocaleDateString('fr-FR', { month: 'short' });
                const ca = factures
                  .filter(f => (f.date || f.created_at || '').startsWith(key))
                  .reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
                const nb = interventions.filter(i => (i.date || '').startsWith(key) && i.statut === 'termine').length;
                return { label, ca, nb };
              });
              const maxCA = Math.max(...caParMois.map(m => m.ca), 1);
              const maxNb = Math.max(...caParMois.map(m => m.nb), 1);

              // Top clients par CA
              const caParClient = {};
              factures.forEach(f => {
                if (!f.client) return;
                caParClient[f.client] = (caParClient[f.client] || 0) + (parseFloat(f.amount) || 0);
              });
              const topClients = Object.entries(caParClient).sort((a, b) => b[1] - a[1]).slice(0, 5);
              const maxClientCA = topClients[0]?.[1] || 1;

              // CA payé vs impayé
              const caPaye = factures.filter(f => f.status === 'paye').reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
              const caImpaye = factures.filter(f => f.status !== 'paye').reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
              const caTotal = caPaye + caImpaye || 1;

              const cardS = { padding: '24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' };

              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '28px' }}>

                  {/* Graphique barres CA par mois */}
                  <div style={{ ...cardS, gridColumn: 'span 2' }}>
                    <p style={{ fontSize: '15px', fontWeight: 700, marginBottom: '20px' }}>CA mensuel — 6 derniers mois</p>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '140px' }}>
                      {caParMois.map((m, i) => (
                        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', height: '100%', justifyContent: 'flex-end' }}>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: '#2563eb' }}>{m.ca > 0 ? `${m.ca.toFixed(0)}€` : ''}</span>
                          <div style={{ width: '100%', position: 'relative', height: `${Math.max((m.ca / maxCA) * 100, 4)}%`, borderRadius: '6px 6px 0 0', background: i === 5 ? '#2563eb' : (darkMode ? '#374151' : '#e0e7ff'), transition: 'height 0.4s ease' }} />
                          <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: 500 }}>{m.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Top clients */}
                  <div style={cardS}>
                    <p style={{ fontSize: '15px', fontWeight: 700, marginBottom: '16px' }}>Top clients</p>
                    {topClients.length === 0 ? (
                      <p style={{ fontSize: '13px', color: '#9ca3af' }}>Aucune facture encore.</p>
                    ) : topClients.map(([nom, ca], i) => (
                      <div key={nom} style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ fontSize: '13px', fontWeight: 600, color: darkMode ? '#e5e7eb' : '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '65%' }}>{nom}</span>
                          <span style={{ fontSize: '13px', fontWeight: 700, color: '#2563eb' }}>{ca.toFixed(0)}€</span>
                        </div>
                        <div style={{ height: '6px', borderRadius: '3px', background: darkMode ? '#374151' : '#f3f4f6', overflow: 'hidden' }}>
                          <div style={{ height: '100%', borderRadius: '3px', background: i === 0 ? '#2563eb' : i === 1 ? '#7c3aed' : '#10b981', width: `${(ca / maxClientCA) * 100}%`, transition: 'width 0.5s ease' }} />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Interventions par mois */}
                  <div style={cardS}>
                    <p style={{ fontSize: '15px', fontWeight: 700, marginBottom: '20px' }}>{isDJ ? 'Bookings' : 'Interventions'} terminé(e)s / mois</p>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '100px' }}>
                      {caParMois.map((m, i) => (
                        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', height: '100%', justifyContent: 'flex-end' }}>
                          {m.nb > 0 && <span style={{ fontSize: '10px', fontWeight: 700, color: '#10b981' }}>{m.nb}</span>}
                          <div style={{ width: '100%', height: `${Math.max((m.nb / maxNb) * 100, 4)}%`, borderRadius: '4px 4px 0 0', background: i === 5 ? '#10b981' : (darkMode ? '#374151' : '#d1fae5') }} />
                          <span style={{ fontSize: '10px', color: '#6b7280' }}>{m.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Répartition payé / impayé */}
                  <div style={cardS}>
                    <p style={{ fontSize: '15px', fontWeight: 700, marginBottom: '16px' }}>Recouvrement</p>
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ fontSize: '13px', color: '#10b981', fontWeight: 600 }}>Encaissé</span>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: '#10b981' }}>{caPaye.toFixed(0)}€</span>
                      </div>
                      <div style={{ height: '10px', borderRadius: '5px', background: darkMode ? '#374151' : '#f3f4f6', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: '5px', background: '#10b981', width: `${(caPaye / caTotal) * 100}%`, transition: 'width 0.5s' }} />
                      </div>
                    </div>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ fontSize: '13px', color: '#ef4444', fontWeight: 600 }}>En attente</span>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: '#ef4444' }}>{caImpaye.toFixed(0)}€</span>
                      </div>
                      <div style={{ height: '10px', borderRadius: '5px', background: darkMode ? '#374151' : '#f3f4f6', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: '5px', background: '#ef4444', width: `${(caImpaye / caTotal) * 100}%`, transition: 'width 0.5s' }} />
                      </div>
                    </div>
                    <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '14px' }}>Taux d'encaissement : <strong style={{ color: darkMode ? '#fff' : '#111' }}>{Math.round((caPaye / caTotal) * 100)}%</strong></p>
                  </div>

                </div>
              );
            })()}
            </>)}

          </div>
        )}

        {activeTab === 'planning' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
              <h2 style={{ fontSize: '32px', fontWeight: 700, letterSpacing: '-0.02em', margin: 0 }}>
                {isDJ ? 'Calendrier des bookings' : metierEffectif === 'beaute' ? 'Planning des rendez-vous' : metierEffectif === 'aide_domicile' ? 'Planning des passages' : 'Planning des interventions'}
              </h2>
              {!isAgent && validatedAgents.length > 0 && (
                <select
                  value={filterTechnicien}
                  onChange={e => setFilterTechnicien(e.target.value)}
                  style={{ padding: '9px 14px', borderRadius: '980px', border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`, background: filterTechnicien ? '#dbeafe' : (darkMode ? '#1c1c1e' : '#f5f5f7'), color: filterTechnicien ? '#1d4ed8' : (darkMode ? '#fff' : '#374151'), fontSize: '14px', fontWeight: filterTechnicien ? 700 : 500, cursor: 'pointer' }}
                >
                  <option value="">Toute l'équipe</option>
                  {[{ entrepriseNom: user.entrepriseNom }, ...validatedAgents].map(a => (
                    <option key={a.entrepriseNom} value={a.entrepriseNom}>{a.entrepriseNom}</option>
                  ))}
                </select>
              )}
              {isAgent && (
                <span style={{ padding: '7px 14px', borderRadius: '980px', background: '#dbeafe', color: '#1d4ed8', fontSize: '13px', fontWeight: 700 }}>
                  Planning de l'équipe
                </span>
              )}
            </div>
            {renderCalendar()}
          </div>
        )}

        {activeTab === 'clients' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold' }}>👤 Fiches Clients</h2>
              <button onClick={openClientCreate} style={{ padding: '12px 24px', background: '#2563eb', color: '#fff', fontWeight: 600, borderRadius: '980px', border: 'none', cursor: 'pointer' }}>➕ Nouveau client</button>
            </div>

            {clientsList.length === 0 && (
              <div style={{ padding: '48px', borderRadius: '12px', background: darkMode ? '#1f2937' : '#fff', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <p style={{ fontSize: '40px', marginBottom: '12px' }}>📇</p>
                <p style={{ fontSize: '16px', color: '#6b7280' }}>Aucune fiche client. Crée ta première fiche pour gagner du temps lors des interventions !</p>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
              {clientsList.map(c => (
                <div key={c.id} style={{ padding: '20px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <h3 style={{ fontSize: '17px', fontWeight: 'bold' }}>
                      {c.type === 'entreprise' ? '🏢' : '🏠'} {c.type === 'entreprise' && c.entreprise ? c.entreprise : c.nom}
                    </h3>
                  </div>
                  {c.type === 'entreprise' && c.entreprise && c.nom && (
                    <p style={{ fontSize: '13px', color: '#6b7280' }}>👤 {c.nom}</p>
                  )}
                  {c.adresse && <p style={{ fontSize: '13px', color: '#6b7280' }}>📍 {c.adresse}</p>}
                  {c.tel && <p style={{ fontSize: '13px', color: '#6b7280' }}>📞 {c.tel}</p>}
                  {c.email && <p style={{ fontSize: '13px', color: '#6b7280' }}>✉️ {c.email}</p>}
                  {c.siret && <p style={{ fontSize: '13px', color: '#6b7280' }}>🧾 SIRET: {c.siret}</p>}
                  {c.notes && <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: '8px', fontStyle: 'italic' }}>{c.notes}</p>}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                    <button onClick={() => openClientEdit(c)} style={{ flex: 1, padding: '8px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>✏️ Éditer</button>
                    {canDeleteFinancial && <button onClick={() => deleteClient(c.id)} style={{ padding: '8px 14px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>🗑️</button>}
                  </div>
                  {metierEffectif === 'aide_domicile' && (
                    <button onClick={() => generateAttestationFiscale(c, new Date().getFullYear() - 1)} style={{ width: '100%', marginTop: '8px', padding: '8px', background: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 700 }} title={`Attestation fiscale ${new Date().getFullYear() - 1} pour le crédit d'impôt`}>
                      📄 Attestation fiscale {new Date().getFullYear() - 1}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'historique' && (() => {
          const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
          const y = histoMonth.getFullYear();
          const mo = histoMonth.getMonth();
          // Interventions du mois
          const monthInterventions = interventions.filter(i => {
            if (!i.date) return false;
            const d = new Date(i.date);
            return d.getFullYear() === y && d.getMonth() === mo;
          });
          const termines = monthInterventions.filter(i => i.statut === 'termine');
          // Temps total (chrono)
          const totalSeconds = monthInterventions.reduce((sum, i) => sum + (i.timer_seconds || 0), 0);
          const totalH = Math.floor(totalSeconds / 3600);
          const totalM = Math.floor((totalSeconds % 3600) / 60);
          // Classement techniciens
          const techCount = {};
          monthInterventions.forEach(i => {
            const techs = [i.technicien, ...(i.techniciens_extra ? JSON.parse(i.techniciens_extra) : [])].filter(Boolean);
            techs.forEach(t => { techCount[t] = (techCount[t] || 0) + 1; });
          });
          const techRanking = Object.entries(techCount).sort((a, b) => b[1] - a[1]);
          // Top clients
          const cliCount = {};
          monthInterventions.forEach(i => { if (i.client) cliCount[i.client] = (cliCount[i.client] || 0) + 1; });
          const cliRanking = Object.entries(cliCount).sort((a, b) => b[1] - a[1]);

          // Évolution des 6 derniers mois (pour le graphique)
          const evolution = [];
          for (let k = 5; k >= 0; k--) {
            const d = new Date(y, mo - k, 1);
            const count = interventions.filter(i => {
              if (!i.date) return false;
              const di = new Date(i.date);
              return di.getFullYear() === d.getFullYear() && di.getMonth() === d.getMonth();
            }).length;
            evolution.push({ mois: monthNames[d.getMonth()].substring(0, 3), count });
          }
          const maxEvol = Math.max(...evolution.map(e => e.count), 1);
          // CA estimé du mois (factures payées du mois)
          const caMois = factures.filter(f => {
            if (f.status !== 'paye' || !f.date) return false;
            const df = new Date(f.date);
            return df.getFullYear() === y && df.getMonth() === mo;
          }).reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);

          return (
            <div>
              {/* Navigation mois */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ fontSize: '24px', fontWeight: 'bold' }}>📚 Historique</h2>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <button onClick={() => setHistoMonth(new Date(y, mo - 1))} style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>←</button>
                  <span style={{ fontSize: '17px', fontWeight: 'bold', minWidth: '160px', textAlign: 'center' }}>{monthNames[mo]} {y}</span>
                  <button onClick={() => setHistoMonth(new Date(y, mo + 1))} style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>→</button>
                </div>
              </div>

              {/* STATS */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
                <div style={{ padding: '24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>📅 Interventions</p>
                  <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#2563eb' }}>{monthInterventions.length}</p>
                </div>
                <div style={{ padding: '24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>✅ Terminées</p>
                  <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#10b981' }}>{termines.length}</p>
                </div>
                <div style={{ padding: '24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>⏱️ Temps total</p>
                  <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#a855f7' }}>{totalH}h{String(totalM).padStart(2, '0')}</p>
                </div>
                <div style={{ padding: '24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>👥 Clients servis</p>
                  <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#06b6d4' }}>{cliRanking.length}</p>
                </div>
              </div>

              {/* GRAPHIQUE ÉVOLUTION 6 MOIS + CA */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div style={{ padding: '28px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '24px' }}>📈 Évolution des interventions (6 mois)</h3>
                  <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '12px', height: '180px' }}>
                    {evolution.map((e, i) => (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: '#2563eb', marginBottom: '6px' }}>{e.count}</span>
                        <div style={{ width: '100%', maxWidth: '44px', height: `${(e.count / maxEvol) * 130}px`, minHeight: e.count > 0 ? '8px' : '2px', background: e.count > 0 ? 'linear-gradient(180deg, #3b82f6, #2563eb)' : '#e5e7eb', borderRadius: '8px 8px 4px 4px', transition: 'height 0.3s' }} />
                        <span style={{ fontSize: '12px', color: '#86868b', marginTop: '8px', textTransform: 'capitalize' }}>{e.mois}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ padding: '28px', borderRadius: '18px', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <p style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>💰 Chiffre d'affaires</p>
                  <p style={{ fontSize: '13px', opacity: 0.8, marginBottom: '4px' }}>{monthNames[mo]} {y}</p>
                  <p style={{ fontSize: '36px', fontWeight: 700 }}>{caMois.toFixed(2)}€</p>
                  <p style={{ fontSize: '12px', opacity: 0.8, marginTop: '8px' }}>Factures payées ce mois</p>
                </div>
              </div>

              {/* Classements */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '32px' }}>
                <div style={{ padding: '24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '16px' }}>🏆 Techniciens les plus actifs</h3>
                  {techRanking.length === 0 && <p style={{ fontSize: '13px', color: '#6b7280' }}>Aucune donnée ce mois-ci.</p>}
                  {techRanking.map(([name, count], idx) => (
                    <div key={name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: idx < techRanking.length - 1 ? `1px solid ${darkMode ? '#374151' : '#f3f4f6'}` : 'none' }}>
                      <span style={{ fontSize: '14px' }}>{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '▪️'} {name}</span>
                      <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#2563eb' }}>{count} interv.</span>
                    </div>
                  ))}
                </div>
                <div style={{ padding: '24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '16px' }}>⭐ Top clients</h3>
                  {cliRanking.length === 0 && <p style={{ fontSize: '13px', color: '#6b7280' }}>Aucune donnée ce mois-ci.</p>}
                  {cliRanking.slice(0, 5).map(([name, count], idx) => (
                    <div key={name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: idx < Math.min(cliRanking.length, 5) - 1 ? `1px solid ${darkMode ? '#374151' : '#f3f4f6'}` : 'none' }}>
                      <span style={{ fontSize: '14px' }}>{name}</span>
                      <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#06b6d4' }}>{count} interv.</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Liste des interventions du mois */}
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>📋 Interventions de {monthNames[mo]}</h3>
              {monthInterventions.length === 0 && (
                <div style={{ padding: '48px', borderRadius: '12px', background: darkMode ? '#1f2937' : '#fff', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                  <p style={{ fontSize: '40px', marginBottom: '12px' }}>🗓️</p>
                  <p style={{ fontSize: '15px', color: '#6b7280' }}>Aucune intervention ce mois-ci.</p>
                </div>
              )}
              <div style={{ display: 'grid', gap: '12px' }}>
                {monthInterventions.sort((a, b) => new Date(b.date) - new Date(a.date)).map(i => (
                  <div key={i.id} onClick={() => openModalEdit(i)} style={{ padding: '20px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', cursor: 'pointer', borderLeft: `4px solid ${i.statut === 'termine' ? '#10b981' : '#2563eb'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <h4 style={{ fontSize: '16px', fontWeight: 'bold' }}>{i.statut === 'termine' ? '✅ ' : '🔵 '}{i.titre}</h4>
                        <p style={{ fontSize: '13px', color: '#6b7280' }}>👤 {i.client}</p>
                        {i.adresse && <p style={{ fontSize: '13px', color: '#6b7280' }}>📍 {i.adresse}</p>}
                        {i.technicien && <p style={{ fontSize: '13px', color: '#6b7280' }}>🔧 {i.technicien}</p>}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontSize: '13px', fontWeight: 'bold' }}>{new Date(i.date).toLocaleDateString('fr-FR')}</p>
                        <p style={{ fontSize: '12px', color: '#6b7280' }}>{i.heure}</p>
                        <span style={{ display: 'inline-block', marginTop: '6px', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', background: i.statut === 'termine' ? '#d1fae5' : '#dbeafe', color: i.statut === 'termine' ? '#065f46' : '#1e40af' }}>
                          {i.statut === 'termine' ? 'Terminée' : 'En cours'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {activeTab === 'devis' && (() => {
          const statutColors = {
            brouillon: { bg: '#f3f4f6', color: '#374151', label: 'Brouillon' },
            envoye: { bg: '#dbeafe', color: '#1d4ed8', label: 'Envoyé' },
            accepte: { bg: '#dcfce7', color: '#16a34a', label: 'Accepté' },
            refuse: { bg: '#fee2e2', color: '#dc2626', label: 'Refusé' },
            expire: { bg: '#fef3c7', color: '#92400e', label: 'Expiré' },
          };
          const devisFiltres = filtreDevis === 'tous' ? devis : devis.filter(d => d.statut === filtreDevis);
          const today = new Date().toISOString().split('T')[0];

          return (
            <div>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <h2 style={{ fontSize: '32px', fontWeight: 800, letterSpacing: '-0.03em', margin: 0 }}>📝 Devis</h2>
                  <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0' }}>{devis.length} devis · {devis.filter(d=>d.statut==='accepte').length} accepté(s)</p>
                </div>
                <button onClick={() => { setEditingDevisId(null); setDevisClient(''); setDevisClientId(''); setDevisObjet(''); setDevisLignes([{description:'',quantite:'1',prix:''}]); setDevisValidite('30'); setDevisNotes(''); setShowModalDevis(true); }}
                  style={{ padding: '12px 22px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 700, fontSize: '14px' }}>
                  + Nouveau devis
                </button>
              </div>

              {/* Filtres */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
                {['tous', 'brouillon', 'envoye', 'accepte', 'refuse'].map(f => (
                  <button key={f} onClick={() => setFiltreDevis(f)} style={{ padding: '7px 16px', borderRadius: '980px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '13px', background: filtreDevis === f ? '#2563eb' : (darkMode ? '#2c2c2e' : '#f3f4f6'), color: filtreDevis === f ? '#fff' : (darkMode ? '#9ca3af' : '#374151') }}>
                    {f === 'tous' ? 'Tous' : statutColors[f]?.label || f}
                    {f !== 'tous' && devis.filter(d=>d.statut===f).length > 0 && ` (${devis.filter(d=>d.statut===f).length})`}
                  </button>
                ))}
              </div>

              {/* Liste */}
              {devisFiltres.length === 0 ? (
                <div style={{ padding: '48px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', textAlign: 'center', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                  <p style={{ fontSize: '36px', marginBottom: '12px' }}>📝</p>
                  <p style={{ fontSize: '15px', color: '#6b7280' }}>Aucun devis{filtreDevis !== 'tous' ? ` avec le statut "${statutColors[filtreDevis]?.label}"` : ''}.</p>
                  <button onClick={() => setShowModalDevis(true)} style={{ marginTop: '16px', padding: '10px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '14px' }}>Créer un devis</button>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {devisFiltres.map(d => {
                    const lignes = JSON.parse(d.lignes || '[]');
                    const total = lignes.reduce((s, l) => s + (parseFloat(l.prix)||0) * (parseFloat(l.quantite)||1), 0);
                    const sc = statutColors[d.statut] || statutColors.brouillon;
                    const isExpire = d.date_expiration && d.date_expiration < today && d.statut !== 'accepte' && d.statut !== 'refuse';
                    return (
                      <div key={d.id} style={{ padding: '18px 20px', borderRadius: '16px', background: darkMode ? '#1c1c1e' : '#fff', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', boxShadow: darkMode ? 'none' : '0 2px 12px rgba(0,0,0,0.05)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 700, fontSize: '16px', color: darkMode ? '#fff' : '#111827' }}>{d.objet || 'Sans objet'}</span>
                              <span style={{ fontSize: '12px', fontWeight: 700, padding: '2px 10px', borderRadius: '980px', background: isExpire ? '#fef3c7' : sc.bg, color: isExpire ? '#92400e' : sc.color }}>{isExpire ? 'Expiré' : sc.label}</span>
                            </div>
                            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 4px' }}>Client : <strong style={{ color: darkMode ? '#e5e7eb' : '#374151' }}>{d.client}</strong></p>
                            <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>
                              {lignes.length} ligne{lignes.length > 1 ? 's' : ''}
                              {d.date_expiration && ` · Expire le ${new Date(d.date_expiration).toLocaleDateString('fr-FR')}`}
                            </p>
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <p style={{ fontSize: '20px', fontWeight: 800, color: '#2563eb', margin: '0 0 8px' }}>{total.toFixed(2)}€</p>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              <button onClick={() => generateDevisPDF(d)} title="Télécharger PDF" style={{ padding: '6px 10px', background: darkMode ? '#2c2c2e' : '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>📄 PDF</button>
                              {d.statut !== 'accepte' && d.statut !== 'refuse' && (
                                <>
                                  <button onClick={() => partagerLienDevis(d)} title="Envoyer le lien de signature en ligne au client" style={{ padding: '6px 10px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>🔗 Faire signer</button>
                                  {d.statut === 'envoye' && <button onClick={() => updateStatutDevis(d.id, 'accepte')} title="Marquer accepté manuellement" style={{ padding: '6px 10px', background: '#dcfce7', color: '#16a34a', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>✅</button>}
                                  {d.statut === 'envoye' && <button onClick={() => updateStatutDevis(d.id, 'refuse')} style={{ padding: '6px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>❌</button>}
                                  <button onClick={() => openDevisEdit(d)} style={{ padding: '6px 10px', background: darkMode ? '#2c2c2e' : '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>✏️</button>
                                </>
                              )}
                              {d.signed_at && (
                                <span style={{ padding: '6px 10px', background: '#ecfdf5', color: '#059669', borderRadius: '8px', fontSize: '12px', fontWeight: 600 }} title={`Signé le ${new Date(d.signed_at).toLocaleString('fr-FR')}`}>✍️ Signé{d.signataire_nom ? ` par ${d.signataire_nom}` : ''}</span>
                              )}
                              {d.statut === 'accepte' && (
                                <button onClick={() => convertirDevisEnFacture(d)} style={{ padding: '6px 12px', background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 700 }}>🧾 → Facture</button>
                              )}
                              <button onClick={() => deleteDevis(d.id)} style={{ padding: '6px 10px', background: darkMode ? '#2c2c2e' : '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#ef4444' }}>🗑️</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Modal création / édition */}
              {showModalDevis && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', overflowY: 'auto' }}>
                  <div style={{ background: darkMode ? '#1c1c1e' : '#fff', borderRadius: '20px', padding: '28px', width: '100%', maxWidth: '640px', marginTop: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                      <h2 style={{ fontSize: '22px', fontWeight: 800, margin: 0 }}>{editingDevisId ? 'Modifier le devis' : 'Nouveau devis'}</h2>
                      <button onClick={() => setShowModalDevis(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>×</button>
                    </div>

                    <div style={{ display: 'grid', gap: '16px' }}>
                      {/* Client */}
                      <div>
                        <label style={{ fontSize: '13px', fontWeight: 700, color: '#374151', display: 'block', marginBottom: '6px' }}>Client *</label>
                        <input list="devis-clients-list" value={devisClient} onChange={e => { setDevisClient(e.target.value); const found = clientsList.find(c => c.nom === e.target.value); setDevisClientId(found?.id || ''); }} placeholder="Nom du client" style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box', background: darkMode ? '#111827' : '#fff', color: darkMode ? '#fff' : '#000' }} />
                        <datalist id="devis-clients-list">{clientsList.map(c => <option key={c.id} value={c.nom} />)}</datalist>
                        <datalist id="catalogue-prestations">{prestations.map(p => <option key={p.id} value={p.nom}>{(parseFloat(p.prix)||0).toFixed(2)}€</option>)}</datalist>
                      </div>

                      {/* Objet */}
                      <div>
                        <label style={{ fontSize: '13px', fontWeight: 700, color: '#374151', display: 'block', marginBottom: '6px' }}>Objet *</label>
                        <input value={devisObjet} onChange={e => setDevisObjet(e.target.value)} placeholder="Ex: Rénovation salle de bain, DJ Mariage, Séance photo, Cours de coaching..." style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box', background: darkMode ? '#111827' : '#fff', color: darkMode ? '#fff' : '#000' }} />
                      </div>

                      {/* Lignes */}
                      <div>
                        <label style={{ fontSize: '13px', fontWeight: 700, color: '#374151', display: 'block', marginBottom: '8px' }}>Lignes *</label>
                        <div style={{ display: 'grid', gap: '8px' }}>
                          {/* En-têtes */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 70px 90px 32px', gap: '6px' }}>
                            <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' }}>Description</span>
                            <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' }}>Qté</span>
                            <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' }}>Prix {labelMontant}</span>
                            <span />
                          </div>
                          {devisLignes.map((l, i) => (
                            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 90px 32px', gap: '6px', alignItems: 'center' }}>
                              <input list="catalogue-prestations" value={l.description} onChange={e => { const nl = [...devisLignes]; nl[i].description = e.target.value; const match = prestations.find(p => p.nom === e.target.value); if (match && !nl[i].prix) nl[i].prix = String(match.prix); setDevisLignes(nl); }} placeholder="Prestation..." style={{ padding: '10px', borderRadius: '8px', border: '1px solid #d2d2d7', fontSize: '13px', background: darkMode ? '#111827' : '#fff', color: darkMode ? '#fff' : '#000' }} />
                              <input type="number" value={l.quantite} onChange={e => { const nl = [...devisLignes]; nl[i].quantite = e.target.value; setDevisLignes(nl); }} min="0.5" step="0.5" style={{ padding: '10px', borderRadius: '8px', border: '1px solid #d2d2d7', fontSize: '13px', background: darkMode ? '#111827' : '#fff', color: darkMode ? '#fff' : '#000' }} />
                              <input type="number" value={l.prix} onChange={e => { const nl = [...devisLignes]; nl[i].prix = e.target.value; setDevisLignes(nl); }} placeholder="0.00" step="0.01" style={{ padding: '10px', borderRadius: '8px', border: '1px solid #d2d2d7', fontSize: '13px', background: darkMode ? '#111827' : '#fff', color: darkMode ? '#fff' : '#000' }} />
                              <button onClick={() => setDevisLignes(devisLignes.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '18px', cursor: 'pointer', padding: 0 }}>×</button>
                            </div>
                          ))}
                          <button onClick={() => setDevisLignes([...devisLignes, { description: '', quantite: '1', prix: '' }])} style={{ padding: '8px', borderRadius: '8px', border: '1.5px dashed #d1d5db', background: 'transparent', cursor: 'pointer', color: '#6b7280', fontSize: '13px', fontWeight: 600 }}>+ Ajouter une ligne</button>
                        </div>
                        {/* Total */}
                        {devisLignes.some(l => l.prix) && (
                          <div style={{ marginTop: '10px', textAlign: 'right', padding: '10px 14px', background: darkMode ? '#111827' : '#f8faff', borderRadius: '10px' }}>
                            <span style={{ fontSize: '14px', fontWeight: 700, color: '#2563eb' }}>
                              Total {labelMontant} : {devisLignes.reduce((s, l) => s + (parseFloat(l.prix)||0) * (parseFloat(l.quantite)||1), 0).toFixed(2)}€
                            </span>
                          </div>
                        )}
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                          <label style={{ fontSize: '13px', fontWeight: 700, color: '#374151', display: 'block', marginBottom: '6px' }}>Validité (jours)</label>
                          <input type="number" value={devisValidite} onChange={e => setDevisValidite(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box', background: darkMode ? '#111827' : '#fff', color: darkMode ? '#fff' : '#000' }} />
                        </div>
                      </div>

                      <div>
                        <label style={{ fontSize: '13px', fontWeight: 700, color: '#374151', display: 'block', marginBottom: '6px' }}>Notes (optionnel)</label>
                        <textarea value={devisNotes} onChange={e => setDevisNotes(e.target.value)} placeholder="Conditions, délai d'exécution, acompte demandé..." style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', minHeight: '70px', boxSizing: 'border-box', background: darkMode ? '#111827' : '#fff', color: darkMode ? '#fff' : '#000' }} />
                      </div>

                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' }}>
                        <button onClick={() => setShowModalDevis(false)} style={{ padding: '12px 22px', background: darkMode ? '#2c2c2e' : '#f3f4f6', color: darkMode ? '#fff' : '#374151', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>Annuler</button>
                        <button onClick={saveDevis} style={{ padding: '12px 28px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 700, fontSize: '14px' }}>
                          {editingDevisId ? 'Enregistrer' : 'Créer le devis'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {activeTab === 'catalogue' && (
          <div>
            <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '8px', letterSpacing: '-0.02em' }}>📚 Catalogue de prestations</h2>
            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>Enregistrez vos prestations courantes avec leur prix pour créer vos devis et factures en quelques secondes.</p>
            <div style={{ padding: '20px', borderRadius: '16px', background: darkMode ? '#1c1c1e' : '#fff', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', marginBottom: '24px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '2 1 220px' }}>
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: '6px' }}>Prestation</label>
                <input value={newPrestaNom} onChange={e => setNewPrestaNom(e.target.value)} placeholder="Ex: Forfait DJ soirée, Débouchage canalisation..." style={{ width: '100%', padding: '11px', borderRadius: '10px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box', background: darkMode ? '#111827' : '#fff', color: darkMode ? '#fff' : '#000' }} />
              </div>
              <div style={{ flex: '1 1 110px' }}>
                <label style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: '6px' }}>Prix {labelMontant} (€)</label>
                <input type="number" value={newPrestaPrix} onChange={e => setNewPrestaPrix(e.target.value)} placeholder="0.00" step="0.01" style={{ width: '100%', padding: '11px', borderRadius: '10px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box', background: darkMode ? '#111827' : '#fff', color: darkMode ? '#fff' : '#000' }} />
              </div>
              <button onClick={savePrestation} style={{ padding: '11px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '14px' }}>+ Ajouter</button>
            </div>
            {prestations.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', borderRadius: '16px', background: darkMode ? '#1c1c1e' : '#fff', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                <p style={{ fontSize: '32px', margin: '0 0 8px' }}>📚</p>Aucune prestation enregistrée pour le moment.
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '8px' }}>
                {prestations.map(p => (
                  <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderRadius: '12px', background: darkMode ? '#1c1c1e' : '#fff', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                    <span style={{ fontWeight: 600, fontSize: '15px', color: darkMode ? '#fff' : '#111827' }}>{p.nom}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                      <span style={{ fontWeight: 700, fontSize: '15px', color: '#2563eb' }}>{(parseFloat(p.prix) || 0).toFixed(2)}€ {labelMontant}</span>
                      <button onClick={() => deletePrestation(p.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '16px' }}>🗑️</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'depenses' && (() => {
          const totalDep = depenses.reduce((s, d) => s + (parseFloat(d.montant) || 0), 0);
          const caEncaisse = factures.filter(f => f.status === 'paye').reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
          const benefice = caEncaisse - totalDep;
          const cats = ['Achats', 'Matériel', 'Carburant', 'Sous-traitance', 'Abonnements', 'Charges', 'Autre'];
          return (
            <div>
              <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '8px', letterSpacing: '-0.02em' }}>💸 Dépenses</h2>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '24px' }}>Suivez vos dépenses pour connaître votre bénéfice net, pas seulement votre chiffre d'affaires.</p>

              {/* Bilan */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
                <div style={{ padding: '20px', borderRadius: '16px', background: darkMode ? '#1c1c1e' : '#fff', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 6px' }}>CA encaissé</p>
                  <p style={{ fontSize: '26px', fontWeight: 800, color: '#10b981', margin: 0 }}>{caEncaisse.toFixed(0)}€</p>
                </div>
                <div style={{ padding: '20px', borderRadius: '16px', background: darkMode ? '#1c1c1e' : '#fff', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 6px' }}>Dépenses</p>
                  <p style={{ fontSize: '26px', fontWeight: 800, color: '#ef4444', margin: 0 }}>{totalDep.toFixed(0)}€</p>
                </div>
                <div style={{ padding: '20px', borderRadius: '16px', background: benefice >= 0 ? 'linear-gradient(135deg, #2563eb, #1e3a8a)' : '#fee2e2', color: benefice >= 0 ? '#fff' : '#991b1b' }}>
                  <p style={{ fontSize: '13px', opacity: 0.9, margin: '0 0 6px' }}>Bénéfice net</p>
                  <p style={{ fontSize: '26px', fontWeight: 800, margin: 0 }}>{benefice.toFixed(0)}€</p>
                </div>
              </div>

              {/* Ajout */}
              <div style={{ padding: '20px', borderRadius: '16px', background: darkMode ? '#1c1c1e' : '#fff', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', marginBottom: '24px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: '2 1 180px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: '6px' }}>Libellé</label>
                  <input value={newDepLibelle} onChange={e => setNewDepLibelle(e.target.value)} placeholder="Ex: Plein gasoil, matériel..." style={{ width: '100%', padding: '11px', borderRadius: '10px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box', background: darkMode ? '#111827' : '#fff', color: darkMode ? '#fff' : '#000' }} />
                </div>
                <div style={{ flex: '1 1 100px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: '6px' }}>Montant (€)</label>
                  <input type="number" value={newDepMontant} onChange={e => setNewDepMontant(e.target.value)} placeholder="0.00" step="0.01" style={{ width: '100%', padding: '11px', borderRadius: '10px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box', background: darkMode ? '#111827' : '#fff', color: darkMode ? '#fff' : '#000' }} />
                </div>
                <div style={{ flex: '1 1 120px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: '6px' }}>Catégorie</label>
                  <select value={newDepCategorie} onChange={e => setNewDepCategorie(e.target.value)} style={{ width: '100%', padding: '11px', borderRadius: '10px', border: '1px solid #d2d2d7', fontSize: '14px', background: darkMode ? '#111827' : '#fff', color: darkMode ? '#fff' : '#000' }}>
                    {cats.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ flex: '1 1 120px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: '6px' }}>Date</label>
                  <input type="date" value={newDepDate} onChange={e => setNewDepDate(e.target.value)} style={{ width: '100%', padding: '11px', borderRadius: '10px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box', background: darkMode ? '#111827' : '#fff', color: darkMode ? '#fff' : '#000' }} />
                </div>
                <button onClick={saveDepense} style={{ padding: '11px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '14px' }}>+ Ajouter</button>
              </div>

              {/* Liste */}
              {depenses.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', borderRadius: '16px', background: darkMode ? '#1c1c1e' : '#fff', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                  <p style={{ fontSize: '32px', margin: '0 0 8px' }}>💸</p>Aucune dépense enregistrée.
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '8px' }}>
                  {depenses.map(d => (
                    <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderRadius: '12px', background: darkMode ? '#1c1c1e' : '#fff', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: '15px', color: darkMode ? '#fff' : '#111827' }}>{d.libelle}</span>
                        <p style={{ fontSize: '12px', color: '#9ca3af', margin: '2px 0 0' }}>{d.categorie} · {d.date ? new Date(d.date).toLocaleDateString('fr-FR') : ''}</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <span style={{ fontWeight: 700, fontSize: '15px', color: '#ef4444' }}>-{(parseFloat(d.montant) || 0).toFixed(2)}€</span>
                        <button onClick={() => deleteDepense(d.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '16px' }}>🗑️</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {activeTab === 'factures' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
              <h2 style={{ fontSize: '32px', fontWeight: 700, letterSpacing: '-0.02em', margin: 0 }}>📄 Factures</h2>
              {factures.length > 0 && (
                <button onClick={exportComptaCSV} title="Exporter toutes les factures en CSV pour votre comptable" style={{ padding: '10px 18px', background: darkMode ? '#2c2c2e' : '#f3f4f6', color: darkMode ? '#fff' : '#374151', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>📊 Export comptable</button>
              )}
            </div>

            {!facturxInfoHidden && (
              <div style={{ padding: '16px 18px', borderRadius: '14px', background: darkMode ? '#0f1f3a' : '#eff6ff', border: `1px solid ${darkMode ? '#1e3a5f' : '#bfdbfe'}`, marginBottom: '24px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '20px' }}>ℹ️</span>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '14px', fontWeight: 700, color: '#2563eb', margin: '0 0 4px' }}>À quoi sert le bouton "⚡ Factur-X" ?</p>
                  <p style={{ fontSize: '13px', color: darkMode ? '#d1d5db' : '#374151', margin: 0, lineHeight: 1.6 }}>
                    Le bouton <strong>📄 PDF</strong> est celui à envoyer à votre client — c'est la facture "normale" que vous connaissez déjà.<br />
                    Le bouton <strong>⚡ Factur-X</strong> télécharge en plus un fichier technique (XML) que la loi rend obligatoire d'ici 2027 pour les entreprises françaises. Vous n'avez besoin de ce fichier que si votre comptable ou une plateforme de facturation officielle vous le demande explicitement — sinon, vous pouvez l'ignorer complètement pour l'instant et continuer à utiliser le PDF comme d'habitude.
                  </p>
                </div>
                <button onClick={() => { localStorage.setItem('planifpro_facturx_info_hidden', '1'); setFacturxInfoHidden(true); }} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '18px', padding: 0, lineHeight: 1 }} aria-label="Masquer">✕</button>
              </div>
            )}

            <div style={{ padding: '24px', borderRadius: '12px', background: darkMode ? '#1f2937' : '#fff', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              {clientsList.length > 0 ? (
                <select value={newFactureClient} onChange={(e) => setNewFactureClient(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '12px', border: '1px solid #2563eb', fontSize: '14px', marginBottom: '12px', background: darkMode ? '#111827' : '#fff', color: darkMode ? '#fff' : '#000' }}>
                  <option value="">— Choisir un client —</option>
                  {clientsList.map(c => (
                    <option key={c.id} value={c.type === 'entreprise' && c.entreprise ? c.entreprise : c.nom}>{c.type === 'entreprise' && c.entreprise ? c.entreprise : c.nom}</option>
                  ))}
                </select>
              ) : (
                <input type="text" placeholder="Client" value={newFactureClient} onChange={(e) => setNewFactureClient(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box' }} />
              )}
              <input type="text" placeholder="Intitulé de la prestation (ex: Rénovation salle de bain, Séance photo...)" value={newFactureIntitule} onChange={(e) => setNewFactureIntitule(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box' }} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <input type="number" placeholder="Montant (€)" value={newFactureAmount} onChange={(e) => setNewFactureAmount(e.target.value)} style={{ padding: '10px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px' }} />
                <select value={newFactureDelai} onChange={(e) => setNewFactureDelai(e.target.value)} style={{ padding: '10px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', background: darkMode ? '#111827' : '#fff', color: darkMode ? '#fff' : '#000' }}>
                  <option value="30">Délai 30 jours</option>
                  <option value="45">Délai 45 jours</option>
                  <option value="60">Délai 60 jours</option>
                </select>
              </div>
              {(user.tva_regime && user.tva_regime !== 'franchise' && user.tva_regime !== 'exonere') && (
                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                  <button type="button" onClick={() => setNewFactureMontantType('ttc')} style={{ flex: 1, padding: '10px', borderRadius: '12px', border: newFactureMontantType === 'ttc' ? '1px solid #2563eb' : '1px solid #d2d2d7', background: newFactureMontantType === 'ttc' ? '#eef4ff' : 'transparent', color: newFactureMontantType === 'ttc' ? '#2563eb' : '#6e6e73', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>Montant TTC</button>
                  <button type="button" onClick={() => setNewFactureMontantType('ht')} style={{ flex: 1, padding: '10px', borderRadius: '12px', border: newFactureMontantType === 'ht' ? '1px solid #2563eb' : '1px solid #d2d2d7', background: newFactureMontantType === 'ht' ? '#eef4ff' : 'transparent', color: newFactureMontantType === 'ht' ? '#2563eb' : '#6e6e73', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>Montant HT</button>
                </div>
              )}
              <button onClick={addFacture} style={{ width: '100%', padding: '12px', background: '#2563eb', color: '#fff', fontWeight: 600, borderRadius: '980px', border: 'none', cursor: 'pointer' }}>💾 Enregistrer la facture</button>
            </div>

            {/* RÉCAPITULATIF DES TOTAUX */}
            {factures.length > 0 && (() => {
              const totalPaye = factures.filter(f => f.status === 'paye').reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
              const totalImpaye = factures.filter(f => f.status !== 'paye').reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
              const nbPaye = factures.filter(f => f.status === 'paye').length;
              const nbImpaye = factures.filter(f => f.status !== 'paye').length;
              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ padding: '18px', borderRadius: '16px', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff' }}>
                    <p style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>✅ Encaissé (payées)</p>
                    <p style={{ fontSize: '24px', fontWeight: 700 }}>{totalPaye.toFixed(2)}€</p>
                    <p style={{ fontSize: '11px', opacity: 0.8 }}>{nbPaye} facture(s)</p>
                  </div>
                  <div style={{ padding: '18px', borderRadius: '16px', background: darkMode ? '#1c1c1e' : '#fff', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                    <p style={{ fontSize: '12px', color: '#f59e0b', marginBottom: '4px' }}>🟡 En attente</p>
                    <p style={{ fontSize: '24px', fontWeight: 700, color: '#f59e0b' }}>{totalImpaye.toFixed(2)}€</p>
                    <p style={{ fontSize: '11px', color: '#9ca3af' }}>{nbImpaye} facture(s)</p>
                  </div>
                  <div style={{ padding: '18px', borderRadius: '16px', background: darkMode ? '#1c1c1e' : '#fff', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                    <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>📊 Total facturé</p>
                    <p style={{ fontSize: '24px', fontWeight: 700, color: '#2563eb' }}>{(totalPaye + totalImpaye).toFixed(2)}€</p>
                    <p style={{ fontSize: '11px', color: '#9ca3af' }}>{factures.length} facture(s)</p>
                  </div>
                </div>
              );
            })()}

            {/* FILTRES */}
            {factures.length > 0 && (
              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                {[
                  { id: 'toutes', label: 'Toutes' },
                  { id: 'paye', label: '✅ Payées' },
                  { id: 'impaye', label: '🟡 En attente' }
                ].map(f => (
                  <button key={f.id} onClick={() => setFiltreFactures(f.id)} style={{
                    padding: '8px 18px', borderRadius: '980px', cursor: 'pointer', fontSize: '14px', fontWeight: 600,
                    border: filtreFactures === f.id ? 'none' : (darkMode ? '1px solid #2c2c2e' : '1px solid #d2d2d7'),
                    background: filtreFactures === f.id ? '#2563eb' : 'transparent',
                    color: filtreFactures === f.id ? '#fff' : (darkMode ? '#fff' : '#6b7280')
                  }}>{f.label}</button>
                ))}
              </div>
            )}

            <div style={{ display: 'grid', gap: '12px' }}>
              {factures.filter(f => filtreFactures === 'toutes' || (filtreFactures === 'paye' && f.status === 'paye') || (filtreFactures === 'impaye' && f.status !== 'paye')).map((f) => {
                const isPaye = f.status === 'paye';
                const enRetard = !isPaye && f.echeance && new Date(f.echeance) < new Date();
                return (
                  <div key={f.id} style={{ padding: '20px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', borderLeft: `4px solid ${isPaye ? '#10b981' : enRetard ? '#ef4444' : '#f59e0b'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        {f.numero && <p style={{ fontSize: '12px', color: f.type === 'avoir' ? '#dc2626' : '#9ca3af', fontWeight: 600, marginBottom: '2px' }}>{f.type === 'avoir' ? '↩️ Avoir' : 'Facture'} N° {f.numero}</p>}
                        <h3 style={{ fontSize: '16px', fontWeight: 'bold' }}>{f.client}</h3>
                        <p style={{ fontSize: '15px', fontWeight: 'bold', color: f.type === 'avoir' ? '#dc2626' : '#2563eb' }}>{f.amount}€</p>
                        {f.type !== 'avoir' && parseFloat(f.acompte) > 0 && parseFloat(f.acompte) < parseFloat(f.amount) && (
                          <p style={{ fontSize: '12px', color: '#7c3aed', fontWeight: 600 }}>💶 Acompte {parseFloat(f.acompte).toFixed(2)}€ · Reste {(parseFloat(f.amount) - parseFloat(f.acompte)).toFixed(2)}€</p>
                        )}
                        {f.echeance && <p style={{ fontSize: '12px', color: enRetard ? '#ef4444' : '#6b7280' }}>Échéance : {new Date(f.echeance).toLocaleDateString('fr-FR')}{enRetard ? ' ⚠️ EN RETARD' : ''}</p>}
                        {/* Date d'encaissement réelle — c'est elle, et non la date
                            d'émission, qui détermine la période de déclaration URSSAF.
                            Absente sur les factures réglées avant cette fonctionnalité :
                            on l'affiche alors comme "à confirmer" plutôt que de faire
                            passer la date d'émission pour une date de règlement. */}
                        {isPaye && f.type !== 'avoir' && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '5px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '12px', color: f.date_paiement ? '#059669' : '#d97706', fontWeight: 600 }}>
                              {f.date_paiement ? '💰 Encaissée le' : '⚠️ Encaissement à confirmer :'}
                            </span>
                            <input type="date" value={f.date_paiement || f.date || ''}
                              onChange={(e) => majDatePaiement(f, e.target.value)}
                              title="Date réelle du règlement. C'est elle qui détermine dans quelle période URSSAF ce montant est déclaré."
                              style={{ padding: '3px 7px', borderRadius: '6px', border: `1px solid ${f.date_paiement ? '#a7f3d0' : '#fcd34d'}`, background: darkMode ? '#2c2c2e' : (f.date_paiement ? '#f0fdf4' : '#fffbeb'), color: darkMode ? '#fff' : '#374151', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }} />
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                        <span style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', background: isPaye ? '#d1fae5' : enRetard ? '#fee2e2' : '#fef3c7', color: isPaye ? '#065f46' : enRetard ? '#991b1b' : '#92400e' }}>
                          {isPaye ? '✅ Payée' : enRetard ? '🔴 Impayée (retard)' : '🟡 En attente'}
                        </span>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => generateFacturePDF(f)} style={{ padding: '6px 12px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>📄 PDF</button>
                          <button onClick={() => envoyerFacture(f)} title="Télécharge le PDF et prépare un email pré-rempli pour votre client" style={{ padding: '6px 12px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>📤 Envoyer</button>
                          <button onClick={() => generateFacturXML(f)} style={{ padding: '6px 12px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }} title="Fichier technique pour votre comptable ou une plateforme de facturation officielle — inutile si personne ne vous l'a demandé">⚡ Factur-X</button>
                          {f.type !== 'avoir' && <button onClick={() => togglePaiement(f)} style={{ padding: '6px 12px', background: isPaye ? '#6b7280' : '#10b981', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>{isPaye ? '↩️ Marquer impayée' : '✅ Marquer payée'}</button>}
                          {f.type !== 'avoir' && !isPaye && <button onClick={() => enregistrerAcompte(f)} title="Enregistrer un acompte / paiement partiel" style={{ padding: '6px 12px', background: '#ede9fe', color: '#6d28d9', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>💶 Acompte</button>}
                          {f.type !== 'avoir' && <button onClick={() => creerAvoir(f)} title="Créer une facture d'avoir (note de crédit)" style={{ padding: '6px 12px', background: '#fef3c7', color: '#92400e', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>↩️ Avoir</button>}
                          {canDeleteFinancial && <button onClick={() => deleteFacture(f.id)} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '18px', cursor: 'pointer' }}>✕</button>}
                        </div>
                      </div>
                    </div>
                    {enRetard && (
                      <div style={{ marginTop: '10px', padding: '8px 12px', background: '#fee2e2', borderRadius: '6px', fontSize: '13px', color: '#991b1b', fontWeight: 'bold' }}>
                        🔔 Pense à relancer ce client !
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'urssaf' && (
          <UrssafDeclaration
            user={user}
            factures={factures}
            darkMode={darkMode}
            onConfigSaved={(maj) => setUser({ ...user, ...maj })}
          />
        )}

        {activeTab === 'team' && (
          <div>
            <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '24px', letterSpacing: '-0.02em' }}>👥 Équipe</h2>
            <div style={{ padding: '24px', borderRadius: '12px', background: darkMode ? '#1f2937' : '#fff', marginBottom: '32px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <input type="text" placeholder="Nom" value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} style={{ padding: '10px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px' }} />
                <input type="text" placeholder="Poste" value={newTeamRole} onChange={(e) => setNewTeamRole(e.target.value)} style={{ padding: '10px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px' }} />
              </div>
              <button onClick={addTeamMember} style={{ width: '100%', padding: '12px', background: '#2563eb', color: '#fff', fontWeight: 600, borderRadius: '980px', border: 'none', cursor: 'pointer' }}>➕ Ajouter</button>
            </div>
            <div style={{ display: 'grid', gap: '12px' }}>
              {team.map((t) => (
                <div key={t.id} style={{ padding: '20px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <h3 style={{ fontSize: '16px', fontWeight: 'bold' }}>{t.name}</h3>
                    <p style={{ fontSize: '13px', color: '#6b7280' }}>{t.role}</p>
                  </div>
                  <button onClick={() => deleteTeamMember(t.id)} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '20px', cursor: 'pointer' }}>✕</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'absences' && (
          <div>
            <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '24px', letterSpacing: '-0.02em' }}>📅 Absences</h2>
            <div style={{ padding: '24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', marginBottom: '32px', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
              <div style={{ marginBottom: '12px' }}>
                {team.length > 0 ? (
                  <select value={newAbsenceName} onChange={(e) => setNewAbsenceName(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #2563eb', fontSize: '14px', background: darkMode ? '#111827' : '#fff', color: darkMode ? '#fff' : '#000', boxSizing: 'border-box' }}>
                    <option value="">— Choisir un agent —</option>
                    {team.map(t => (
                      <option key={t.id} value={t.name}>{t.name}{t.role ? ` (${t.role})` : ''}</option>
                    ))}
                  </select>
                ) : (
                  <input type="text" placeholder="Personne" value={newAbsenceName} onChange={(e) => setNewAbsenceName(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                )}
              </div>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 140px' }}>
                  <label style={{ fontSize: '12px', color: '#86868b', display: 'block', marginBottom: '4px' }}>Du</label>
                  <input type="date" value={newAbsenceDate} onChange={(e) => setNewAbsenceDate(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
                <div style={{ flex: '1 1 140px' }}>
                  <label style={{ fontSize: '12px', color: '#86868b', display: 'block', marginBottom: '4px' }}>Au (optionnel)</label>
                  <input type="date" value={newAbsenceDateFin} onChange={(e) => setNewAbsenceDateFin(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
              </div>
              {team.length === 0 && <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '12px' }}>💡 Ajoute des membres dans l'onglet Équipe pour les sélectionner ici.</p>}
              <button onClick={addAbsence} style={{ width: '100%', padding: '12px', background: '#2563eb', color: '#fff', fontWeight: 600, borderRadius: '980px', border: 'none', cursor: 'pointer' }}>📅 Ajouter</button>
            </div>
            <div style={{ display: 'grid', gap: '12px' }}>
              {absences.map((a) => {
                const memeJour = !a.date_fin || a.date_fin === a.date;
                return (
                  <div key={a.id} style={{ padding: '20px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <h3 style={{ fontSize: '16px', fontWeight: 'bold' }}>{a.name}</h3>
                      <p style={{ fontSize: '13px', color: '#6b7280' }}>
                        📅 {memeJour ? new Date(a.date).toLocaleDateString('fr-FR') : `Du ${new Date(a.date).toLocaleDateString('fr-FR')} au ${new Date(a.date_fin).toLocaleDateString('fr-FR')}`}
                      </p>
                    </div>
                    <button onClick={() => deleteAbsence(a.id)} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '20px', cursor: 'pointer' }}>✕</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'compte' && (() => {
          const formuleNoms = { '1month': 'Indépendant', '6months': 'Pro', '12months': 'Business', 'lifetime': 'Accès illimité' };
          const formulePrix = { '1month': '14,99€', '6months': '74,99€', '12months': '119,99€', 'lifetime': '—' };
          const nomFormule = formuleNoms[user.subscriptionType] || 'Aucune';
          const prixFormule = formulePrix[user.subscriptionType] || '—';
          const finAbo = user.subscriptionEnd ? new Date(user.subscriptionEnd) : null;

          // ⚠️ Remplace ce lien par ton lien de portail Stripe (voir instructions)
          const STRIPE_PORTAL_URL = 'https://billing.stripe.com/p/login/14A28q4Hs72D0KwfOngjC00';

          // Score complétion profil
          const profilFields = [
            { label: 'Nom entreprise', ok: !!user.entrepriseNom },
            { label: 'Secteur d\'activité', ok: !!user.metier },
            { label: 'SIRET', ok: !!user.siret },
            { label: 'Adresse', ok: !!user.adresse },
            { label: 'Téléphone', ok: !!user.tel },
            { label: 'Régime TVA', ok: !!user.tva_regime },
            { label: 'RIB / IBAN', ok: !!user.iban },
          ];
          const profilScore = Math.round(profilFields.filter(f => f.ok).length / profilFields.length * 100);
          const profilColor = profilScore === 100 ? '#10b981' : profilScore >= 60 ? '#f59e0b' : '#ef4444';

          return (
            <div style={{ maxWidth: '760px' }}>
              <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '24px', letterSpacing: '-0.02em' }}>Mon compte</h2>

              {/* Score complétion */}
              {!isAgent && (
                <div style={{ padding: '20px 24px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: '15px', color: darkMode ? '#fff' : '#111827' }}>Profil complété à </span>
                      <span style={{ fontWeight: 800, fontSize: '20px', color: profilColor }}>{profilScore}%</span>
                    </div>
                    {profilScore < 100 && (
                      <button onClick={openEditProfil} style={{ padding: '7px 16px', background: profilColor, color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>
                        Compléter →
                      </button>
                    )}
                    {profilScore === 100 && <span style={{ fontSize: '20px' }}>✅</span>}
                  </div>
                  <div style={{ height: '8px', borderRadius: '4px', background: darkMode ? '#2c2c2e' : '#f3f4f6', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${profilScore}%`, background: `linear-gradient(90deg, ${profilColor}, ${profilColor}cc)`, borderRadius: '4px', transition: 'width 0.6s ease' }} />
                  </div>
                  {profilScore < 100 && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
                      {profilFields.filter(f => !f.ok).map(f => (
                        <span key={f.label} style={{ fontSize: '12px', padding: '3px 10px', borderRadius: '980px', background: darkMode ? '#2c2c2e' : '#fef9c3', color: '#92400e', fontWeight: 500 }}>
                          + {f.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Infos personnelles */}
              <div style={{ padding: '28px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>👤 Informations</h3>
                  {!editProfil && (
                    <button onClick={openEditProfil} style={{ padding: '8px 16px', background: darkMode ? '#2c2c2e' : '#f5f5f7', color: darkMode ? '#fff' : '#0a0a0a', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 500, fontSize: '13px' }}>✏️ Modifier</button>
                  )}
                </div>

                {!editProfil ? (
                  <div style={{ display: 'grid', gap: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '14px', borderBottom: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                      <span style={{ color: '#86868b', fontSize: '14px' }}>{isAgent ? 'Nom' : "Entreprise"}</span>
                      <span style={{ fontWeight: 600, fontSize: '14px' }}>{user.entrepriseNom}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '14px', borderBottom: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                      <span style={{ color: '#86868b', fontSize: '14px' }}>Email</span>
                      <span style={{ fontWeight: 600, fontSize: '14px' }}>{user.email}</span>
                    </div>
                    {!isAgent && (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '14px', borderBottom: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                          <span style={{ color: '#86868b', fontSize: '14px' }}>SIRET</span>
                          <span style={{ fontWeight: 600, fontSize: '14px' }}>{user.siret || '—'}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '14px', borderBottom: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                          <span style={{ color: '#86868b', fontSize: '14px' }}>Adresse</span>
                          <span style={{ fontWeight: 600, fontSize: '14px', textAlign: 'right', maxWidth: '60%' }}>{user.adresse || '—'}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '14px', borderBottom: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                          <span style={{ color: '#86868b', fontSize: '14px' }}>Téléphone</span>
                          <span style={{ fontWeight: 600, fontSize: '14px' }}>{user.tel || '—'}</span>
                        </div>
                      </>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#86868b', fontSize: '14px' }}>Rôle</span>
                      <span style={{ fontWeight: 600, fontSize: '14px' }}>{isAgent ? '🔧 Collaborateur' : '👔 Direction'}</span>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '14px' }}>
                    <div>
                      <label style={{ fontSize: '13px', color: '#86868b', display: 'block', marginBottom: '6px' }}>{isAgent ? 'Nom' : "Nom de l'entreprise"}</label>
                      <input type="text" value={profilNom} onChange={(e) => setProfilNom(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '15px', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: '13px', color: '#86868b', display: 'block', marginBottom: '6px' }}>Email (non modifiable)</label>
                      <input type="text" value={user.email} disabled style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #e5e7eb', fontSize: '15px', background: '#f5f5f7', color: '#86868b', boxSizing: 'border-box' }} />
                    </div>
                    {!isAgent && (
                      <>
                        <div>
                          <label style={{ fontSize: '13px', color: '#86868b', display: 'block', marginBottom: '6px' }}>SIRET</label>
                          <input type="text" value={profilSiret} onChange={(e) => setProfilSiret(e.target.value)} placeholder="Numéro SIRET" style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '15px', boxSizing: 'border-box' }} />
                        </div>
                        <div>
                          <label style={{ fontSize: '13px', color: '#86868b', display: 'block', marginBottom: '6px' }}>Adresse <span style={{ color: '#9ca3af' }}>(privée — sur factures uniquement)</span></label>
                          <AddressAutocomplete value={profilAdresse} onChange={setProfilAdresse} onSelectCoords={(lat, lng) => { setProfilLat(lat); setProfilLng(lng); }} darkMode={darkMode} placeholder="Adresse de l'entreprise" style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '15px', boxSizing: 'border-box' }} />
                        </div>
                        <div>
                          <label style={{ fontSize: '13px', color: '#86868b', display: 'block', marginBottom: '6px' }}>Ville <span style={{ color: '#9ca3af' }}>(publique — affichée sur votre page pro)</span></label>
                          <input type="text" value={profilVille} onChange={(e) => setProfilVille(e.target.value)} placeholder="Ex : Lille" style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '15px', boxSizing: 'border-box' }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', borderRadius: '12px', background: darkMode ? '#111827' : '#f5f5f7' }}>
                          <input type="checkbox" id="annuaireVisible" checked={profilAnnuaireVisible} onChange={(e) => setProfilAnnuaireVisible(e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                          <label htmlFor="annuaireVisible" style={{ fontSize: '13px', cursor: 'pointer' }}>
                            📍 Apparaître dans l'annuaire de proximité <span style={{ color: '#9ca3af' }}>(visible par les clients qui cherchent un pro près de chez eux — nécessite une adresse renseignée ci-dessus)</span>
                          </label>
                        </div>

                        {/* Page publique : photo + présentation */}
                        <div style={{ borderTop: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', paddingTop: '14px' }}>
                          <p style={{ fontSize: '13px', fontWeight: 700, color: darkMode ? '#fff' : '#374151', marginBottom: '10px' }}>🌐 Votre page publique (avis)</p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '12px' }}>
                            <div style={{ width: '64px', height: '64px', borderRadius: '50%', overflow: 'hidden', background: '#f3f4f6', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e5e7eb' }}>
                              {profilPhoto ? <img src={profilPhoto} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: '24px' }}>📷</span>}
                            </div>
                            <div>
                              <input id="photo-pro-input" type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => {
                                const file = e.target.files[0]; if (!file) return;
                                if (file.size > 2 * 1024 * 1024) { alert('Image trop lourde (max 2 Mo).'); return; }
                                const reader = new FileReader();
                                reader.onload = (ev) => setProfilPhoto(ev.target.result);
                                reader.readAsDataURL(file);
                              }} />
                              <button type="button" onClick={() => document.getElementById('photo-pro-input').click()} style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>Choisir une photo</button>
                              {profilPhoto && <button type="button" onClick={() => setProfilPhoto('')} style={{ marginLeft: '8px', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '13px' }}>Retirer</button>}
                            </div>
                          </div>
                          <label style={{ fontSize: '13px', color: '#86868b', display: 'block', marginBottom: '6px' }}>Présentation</label>
                          <textarea value={profilBio} onChange={(e) => setProfilBio(e.target.value)} placeholder="Ex : Plombier chauffagiste depuis 15 ans à Lille. Intervention rapide, devis gratuit, travail soigné..." style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '15px', minHeight: '80px', boxSizing: 'border-box' }} />
                        </div>
                        <div>
                          <label style={{ fontSize: '13px', color: '#86868b', display: 'block', marginBottom: '6px' }}>Téléphone</label>
                          <input type="text" value={profilTel} onChange={(e) => setProfilTel(e.target.value)} placeholder="Téléphone" style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '15px', boxSizing: 'border-box' }} />
                        </div>
                        <div>
                          <label style={{ fontSize: '13px', color: '#86868b', display: 'block', marginBottom: '6px' }}>Secteur d'activité</label>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <button type="button" onClick={() => setProfilMetier('artisan')} style={{ padding: '12px', borderRadius: '12px', border: profilMetier === 'artisan' ? '2px solid #2563eb' : '1px solid #d2d2d7', background: profilMetier === 'artisan' ? '#eef4ff' : 'transparent', color: profilMetier === 'artisan' ? '#2563eb' : '#6e6e73', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>🔧 Services / Artisan</button>
                            <button type="button" onClick={() => setProfilMetier('dj')} style={{ padding: '12px', borderRadius: '12px', border: profilMetier === 'dj' ? '2px solid #2563eb' : '1px solid #d2d2d7', background: profilMetier === 'dj' ? '#eef4ff' : 'transparent', color: profilMetier === 'dj' ? '#2563eb' : '#6e6e73', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>🎧 DJ / Artiste</button>
                            <button type="button" onClick={() => setProfilMetier('beaute')} style={{ padding: '12px', borderRadius: '12px', border: profilMetier === 'beaute' ? '2px solid #2563eb' : '1px solid #d2d2d7', background: profilMetier === 'beaute' ? '#eef4ff' : 'transparent', color: profilMetier === 'beaute' ? '#2563eb' : '#6e6e73', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>💇 Beauté / Bien-être</button>
                            <button type="button" onClick={() => setProfilMetier('aide_domicile')} style={{ padding: '12px', borderRadius: '12px', border: profilMetier === 'aide_domicile' ? '2px solid #2563eb' : '1px solid #d2d2d7', background: profilMetier === 'aide_domicile' ? '#eef4ff' : 'transparent', color: profilMetier === 'aide_domicile' ? '#2563eb' : '#6e6e73', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>🏠 Aide à domicile</button>
                            <button type="button" onClick={() => setProfilMetier('paysagiste')} style={{ padding: '12px', borderRadius: '12px', border: profilMetier === 'paysagiste' ? '2px solid #2563eb' : '1px solid #d2d2d7', background: profilMetier === 'paysagiste' ? '#eef4ff' : 'transparent', color: profilMetier === 'paysagiste' ? '#2563eb' : '#6e6e73', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>🌳 Paysagiste</button>
                            <button type="button" onClick={() => setProfilAutreMode(true)} style={{ padding: '12px', borderRadius: '12px', border: profilAutreMode ? '2px solid #2563eb' : '1px solid #d2d2d7', background: profilAutreMode ? '#eef4ff' : 'transparent', color: profilAutreMode ? '#2563eb' : '#6e6e73', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>✨ Autre métier</button>
                          </div>
                          {profilAutreMode && (
                            <div style={{ marginTop: '10px', padding: '14px', borderRadius: '12px', background: '#f0f9ff', border: '1px solid #bae6fd' }}>
                              <input type="text" value={profilMetierLabel} onChange={(e) => setProfilMetierLabel(e.target.value)}
                                placeholder="Votre métier (ex : Photographe, Coach sportif...)"
                                style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box', marginBottom: '10px' }} />
                              <p style={{ fontSize: '12px', fontWeight: 600, color: '#6e6e73', margin: '0 0 6px' }}>Comment travaillez-vous ?</p>
                              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                {[['artisan', '🔧 Interventions'], ['beaute', '📅 Rendez-vous'], ['dj', '🎫 Événements']].map(([val, lbl]) => (
                                  <button key={val} type="button" onClick={() => setProfilMetier(val)}
                                    style={{ padding: '7px 12px', borderRadius: '980px', border: profilMetier === val ? '2px solid #2563eb' : '1px solid #d2d2d7', background: profilMetier === val ? '#fff' : 'transparent', color: profilMetier === val ? '#2563eb' : '#6e6e73', cursor: 'pointer', fontWeight: 600, fontSize: '12px' }}>
                                    {lbl}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <div>
                          <label style={{ fontSize: '13px', color: '#86868b', display: 'block', marginBottom: '6px' }}>Régime de TVA (pour vos factures)</label>
                          <select value={profilTvaRegime} onChange={(e) => setProfilTvaRegime(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '15px', boxSizing: 'border-box', background: darkMode ? '#1c1c1e' : '#fff', color: darkMode ? '#fff' : '#000' }}>
                            <option value="franchise">Franchise en base (micro-entreprise) — pas de TVA</option>
                            <option value="20">TVA 20% (taux normal)</option>
                            <option value="10">TVA 10% (taux intermédiaire)</option>
                            <option value="5.5">TVA 5,5% (taux réduit)</option>
                            <option value="2.1">TVA 2,1% (taux particulier)</option>
                            <option value="exonere">Exonéré de TVA</option>
                          </select>
                        </div>
                        {profilTvaRegime !== 'franchise' && profilTvaRegime !== 'exonere' && (
                          <div>
                            <label style={{ fontSize: '13px', color: '#86868b', display: 'block', marginBottom: '6px' }}>N° TVA intracommunautaire (optionnel)</label>
                            <input type="text" value={profilTvaNumero} onChange={(e) => setProfilTvaNumero(e.target.value)} placeholder="FR XX XXXXXXXXX" style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '15px', boxSizing: 'border-box' }} />
                          </div>
                        )}
                      {/* RIB */}
                      <div style={{ marginTop: '8px', padding: '16px', borderRadius: '12px', background: darkMode ? '#2c2c2e' : '#f0f4ff', border: '1px solid #dbeafe' }}>
                        <label style={{ fontSize: '13px', color: '#2563eb', fontWeight: 700, display: 'block', marginBottom: '12px' }}>Coordonnées bancaires (RIB — affiché page 2 des factures PDF)</label>
                        <div style={{ display: 'grid', gap: '10px' }}>
                          <div>
                            <label style={{ fontSize: '12px', color: '#86868b', display: 'block', marginBottom: '4px' }}>Banque</label>
                            <input type="text" value={profilBanque} onChange={e => setProfilBanque(e.target.value)} placeholder="Ex : Revolut, BNP, Crédit Agricole..." style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                          </div>
                          <div>
                            <label style={{ fontSize: '12px', color: '#86868b', display: 'block', marginBottom: '4px' }}>IBAN</label>
                            <input type="text" value={profilIban} onChange={e => setProfilIban(e.target.value.toUpperCase())} placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX" style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box', letterSpacing: '0.05em' }} />
                          </div>
                          <div>
                            <label style={{ fontSize: '12px', color: '#86868b', display: 'block', marginBottom: '4px' }}>BIC / SWIFT</label>
                            <input type="text" value={profilBic} onChange={e => setProfilBic(e.target.value.toUpperCase())} placeholder="Ex : REVOGB21" style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                          </div>
                        </div>
                      </div>
                      </>
                    )}

                    {/* Mentions légales & pénalités de retard */}
                    <div style={{ borderTop: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', paddingTop: '16px', marginTop: '8px' }}>
                      <p style={{ fontSize: '14px', fontWeight: 700, color: darkMode ? '#fff' : '#374151', marginBottom: '12px' }}>⚖️ Mentions légales (factures & devis)</p>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ fontSize: '12px', color: '#86868b', display: 'block', marginBottom: '4px' }}>Taux de pénalités de retard (% annuel)</label>
                        <input type="number" value={profilPenalitesTaux} onChange={e => setProfilPenalitesTaux(e.target.value)} placeholder="Ex : 10 (laisser vide = mention standard)" style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                        <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Une mention légale + l'indemnité forfaitaire de 40 € apparaîtront automatiquement sur les factures pro.</p>
                      </div>
                      <div>
                        <label style={{ fontSize: '12px', color: '#86868b', display: 'block', marginBottom: '4px' }}>Conditions générales / mentions personnalisées</label>
                        <textarea value={profilCgv} onChange={e => setProfilCgv(e.target.value)} placeholder="Ex : Paiement à réception. Garantie pièces et main d'œuvre 1 an. Acompte de 30% à la commande..." style={{ width: '100%', padding: '10px 12px', borderRadius: '10px', border: '1px solid #d2d2d7', fontSize: '14px', minHeight: '70px', boxSizing: 'border-box' }} />
                        <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Ce texte apparaîtra en bas de vos factures et devis.</p>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                      <button onClick={saveProfil} disabled={savingProfil} style={{ flex: 1, padding: '13px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '15px' }}>{savingProfil ? 'Enregistrement...' : 'Enregistrer'}</button>
                      <button onClick={() => setEditProfil(false)} style={{ flex: 1, padding: '13px', background: 'transparent', color: '#86868b', border: '1px solid #d2d2d7', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '15px' }}>Annuler</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Abonnement (directeur uniquement) */}
              {!isAgent && (
                <div style={{ padding: '28px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '20px' }}>💳 Mon abonnement</h3>
                  <div style={{ display: 'grid', gap: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '14px', borderBottom: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                      <span style={{ color: '#86868b', fontSize: '14px' }}>Formule</span>
                      <span style={{ fontWeight: 600, fontSize: '14px', color: '#2563eb' }}>{nomFormule}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '14px', borderBottom: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                      <span style={{ color: '#86868b', fontSize: '14px' }}>Prix</span>
                      <span style={{ fontWeight: 600, fontSize: '14px' }}>{prixFormule}{user.subscriptionType !== 'lifetime' ? ' / mois' : ''}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '14px', borderBottom: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                      <span style={{ color: '#86868b', fontSize: '14px' }}>Techniciens</span>
                      <span style={{ fontWeight: 600, fontSize: '14px' }}>{techniciensActuels} / {maxTechniciens}</span>
                    </div>
                    {finAbo && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#86868b', fontSize: '14px' }}>Prochaine échéance</span>
                        <span style={{ fontWeight: 600, fontSize: '14px' }}>{finAbo.toLocaleDateString('fr-FR')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Factures Stripe (directeur uniquement) */}
              {!isAgent && (
                <div style={{ padding: '28px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '12px' }}>🧾 Mes factures</h3>
                  <p style={{ fontSize: '14px', color: '#86868b', marginBottom: '20px' }}>Consultez et téléchargez toutes vos factures, mettez à jour votre carte ou gérez votre abonnement depuis votre espace de facturation sécurisé.</p>
                  <button
                    onClick={() => window.open(STRIPE_PORTAL_URL, '_blank')}
                    style={{ padding: '13px 24px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '15px' }}>
                    Accéder à mes factures →
                  </button>
                </div>
              )}

              {/* QR Code avis clients (directeur uniquement) */}
              {!isAgent && user && (() => {
                const reviewUrl = `${window.location.origin}/avis/${user.id}`;
                const proSlugLocal = (user.entrepriseNom || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
                const proPageUrl = `${window.location.origin}/pro/${proSlugLocal}`;
                const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(reviewUrl)}&bgcolor=ffffff&color=2563eb&margin=10`;
                return (
                  <div style={{ padding: '28px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', marginBottom: '20px' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>⭐ QR Code avis clients</h3>
                    <p style={{ fontSize: '14px', color: '#86868b', marginBottom: '24px', lineHeight: 1.6 }}>
                      Montrez ce QR code à votre client après la prestation. Il scanne et laisse un avis en 30 secondes, sans créer de compte.
                    </p>
                    <div style={{ display: 'flex', gap: '28px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <div style={{ background: '#fff', padding: '16px', borderRadius: '16px', border: '2px solid #e5e7eb', display: 'inline-block' }}>
                        <img
                          src={qrUrl}
                          alt="QR code avis"
                          width="160"
                          height="160"
                          style={{ display: 'block', borderRadius: '8px' }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <p style={{ fontSize: '13px', color: '#86868b', marginBottom: '8px', fontWeight: 500 }}>Lien direct</p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: darkMode ? '#2c2c2e' : '#f5f5f7', padding: '10px 14px', borderRadius: '10px', marginBottom: '16px' }}>
                          <span style={{ fontSize: '12px', color: darkMode ? '#86868b' : '#6e6e73', wordBreak: 'break-all', flex: 1 }}>{reviewUrl}</span>
                          <button
                            onClick={() => { navigator.clipboard.writeText(reviewUrl); }}
                            style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}
                          >
                            Copier
                          </button>
                        </div>
                        <button
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = qrUrl;
                            link.download = 'qr-avis-planifpro.png';
                            link.click();
                          }}
                          style={{ padding: '11px 20px', background: 'transparent', color: '#2563eb', border: '1.5px solid #2563eb', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}
                        >
                          Télécharger le QR code
                        </button>
                        <p style={{ fontSize: '12px', color: '#86868b', marginTop: '12px', lineHeight: 1.5 }}>
                          Imprimez-le, affichez-le sur votre téléphone ou ajoutez-le à votre carte de visite.
                        </p>
                        <div style={{ marginTop: '16px', padding: '12px 14px', borderRadius: '10px', background: darkMode ? '#2c2c2e' : '#f0f4ff', border: `1px solid ${darkMode ? '#3c3c3e' : '#dbeafe'}` }}>
                          <p style={{ fontSize: '12px', color: '#86868b', margin: '0 0 4px', fontWeight: 600 }}>Votre page publique d'avis</p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '12px', color: '#2563eb', wordBreak: 'break-all', flex: 1 }}>{proPageUrl}</span>
                            <button onClick={() => navigator.clipboard.writeText(proPageUrl)} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' }}>Copier</button>
                          </div>
                          <p style={{ fontSize: '11px', color: '#9ca3af', margin: '6px 0 0' }}>Partagez ce lien sur Instagram, WhatsApp, votre carte de visite…</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Avis reçus (directeur uniquement) */}
              {!isAgent && user && <AvisRecus userId={user.id} darkMode={darkMode} onNewAvis={(a) => setRealtimeToast({ message: `⭐ Nouvel avis reçu (${a.note}/5)` })} />}

              {/* Profil professionnel auto-généré (directeur uniquement) */}
              {!isAgent && (
                <div style={{ padding: '24px 28px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>📄 Mon profil professionnel</h3>
                  <p style={{ fontSize: '14px', color: '#86868b', marginBottom: '18px' }}>Un CV/profil PDF généré automatiquement à partir de votre activité réelle sur PlanifPro (clients, interventions, chiffre d'affaires, avis) — utile pour candidater, démarcher, ou simplement valoriser votre parcours.</p>
                  <button onClick={genererCV} disabled={generatingCV} style={{ padding: '12px 24px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '980px', cursor: generatingCV ? 'wait' : 'pointer', fontWeight: 600, fontSize: '14px' }}>
                    {generatingCV ? 'Génération...' : '📥 Télécharger mon profil PDF'}
                  </button>
                </div>
              )}

              {/* Sécurité : changer son mot de passe */}
              <div style={{ padding: '24px 28px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: pwChangeOpen ? '16px' : 0 }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>🔒 Sécurité</h3>
                  {!pwChangeOpen && (
                    <button onClick={() => setPwChangeOpen(true)} style={{ padding: '8px 16px', background: darkMode ? '#2c2c2e' : '#f5f5f7', color: darkMode ? '#fff' : '#0a0a0a', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 500, fontSize: '13px' }}>Changer le mot de passe</button>
                  )}
                </div>
                {pwChangeOpen && (
                  <div style={{ display: 'grid', gap: '10px', maxWidth: '360px' }}>
                    <input type="password" placeholder="Nouveau mot de passe (6 caractères min.)" value={pwNew} onChange={(e) => setPwNew(e.target.value)} style={{ padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                    <input type="password" placeholder="Confirmer le mot de passe" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)} style={{ padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={changePasswordInApp} disabled={pwSaving} style={{ flex: 1, padding: '11px', background: pwSaving ? '#ccc' : '#2563eb', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>
                        {pwSaving ? 'Enregistrement...' : 'Enregistrer'}
                      </button>
                      <button onClick={() => { setPwChangeOpen(false); setPwNew(''); setPwConfirm(''); }} style={{ padding: '11px 18px', background: 'none', border: 'none', color: '#86868b', cursor: 'pointer', fontWeight: 500, fontSize: '14px' }}>Annuler</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Admin (owner uniquement) */}
              {user && ['kreeswaves@gmail.com', 'admin@promanager.com'].includes(user.email) && (
                <div style={{ padding: '20px 28px', borderRadius: '18px', background: darkMode ? '#1a0a2e' : '#faf5ff', border: '1px solid #e9d5ff', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ fontWeight: 700, fontSize: '15px', color: '#7c3aed', margin: '0 0 2px' }}>Dashboard admin</p>
                    <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>Stats, utilisateurs, conversions</p>
                  </div>
                  <button onClick={() => setShowAdmin(true)} style={{ padding: '10px 20px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 700, fontSize: '14px' }}>
                    Ouvrir →
                  </button>
                </div>
              )}

            </div>
          );
        })()}

        {activeTab === 'analytics' && (() => {
          const factFiables = factures.filter(f => f.type === 'facture');
          const now = new Date();
          // CA encaissé (factures payées) par mois, 12 derniers mois
          const mois12 = Array.from({ length: 12 }, (_, i) => {
            const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
            return { key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: d.toLocaleDateString('fr-FR', { month: 'short' }) };
          });
          const caParMois = mois12.map(m => ({
            ...m,
            total: factFiables.filter(f => f.status === 'paye' && (f.date || '').startsWith(m.key)).reduce((s, f) => s + (parseFloat(f.amount) || 0), 0),
          }));
          const maxMois = Math.max(1, ...caParMois.map(m => m.total));
          const caTotal12Mois = caParMois.reduce((s, m) => s + m.total, 0);

          // Top clients par CA facturé (tous statuts confondus)
          const parClient = {};
          factFiables.forEach(f => { const c = f.client || 'Sans nom'; parClient[c] = (parClient[c] || 0) + (parseFloat(f.amount) || 0); });
          const topClients = Object.entries(parClient).sort((a, b) => b[1] - a[1]).slice(0, 5);
          const maxClient = Math.max(1, ...topClients.map(([, v]) => v));

          // Répartition géographique : ville extraite de l'adresse d'intervention
          // (pas celle du client — un chantier/booking peut avoir lieu ailleurs
          // que l'adresse du client). Pas de colonne "ville" séparée en base, les
          // adresses viennent de l'autocomplétion BAN au format "N rue, CP Ville".
          const parVille = {};
          interventions.forEach(i => {
            const m = (i.adresse || '').match(/\d{5}\s+(.+)$/);
            const ville = m ? m[1].trim() : null;
            if (ville) parVille[ville] = (parVille[ville] || 0) + 1;
          });
          const topVilles = Object.entries(parVille).sort((a, b) => b[1] - a[1]).slice(0, 6);
          const maxVille = Math.max(1, ...topVilles.map(([, v]) => v));

          // Répartition payé / impayé
          const totalPaye = factFiables.filter(f => f.status === 'paye').reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
          const totalImpaye = factFiables.filter(f => f.status !== 'paye').reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
          const totalGlobal = totalPaye + totalImpaye || 1;

          // ── Score de santé de l'entreprise (/100), 4 piliers de 25 pts ──
          const today = now.toISOString().split('T')[0];
          const interTerminees = interventions.filter(i => i.statut === 'termine');
          const interPlanifiees = interventions.filter(i => i.statut !== 'termine');
          const interEnRetard = interPlanifiees.filter(i => (i.date || '9999') < today);
          const ponctualite = interPlanifiees.length ? Math.round((1 - interEnRetard.length / interPlanifiees.length) * 25) : 25;
          const facturees = interTerminees.filter(i => factFiables.some(f => f.client === i.client));
          const facturationScore = interTerminees.length ? Math.round((facturees.length / interTerminees.length) * 25) : 25;
          const recouvrementScore = Math.round((totalPaye / totalGlobal) * 25);
          const satisfactionScore = avisMoyenne != null ? Math.round((avisMoyenne / 5) * 25) : 20; // neutre si pas encore d'avis
          const scoreSante = ponctualite + facturationScore + recouvrementScore + satisfactionScore;
          const scoreColor = scoreSante >= 80 ? '#10b981' : scoreSante >= 60 ? '#f59e0b' : '#ef4444';

          const card2 = { background: darkMode ? '#1c1c1e' : '#fff', borderRadius: '18px', padding: '24px 28px', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', marginBottom: '20px' };

          return (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '8px' }}>
                <h2 style={{ fontSize: '32px', fontWeight: 700, margin: 0, letterSpacing: '-0.02em' }}>📈 Analytics</h2>
                <button onClick={() => setShowCareerWrapped(true)} style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #1e3a8a, #2563eb)', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 700, fontSize: '13px', whiteSpace: 'nowrap' }}>✨ Mon bilan (Wrapped)</button>
              </div>
              <p style={{ fontSize: '15px', color: '#6b7280', marginBottom: '24px' }}>Vue d'ensemble de votre activité et de votre chiffre d'affaires.</p>

              {/* Score de santé de l'entreprise */}
              <div style={{ ...card2, display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
                <div style={{ width: '90px', height: '90px', borderRadius: '50%', flexShrink: 0, background: `conic-gradient(${scoreColor} ${scoreSante * 3.6}deg, ${darkMode ? '#2c2c2e' : '#f3f4f6'} 0deg)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: '70px', height: '70px', borderRadius: '50%', background: darkMode ? '#1c1c1e' : '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '22px', fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{scoreSante}</span>
                    <span style={{ fontSize: '9px', color: '#9ca3af', fontWeight: 600 }}>/100</span>
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: '220px' }}>
                  <p style={{ fontSize: '15px', fontWeight: 700, margin: '0 0 10px', color: darkMode ? '#fff' : '#111827' }}>Score de santé de l'entreprise</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px', fontSize: '12px' }}>
                    {[
                      ['Facturation', facturationScore], ['Recouvrement', recouvrementScore],
                      ['Ponctualité', ponctualite], ['Satisfaction', satisfactionScore],
                    ].map(([label, val]) => (
                      <div key={label}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                          <span style={{ color: '#6b7280' }}>{label}</span><span style={{ fontWeight: 700, color: darkMode ? '#fff' : '#111827' }}>{val}/25</span>
                        </div>
                        <div style={{ height: '4px', borderRadius: '2px', background: darkMode ? '#2c2c2e' : '#f3f4f6', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${(val / 25) * 100}%`, background: scoreColor }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                {[
                  { label: 'CA encaissé (12 mois)', value: `${caTotal12Mois.toFixed(0)}€`, color: '#10b981' },
                  { label: 'Factures payées', value: `${totalPaye.toFixed(0)}€`, color: '#2563eb' },
                  { label: 'En attente de paiement', value: `${totalImpaye.toFixed(0)}€`, color: '#f59e0b' },
                ].map(k => (
                  <div key={k.label} style={{ ...card2, marginBottom: 0, borderTop: `3px solid ${k.color}` }}>
                    <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 6px', fontWeight: 500 }}>{k.label}</p>
                    <p style={{ fontSize: '28px', fontWeight: 800, margin: 0, color: darkMode ? '#fff' : '#111827' }}>{k.value}</p>
                  </div>
                ))}
              </div>

              <div style={card2}>
                <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 20px' }}>CA encaissé par mois</h3>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '140px' }}>
                  {caParMois.map(m => (
                    <div key={m.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', height: '100%', justifyContent: 'flex-end' }}>
                      <span style={{ fontSize: '10px', color: '#9ca3af' }}>{m.total > 0 ? `${m.total.toFixed(0)}€` : ''}</span>
                      <div title={`${m.label} : ${m.total.toFixed(0)}€`} style={{ width: '100%', minHeight: '3px', height: `${Math.max(3, (m.total / maxMois) * 100)}px`, background: '#2563eb', borderRadius: '4px 4px 0 0' }} />
                      <span style={{ fontSize: '11px', color: '#6b7280', textTransform: 'capitalize' }}>{m.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
                <div style={card2}>
                  <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 16px' }}>Top clients</h3>
                  {topClients.length === 0 ? <p style={{ fontSize: '14px', color: '#9ca3af' }}>Aucune facture pour le moment.</p> : topClients.map(([nom, total]) => (
                    <div key={nom} style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontSize: '14px', color: darkMode ? '#fff' : '#374151', fontWeight: 500 }}>{nom}</span>
                        <span style={{ fontSize: '14px', fontWeight: 700, color: darkMode ? '#fff' : '#111827' }}>{total.toFixed(0)}€</span>
                      </div>
                      <div style={{ height: '6px', borderRadius: '3px', background: darkMode ? '#2c2c2e' : '#f3f4f6', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: '3px', background: '#2563eb', width: `${(total / maxClient) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>

                <div style={card2}>
                  <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 16px' }}>Payé vs en attente</h3>
                  <div style={{ display: 'flex', height: '10px', borderRadius: '5px', overflow: 'hidden', marginBottom: '16px' }}>
                    <div style={{ width: `${(totalPaye / totalGlobal) * 100}%`, background: '#10b981' }} />
                    <div style={{ width: `${(totalImpaye / totalGlobal) * 100}%`, background: '#f59e0b' }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                    <span style={{ color: '#10b981', fontWeight: 600 }}>● Payé — {totalPaye.toFixed(0)}€</span>
                    <span style={{ color: '#f59e0b', fontWeight: 600 }}>● En attente — {totalImpaye.toFixed(0)}€</span>
                  </div>
                </div>

                <div style={card2}>
                  <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 4px' }}>📍 Où tu travailles le plus</h3>
                  <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 16px' }}>Utile pour savoir où concentrer ta pub locale.</p>
                  {topVilles.length === 0 ? <p style={{ fontSize: '14px', color: '#9ca3af' }}>Pas encore assez d'adresses renseignées.</p> : topVilles.map(([ville, n]) => (
                    <div key={ville} style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontSize: '14px', color: darkMode ? '#fff' : '#374151', fontWeight: 500 }}>{ville}</span>
                        <span style={{ fontSize: '14px', fontWeight: 700, color: darkMode ? '#fff' : '#111827' }}>{n}</span>
                      </div>
                      <div style={{ height: '6px', borderRadius: '3px', background: darkMode ? '#2c2c2e' : '#f3f4f6', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: '3px', background: '#f59e0b', width: `${(n / maxVille) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>

                <div style={card2}>
                  <h3 style={{ fontSize: '16px', fontWeight: 700, margin: '0 0 4px' }}>🌍 PlanifPro Insights</h3>
                  <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 16px' }}>Comparez-vous, anonymement, aux autres pros de votre métier sur PlanifPro.</p>
                  {benchmark === undefined ? (
                    <p style={{ fontSize: '14px', color: '#9ca3af' }}>Chargement...</p>
                  ) : benchmark === null ? (
                    <p style={{ fontSize: '13px', color: '#9ca3af', lineHeight: 1.6 }}>Pas encore assez d'entreprises de votre métier sur PlanifPro pour afficher une moyenne sans vous identifier (minimum 5). Ça s'activera tout seul dès que ce sera le cas — rien à faire de votre côté.</p>
                  ) : (
                    <div style={{ display: 'grid', gap: '12px' }}>
                      <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0 }}>Basé sur {benchmark.nb_entreprises} entreprises de votre secteur</p>
                      {[
                        ['Panier moyen', benchmark.panier_moyen != null ? `${Number(benchmark.panier_moyen).toFixed(0)} €` : '—'],
                        ['Temps moyen sur chantier', benchmark.temps_moyen_chantier_min != null ? `${Number(benchmark.temps_moyen_chantier_min).toFixed(0)} min` : '—'],
                        ['Interventions cette semaine (secteur)', benchmark.interventions_semaine ?? '—'],
                      ].map(([label, val]) => (
                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                          <span style={{ color: darkMode ? '#d1d5db' : '#374151' }}>{label}</span>
                          <span style={{ fontWeight: 700, color: darkMode ? '#fff' : '#111827' }}>{val}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {activeTab === 'controle' && (() => {
          const now = new Date();
          const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
          const nowHHMM = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;

          // 1. Interventions en retard : date passée OU (aujourd'hui + heure dépassée) et pas terminées
          const retard = interventions.filter(i => {
            if (i.statut === 'termine') return false;
            if (i.date < todayStr) return true;
            if (i.date === todayStr && i.heure && i.heure < nowHHMM) return true;
            return false;
          });

          // 2. Techniciens actifs : interventions en cours aujourd'hui
          const enCours = interventions.filter(i => i.date === todayStr && i.statut === 'en_cours');
          const techsActifs = [...new Set(enCours.map(i => i.technicien).filter(Boolean))];

          // 3. Interventions terminées sans facture associée (triées : plus vieilles d'abord = plus à risque d'oubli)
          const clientsAvecFacture = new Set(factures.map(f => f.client));
          const joursDepuis = (dateStr) => dateStr ? Math.floor((now - new Date(dateStr)) / 86400000) : 0;
          const sansFact = interventions
            .filter(i => i.statut === 'termine' && i.client && !clientsAvecFacture.has(i.client))
            .sort((a, b) => joursDepuis(b.date) - joursDepuis(a.date));

          // 4. Factures impayées > 30 jours
          const trente = new Date(); trente.setDate(trente.getDate() - 30);
          const impayeeVieux = factures.filter(f => f.status !== 'paye' && f.date && new Date(f.date) < trente);

          // 5. Paiements reçus cette semaine
          const lundiSemaine = new Date(now); lundiSemaine.setDate(now.getDate() - now.getDay() + 1); lundiSemaine.setHours(0,0,0,0);
          const payesSemaine = factures.filter(f => f.status === 'paye' && f.date && new Date(f.date) >= lundiSemaine);
          const caSemaine = payesSemaine.reduce((s, f) => s + (parseFloat(f.amount)||0), 0);

          // 6. Demandes nouvelles
          const nouvellesDemandes = demandes.filter(d => d.statut === 'nouvelle');

          // 7. Planning aujourd'hui
          const planifAujourdhui = interventions.filter(i => i.date === todayStr);
          const terminesAujourdhui = planifAujourdhui.filter(i => i.statut === 'termine');

          // 8. Tournée optimisée du jour (uniquement les interventions non terminées avec coordonnées GPS)
          const aVisiter = planifAujourdhui.filter(i => i.statut !== 'termine' && i.lat != null && i.lng != null);
          const distanceActuelle = aVisiter.slice(1).reduce((s, p, idx) => s + distanceKm(aVisiter[idx], p), 0);
          const { ordre: ordreOptimise, distanceKm: distanceOptimisee } = aVisiter.length >= 2 ? optimiserTournee(aVisiter) : { ordre: aVisiter, distanceKm: 0 };
          const kmEconomises = Math.max(0, distanceActuelle - distanceOptimisee);

          const alerts = [
            {
              id: 'retard',
              count: retard.length,
              label: retard.length === 0 ? 'Aucun retard' : `${retard.length} intervention${retard.length > 1 ? 's' : ''} en retard`,
              color: retard.length > 0 ? '#ef4444' : '#10b981',
              dot: retard.length > 0 ? '#ef4444' : '#10b981',
              icon: retard.length > 0 ? '🔴' : '🟢',
              items: retard.map(i => ({ label: i.titre, sub: `${i.client} — ${i.date} ${i.heure || ''}`.trim(), action: () => { setActiveTab('planning'); openModalEdit(i); } })),
            },
            {
              id: 'demandes',
              count: nouvellesDemandes.length,
              label: nouvellesDemandes.length === 0 ? 'Aucune demande en attente' : `${nouvellesDemandes.length} demande${nouvellesDemandes.length > 1 ? 's' : ''} à traiter`,
              color: nouvellesDemandes.length > 0 ? '#f59e0b' : '#10b981',
              dot: nouvellesDemandes.length > 0 ? '#f59e0b' : '#10b981',
              icon: nouvellesDemandes.length > 0 ? '🟡' : '🟢',
              items: nouvellesDemandes.map(d => ({ label: d.nom || d.client || 'Demande', sub: d.message?.slice(0,60) || '', action: () => setActiveTab('demandes') })),
            },
            {
              id: 'actifs',
              count: planifAujourdhui.length,
              label: `${terminesAujourdhui.length}/${planifAujourdhui.length} intervention${planifAujourdhui.length > 1 ? 's' : ''} aujourd'hui`,
              color: '#2563eb',
              dot: '#2563eb',
              icon: '🔵',
              items: planifAujourdhui.sort((a,b)=>(a.heure||'').localeCompare(b.heure||'')).map(i => ({
                label: `${i.heure || '--:--'} — ${i.titre}`,
                sub: `${i.client}${i.technicien ? ' · ' + i.technicien : ''}`,
                statut: i.statut,
                action: () => { setActiveTab('planning'); openModalEdit(i); }
              })),
            },
            {
              id: 'sansfact',
              count: sansFact.length,
              label: sansFact.length === 0 ? 'Toutes les interventions sont facturées'
                : `${sansFact.length} facture${sansFact.length > 1 ? 's' : ''} à envoyer${joursDepuis(sansFact[0]?.date) >= 5 ? ` (la plus vieille : ${joursDepuis(sansFact[0].date)}j)` : ''}`,
              color: joursDepuis(sansFact[0]?.date) >= 5 ? '#ef4444' : sansFact.length > 0 ? '#f59e0b' : '#10b981',
              dot: sansFact.length > 0 ? '#f59e0b' : '#10b981',
              icon: joursDepuis(sansFact[0]?.date) >= 5 ? '🔴' : sansFact.length > 0 ? '🟡' : '🟢',
              items: sansFact.slice(0,5).map(i => ({ label: i.titre, sub: `${i.client} — terminée depuis ${joursDepuis(i.date)} jour${joursDepuis(i.date) > 1 ? 's' : ''}`, action: () => setActiveTab('factures') })),
            },
            {
              id: 'impaye',
              count: impayeeVieux.length,
              label: impayeeVieux.length === 0 ? 'Aucun impayé > 30j' : `${impayeeVieux.length} impayé${impayeeVieux.length > 1 ? 's' : ''} de plus de 30 jours`,
              color: impayeeVieux.length > 0 ? '#ef4444' : '#10b981',
              dot: impayeeVieux.length > 0 ? '#ef4444' : '#10b981',
              icon: impayeeVieux.length > 0 ? '🔴' : '🟢',
              items: impayeeVieux.slice(0,5).map(f => ({ label: `${f.client} — ${parseFloat(f.amount)||0}€`, sub: `Facturé le ${f.date || '?'}`, action: () => setActiveTab('factures') })),
            },
            {
              id: 'paye',
              count: payesSemaine.length,
              label: `${payesSemaine.length} paiement${payesSemaine.length > 1 ? 's' : ''} cette semaine — ${caSemaine.toFixed(0)}€`,
              color: '#10b981',
              dot: '#10b981',
              icon: '🟢',
              items: payesSemaine.slice(0,5).map(f => ({ label: `${f.client} — ${parseFloat(f.amount)||0}€`, sub: f.date || '', action: () => setActiveTab('factures') })),
            },
          ];

          return (
            <div>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <h2 style={{ fontSize: '32px', fontWeight: 800, letterSpacing: '-0.03em', margin: '0 0 6px' }}>🗼 Tour de contrôle</h2>
                  <p style={{ fontSize: '15px', color: '#6b7280', margin: 0 }}>{now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} · {nowHHMM}</p>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {planifAujourdhui.length > 0 && (
                    <button onClick={() => setShowJourneyMap(true)} style={{ padding: '10px 16px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #1e3a8a, #2563eb)', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '13px' }}>🗺️ Carte du jour</button>
                  )}
                  {[{ c: retard.length + impayeeVieux.length, label: 'Urgent', col: '#ef4444' }, { c: sansFact.length + nouvellesDemandes.length, label: 'À traiter', col: '#f59e0b' }, { c: payesSemaine.length, label: 'OK cette semaine', col: '#10b981' }].map(s => (
                    <div key={s.label} style={{ padding: '10px 18px', borderRadius: '12px', background: darkMode ? '#1c1c1e' : '#fff', border: `2px solid ${s.col}20`, textAlign: 'center' }}>
                      <p style={{ fontSize: '22px', fontWeight: 800, color: s.col, margin: 0 }}>{s.c}</p>
                      <p style={{ fontSize: '11px', color: '#6b7280', margin: 0 }}>{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Grille des alertes */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' }}>
                {alerts.map(alert => (
                  <div key={alert.id} style={{ borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', border: darkMode ? `1px solid #2c2c2e` : '1px solid #f0f0f0', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                    {/* En-tête carte */}
                    <div style={{ padding: '18px 20px', borderBottom: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: alert.dot, flexShrink: 0, boxShadow: `0 0 0 3px ${alert.dot}30` }} />
                      <span style={{ fontWeight: 700, fontSize: '15px', flex: 1, color: darkMode ? '#fff' : '#111827' }}>{alert.label}</span>
                      {alert.count > 0 && (
                        <span style={{ fontSize: '20px', fontWeight: 800, color: alert.color }}>{alert.count}</span>
                      )}
                    </div>
                    {/* Liste items */}
                    {alert.items.length > 0 ? (
                      <div style={{ padding: '8px 0', maxHeight: '220px', overflowY: 'auto' }}>
                        {alert.items.map((item, idx) => (
                          <div
                            key={idx}
                            onClick={item.action}
                            style={{ padding: '10px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', borderBottom: idx < alert.items.length - 1 ? (darkMode ? '1px solid #2c2c2e' : '1px solid #f9f9f9') : 'none' }}
                            onMouseEnter={e => e.currentTarget.style.background = darkMode ? '#111827' : '#f8faff'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <div style={{ minWidth: 0 }}>
                              <p style={{ fontSize: '14px', fontWeight: 600, color: darkMode ? '#fff' : '#111827', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</p>
                              {item.sub && <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.sub}</p>}
                            </div>
                            {item.statut && (
                              <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 8px', borderRadius: '6px', flexShrink: 0, background: item.statut === 'termine' ? '#d1fae5' : item.statut === 'en_cours' ? '#dbeafe' : '#f3f4f6', color: item.statut === 'termine' ? '#065f46' : item.statut === 'en_cours' ? '#1e40af' : '#374151' }}>
                                {item.statut === 'termine' ? '✓ Terminé' : item.statut === 'en_cours' ? '⚡ En cours' : '● Planifié'}
                              </span>
                            )}
                            <span style={{ color: '#9ca3af', fontSize: '16px', flexShrink: 0 }}>›</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ padding: '20px', textAlign: 'center' }}>
                        <span style={{ fontSize: '24px' }}>✅</span>
                        <p style={{ fontSize: '13px', color: '#6b7280', margin: '6px 0 0' }}>Tout est bon ici</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Techniciens actifs si team */}
              {techsActifs.length > 0 && (
                <div style={{ marginTop: '24px', padding: '20px', borderRadius: '18px', background: 'linear-gradient(135deg, #1e3a8a, #2563eb)', color: '#fff' }}>
                  <p style={{ fontWeight: 700, fontSize: '15px', marginBottom: '12px' }}>🟢 {techsActifs.length} technicien{techsActifs.length > 1 ? 's' : ''} actif{techsActifs.length > 1 ? 's' : ''} en ce moment</p>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {techsActifs.map(t => (
                      <span key={t} style={{ padding: '6px 14px', background: 'rgba(255,255,255,0.2)', borderRadius: '980px', fontSize: '14px', fontWeight: 600 }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Tournée optimisée du jour */}
              {aVisiter.length >= 2 && (
                <div style={{ marginTop: '24px', padding: '20px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '14px' }}>
                    <p style={{ fontWeight: 700, fontSize: '15px', margin: 0, color: darkMode ? '#fff' : '#111827' }}>🗺️ Tournée du jour</p>
                    {kmEconomises >= 1 && (
                      <span style={{ fontSize: '13px', fontWeight: 700, color: '#10b981', background: '#10b98118', padding: '4px 12px', borderRadius: '980px' }}>
                        Ordre optimal : −{kmEconomises.toFixed(0)} km vs l'ordre actuel
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {ordreOptimise.map((i, idx) => (
                      <div key={i.id} onClick={() => { setActiveTab('planning'); openModalEdit(i); }} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', borderRadius: '12px', background: darkMode ? '#111827' : '#f8faff', cursor: 'pointer' }}>
                        <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#2563eb', color: '#fff', fontSize: '12px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{idx + 1}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: '14px', fontWeight: 600, color: darkMode ? '#fff' : '#111827', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{i.titre}</p>
                          <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 0' }}>{i.client}{i.heure ? ` · ${i.heure}` : ''}</p>
                        </div>
                        {idx < ordreOptimise.length - 1 && <span style={{ fontSize: '11px', color: '#9ca3af', flexShrink: 0 }}>{distanceKm(i, ordreOptimise[idx + 1]).toFixed(1)} km →</span>}
                      </div>
                    ))}
                  </div>
                  <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '12px', margin: '12px 0 0' }}>
                    Suggestion basée sur les adresses géolocalisées ({aVisiter.length} sur {planifAujourdhui.filter(i => i.statut !== 'termine').length} interventions restantes du jour). L'ordre des rendez-vous à heure fixe n'est pas modifié automatiquement.
                  </p>
                </div>
              )}
            </div>
          );
        })()}

        {activeTab === 'faq' && (
          <div style={{ maxWidth: '760px' }}>
            <h2 style={{ fontSize: '32px', fontWeight: 700, marginBottom: '12px', letterSpacing: '-0.02em' }}>Aide & FAQ</h2>
            <p style={{ fontSize: '16px', color: '#86868b', marginBottom: '28px' }}>Retrouvez les réponses aux questions les plus fréquentes sur PlanifPro.</p>
            <div style={{ padding: '28px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
              <div style={{ display: 'grid', gap: '8px' }}>
                {(isDJ ? [
                  { q: 'Comment ajouter un booking ?', r: "Allez dans l'onglet Calendrier, cliquez sur une date, puis renseignez le client, le lieu, le type d'événement (mariage, club, privé...), le cachet et les horaires." },
                  { q: 'Comment gérer mon matériel en location ?', r: "Dans l'onglet « Mon matériel », ajoutez vos équipements (platines, sono...). Vous pouvez marquer chaque élément comme loué (à qui, avec une date de retour) ou disponible." },
                  { q: 'Comment générer une facture pour un booking ?', r: "Ouvrez le booking concerné et cliquez sur « Générer une facture pour ce booking ». Vous pouvez ensuite la télécharger en PDF avec votre numéro de facture." },
                  { q: 'Comment relancer mes anciens clients ?', r: "Dans l'onglet « Clients à recontacter », retrouvez les organisateurs que vous n'avez pas revus depuis un moment et envoyez-leur un SMS de relance pré-rempli en un clic." },
                  { q: 'Comment savoir ce qui est payé ou en attente ?', r: "Dans l'onglet Factures, utilisez les filtres « Payées » / « En attente ». Le total encaissé s'affiche automatiquement, pratique pour vos déclarations." },
                  { q: 'Comment changer mes informations (SIRET, TVA...) ?', r: "Dans « Mon compte », cliquez sur « Modifier ». Vous pouvez y mettre à jour votre secteur, vos coordonnées et votre régime de TVA." },
                  { q: 'Comment résilier mon abonnement ?', r: "Depuis votre espace de facturation (Mon compte → Mes factures), vous pouvez gérer ou annuler votre abonnement à tout moment, sans engagement." },
                  { q: 'Mes données sont-elles sécurisées ?', r: "Oui. Vos mots de passe sont chiffrés et vos données sont protégées. Elles ne sont jamais revendues à des tiers, conformément au RGPD." }
                ] : metierEffectif === 'aide_domicile' ? [
                  { q: 'Comment ajouter une intervention ?', r: "Allez dans l'onglet Planning, cliquez sur une date, puis renseignez le bénéficiaire, l'adresse et le type de prestation (ménage, repassage, courses...). Vous pouvez noter l'heure de passage et la durée." },
                  { q: 'Comment suivre les tâches effectuées ?', r: "Dans le détail d'une intervention, l'onglet « Checklist » vous permet de cocher les tâches réalisées (ménage, repassage, aide aux repas...). Pratique pour le suivi et la traçabilité." },
                  { q: 'Comment générer un compte-rendu PDF ?', r: "Ouvrez l'intervention, remplissez les onglets (rapport, photos, signature), puis cliquez sur « Générer le Rapport PDF ». Idéal pour les familles et les organismes financeurs." },
                  { q: 'Comment relancer mes bénéficiaires inactifs ?', r: "Dans l'onglet « Clients à recontacter », retrouvez automatiquement ceux qui n'ont pas eu de passage depuis un certain temps. Envoyez un SMS de relance pré-rempli en un clic." },
                  { q: 'Comment créer une facture pour un bénéficiaire ?', r: "Dans l'onglet Factures, indiquez le bénéficiaire, le montant et la date, puis enregistrez. Vous pouvez télécharger la facture en PDF. Utile aussi pour les attestations fiscales (crédit d'impôt)." },
                  { q: 'Comment changer mes informations (SIRET, TVA...) ?', r: "Dans « Mon compte », cliquez sur « Modifier ». Vous pouvez y mettre à jour votre secteur, vos coordonnées et votre régime de TVA." },
                  { q: 'Comment résilier mon abonnement ?', r: "Depuis votre espace de facturation (Mon compte → Mes factures), vous pouvez gérer ou annuler votre abonnement à tout moment, sans engagement." },
                  { q: 'Mes données sont-elles sécurisées ?', r: "Oui. Vos mots de passe sont chiffrés et vos données sont protégées. Elles ne sont jamais revendues à des tiers, conformément au RGPD." }
                ] : isBeaute ? [
                  { q: 'Comment ajouter un rendez-vous ?', r: "Allez dans l'onglet Planning, cliquez sur une date, puis renseignez votre cliente, l'adresse et la prestation. Vous pouvez aussi noter l'heure de début et de fin." },
                  { q: 'Comment fonctionne la carte de fidélité ?', r: "Dans l'onglet « Fidélité », définissez vos prestations et leurs points, votre objectif et votre récompense. Pour chaque cliente, ajoutez les points d'un clic à chaque visite. Une fois l'objectif atteint, la carte se met en valeur avec la récompense." },
                  { q: 'Comment relancer mes clientes ?', r: "Dans l'onglet « Clients à recontacter », retrouvez automatiquement celles qui ne sont pas revenues depuis un certain temps. Cliquez sur « Recontacter » : votre application SMS s'ouvre avec un message pré-rempli, prêt à envoyer." },
                  { q: 'Comment personnaliser le délai et le message de relance ?', r: "Dans l'onglet « Clients à recontacter », réglez le nombre de jours d'inactivité et modifiez le texte du SMS selon votre style. Vos réglages s'appliquent à toutes vos relances." },
                  { q: 'Comment créer une facture pour une cliente ?', r: "Dans l'onglet Factures, indiquez la cliente, le montant et le délai, puis enregistrez. Vous pouvez télécharger la facture en PDF avec votre numéro et vos informations." },
                  { q: 'Comment changer mes informations (SIRET, TVA...) ?', r: "Dans « Mon compte », cliquez sur « Modifier ». Vous pouvez y mettre à jour votre secteur, vos coordonnées et votre régime de TVA." },
                  { q: 'Comment résilier mon abonnement ?', r: "Depuis votre espace de facturation (Mon compte → Mes factures), vous pouvez gérer ou annuler votre abonnement à tout moment, sans engagement." },
                  { q: 'Mes données sont-elles sécurisées ?', r: "Oui. Vos mots de passe sont chiffrés et vos données sont protégées. Elles ne sont jamais revendues à des tiers, conformément au RGPD." }
                ] : [
                  { q: 'Comment ajouter un technicien à mon équipe ?', r: "Donnez votre code société (visible sur le tableau de bord) à votre technicien. Il s'inscrit comme « Collaborateur » avec ce code, puis vous le validez depuis votre tableau de bord." },
                  { q: 'Combien de techniciens puis-je ajouter ?', r: "Cela dépend de votre formule : Indépendant (vous seul), Pro (jusqu'à 6 personnes), Business (jusqu'à 12 personnes). Vous pouvez changer de formule à tout moment." },
                  { q: "Comment générer un rapport d'intervention ?", r: "Ouvrez une intervention, remplissez les onglets (infos, rapport, photos, signatures), puis cliquez sur « Générer le Rapport PDF »." },
                  { q: 'Comment envoyer le rapport à mon client ?', r: "Dans l'intervention, cliquez sur « Envoyer au client ». Votre messagerie s'ouvre avec un email pré-rempli. Joignez le PDF que vous avez généré." },
                  { q: 'Comment relancer mes clients pour l\'entretien ?', r: "Dans l'onglet « Clients à recontacter », retrouvez les clients sans intervention récente et envoyez-leur un SMS de relance pré-rempli en un clic (pratique pour les entretiens réguliers)." },
                  { q: 'Comment marquer une intervention comme terminée ?', r: "Ouvrez l'intervention et cliquez sur « Clôturer l'intervention ». Elle apparaîtra en vert sur le planning et dans l'historique." },
                  { q: 'Où trouver mes factures d\'abonnement ?', r: "Dans « Mon compte », section « Mes factures », cliquez sur « Accéder à mes factures » pour ouvrir votre espace de facturation sécurisé Stripe." },
                  { q: 'Comment résilier mon abonnement ?', r: "Depuis votre espace de facturation (Mon compte → Mes factures), vous pouvez gérer ou annuler votre abonnement à tout moment, sans engagement." },
                  { q: 'Mes données sont-elles sécurisées ?', r: "Oui. Vos mots de passe sont chiffrés et vos données sont protégées. Elles ne sont jamais revendues à des tiers, conformément au RGPD." }
                ]).map((item, i) => (
                  <details key={i} style={{ borderBottom: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0', paddingBottom: '8px' }}>
                    <summary style={{ cursor: 'pointer', padding: '12px 0', fontWeight: 600, fontSize: '15px', listStyle: 'none' }}>{item.q}</summary>
                    <p style={{ fontSize: '14px', color: '#86868b', lineHeight: '1.6', padding: '0 0 8px' }}>{item.r}</p>
                  </details>
                ))}
              </div>
              <p style={{ fontSize: '14px', color: '#86868b', marginTop: '20px' }}>
                Besoin d'aide supplémentaire ? Contactez-nous à <a href="mailto:contact@kreeswaves.fr" style={{ color: '#2563eb' }}>contact@kreeswaves.fr</a>
              </p>
            </div>

            {/* FORMULAIRE CONTACT SUPPORT */}
            <div style={{ marginTop: '20px', padding: '28px', borderRadius: '18px', background: darkMode ? '#1c1c1e' : '#fff', boxShadow: darkMode ? 'none' : '0 4px 20px rgba(0,0,0,0.06)', border: darkMode ? '1px solid #2c2c2e' : '1px solid #f0f0f0' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>✉️ Nous contacter</h3>
              <p style={{ fontSize: '14px', color: '#86868b', marginBottom: '20px' }}>Une question, un bug, une suggestion ? Envoyez-nous un message, nous vous répondrons par email.</p>
              <div style={{ display: 'grid', gap: '12px' }}>
                <input
                  type="text"
                  placeholder="Sujet de votre demande"
                  value={ticketSubject}
                  onChange={(e) => setTicketSubject(e.target.value)}
                  style={{ padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '15px', boxSizing: 'border-box' }}
                />
                <textarea
                  placeholder="Décrivez votre demande en détail..."
                  value={ticketMessage}
                  onChange={(e) => setTicketMessage(e.target.value)}
                  style={{ padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '15px', minHeight: '120px', boxSizing: 'border-box', fontFamily: 'inherit' }}
                />
                <button
                  onClick={createTicket}
                  style={{ padding: '14px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '15px' }}>
                  Envoyer ma demande
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* MODAL INTERVENTION */}
      {showModalIntervention && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: darkMode ? '#1f2937' : '#fff', borderRadius: '20px 20px 0 0', padding: '24px 20px', maxWidth: '600px', width: '100%', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 -8px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: darkMode ? '#fff' : '#000', margin: 0 }}>
                {modalMode === 'create' ? '➕ Nouvelle Intervention' : '✏️ Rapport d\'Intervention'}
              </h2>
              <button onClick={closeModal} style={{ background: darkMode ? '#374151' : '#f3f4f6', border: 'none', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', fontSize: '18px', color: darkMode ? '#fff' : '#374151', flexShrink: 0 }}>✕</button>
            </div>

            {/* SÉLECTEUR DE TYPE (à la création, sauf DJ) */}
            {modalMode === 'create' && isArtisan && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                <button onClick={() => { setFormTypeMission('rdv'); setActiveModalTab('infos'); }} style={{ padding: '16px', borderRadius: '14px', border: formTypeMission === 'rdv' ? '2px solid #2563eb' : `1px solid ${darkMode ? '#2c2c2e' : '#d2d2d7'}`, background: formTypeMission === 'rdv' ? '#eef4ff' : 'transparent', color: formTypeMission === 'rdv' ? '#2563eb' : (darkMode ? '#fff' : '#6e6e73'), cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>
                  📅 Prise de RDV
                  <span style={{ display: 'block', fontSize: '11px', fontWeight: 400, marginTop: '4px', opacity: 0.8 }}>Planifier pour plus tard</span>
                </button>
                <button onClick={() => { setFormTypeMission('intervention'); }} style={{ padding: '16px', borderRadius: '14px', border: formTypeMission === 'intervention' ? '2px solid #2563eb' : `1px solid ${darkMode ? '#2c2c2e' : '#d2d2d7'}`, background: formTypeMission === 'intervention' ? '#eef4ff' : 'transparent', color: formTypeMission === 'intervention' ? '#2563eb' : (darkMode ? '#fff' : '#6e6e73'), cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>
                  🔧 Intervention immédiate
                  <span style={{ display: 'block', fontSize: '11px', fontWeight: 400, marginTop: '4px', opacity: 0.8 }}>Rapport complet maintenant</span>
                </button>
              </div>
            )}

            {/* ONGLETS MODAL (DJ = seulement Infos ; RDV = seulement Infos) */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: `2px solid ${darkMode ? '#374151' : '#e5e7eb'}`, overflowX: 'auto' }}>
              {((isDJ || formTypeMission === 'rdv')
                ? [{ id: 'infos', label: '📋 Infos' }]
                : isBeaute
                ? [
                  { id: 'infos', label: '📋 Infos' },
                  { id: 'checklist', label: '💄 Produits utilisés' },
                  { id: 'photos', label: '📸 Photos avant/après' }
                ]
                : [
                  { id: 'infos', label: '📋 Infos' },
                  { id: 'rapport', label: '📝 Rapport' },
                  { id: 'checklist', label: '✅ Check-list' },
                  { id: 'photos', label: '📸 Photos' },
                  { id: 'signatures', label: '✍️ Signatures' }
                ]
              ).map(t => (
                <button
                  key={t.id}
                  onClick={() => setActiveModalTab(t.id)}
                  style={{
                    padding: '10px 14px',
                    background: activeModalTab === t.id ? '#2563eb' : 'transparent',
                    color: activeModalTab === t.id ? '#fff' : (darkMode ? '#9ca3af' : '#6b7280'),
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    cursor: 'pointer',
                    fontWeight: activeModalTab === t.id ? 'bold' : '500',
                    fontSize: '13px',
                    whiteSpace: 'nowrap'
                  }}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* Bouton démarrer l'intervention (pour un RDV en mode édition) */}
            {modalMode === 'edit' && formTypeMission === 'rdv' && (
              <div style={{ marginBottom: '20px', padding: '16px', borderRadius: '14px', background: darkMode ? '#1e3a8a22' : '#eef4ff', border: '1px solid #2563eb' }}>
                <p style={{ fontSize: '14px', color: darkMode ? '#fff' : '#1e3a8a', marginBottom: '12px' }}>📅 Ceci est un RDV planifié. Le technicien est sur place ?</p>
                <button onClick={() => { setFormTypeMission('intervention'); setActiveModalTab('rapport'); }} style={{ padding: '12px 24px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>
                  🔧 Démarrer l'intervention
                </button>
              </div>
            )}

            {/* Suivi de position vers le client */}
            {modalMode === 'edit' && selectedIntervention && (
              trackingId === selectedIntervention.id ? (
                <div style={{ marginBottom: '20px', padding: '14px 16px', borderRadius: '14px', background: '#10b98118', border: '1px solid #10b981', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: darkMode ? '#fff' : '#065f46' }}>🚗 Vous partagez votre position en direct</span>
                  <button onClick={() => stopTracking(true)} style={{ padding: '8px 16px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 700, fontSize: '13px' }}>✅ Je suis arrivé</button>
                </div>
              ) : (
                <button onClick={() => startTracking(selectedIntervention)} style={{ width: '100%', marginBottom: '20px', padding: '12px', background: darkMode ? '#0f2f24' : '#ecfdf5', color: '#059669', border: '1px solid #10b981', borderRadius: '14px', cursor: 'pointer', fontWeight: 700, fontSize: '14px' }}>
                  🚗 Je pars — partager ma position au client (suivi type Uber)
                </button>
              )
            )}

            {/* ONGLET INFOS */}
            {activeModalTab === 'infos' && (
              <div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }}>
                {clientsList.length > 0 && (
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: 'bold', color: darkMode ? '#fff' : '#374151', display: 'block', marginBottom: '6px' }}>📇 Choisir un client existant</label>
                    <select value={formClientId} onChange={(e) => applyClientToForm(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #2563eb', fontSize: '14px', background: darkMode ? '#111827' : '#fff', color: darkMode ? '#fff' : '#000' }}>
                      <option value="">— Saisie manuelle —</option>
                      {clientsList.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.type === 'entreprise' && c.entreprise ? c.entreprise : c.nom}{c.adresse ? ` — ${c.adresse}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* 🧠 Mémoire client : infos importantes à ne jamais oublier avant d'y aller */}
                {(() => {
                  const ficheActive = clientsList.find(c => String(c.id) === String(formClientId));
                  if (!ficheActive?.notes) return null;
                  return (
                    <div style={{ padding: '14px 16px', borderRadius: '14px', background: darkMode ? '#3a2a0a' : '#fffbeb', border: '1.5px solid #f59e0b', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '20px', flexShrink: 0 }}>⚠️</span>
                      <div>
                        <p style={{ fontSize: '12px', fontWeight: 800, color: '#b45309', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>À savoir avant d'y aller</p>
                        <p style={{ fontSize: '14px', color: darkMode ? '#fde68a' : '#78350f', margin: 0, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{ficheActive.notes}</p>
                      </div>
                    </div>
                  );
                })()}

                <div>
                  <label style={{ fontSize: '13px', fontWeight: 'bold', color: darkMode ? '#fff' : '#374151', display: 'block', marginBottom: '6px' }}>📝 {isDJ ? 'Titre du booking' : metierEffectif === 'beaute' ? 'Titre du rendez-vous' : metierEffectif === 'aide_domicile' ? 'Titre du passage' : 'Titre de l\'intervention'}</label>
                  <input type="text" placeholder={isDJ ? 'Ex: Mariage Sophie & Marc' : metierEffectif === 'beaute' ? 'Ex: Coupe Madame Martin' : metierEffectif === 'aide_domicile' ? 'Ex: Passage chez Mme Dupont' : "Titre de l'intervention"} value={formTitre} onChange={(e) => setFormTitre(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 'bold', color: darkMode ? '#fff' : '#374151', display: 'block', marginBottom: '6px' }}>👤 Client</label>
                  <input type="text" placeholder="Nom du client" value={formClient} onChange={(e) => setFormClient(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 'bold', color: darkMode ? '#fff' : '#374151', display: 'block', marginBottom: '6px' }}>📍 Lieu / Adresse</label>
                  <AddressAutocomplete placeholder="Adresse de l'intervention" value={formAdresse} onChange={setFormAdresse} onSelectCoords={(lat, lng) => { setFormLat(lat); setFormLng(lng); }} darkMode={darkMode} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                  {formAdresse && (
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(formAdresse)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '10px', padding: '10px 18px', background: '#2563eb', color: '#fff', borderRadius: '980px', textDecoration: 'none', fontWeight: 600, fontSize: '14px' }}>
                      🧭 Itinéraire
                    </a>
                  )}
                </div>

                {/* Intervenant principal — libellé adapté au métier */}
                {(() => {
                  const labelPrincipal = isDJ ? '🎧 Intervenant assigné' : metierEffectif === 'beaute' ? '💇 Collaborateur assigné' : metierEffectif === 'aide_domicile' ? '🏠 Intervenant assigné' : '🔧 Technicien principal';
                  const motMembre = isDJ ? 'intervenant' : (metierEffectif === 'beaute' || metierEffectif === 'aide_domicile') ? 'collaborateur' : 'technicien';
                  return (
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 'bold', color: darkMode ? '#fff' : '#374151', display: 'block', marginBottom: '6px' }}>{labelPrincipal}</label>
                  {team.length > 0 ? (
                    <select value={formTechnicien} onChange={(e) => setFormTechnicien(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', background: darkMode ? '#111827' : '#fff', color: darkMode ? '#fff' : '#000' }}>
                      <option value="">— Choisir un {motMembre} —</option>
                      {team.map(t => (
                        <option key={t.id} value={t.name}>{t.name}{t.role ? ` (${t.role})` : ''}</option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" placeholder={`Nom du ${motMembre}`} value={formTechnicien} onChange={(e) => setFormTechnicien(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                  )}
                  {team.length === 0 && (
                    <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>💡 Ajoute des membres dans l'onglet Équipe pour les sélectionner ici.</p>
                  )}
                </div>
                  );
                })()}

                {/* Intervenants supplémentaires */}
                {team.length > 0 && (
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: 'bold', color: darkMode ? '#fff' : '#374151', display: 'block', marginBottom: '6px' }}>➕ {isDJ ? 'Autres intervenants' : (metierEffectif === 'beaute' || metierEffectif === 'aide_domicile') ? 'Autres collaborateurs' : 'Techniciens supplémentaires'}</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {team.filter(t => t.name !== formTechnicien).map(t => {
                        const isSelected = formTechniciensExtra.includes(t.name);
                        return (
                          <button
                            key={t.id}
                            onClick={() => {
                              if (isSelected) setFormTechniciensExtra(formTechniciensExtra.filter(n => n !== t.name));
                              else setFormTechniciensExtra([...formTechniciensExtra, t.name]);
                            }}
                            style={{ padding: '8px 14px', borderRadius: '20px', border: isSelected ? '2px solid #2563eb' : '2px solid #e5e7eb', background: isSelected ? '#dbeafe' : 'transparent', color: isSelected ? '#2563eb' : '#6b7280', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
                            {isSelected ? '✓ ' : '+ '}{t.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Véhicule + Nature (artisan) OU Prestation (beauté) OU Type d'événement (DJ) */}
                {isBeaute ? (
                  <div>
                    <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '6px' }}>{metierEffectif === 'aide_domicile' ? '🏠 Type d\'intervention' : '💇 Prestation'}</label>
                    <input type="text" placeholder={metierEffectif === 'aide_domicile' ? "Ex: Ménage, Repassage, Courses..." : "Ex: Coupe + brushing, Coloration..."} value={formNature} onChange={(e) => setFormNature(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                  </div>
                ) : !isDJ ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '6px' }}>🚚 Véhicule utilisé</label>
                      <input type="text" placeholder="Ex: Nissan Cabstar" value={formVehicule} onChange={(e) => setFormVehicule(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '6px' }}>🛠️ Nature de la demande</label>
                      <input type="text" placeholder="Ex: Dégorgement" value={formNature} onChange={(e) => setFormNature(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                    </div>
                  </div>
                ) : (
                  <div>
                    <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '6px' }}>🎉 Type d'événement</label>
                    <select value={formNature} onChange={(e) => setFormNature(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', background: darkMode ? '#111827' : '#fff', color: darkMode ? '#fff' : '#000', boxSizing: 'border-box' }}>
                      <option value="">— Type d'événement —</option>
                      <option value="Mariage">💍 Mariage</option>
                      <option value="Club">🎶 Club / Soirée</option>
                      <option value="Privé">🏠 Événement privé</option>
                      <option value="Entreprise">🏢 Soirée entreprise</option>
                      <option value="Bar">🍸 Bar / Lounge</option>
                      <option value="Festival">🎪 Festival</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                )}

                {/* Heure planifiée + Durée (masqués en DJ et beauté) */}
                {isArtisan ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '6px' }}>Heure planifiée</label>
                      <input type="time" value={formHeure} onChange={(e) => setFormHeure(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                      <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '6px' }}>Durée (min)</label>
                      <input type="number" value={formDuree} onChange={(e) => setFormDuree(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                    </div>
                  </div>
                ) : null}

                {/* Récurrence (création uniquement) */}
                {modalMode === 'create' && (
                  <div style={{ background: darkMode ? '#111827' : '#f8faff', borderRadius: '12px', padding: '14px 16px' }}>
                    <label style={{ fontSize: '13px', fontWeight: 700, color: darkMode ? '#fff' : '#374151', display: 'block', marginBottom: '8px' }}>🔁 Récurrence</label>
                    <div style={{ display: 'grid', gridTemplateColumns: formRecurrence === 'none' ? '1fr' : '2fr 1fr', gap: '10px' }}>
                      <select value={formRecurrence} onChange={(e) => setFormRecurrence(e.target.value)} style={{ width: '100%', padding: '11px', borderRadius: '10px', border: '1px solid #d2d2d7', fontSize: '14px', background: darkMode ? '#1c1c1e' : '#fff', color: darkMode ? '#fff' : '#000' }}>
                        <option value="none">Aucune (une seule fois)</option>
                        <option value="weekly">Chaque semaine</option>
                        <option value="biweekly">Toutes les 2 semaines</option>
                        <option value="monthly">Chaque mois</option>
                      </select>
                      {formRecurrence !== 'none' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <input type="number" min="2" max="52" value={formRecurrenceCount} onChange={(e) => setFormRecurrenceCount(e.target.value)} style={{ width: '100%', padding: '11px', borderRadius: '10px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                          <span style={{ fontSize: '13px', color: '#6b7280', whiteSpace: 'nowrap' }}>fois</span>
                        </div>
                      )}
                    </div>
                    {formRecurrence !== 'none' && (
                      <p style={{ fontSize: '12px', color: '#9ca3af', margin: '8px 0 0' }}>Crée automatiquement {formRecurrenceCount} interventions identiques à intervalle régulier.</p>
                    )}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '6px' }}>🕐 {(isDJ || isBeaute) ? 'Début' : 'Heure d\'arrivée'}</label>
                    <input type="time" value={formHeureArrivee} onChange={(e) => setFormHeureArrivee(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '6px' }}>🕐 {(isDJ || isBeaute) ? 'Fin' : 'Heure de départ'}</label>
                    <input type="time" value={formHeureDepart} onChange={(e) => setFormHeureDepart(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
                  </div>
                </div>

                {/* Minuteur */}
                <div style={{ background: darkMode ? '#111827' : '#f3f4f6', padding: '16px', borderRadius: '12px', textAlign: 'center' }}>
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>⏱️ Minuteur (temps réel sur place)</p>
                  <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#2563eb', marginBottom: '16px', fontFamily: 'monospace' }}>
                    {formatTimer(timerSeconds)}
                  </div>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                    <button onClick={() => setTimerRunning(!timerRunning)} style={{ padding: '10px 24px', background: timerRunning ? '#ef4444' : '#10b981', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                      {timerRunning ? '⏸️ Pause' : '▶️ Start'}
                    </button>
                    <button onClick={() => { setTimerSeconds(0); setTimerRunning(false); }} style={{ padding: '10px 24px', background: '#6b7280', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                      🔄 Reset
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ONGLET RAPPORT */}
            {activeModalTab === 'rapport' && (
              <div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }}>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 'bold', color: darkMode ? '#fff' : '#374151', display: 'block', marginBottom: '6px' }}>🔧 Travaux réalisés</label>
                  <textarea placeholder="Décrivez les travaux effectués..." value={formTravaux} onChange={(e) => setFormTravaux(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', minHeight: '80px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 'bold', color: darkMode ? '#fff' : '#374151', display: 'block', marginBottom: '6px' }}>💬 Observations</label>
                  <textarea placeholder="Remarques, commentaires..." value={formObservations} onChange={(e) => setFormObservations(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', minHeight: '70px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 'bold', color: darkMode ? '#fff' : '#374151', display: 'block', marginBottom: '6px' }}>🧰 Matériel utilisé</label>
                  <textarea placeholder="Liste du matériel..." value={formMateriel} onChange={(e) => setFormMateriel(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', minHeight: '60px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 'bold', color: darkMode ? '#fff' : '#374151', display: 'block', marginBottom: '6px' }}>🔩 Pièces remplacées</label>
                  <textarea placeholder="Pièces changées..." value={formPieces} onChange={(e) => setFormPieces(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', minHeight: '60px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 'bold', color: darkMode ? '#fff' : '#374151', display: 'block', marginBottom: '6px' }}>📊 Bilan de l'intervention</label>
                  <textarea placeholder="Conclusion, recommandations (ex: prévoir passage caméra si récurrent)..." value={formBilan} onChange={(e) => setFormBilan(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', minHeight: '70px', boxSizing: 'border-box' }} />
                </div>
              </div>
            )}

            {/* ONGLET CHECKLIST */}
            {activeModalTab === 'checklist' && (
              <div style={{ marginBottom: '24px' }}>
                {metierEffectif === 'beaute' && (
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>Notez les produits appliqués lors de ce rendez-vous (coloration, soin, marque...). Pratique pour vous souvenir d'une fois sur l'autre.</p>
                )}
                {metierEffectif === 'aide_domicile' && (
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>Cochez les tâches effectuées lors de cette intervention (ménage, repassage, courses...).</p>
                )}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                  <input
                    type="text"
                    placeholder={metierEffectif === 'beaute' ? "Ajouter un produit utilisé..." : "Ajouter une tâche..."}
                    value={newChecklistItem}
                    onChange={(e) => setNewChecklistItem(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') addChecklistItem(); }}
                    style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px' }}
                  />
                  <button onClick={addChecklistItem} style={{ padding: '12px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>➕</button>
                </div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {formChecklist.length === 0 && (
                    <p style={{ fontSize: '13px', color: '#6b7280', textAlign: 'center', padding: '20px' }}>{metierEffectif === 'beaute' ? 'Aucun produit noté. Ajoutez-en un ci-dessus.' : 'Aucune tâche. Ajoutez-en une ci-dessus.'}</p>
                  )}
                  {formChecklist.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', background: darkMode ? '#111827' : '#f3f4f6', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <button onClick={() => moveChecklistItem(idx, -1)} disabled={idx === 0} style={{ background: 'none', border: 'none', cursor: idx === 0 ? 'default' : 'pointer', color: idx === 0 ? '#d1d5db' : '#6b7280', fontSize: '12px', lineHeight: 1, padding: '2px' }}>▲</button>
                        <button onClick={() => moveChecklistItem(idx, 1)} disabled={idx === formChecklist.length - 1} style={{ background: 'none', border: 'none', cursor: idx === formChecklist.length - 1 ? 'default' : 'pointer', color: idx === formChecklist.length - 1 ? '#d1d5db' : '#6b7280', fontSize: '12px', lineHeight: 1, padding: '2px' }}>▼</button>
                      </div>
                      {metierEffectif === 'beaute' ? (
                        <span style={{ fontSize: '16px' }}>💄</span>
                      ) : (
                        <input type="checkbox" checked={item.done} onChange={() => toggleChecklistItem(idx)} style={{ width: '20px', height: '20px', cursor: 'pointer' }} />
                      )}
                      <span style={{ flex: 1, fontSize: '14px', textDecoration: (metierEffectif !== 'beaute' && item.done) ? 'line-through' : 'none', color: (metierEffectif !== 'beaute' && item.done) ? '#9ca3af' : (darkMode ? '#fff' : '#000') }}>{item.text}</span>
                      <button onClick={() => removeChecklistItem(idx)} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '18px', cursor: 'pointer' }}>✕</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ONGLET PHOTOS */}
            {activeModalTab === 'photos' && (
              <div style={{ marginBottom: '24px' }}>
                {/* Sélecteur de catégorie pour la prochaine photo */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 'bold', color: darkMode ? '#fff' : '#374151', display: 'block', marginBottom: '8px' }}>Catégorie de la prochaine photo :</label>
                  <div style={{ display: 'grid', gridTemplateColumns: metierEffectif === 'beaute' ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: '8px' }}>
                    {(metierEffectif === 'beaute' ? [
                      { id: 'avant', label: '⬅️ Avant', color: '#f59e0b' },
                      { id: 'apres', label: '✨ Après', color: '#10b981' }
                    ] : [
                      { id: 'avant', label: '⬅️ Avant', color: '#f59e0b' },
                      { id: 'pendant', label: '🔧 Pendant', color: '#2563eb' },
                      { id: 'apres', label: '✅ Après', color: '#10b981' }
                    ]).map(cat => (
                      <button key={cat.id} onClick={() => setPhotoCategorie(cat.id)} style={{ padding: '10px', borderRadius: '8px', border: photoCategorie === cat.id ? `2px solid ${cat.color}` : '2px solid #e5e7eb', background: photoCategorie === cat.id ? cat.color : 'transparent', color: photoCategorie === cat.id ? '#fff' : '#6b7280', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}>
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>

                <label style={{ display: 'block', padding: '16px', border: '2px dashed #2563eb', borderRadius: '8px', textAlign: 'center', cursor: 'pointer', background: darkMode ? '#111827' : '#f3f4f6', marginBottom: '16px' }}>
                  <input type="file" accept="image/*" onChange={handlePhotoUpload} disabled={uploadingPhoto} style={{ display: 'none' }} />
                  {uploadingPhoto ? '📤 Upload...' : `📸 Ajouter une photo (${photoCategorie})`}
                </label>

                {/* Liste des photos avec légende et catégorie éditables */}
                <div style={{ display: 'grid', gap: '12px' }}>
                  {photos.length === 0 && (
                    <p style={{ fontSize: '13px', color: '#6b7280', textAlign: 'center', padding: '12px' }}>Aucune photo pour l'instant.</p>
                  )}
                  {photos.map((photo, idx) => {
                    const catColors = { avant: '#f59e0b', pendant: '#2563eb', apres: '#10b981' };
                    return (
                      <div key={idx} style={{ display: 'flex', gap: '12px', padding: '12px', background: darkMode ? '#111827' : '#f9fafb', borderRadius: '8px', alignItems: 'flex-start' }}>
                        <img src={photo.url} alt="" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <select value={photo.categorie} onChange={(e) => updatePhotoCategorie(idx, e.target.value)} style={{ padding: '6px 10px', borderRadius: '6px', border: 'none', background: catColors[photo.categorie] || '#2563eb', color: '#fff', fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', cursor: 'pointer' }}>
                            <option value="avant">⬅️ Avant</option>
                            <option value="pendant">🔧 Pendant</option>
                            <option value="apres">✅ Après</option>
                          </select>
                          <input type="text" placeholder="Légende (ex: Passage du HP sur tampon...)" value={photo.legende || ''} onChange={(e) => updatePhotoLegende(idx, e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '2px solid #e5e7eb', fontSize: '13px', boxSizing: 'border-box' }} />
                        </div>
                        <button onClick={() => removePhoto(idx)} style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer', fontWeight: 'bold', flexShrink: 0 }}>✕</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ONGLET SIGNATURES */}
            {activeModalTab === 'signatures' && (
              <div style={{ display: 'grid', gap: '24px', marginBottom: '24px' }}>
                <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>✍️ Faites signer avec le doigt (sur mobile/tablette) ou la souris.</p>
                <SignaturePad value={signatureTech} onChange={setSignatureTech} label="🔧 Signature du technicien" darkMode={darkMode} />
                <SignaturePad value={signatureClient} onChange={setSignatureClient} label="👤 Signature du client" darkMode={darkMode} />
              </div>
            )}

            {/* BOUTONS FINAUX : visibles seulement après signature (sauf DJ) */}
            {modalMode === 'edit' && isArtisan && formTypeMission !== 'rdv' && !(signatureClient || signatureTech) && (
              <div style={{ padding: '14px', borderRadius: '12px', background: darkMode ? '#1c1c1e' : '#fff7ed', border: '1px solid #f59e0b', marginBottom: '12px', textAlign: 'center' }}>
                <p style={{ fontSize: '13px', color: darkMode ? '#fbbf24' : '#92400e' }}>✍️ Faites signer le client (onglet Signatures) pour débloquer la génération du rapport, la clôture et la facturation.</p>
              </div>
            )}

            {/* MODE DJ : juste générer une facture pour le booking */}
            {modalMode === 'edit' && isDJ && (
              <button
                onClick={createFactureFromIntervention}
                style={{ width: '100%', marginBottom: '12px', padding: '14px', background: '#a855f7', color: '#fff', fontWeight: 'bold', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '15px' }}>
                🧾 Générer une facture pour ce booking
              </button>
            )}

            {/* BOUTON PDF (édition + signature présente, sauf DJ) */}
            {modalMode === 'edit' && !isDJ && (isBeaute || signatureClient || signatureTech) && (
              <button
                onClick={generatePDF}
                disabled={generatingPDF}
                style={{ width: '100%', marginBottom: '12px', padding: '14px', background: generatingPDF ? '#ccc' : 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)', color: '#fff', fontWeight: 'bold', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '15px' }}>
                {generatingPDF ? '⏳ Génération...' : '📄 Générer le Rapport PDF'}
              </button>
            )}

            {/* BOUTON CLOTURER */}
            {modalMode === 'edit' && selectedIntervention && (isDJ || isBeaute || signatureClient || signatureTech) && (
              <button
                onClick={() => toggleCloture(selectedIntervention)}
                style={{ width: '100%', marginBottom: '12px', padding: '14px', background: selectedIntervention.statut === 'termine' ? '#f59e0b' : '#10b981', color: '#fff', fontWeight: 'bold', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '15px' }}>
                {selectedIntervention.statut === 'termine'
                  ? `↩️ Rouvrir ${isDJ ? 'le booking' : 'l\'intervention'}`
                  : `✅ Clôturer ${isDJ ? 'le booking' : 'l\'intervention'}`}
              </button>
            )}

            {/* Replay GPS : trajet réellement enregistré vers cette intervention.
                Aucune restriction de métier — le bouton "Je pars" qui enregistre
                le trajet n'en a pas non plus, donc le restreindre ici créait un
                trou : la donnée s'enregistrait pour tout le monde mais ne
                pouvait être revue que par artisan/paysagiste. */}
            {modalMode === 'edit' && selectedIntervention && selectedIntervention.statut === 'termine' && (
              <button
                onClick={() => setReplayIntervention(selectedIntervention)}
                style={{ width: '100%', marginBottom: '12px', padding: '12px', background: 'transparent', color: '#2563eb', fontWeight: 700, borderRadius: '8px', border: '1.5px solid #2563eb40', cursor: 'pointer', fontSize: '14px' }}>
                🧭 Voir le trajet réel
              </button>
            )}

            {/* Carnet de vie du bâtiment : lien/QR à imprimer et coller sur place */}
            {modalMode === 'edit' && selectedIntervention && selectedIntervention.statut === 'termine' && selectedIntervention.batiment_id && (
              <button
                onClick={() => setBatimentQrId(selectedIntervention.batiment_id)}
                style={{ width: '100%', marginBottom: '12px', padding: '12px', background: 'transparent', color: '#7c3aed', fontWeight: 700, borderRadius: '8px', border: '1.5px solid #7c3aed40', cursor: 'pointer', fontSize: '14px' }}>
                🏠 Carnet de vie du bâtiment (QR à coller)
              </button>
            )}

            {/* BOUTON SMS LIEN AVIS (après clôture) — ouvre l'app SMS du téléphone,
                message pré-rempli, envoyé par le pro lui-même via son propre forfait
                (gratuit pour PlanifPro, pas de service tiers type Twilio à payer/configurer). */}
            {modalMode === 'edit' && selectedIntervention && selectedIntervention.statut === 'termine' && selectedIntervention.tel_client && !isAgent && (
              <a
                href={(() => {
                  const reviewUrl = `${window.location.origin}/avis/${user.id}`;
                  const msg = `Bonjour ${selectedIntervention.client || ''}, merci pour votre confiance ! Laissez-nous un avis en 30 secondes : ${reviewUrl}`;
                  const tel = (selectedIntervention.tel_client || '').replace(/[^0-9+]/g, '');
                  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                  return `sms:${tel}${isIOS ? '&' : '?'}body=${encodeURIComponent(msg)}`;
                })()}
                style={{ display: 'block', textAlign: 'center', width: '100%', marginBottom: '12px', padding: '13px', background: '#0ea5e9', color: '#fff', fontWeight: 700, borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px', textDecoration: 'none', boxSizing: 'border-box' }}>
                💬 SMS lien d'avis au client
              </a>
            )}

            {/* BOUTONS EMAIL + FACTURE (édition + signature présente, sauf DJ) */}
            {modalMode === 'edit' && !isDJ && (isBeaute || signatureClient || signatureTech) && (
              <div style={{ display: 'grid', gridTemplateColumns: isAgent ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <button
                  onClick={sendByEmail}
                  style={{ padding: '14px', background: '#0ea5e9', color: '#fff', fontWeight: 'bold', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px' }}>
                  📧 Envoyer au client
                </button>
                {!isAgent && (
                  <button
                    onClick={createFactureFromIntervention}
                    style={{ padding: '14px', background: '#a855f7', color: '#fff', fontWeight: 'bold', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px' }}>
                    🧾 Générer une facture
                  </button>
                )}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <button
                onClick={saveIntervention}
                disabled={savingIntervention}
                style={{ padding: '14px', background: savingIntervention ? '#93c5fd' : '#2563eb', color: '#fff', fontWeight: 600, borderRadius: '980px', border: 'none', cursor: savingIntervention ? 'not-allowed' : 'pointer' }}>
                {savingIntervention ? 'Enregistrement...' : `✅ ${modalMode === 'create' ? 'Créer' : 'Sauvegarder'}`}
              </button>
              <button 
                onClick={closeModal}
                style={{ padding: '14px', background: '#6b7280', color: '#fff', fontWeight: 'bold', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
                ❌ Annuler
              </button>
            </div>

            {modalMode === 'edit' && selectedIntervention && (
              <button 
                onClick={() => deleteIntervention(selectedIntervention.id)}
                style={{ width: '100%', marginTop: '12px', padding: '14px', background: '#ef4444', color: '#fff', fontWeight: 'bold', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
                🗑️ Supprimer
              </button>
            )}
          </div>
        </div>
      )}

      {/* MODAL FICHE CLIENT */}
      {showModalClient && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: darkMode ? '#1f2937' : '#fff', borderRadius: '20px 20px 0 0', padding: '24px 20px', maxWidth: '500px', width: '100%', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 -8px 40px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: '22px', fontWeight: 'bold', marginBottom: '24px', color: darkMode ? '#fff' : '#000' }}>
              {editingClientId ? '✏️ Éditer la fiche client' : '➕ Nouvelle fiche client'}
            </h2>

            <div style={{ display: 'grid', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '13px', fontWeight: 'bold', color: darkMode ? '#fff' : '#374151', display: 'block', marginBottom: '6px' }}>Type de client</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <button onClick={() => setCliType('particulier')} style={{ padding: '12px', borderRadius: '8px', border: cliType === 'particulier' ? '2px solid #2563eb' : '2px solid #e5e7eb', background: cliType === 'particulier' ? '#dbeafe' : 'transparent', color: cliType === 'particulier' ? '#2563eb' : '#6b7280', cursor: 'pointer', fontWeight: 'bold' }}>🏠 Particulier</button>
                  <button onClick={() => setCliType('entreprise')} style={{ padding: '12px', borderRadius: '8px', border: cliType === 'entreprise' ? '2px solid #2563eb' : '2px solid #e5e7eb', background: cliType === 'entreprise' ? '#dbeafe' : 'transparent', color: cliType === 'entreprise' ? '#2563eb' : '#6b7280', cursor: 'pointer', fontWeight: 'bold' }}>🏢 Entreprise</button>
                </div>
              </div>

              <input type="text" placeholder={cliType === 'entreprise' ? 'Nom du contact' : 'Nom complet'} value={cliNom} onChange={(e) => setCliNom(e.target.value)} style={{ padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px' }} />

              {cliType === 'entreprise' && (
                <>
                  <input type="text" placeholder="Nom de l'entreprise" value={cliEntreprise} onChange={(e) => setCliEntreprise(e.target.value)} style={{ padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px' }} />
                  <input type="text" placeholder="Numéro SIRET" value={cliSiret} onChange={(e) => setCliSiret(e.target.value)} style={{ padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px' }} />
                </>
              )}

              <AddressAutocomplete placeholder="Adresse complète" value={cliAdresse} onChange={setCliAdresse} onSelectCoords={(lat, lng) => { setCliLat(lat); setCliLng(lng); }} darkMode={darkMode} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', boxSizing: 'border-box' }} />
              <input type="tel" placeholder="Téléphone" value={cliTel} onChange={(e) => setCliTel(e.target.value)} style={{ padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px' }} />
              <input type="email" placeholder="Email" value={cliEmail} onChange={(e) => setCliEmail(e.target.value)} style={{ padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px' }} />
              <textarea placeholder="Notes (code portail, étage, infos utiles...)" value={cliNotes} onChange={(e) => setCliNotes(e.target.value)} style={{ padding: '12px', borderRadius: '12px', border: '1px solid #d2d2d7', fontSize: '14px', minHeight: '70px', boxSizing: 'border-box' }} />

              <div>
                <label style={{ fontSize: '13px', fontWeight: 'bold', color: darkMode ? '#fff' : '#374151', display: 'block', marginBottom: '6px' }}>🖼️ Logo (apparaîtra sur le rapport PDF)</label>
                {cliLogo && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <img src={cliLogo} alt="logo" style={{ height: '50px', maxWidth: '120px', objectFit: 'contain', borderRadius: '6px', border: '1px solid #e5e7eb', padding: '4px', background: '#fff' }} />
                    <button onClick={() => setCliLogo('')} style={{ padding: '6px 12px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>Retirer</button>
                  </div>
                )}
                <label style={{ display: 'block', padding: '12px', border: '2px dashed #2563eb', borderRadius: '8px', textAlign: 'center', cursor: 'pointer', background: darkMode ? '#111827' : '#f3f4f6', fontSize: '13px' }}>
                  <input type="file" accept="image/*" onChange={handleLogoUpload} disabled={uploadingLogo} style={{ display: 'none' }} />
                  {uploadingLogo ? '📤 Upload...' : (cliLogo ? '🔄 Changer le logo' : '🖼️ Ajouter un logo')}
                </label>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '20px' }}>
              <button onClick={saveClient} disabled={savingClient} style={{ padding: '14px', background: savingClient ? '#93c5fd' : '#2563eb', color: '#fff', fontWeight: 600, borderRadius: '980px', border: 'none', cursor: savingClient ? 'not-allowed' : 'pointer' }}>{savingClient ? 'Enregistrement...' : '✅ Enregistrer'}</button>
              <button onClick={() => setShowModalClient(false)} style={{ padding: '14px', background: '#6b7280', color: '#fff', fontWeight: 'bold', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>❌ Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== NOTIFICATIONS PUSH ===== */}
      {user && !isAgent && !isClientFinal && !isAdmin && (
        <PushNotifications user={user} darkMode={darkMode} />
      )}

      {/* ===== ASSISTANT IA =====
          Mis de côté volontairement (2026-07-13) : Gemini demande maintenant
          un compte Google avec facturation activée, décision en attente côté
          utilisateur. Rien n'est supprimé — composant, edge function et clé
          restent tous fonctionnels. Pour réactiver : repasser AI_ASSISTANT_ACTIVE
          à true ci-dessous, aucune autre modification nécessaire. */}
      {AI_ASSISTANT_ACTIVE && user && !isClientFinal && !isAdmin && (
        <AiAssistant
          user={user}
          dataOwnerId={dataOwnerId}
          metierLabel={user.metier_label || { artisan: 'artisan', dj: 'DJ', beaute: 'professionnel de la beauté', aide_domicile: 'professionnel de l\'aide à domicile', paysagiste: 'paysagiste' }[user.metier] || 'auto-entrepreneur'}
          darkMode={darkMode}
          onActionDone={() => loadClientData(dataOwnerId)}
        />
      )}

      {/* ===== BILAN "WRAPPED" ===== */}
      {showCareerWrapped && (
        <CareerWrapped
          user={user}
          interventions={interventions}
          factures={factures}
          clientsList={clientsList}
          darkMode={darkMode}
          onClose={() => setShowCareerWrapped(false)}
        />
      )}

      {/* ===== CARTE DU JOUR ===== */}
      {showJourneyMap && (
        <JourneyMap
          interventions={interventions.filter(i => i.date === new Date().toISOString().split('T')[0])}
          darkMode={darkMode}
          onClose={() => setShowJourneyMap(false)}
        />
      )}

      {/* ===== REPLAY GPS (trajet réel d'une intervention terminée) ===== */}
      {replayIntervention && (
        <TrajetReplay
          interventionId={replayIntervention.id}
          clientNom={replayIntervention.client}
          darkMode={darkMode}
          onClose={() => setReplayIntervention(null)}
        />
      )}

      {/* ===== CARNET DE VIE DU BÂTIMENT : lien + QR à imprimer ===== */}
      {batimentQrId && (
        <div onClick={() => setBatimentQrId(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,10,26,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, padding: '20px' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: '380px', background: darkMode ? '#1c1c1e' : '#fff', borderRadius: '20px', padding: '28px', textAlign: 'center', boxShadow: '0 24px 70px rgba(0,0,0,0.35)' }}>
            <p style={{ fontSize: '32px', margin: '0 0 8px' }}>🏠</p>
            <h3 style={{ fontSize: '17px', fontWeight: 800, margin: '0 0 8px' }}>Carnet de vie du bâtiment</h3>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 18px', lineHeight: 1.6 }}>Imprimez ce QR code et collez-le sur place (tableau électrique, chaudière...). Le prochain artisan qui intervient ici pourra le scanner et voir qui est déjà passé.</p>
            {batimentQrDataUrl ? (
              <img src={batimentQrDataUrl} alt="QR code du carnet de bâtiment" style={{ width: '200px', height: '200px', margin: '0 auto 18px' }} />
            ) : (
              <p style={{ fontSize: '13px', color: '#9ca3af', margin: '0 0 18px' }}>Génération du QR code...</p>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={async () => { try { await navigator.clipboard.writeText(`${window.location.origin}/batiment/${batimentQrId}`); alert('Lien copié !'); } catch (_) {} }}
                style={{ flex: 1, padding: '11px', background: darkMode ? '#2c2c2e' : '#f3f4f6', color: darkMode ? '#fff' : '#374151', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>
                📋 Copier le lien
              </button>
              <button onClick={() => setBatimentQrId(null)} style={{ flex: 1, padding: '11px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 700, fontSize: '13px' }}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== CONSENTEMENT GÉOLOCALISATION (avant 1re activation du suivi) ===== */}
      {showGeolocConsent && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,10,26,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4000, padding: '20px' }}>
          <div style={{ width: '100%', maxWidth: '460px', background: darkMode ? '#1c1c1e' : '#fff', borderRadius: '20px', padding: '28px', boxShadow: '0 24px 70px rgba(0,0,0,0.35)' }}>
            <p style={{ fontSize: '32px', margin: '0 0 12px', textAlign: 'center' }}>📍</p>
            <h3 style={{ fontSize: '18px', fontWeight: 800, margin: '0 0 12px', textAlign: 'center' }}>Partage de position</h3>
            <div style={{ fontSize: '14px', color: darkMode ? '#d1d5db' : '#374151', lineHeight: 1.7, marginBottom: '20px' }}>
              <p style={{ margin: '0 0 10px' }}>Avant d'activer le suivi, voici exactement ce qui est enregistré :</p>
              <p style={{ margin: '0 0 6px' }}>✅ Votre position est enregistrée <strong>uniquement pendant un trajet actif</strong> ("Je pars" → arrivée chez le client).</p>
              <p style={{ margin: '0 0 6px' }}>❌ Rien n'est jamais enregistré en dehors d'un trajet, ni sur votre temps personnel.</p>
              <p style={{ margin: '0 0 6px' }}>🗑️ Chaque trajet est supprimé automatiquement au bout de 30 jours, sauf s'il est gardé pour un litige précis.</p>
              <p style={{ margin: 0 }}>🔒 Seuls le/la responsable de l'entreprise et vous-même (pour vos propres trajets) pouvez consulter ces données — jamais les autres collègues.</p>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => { setShowGeolocConsent(false); setPendingTrackIntervention(null); }} style={{ flex: 1, padding: '13px', background: 'transparent', color: '#6b7280', border: `1px solid ${darkMode ? '#2c2c2e' : '#d2d2d7'}`, borderRadius: '980px', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}>Annuler</button>
              <button onClick={accepterGeolocConsent} style={{ flex: 1, padding: '13px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '980px', cursor: 'pointer', fontWeight: 700, fontSize: '14px' }}>J'ai compris, activer</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== TUTORIEL PREMIER LANCEMENT ===== */}
      {showTutorial && (
        <Tutorial
          isAgent={isAgent}
          onFinish={() => {
            localStorage.setItem('planifpro_tutorial_done', '1');
            setShowTutorial(false);
            setActiveTab('dashboard');
          }}
          setActiveTab={setActiveTab}
        />
      )}
    </div>
  );
}